import React, { Component, useCallback, useEffect, useRef, useState } from "react";
import RequestAddressFields, { requestAddressText } from "./RequestAddressFields";
import RequestPhoneInput, { formatRequestPhone, isValidRequestPhone, hasUsableRequestAttachment } from "./RequestCustomerFields";
const safeLocalStorageGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn("localStorage 읽기 실패:", key, error);
    return null;
  }
};
const safeLocalStorageSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn("localStorage 저장 실패:", key, error);
  };
};
const safeLocalStorageRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn("localStorage 삭제 실패:", key, error);
  }
};
const safeLocalStorageClear = () => {
  try {
    localStorage.clear();
  } catch (error) {
    console.warn("localStorage 전체 삭제 실패:", error);
  }
};
const parseLocalStorageJson = (key, fallback) => {
  const saved = safeLocalStorageGetItem(key);
  if (!saved) return fallback;
  try {
    return JSON.parse(saved);
  } catch (error) {
    console.warn("localStorage JSON 파싱 실패:", key, error);
    return fallback;
  }
};
const getIsMobileViewport = () => {
  try {
    return typeof window !== "undefined" && window.innerWidth <= 768;
  } catch {
    return false;
  }
};
const isIOSLikeDevice = () => {
  try {
    if (typeof navigator === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  } catch {
    return false;
  }
};
const DASHBOARD_MENU = "대시보드";
const LOGIN_AT_KEY = "rc_login_at";
const LOGIN_TTL_MS = 24 * 60 * 60 * 1000;
const WORK_REQUEST_APP_DATA_KEY = "rc_work_requests";
const WORK_REQUEST_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
const WORK_REQUEST_ATTACHMENT_TOTAL_MAX_BYTES = 15 * 1024 * 1024;
const WORK_REQUEST_SAVE_TIMEOUT_MS = 10000;
const getWorkRequestDetailDataKey = (requestId) => `${WORK_REQUEST_APP_DATA_KEY}/detail/${String(requestId || "")}`;
const getJsonPayloadByteSize = (value) => {
  try {
    return JSON.stringify(value)?.length || 0;
  } catch {
    return -1;
  }
};
const createSaveTimingLogger = (label) => {
  const now = () => {
    try {
      return typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    } catch {
      return Date.now();
    }
  };
  const roundMs = (value) => Math.round(value);
  const startedAt = now();
  let lastAt = startedAt;
  const write = (message, extra) => {
    try {
      if (extra && Object.keys(extra).length > 0) console.log(`[${label}] ${message}`, extra);
      else console.log(`[${label}] ${message}`);
    } catch {
      // Logging must never block the save flow.
    }
  };

  write("start");

  return {
    step: (name, extra = {}) => {
      const current = now();
      const stepMs = roundMs(current - lastAt);
      const totalMs = roundMs(current - startedAt);
      lastAt = current;
      write(`${name}: ${stepMs}ms`, { totalMs, ...extra });
    },
    total: (extra = {}) => {
      const current = now();
      write(`total: ${roundMs(current - startedAt)}ms`, extra);
    },
  };
};
const withTimeout = (promise, timeoutMs, label) => new Promise((resolve, reject) => {
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    reject(new Error(`${label || "operation"} timeout after ${timeoutMs}ms`));
  }, timeoutMs);

  Promise.resolve(promise).then(
    (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    },
    (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(error);
    }
  );
});
const withTimeoutTask = (task, timeoutMs, label) => new Promise((resolve, reject) => {
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    reject(new Error(`${label || "operation"} timeout after ${timeoutMs}ms`));
  }, timeoutMs);

  Promise.resolve().then(task).then(
    (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    },
    (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(error);
    }
  );
});
const SALES_EXCLUDED_STORE_RULES = [
  {
    storeNames: [
      "레컨-대구신천점",
      "레컨-대구신천점(미매핑)",
    ],
    excludeFromDate: null,
  },
  {
    storeNames: [
      "정왕24시점",
      "정왕점",
    ],
    excludeFromDate: "2026-06-18",
  },
  {
    storeNames: [
      "청담점",
    ],
    excludeFromDate: "2026-06-22",
  },
];
const normalizeSalesExcludedStoreName = (value) => String(value || "").trim().replace(/\s/g, "");
const getSalesExcludedRecordDate = (record) => String(record?.date || record?.salesDate || record?.sales_date || "").trim().slice(0, 10).replace(/[./]/g, "-");
const isSalesExcluded = (record) => {
  const storeName = normalizeSalesExcludedStoreName(record?.store || record?.storeName || record?.store_name);
  const recordDate = getSalesExcludedRecordDate(record);
  if (!storeName) return false;

  return SALES_EXCLUDED_STORE_RULES.some((rule) => {
    const storeMatched = rule.storeNames.some((name) => normalizeSalesExcludedStoreName(name) === storeName);
    if (!storeMatched) return false;
    if (!rule.excludeFromDate) return true;
    return Boolean(recordDate) && recordDate >= rule.excludeFromDate;
  });
};
const LOCAL_STORAGE_MIRRORED_COMMON_APP_DATA_KEYS = new Set(["rc_employees"]);
const STALE_SAFE_GRANULAR_APP_DATA_KEYS = new Set(["rc_attendance", "rc_supply_orders", "rc_supply_items", "rc_product_list", "rc_ads", "rc_partners", "rc_production_records", "rc_purchase_records"]);
const GRANULAR_APP_DATA_ID_FIELDS = ["id", "code", "employeeId", "name"];
const CLOSED_ATTENDANCE_STATUSES = new Set(["퇴근완료", "무효처리"]);
const normalizeAttendanceStatus = (row) => String(row?.status || "").trim();
const isOpenAttendanceRecord = (row) => {
  if (!row) return false;
  if (CLOSED_ATTENDANCE_STATUSES.has(normalizeAttendanceStatus(row))) return false;
  const endText = String(row.end || "").trim();
  return !(endText && endText !== "-");
};
const getGranularAppDataItemId = (item) => {
  if (!item || typeof item !== "object") return "";
  const idField = GRANULAR_APP_DATA_ID_FIELDS.find((field) => item[field] !== null && item[field] !== undefined && item[field] !== "");
  return idField ? `${idField}:${String(item[idField])}` : "";
};
const parseGranularAppDataListPayload = (payloadText) => {
  try {
    const parsed = JSON.parse(payloadText || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const scrollToPageTop = () => {
  if (typeof window === "undefined") return;
  window.setTimeout(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, 0);
};
const mergeGranularListMutationOntoLatest = (latestList, previousList, desiredList) => {
  const latest = Array.isArray(latestList) ? latestList : [];
  const previous = Array.isArray(previousList) ? previousList : [];
  const desired = Array.isArray(desiredList) ? desiredList : [];
  const previousById = new Map(previous.map((item) => [getGranularAppDataItemId(item), item]).filter(([id]) => Boolean(id)));
  const desiredById = new Map(desired.map((item) => [getGranularAppDataItemId(item), item]).filter(([id]) => Boolean(id)));
  const removedIds = new Set([...previousById.keys()].filter((id) => !desiredById.has(id)));
  const next = latest.filter((item) => {
    const id = getGranularAppDataItemId(item);
    return !id || !removedIds.has(id);
  });
  const nextIndexById = new Map(next.map((item, index) => [getGranularAppDataItemId(item), index]).filter(([id]) => Boolean(id)));
  const changedItems = desired
    .map((item) => {
      const id = getGranularAppDataItemId(item);
      const previousItem = id ? previousById.get(id) : null;
      const existedBefore = Boolean(id && previousById.has(id));
      const changed = !id || !previousItem || JSON.stringify(previousItem) !== JSON.stringify(item);
      return { id, item, existedBefore, changed };
    })
    .filter(({ changed }) => changed);

  [...changedItems].reverse().forEach(({ id, item, existedBefore }) => {
    if (!id) {
      next.unshift(item);
      return;
    }

    const nextIndex = nextIndexById.get(id);
    if (nextIndex !== undefined) {
      next[nextIndex] = item;
      return;
    }

    if (!existedBefore) {
      next.unshift(item);
    }
  });

  return next;
};
const clearStoredLoginSession = () => {
  ["rc_is_logged_in", "rc_user_role", "rc_current_user", LOGIN_AT_KEY].forEach((key) => {
    safeLocalStorageRemoveItem(key);
  });
};
const getStoredLoginSession = () => {
  try {
    const isStoredLoggedIn = safeLocalStorageGetItem("rc_is_logged_in") === "true";
    const loginAt = Number(safeLocalStorageGetItem(LOGIN_AT_KEY));
    const isValid = isStoredLoggedIn && Number.isFinite(loginAt) && loginAt > 0 && Date.now() - loginAt < LOGIN_TTL_MS;

    if (!isValid) {
      if (isStoredLoggedIn) clearStoredLoginSession();
      return { isValid: false, role: "staff", user: "" };
    }

    return {
      isValid: true,
      role: safeLocalStorageGetItem("rc_user_role") || "staff",
      user: safeLocalStorageGetItem("rc_current_user") || "",
      loginAt,
    };
  } catch (error) {
    console.warn("로그인 세션 확인 실패:", error);
    return { isValid: false, role: "staff", user: "" };
  }
};
class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("React 화면 렌더링 오류:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", padding: 24, background: "#fff", color: "#111827", fontFamily: "Arial, sans-serif", boxSizing: "border-box" }}>
          <div style={{ maxWidth: 640, margin: "60px auto", border: "1px solid #fecaca", borderRadius: 18, padding: 20, background: "#fff5f5" }}>
            <h1 style={{ marginTop: 0, color: "#b91c1c", fontSize: 22 }}>화면을 불러오지 못했습니다.</h1>
            <p style={{ lineHeight: 1.7, color: "#374151" }}>
              일시적인 브라우저 저장소 또는 화면 렌더링 오류가 발생했습니다. 아래 버튼으로 다시 시도해 주세요.
            </p>
            <pre style={{ whiteSpace: "pre-wrap", background: "#fff", border: "1px solid #fee2e2", borderRadius: 12, padding: 12, color: "#991b1b", fontSize: 12, maxHeight: 180, overflow: "auto" }}>
              {String(this.state.error?.message || this.state.error || "Unknown error")}
            </pre>
            <button
              type="button"
              onClick={() => {
                clearStoredLoginSession();
                if (typeof window !== "undefined") window.location.reload();
              }}
              style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 12, padding: "12px 16px", cursor: "pointer", fontWeight: "bold" }}
            >
              다시 시도
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function RedContainerStaffSiteDraftContent() {
  const initialLoginSessionRef = useRef(null);
  if (!initialLoginSessionRef.current) initialLoginSessionRef.current = getStoredLoginSession();
  const initialLoginSession = initialLoginSessionRef.current;
  const [isLoggedIn, setIsLoggedIn] = useState(() => initialLoginSession.isValid);
  const [userRole, setUserRole] = useState(() => initialLoginSession.role || "staff");
  const [currentUser, setCurrentUser] = useState(() => initialLoginSession.user || "");
  const loginIdRef = useRef(null);
  const loginPwRef = useRef(null);
  const [staffMenu, setStaffMenu] = useState(DASHBOARD_MENU);
  const [isWorking, setIsWorking] = useState(false);
  const [selectedStore, setSelectedStore] = useState("강남점");
  const [activeStore, setActiveStore] = useState("강남점");
  const [pendingStore, setPendingStore] = useState(null);
  const [workStartTime, setWorkStartTime] = useState(null);
  const [workEndTime, setWorkEndTime] = useState(null);
  const [adminMenu, setAdminMenu] = useState(DASHBOARD_MENU);
  const [confirmModal, setConfirmModal] = useState(null);
  const [isMobile, setIsMobile] = useState(getIsMobileViewport);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuReloadTick, setMenuReloadTick] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobileViewport());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    safeLocalStorageSetItem("rc_staff_menu", staffMenu);
  }, [staffMenu]);

  useEffect(() => {
    safeLocalStorageSetItem("rc_admin_menu", adminMenu);
  }, [adminMenu]);

  useEffect(() => {
    safeLocalStorageSetItem("rc_is_logged_in", isLoggedIn ? "true" : "false");
    safeLocalStorageSetItem("rc_user_role", userRole || "staff");
    safeLocalStorageSetItem("rc_current_user", currentUser || "");
  }, [isLoggedIn, userRole, currentUser]);

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const redButton = { background: "#dc2626", color: "#fff", border: "none", borderRadius: 12, padding: "10px 16px", cursor: "pointer", fontWeight: "bold" };
  const darkButton = { background: "#111827", color: "#fff", border: "none", borderRadius: 12, padding: "10px 16px", cursor: "pointer" };
  const grayButton = { background: "#6b7280", color: "#fff", border: "none", borderRadius: 12, padding: "10px 16px", cursor: "pointer" };
  const orangeButton = { background: "#f97316", color: "#fff", border: "none", borderRadius: 12, padding: "10px 16px", cursor: "pointer", fontWeight: "bold" };
  const blueButton = { background: "#2563eb", color: "#fff", border: "none", borderRadius: 12, padding: "10px 16px", cursor: "pointer", fontWeight: "bold" };
  const input = { border: "1px solid #d1d5db", borderRadius: 12, padding: 12, boxSizing: "border-box" };
  const mobileFull = isMobile ? { width: "100%" } : {};
  const tableScroll = { overflowX: "auto", width: "100%" };
  const clickableCardStyle = {
    textAlign: "left",
    cursor: "pointer",
    color: "#111827",
    width: "100%",
    boxSizing: "border-box",
    font: "inherit",
    transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
  };
  const handleClickableCardMouseEnter = (e) => {
    e.currentTarget.style.transform = "translateY(-1px)";
    e.currentTarget.style.boxShadow = "0 10px 22px rgba(0,0,0,0.08)";
  };
  const handleClickableCardMouseLeave = (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "none";
  };

  const defaultStoreRecords = [
    { id: 1, name: "강남점", address: "서울시 강남구", owner: "-", businessNumber: "-", contact: "02-0000-0001", extension: "101", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 2, name: "가로수길점", address: "서울시 강남구 가로수길", owner: "-", businessNumber: "-", contact: "02-0000-0002", extension: "102", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 3, name: "청담점", address: "서울시 강남구 청담동", owner: "-", businessNumber: "-", contact: "02-0000-0003", extension: "103", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 4, name: "명동점", address: "서울시 중구 명동", owner: "-", businessNumber: "-", contact: "02-0000-0004", extension: "104", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 5, name: "이태원점", address: "서울시 용산구 이태원동", owner: "-", businessNumber: "-", contact: "02-0000-0005", extension: "105", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 6, name: "건대점", address: "서울시 광진구", owner: "-", businessNumber: "-", contact: "02-0000-0006", extension: "106", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 7, name: "연남점", address: "서울시 마포구 연남동", owner: "-", businessNumber: "-", contact: "02-0000-0007", extension: "107", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 8, name: "홍대입구역점", address: "서울시 마포구 홍대입구역", owner: "-", businessNumber: "-", contact: "02-0000-0008", extension: "108", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 9, name: "홍대어울마당점", address: "서울시 마포구 어울마당로", owner: "-", businessNumber: "-", contact: "02-0000-0009", extension: "109", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 10, name: "신림점", address: "서울시 관악구 신림동", owner: "-", businessNumber: "-", contact: "02-0000-0010", extension: "110", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
    { id: 11, name: "부천점", address: "경기도 부천시", owner: "-", businessNumber: "-", contact: "032-0000-0011", extension: "111", leaseStart: "", leaseEnd: "", rentPayDay: "", bank1: "", bank2: "", status: "활성화" },
  ];
  const STORE_API_BASE = "/api/stores";
  const [storeRecords, setStoreRecords] = useState([]);
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState("");
  const STORE_ALIAS_API_BASE = "/api/store-aliases";
  const SYSTEM_STORE_NAMES = ["온라인몰", "온라인 몰", "온라인", "카페24", "Cafe24", "KSNET", "ksnet", "인형뽑기", "인형 뽑기", "본사", "기타"];
  const normalizeSystemStoreName = (value) => String(value || "").replace(/\s/g, "").toLowerCase();
  const isSystemStoreName = (value) => {
    const key = normalizeSystemStoreName(value);
    return SYSTEM_STORE_NAMES.some((name) => normalizeSystemStoreName(name) === key);
  };
  const [storeAliases, setStoreAliases] = useState([]);
  const [unmappedStores, setUnmappedStores] = useState([]);
  const [storeAliasLoading, setStoreAliasLoading] = useState(false);
  const [storeAliasError, setStoreAliasError] = useState("");
  const [unmappedDrafts, setUnmappedDrafts] = useState({});
  const stores = storeRecords.filter((store) => store.status === "활성화" && !isSystemStoreName(store.name)).map((store) => store.name);
  const requestTypes = ["이슈보고", "A/S 요청", "재고이동", "반품 요청", "배송 요청", "기타"];
  const locationOptions = ["본사", ...stores];
  const card = { background: "#fff", borderRadius: 24, padding: 24, boxShadow: "0 10px 24px rgba(0,0,0,0.08)" };
  const getTodayInputDate = () => {
    const date = new Date();
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  };

  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeRoleFilter, setEmployeeRoleFilter] = useState("전체");
  const [employeeForm, setEmployeeForm] = useState({ name: "", id: "", contact: "", address: "", hireDate: "", role: "직원" });
  const [isSavingMyPage, setIsSavingMyPage] = useState(false);
  const myPageSavingRef = useRef(false);
  const [selectedEmployeeStores, setSelectedEmployeeStores] = useState(["강남점", "홍대입구역점", "이태원점"]);
  const [tempPassword, setTempPassword] = useState("7429");
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [editEmployeeStores, setEditEmployeeStores] = useState([]);
  const [staffAttendanceFilter, setStaffAttendanceFilter] = useState({ store: "전체 매장", start: "", end: "" });
  const [adminAttendanceFilter, setAdminAttendanceFilter] = useState(() => {
    const today = getTodayInputDate();
    return { store: "전체 매장", start: today, end: today, name: "", status: "전체" };
  });
  const [adminAttendancePage, setAdminAttendancePage] = useState(1);
  const adminAttendancePageSize = 10;
  const [editingAttendanceId, setEditingAttendanceId] = useState(null);
  const [attendanceEditDraft, setAttendanceEditDraft] = useState(null);
  const [showUnclosedOnly, setShowUnclosedOnly] = useState(false);

  const employeeNameRef = useRef(null);
  const employeeIdRef = useRef(null);
  const employeeContactRef = useRef(null);
  const employeeAddressRef = useRef(null);
  const employeeHireDateRef = useRef(null);
  const employeeRoleRef = useRef(null);
  const employeeSearchRef = useRef(null);
  const employeeRoleFilterRef = useRef(null);
  const employeeImportFileRef = useRef(null);
  const [expandedEmployeeIds, setExpandedEmployeeIds] = useState({});
  const editEmployeeNameRef = useRef(null);
  const editEmployeeContactRef = useRef(null);
  const editEmployeeAddressRef = useRef(null);
  const editEmployeeHireDateRef = useRef(null);
  const editEmployeeRoleRef = useRef(null);
  const adminAttendanceStoreRef = useRef(null);
  const adminAttendanceStartRef = useRef(null);
  const adminAttendanceEndRef = useRef(null);
  const adminAttendanceNameRef = useRef(null);
  const adminAttendanceStatusRef = useRef(null);
  const newAttendanceNameRef = useRef(null);
  const newAttendanceStoreRef = useRef(null);
  const newAttendanceDateRef = useRef(null);
  const newAttendanceEndDateRef = useRef(null);
  const newAttendanceStartRef = useRef(null);
  const newAttendanceBreakStartRef = useRef(null);
  const newAttendanceBreakEndRef = useRef(null);
  const newAttendanceEndRef = useRef(null);
  const newAttendanceStatusRef = useRef(null);
  const storeNameRef = useRef(null);
  const storeAddressRef = useRef(null);
  const storeOwnerRef = useRef(null);
  const storeBusinessNumberRef = useRef(null);
  const storeContactRef = useRef(null);
  const storeExtensionRef = useRef(null);
  const storeLeaseStartRef = useRef(null);
  const storeLeaseEndRef = useRef(null);
  const storeRentPayDayRef = useRef(null);
  const storeBank1Ref = useRef(null);
  const storeBank2Ref = useRef(null);
  const storeMemoRef = useRef(null);
  const storeSearchRef = useRef(null);
  const aliasNameRef = useRef(null);
  const aliasStoreRef = useRef(null);
  const aliasSourceRef = useRef(null);
  const editStoreNameRef = useRef(null);
  const editStoreAddressRef = useRef(null);
  const editStoreOwnerRef = useRef(null);
  const editStoreBusinessNumberRef = useRef(null);
  const editStoreContactRef = useRef(null);
  const editStoreExtensionRef = useRef(null);
  const editStoreLeaseStartRef = useRef(null);
  const editStoreLeaseEndRef = useRef(null);
  const editStoreRentPayDayRef = useRef(null);
  const editStoreBank1Ref = useRef(null);
  const editStoreBank2Ref = useRef(null);
  const editStoreMemoRef = useRef(null);
  const [storeSearch, setStoreSearch] = useState("");
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [expandedStoreIds, setExpandedStoreIds] = useState([]);
  const [storeAttachments, setStoreAttachments] = useState([]);
  const storeFileRef = useRef(null);
  const editStoreFileRef = useRef(null);
  const rentPayDayOptions = Array.from({ length: 31 }, (_, i) => String(i + 1));

  const myContactRef = useRef(null);
  const myAddressRef = useRef(null);
  const currentPwRef = useRef(null);
  const newPwRef = useRef(null);
  const newPwConfirmRef = useRef(null);

  const staffMenus = ["대시보드", "공지사항", "인수인계", "업무 요청", "소모품 주문", "유통기한 관리", "매장정보", "내 근무 기록", "마이페이지"];
  const adminMenus = ["대시보드", "매출조회", "직원 계정 관리", "매장 관리", "매장 매핑 관리", "공지 작성 / 관리", "직원 근무일지", "업무 요청 관리", "인수인계 관리", "소모품 주문 관리", "소모품 DB 관리", "상품 DB 관리", "생산관리", "수입/구입 관리", "광고관리", "거래처관리"];

  const baseUsers = [
    { id: "admin", pw: "1234", role: "admin", name: "관리자" },
  ];

  const baseProductCategories = ["콘돔", "젤", "향수", "소모성 상품", "기타"];

  const defaultProductList = [];

  const [productList, setProductList] = useState([]);
  const activeProductList = productList.filter((product) => product.status !== "비활성화");
  const productCategories = Array.from(new Set([...baseProductCategories, ...productList.map((product) => product.category || "기타")])).filter(Boolean);

  const defaultCompanyNotices = [];

  const [companyNotices, setCompanyNotices] = useState([]);
  const [openNoticeId, setOpenNoticeId] = useState(null);
  const [staffNoticeCategoryFilter, setStaffNoticeCategoryFilter] = useState("전체");
  const [noticeTaskMemos, setNoticeTaskMemos] = useState({});
  const [noticeTaskFiles, setNoticeTaskFiles] = useState({});
  const noticeTaskFileRefs = useRef({});
  const adminNoticeTitleRef = useRef(null);
  const adminNoticeContentRef = useRef(null);
  const adminNoticeCategoryRef = useRef(null);
  const adminNoticeTargetRef = useRef(null);
  const adminNoticeStartRef = useRef(null);
  const adminNoticeEndRef = useRef(null);
  const adminNoticeFileRef = useRef(null);
  const [adminNoticeAttachments, setAdminNoticeAttachments] = useState([]);
  const [adminNoticeProductResults, setAdminNoticeProductResults] = useState([]);
  const [adminNoticeSelectedProducts, setAdminNoticeSelectedProducts] = useState([]);
  const [adminNoticeProductVisibleCount, setAdminNoticeProductVisibleCount] = useState(5);
  const adminNoticeProductKeywordRef = useRef(null);
  const [adminNoticeFilter, setAdminNoticeFilter] = useState({ category: "전체", target: "전체", keyword: "", visibleOnly: false });
  const [adminNoticeSaving, setAdminNoticeSaving] = useState(false);
  const adminNoticeSavingRef = useRef(false);
  const [noticeSavingIds, setNoticeSavingIds] = useState({});
  const noticeActionSavingRefs = useRef({});
  const [editingNoticeId, setEditingNoticeId] = useState(null);
  const [openAdminNoticeId, setOpenAdminNoticeId] = useState(null);

  const noticeChecks = [
    { name: "김민수", store: "강남점", status: "미확인", time: "-" },
    { name: "이지은", store: "이태원점", status: "확인완료", time: "2026.05.07 11:18" },
    { name: "박준호", store: "건대점", status: "확인완료", time: "2026.05.07 13:22" },
  ];

  const defaultHandovers = [];

  const [handovers, setHandovers] = useState([]);
  const [handoverAttachments, setHandoverAttachments] = useState([]);
  const [handoverFilter, setHandoverFilter] = useState({ store: "전체 매장", status: "전체", start: "", end: "" });
  const [isSubmittingHandover, setIsSubmittingHandover] = useState(false);
  const handoverSubmittingRef = useRef(false);
  const [staffHandoverPage, setStaffHandoverPage] = useState(1);
  const staffHandoverPageSize = 10;
  const [adminHandoverFilter, setAdminHandoverFilter] = useState({ store: "전체 매장", status: "전체", start: "", end: "", keyword: "", importantOnly: false });
  const [openAdminHandoverId, setOpenAdminHandoverId] = useState(null);
  const handoverTitleRef = useRef(null);
  const handoverContentRef = useRef(null);
  const handoverImportantRef = useRef(null);
  const handoverNeedCheckRef = useRef(null);
  const handoverFileRef = useRef(null);

  const defaultAttendance = [];


  const [attendance, setAttendance] = useState(defaultAttendance);

  const defaultWorkRequests = [];

  const [workRequests, setWorkRequests] = useState(() => parseLocalStorageJson("rc_work_requests", defaultWorkRequests));
  const [workRequestDetailsById, setWorkRequestDetailsById] = useState({});
  const [isSubmittingWorkRequest, setIsSubmittingWorkRequest] = useState(false);
  const workRequestSubmittingRef = useRef(false);
  const workRequestDetailFetchInFlightRef = useRef(new Set());
  const workRequestDetailFetchAttemptedRef = useRef(new Set());
  const [requestType, setRequestType] = useState("이슈보고");
  const [requestAddress, setRequestAddress] = useState({ postalCode: "", baseAddress: "", detailAddress: "" });
  const [requestReceiveStore, setRequestReceiveStore] = useState(selectedStore);
  const [requestTargetStore, setRequestTargetStore] = useState("본사");
  const [productResults, setProductResults] = useState([]);
  const [lastProductSearchKeyword, setLastProductSearchKeyword] = useState("");
  const [productResultVisibleCount, setProductResultVisibleCount] = useState(5);
  const [productDrafts, setProductDrafts] = useState({});
  const [selectedRequestProducts, setSelectedRequestProducts] = useState([]);
  const [requestAttachments, setRequestAttachments] = useState([]);
  const [requestFilesReading, setRequestFilesReading] = useState(false);
  const [requestFilesReadError, setRequestFilesReadError] = useState(false);
  const requestFilesReadingRef = useRef(false);
  const requestFileGenerationRef = useRef(0);
  const [workRequestFilter, setWorkRequestFilter] = useState({ type: "전체", status: "전체", start: "", end: "" });
  const [adminWorkRequestFilter, setAdminWorkRequestFilter] = useState({ type: "전체", status: "전체", store: "전체 매장", start: "", end: "", keyword: "" });
  const [openAdminWorkRequestId, setOpenAdminWorkRequestId] = useState(null);
  const [adminWorkRequestMemos, setAdminWorkRequestMemos] = useState({});
  const productKeywordRef = useRef(null);
  const requestContentRef = useRef(null);
  const requestFileRef = useRef(null);
  const requestCustomerNameRef = useRef(null);
  const requestCustomerContactRef = useRef(null);
  const asPurchaseStoreRef = useRef(null);
  const asPurchaseDateRef = useRef(null);
  const asSymptomRef = useRef(null);
  const asReceiveMethodRef = useRef(null);
  const workRequestTypeFilterRef = useRef(null);
  const workRequestStatusFilterRef = useRef(null);
  const workRequestStartFilterRef = useRef(null);
  const workRequestEndFilterRef = useRef(null);
  const adminMemoRef = useRef(null);

  const defaultSupplyItems = [];

  const [supplyItems, setSupplyItems] = useState([]);
  const activeSupplyItems = supplyItems.filter((item) => item.status === "활성화");
  const supplyItemCategoryOptions = Array.from(new Set(supplyItems.map((item) => item.category || "기타").filter(Boolean))).sort();
  const activeSupplyItemCategoryOptions = Array.from(new Set(activeSupplyItems.map((item) => item.category || "기타").filter(Boolean))).sort();

  const defaultSupplyOrders = [];


  const [supplyCart, setSupplyCart] = useState([]);
  const [otherSupplyRows, setOtherSupplyRows] = useState([]);
  const [supplyOrderReason, setSupplyOrderReason] = useState("");
  const [isSubmittingSupplyOrder, setIsSubmittingSupplyOrder] = useState(false);
  const supplyOrderSubmittingRef = useRef(false);
  const [supplyOrderFilter, setSupplyOrderFilter] = useState({ store: "전체 매장", status: "전체", start: "", end: "" });
  const [adminSupplyOrderFilter, setAdminSupplyOrderFilter] = useState({ store: "전체 매장", status: "전체", start: "", end: "", keyword: "" });
  const [openAdminSupplyOrderId, setOpenAdminSupplyOrderId] = useState(null);
  const [adminSupplyOrderMemos, setAdminSupplyOrderMemos] = useState({});
  const [supplyItemSearch, setSupplyItemSearch] = useState("");
  const [supplyItemCategoryFilter, setSupplyItemCategoryFilter] = useState("전체");
  const [supplyItemPage, setSupplyItemPage] = useState(1);
  const supplyItemPageSize = 10;
  const [staffSupplyItemCategoryFilter, setStaffSupplyItemCategoryFilter] = useState("전체");
  const [staffSupplyItemSearch, setStaffSupplyItemSearch] = useState("");
  const [staffSupplyItemPage, setStaffSupplyItemPage] = useState(1);
  const staffSupplyItemPageSize = 10;
  const [productDbSearch, setProductDbSearch] = useState("");
  const [productDbCategoryFilter, setProductDbCategoryFilter] = useState("전체");
  const [productDbPage, setProductDbPage] = useState(1);
  const productDbPageSize = 10;
  const [editingProductCode, setEditingProductCode] = useState(null);
  const [isImportingProductCsv, setIsImportingProductCsv] = useState(false);
  const productCsvImportInProgressRef = useRef(false);
  const productCsvImportStartedRef = useRef(false);
  const productCodeRef = useRef(null);
  const productNameRef = useRef(null);
  const productCategoryRef = useRef(null);
  const productBarcodeRef = useRef(null);
  const productSalePriceRef = useRef(null);
  const productWholesalePriceRef = useRef(null);
  const productCtQuantityRef = useRef(null);
  const productProductionDateRef = useRef(null);
  const productExpiryRef = useRef(null);
  const productNoteRef = useRef(null);
  const productImageUrlRef = useRef(null);
  const productImportFileRef = useRef(null);
  const productDbSearchRef = useRef(null);
  const editProductCodeRef = useRef(null);
  const editProductNameRef = useRef(null);
  const editProductCategoryRef = useRef(null);
  const editProductBarcodeRef = useRef(null);
  const editProductSalePriceRef = useRef(null);
  const editProductWholesalePriceRef = useRef(null);
  const editProductCtQuantityRef = useRef(null);
  const editProductProductionDateRef = useRef(null);
  const editProductExpiryRef = useRef(null);
  const editProductNoteRef = useRef(null);
  const editProductImageUrlRef = useRef(null);

  const PRODUCTION_APP_DATA_KEY = "rc_production_records";
  const PURCHASE_APP_DATA_KEY = "rc_purchase_records";
  const createProductionMaterialRow = () => ({ id: `${Date.now()}-${Math.random()}`, name: "", supplierName: "", qty: "", unitPrice: "", supplierResults: [] });
  const [productionRecords, setProductionRecords] = useState(() => parseLocalStorageJson(PRODUCTION_APP_DATA_KEY, []));
  const [purchaseRecords, setPurchaseRecords] = useState(() => parseLocalStorageJson(PURCHASE_APP_DATA_KEY, []));
  const [selectedProductionProduct, setSelectedProductionProduct] = useState(null);
  const [selectedPurchaseProduct, setSelectedPurchaseProduct] = useState(null);
  const [selectedPurchasePartner, setSelectedPurchasePartner] = useState(null);
  const [productionProductResults, setProductionProductResults] = useState([]);
  const [purchaseProductResults, setPurchaseProductResults] = useState([]);
  const [purchasePartnerResults, setPurchasePartnerResults] = useState([]);
  const [productionMaterialRows, setProductionMaterialRows] = useState(() => [createProductionMaterialRow()]);
  const [productionAttachments, setProductionAttachments] = useState([]);
  const [purchaseAttachments, setPurchaseAttachments] = useState([]);
  const [editingProductionId, setEditingProductionId] = useState(null);
  const [editingPurchaseId, setEditingPurchaseId] = useState(null);
  const [openProductionRecordIds, setOpenProductionRecordIds] = useState({});
  const [openPurchaseRecordIds, setOpenPurchaseRecordIds] = useState({});
  const [productionFilter, setProductionFilter] = useState({ start: "", end: "", keyword: "" });
  const [purchaseFilter, setPurchaseFilter] = useState({ start: "", end: "", type: "전체", currency: "전체", partner: "전체", keyword: "" });
  const [productionPage, setProductionPage] = useState(1);
  const [purchasePage, setPurchasePage] = useState(1);
  const productionPageSize = 10;
  const purchasePageSize = 10;
  const [, setProductionCalcTick] = useState(0);
  const [, setPurchaseCalcTick] = useState(0);
  const productionProductKeywordRef = useRef(null);
  const productionDateRef = useRef(null);
  const productionExpiryRef = useRef(null);
  const productionQtyRef = useRef(null);
  const productionOtherCostRef = useRef(null);
  const productionMemoRef = useRef(null);
  const productionFileRef = useRef(null);
  const productionImportFileRef = useRef(null);
  const productionFilterKeywordRef = useRef(null);
  const purchaseProductKeywordRef = useRef(null);
  const purchasePartnerKeywordRef = useRef(null);
  const purchaseDateRef = useRef(null);
  const purchaseTypeRef = useRef(null);
  const purchasePartnerRef = useRef(null);
  const purchasePartnerNameRef = useRef(null);
  const purchaseQtyRef = useRef(null);
  const purchaseCurrencyRef = useRef(null);
  const purchaseForeignUnitPriceRef = useRef(null);
  const purchaseExchangeRateRef = useRef(null);
  const purchaseMemoRef = useRef(null);
  const purchaseFileRef = useRef(null);
  const purchaseImportFileRef = useRef(null);
  const purchaseFilterKeywordRef = useRef(null);

  const [editingSupplyItemId, setEditingSupplyItemId] = useState(null);
  const supplyItemNameRef = useRef(null);
  const supplyItemUnitRef = useRef(null);
  const supplyItemCategoryRef = useRef(null);
  const supplyItemMemoRef = useRef(null);
  const supplyItemSearchRef = useRef(null);
  const editSupplyItemNameRef = useRef(null);
  const editSupplyItemUnitRef = useRef(null);
  const editSupplyItemCategoryRef = useRef(null);
  const editSupplyItemMemoRef = useRef(null);
  const [supplyOrders, setSupplyOrders] = useState(defaultSupplyOrders);

  const expiryProductList = activeProductList
    .filter((product) => product.expiry || product.productionDate)
    .map((product, index) => ({
      id: product.code || index,
      category: product.category || "기타",
      code: product.code,
      barcode: product.barcode || "",
      name: product.name,
      lot: product.productionDate ? `${product.productionDate} 생산` : "생산일자 미입력",
      productionDate: product.productionDate || "",
      expiry: product.expiry || "",
      salePrice: product.salePrice || "",
      ctQuantity: getProductCtQuantity(product),
      note: product.note || "-",
      imageUrl: product.imageUrl || "",
    }));
  const [expirySearch, setExpirySearch] = useState("");
  const [expiryPage, setExpiryPage] = useState(1);
  const expiryPageSize = 10;
  const [expiryCategory, setExpiryCategory] = useState("전체");
  const [expiryRangeFilter, setExpiryRangeFilter] = useState("전체");
  const [productDbExpiryFilter, setProductDbExpiryFilter] = useState("전체");
  const [expirySort, setExpirySort] = useState({ key: "expiry", order: "asc" });

  const defaultEmployees = [
    { name: "김민수", id: "minsu", contact: "010-1234-5678", address: "서울시 강남구", hireDate: "2026-05-01", stores: "강남점, 홍대입구역점, 이태원점", status: "활성화", role: "직원", tempPassword: "1111" },
    { name: "이지은", id: "jieun", contact: "010-2222-3333", address: "서울시 마포구", hireDate: "2026-05-01", stores: "명동점, 이태원점, 연남점", status: "활성화", role: "직원", tempPassword: "1111" },
    { name: "관리자", id: "admin", contact: "010-0000-0000", address: "본사", hireDate: "2026-05-01", stores: "전체", status: "활성화", role: "관리자", tempPassword: "1234" },
  ];

  const [employees, setEmployees] = useState(() => parseLocalStorageJson("rc_employees", defaultEmployees));

  const partnerTypes = ["전체", "입점사", "광고사", "미팅업체", "생관관련", "기타"];
  const defaultPartners = [
    { id: 1, type: "입점사", name: "샘플 입점사", businessNumber: "000-00-00000", ceo: "홍길동", address: "서울시 강남구", contact: "02-0000-0000", email: "sample@partner.com", manager: "김담당", managerContact: "010-0000-0000", managerEmail: "manager@partner.com", note: "샘플 거래처입니다.", attachments: [] },
  ];
  const APP_DATA_API_BASE = "/api/app-data";

  const fetchAppDataItem = async (key) => {
    const res = await fetch(`${APP_DATA_API_BASE}/item/${encodeURIComponent(key)}?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) throw new Error(`app-data item 조회 실패 (${key}, ${res.status})`);
    const data = await res.json();
    return data?.value;
  };

  const fetchAppDataItemFull = async (key) => {
    const res = await fetch(`${APP_DATA_API_BASE}/item/${encodeURIComponent(key)}/full?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) throw new Error(`app-data full 조회 실패 (${key}, ${res.status})`);
    const data = await res.json();
    return data?.value;
  };

  const fetchAppDataExactKeyValue = async (dataKey) => {
    const res = await fetch(
      `${APP_DATA_API_BASE}/item/${encodeURIComponent(dataKey)}/full?_=${Date.now()}`,
      { cache: "no-store" }
    );
    if (!res.ok) throw new Error(`app-data exact key 조회 실패 (${dataKey}, ${res.status})`);
    const data = await res.json();
    const entry = Object.prototype.hasOwnProperty.call(data || {}, "value")
      ? data.value
      : data?.[dataKey];
    if (entry && typeof entry === "object" && Object.prototype.hasOwnProperty.call(entry, "value")) {
      return entry.value;
    }
    return entry;
  };

  const getAttendanceUserAliases = useCallback((userName = currentUser) => {
    const rawUserName = String(userName || "").trim();
    const employee = employees.find((item) =>
      String(item.name || "").trim() === rawUserName ||
      String(item.id || "").trim() === rawUserName
    );

    return new Set(
      [rawUserName, employee?.name, employee?.id]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    );
  }, [currentUser, employees]);

  const isAttendanceForUser = useCallback((row, userName = currentUser) => {
    const rowName = String(row?.name || "").trim();
    if (!rowName) return false;
    return getAttendanceUserAliases(userName).has(rowName);
  }, [currentUser, getAttendanceUserAliases]);

  const getOpenAttendanceRecordForUser = useCallback((baseList, userName = currentUser) => {
    return (Array.isArray(baseList) ? baseList : []).find((row) =>
      isAttendanceForUser(row, userName) &&
      isOpenAttendanceRecord(row)
    );
  }, [currentUser, isAttendanceForUser]);

  const getLatestAttendanceListForMutation = async () => {
    try {
      const serverAttendance = await fetchAppDataItemFull("rc_attendance");
      if (Array.isArray(serverAttendance)) return serverAttendance;
    } catch (error) {
      console.error("근무일지 최신 서버값 조회 실패. 현재 화면 데이터를 사용합니다:", error);
    }

    return Array.isArray(attendance) ? attendance : [];
  };


  const fetchAppDataItemDetail = async (key, itemId) => {
    if (itemId === null || itemId === undefined || itemId === "") return null;
    if (key === WORK_REQUEST_APP_DATA_KEY) {
      try {
        const detailValue = await fetchAppDataExactKeyValue(getWorkRequestDetailDataKey(itemId));
        if (detailValue) return detailValue;
      } catch (error) {
        console.warn("업무요청 detail key 조회 실패, 기존 detail API로 재시도:", itemId, error);
      }
    }
    const res = await fetch(`${APP_DATA_API_BASE}/item/${encodeURIComponent(key)}/detail/${encodeURIComponent(String(itemId))}?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) throw new Error(`app-data detail 조회 실패 (${key}, ${itemId}, ${res.status})`);
    const data = await res.json();
    return data?.value || null;
  };

  const APP_DATA_DETAIL_PRESERVED_KEYS = new Set([
    "rc_company_notices",
    "rc_work_requests",
    "rc_handovers",
  ]);

  const APP_DATA_DETAIL_ID_FIELDS = [
    "id",
    "noticeId",
    "notice_id",
    "requestId",
    "request_id",
    "handoverId",
    "handover_id",
  ];

  const APP_DATA_ATTACHMENT_FIELDS = ["attachments", "taskFiles"];
  const APP_DATA_ATTACHMENT_PAYLOAD_FIELDS = ["previewUrl", "previewurl", "dataUrl", "dataURL", "dataurl", "base64"];

  const getAppDataItemId = (item) => {
    if (!item || typeof item !== "object") return "";
    const idField = APP_DATA_DETAIL_ID_FIELDS.find((field) => item[field] !== null && item[field] !== undefined && item[field] !== "");
    return idField ? String(item[idField]) : "";
  };

  const getAttachmentIdentity = (file) => {
    if (!file || typeof file !== "object") return "";
    if (file.id !== null && file.id !== undefined && file.id !== "") return String(file.id);
    const name = file.name || "";
    const size = file.size ?? "";
    const type = file.type || "";
    return name ? `${name}__${size}__${type}` : "";
  };

  const mergeAttachmentPreservingPayload = (previousFile, nextFile) => {
    if (!previousFile) return nextFile;
    if (!nextFile) return previousFile;

    const merged = { ...previousFile, ...nextFile };
    APP_DATA_ATTACHMENT_PAYLOAD_FIELDS.forEach((field) => {
      if ((nextFile[field] === undefined || nextFile[field] === null || nextFile[field] === "") && previousFile[field]) {
        merged[field] = previousFile[field];
      }
    });
    return merged;
  };

  const mergeAttachmentListPreservingPayload = (previousFiles, nextFiles) => {
    if (!Array.isArray(nextFiles)) return Array.isArray(previousFiles) ? previousFiles : nextFiles;

    const previousById = new Map(
      (Array.isArray(previousFiles) ? previousFiles : [])
        .map((file) => [getAttachmentIdentity(file), file])
        .filter(([id]) => Boolean(id))
    );

    return nextFiles.map((file) => {
      const id = getAttachmentIdentity(file);
      const previousFile = id ? previousById.get(id) : null;
      return previousFile ? mergeAttachmentPreservingPayload(previousFile, file) : file;
    });
  };

  const mergeAppDataItemPreservingDetails = (key, previousItem, nextItem) => {
    if (!previousItem || !nextItem || !APP_DATA_DETAIL_PRESERVED_KEYS.has(key)) return nextItem;

    const merged = { ...previousItem, ...nextItem };
    APP_DATA_ATTACHMENT_FIELDS.forEach((field) => {
      if (Array.isArray(nextItem[field])) {
        merged[field] = mergeAttachmentListPreservingPayload(previousItem[field], nextItem[field]);
        return;
      }
      if ((nextItem[field] === undefined || nextItem[field] === null) && Array.isArray(previousItem[field])) {
        merged[field] = previousItem[field];
      }
    });
    return merged;
  };

  const mergeAppDataListPreservingDetails = (key, previousList, nextList) => {
    const safeNextList = Array.isArray(nextList) ? nextList : [];
    if (!APP_DATA_DETAIL_PRESERVED_KEYS.has(key)) return safeNextList;

    const previousById = new Map(
      (Array.isArray(previousList) ? previousList : [])
        .map((item) => [getAppDataItemId(item), item])
        .filter(([id]) => Boolean(id))
    );

    return safeNextList.map((item) => {
      const id = getAppDataItemId(item);
      const previousItem = id ? previousById.get(id) : null;
      return previousItem ? mergeAppDataItemPreservingDetails(key, previousItem, item) : item;
    });
  };

  const mergeDetailIntoListById = (list, detail, key = "") => {
    const detailId = getAppDataItemId(detail);
    if (!detailId) return list;
    return (Array.isArray(list) ? list : []).map((item) =>
      getAppDataItemId(item) === detailId
        ? mergeAppDataItemPreservingDetails(key, item, detail)
        : item
    );
  };

  const hasAttachmentPayload = (file) =>
    Boolean(file && APP_DATA_ATTACHMENT_PAYLOAD_FIELDS.some((field) => file[field]));

  const itemNeedsAttachmentDetail = (item) =>
    APP_DATA_ATTACHMENT_FIELDS.some((field) =>
      Array.isArray(item?.[field]) &&
      item[field].length > 0 &&
      item[field].some((file) => !hasAttachmentPayload(file))
    );

  const itemHasAttachmentPayload = (item) =>
    APP_DATA_ATTACHMENT_FIELDS.some((field) =>
      Array.isArray(item?.[field]) &&
      item[field].some((file) => hasAttachmentPayload(file))
    );

  const normalizeWorkRequestDetailPayload = (detail) => {
    if (!detail) return null;
    if (typeof detail === "string") {
      try {
        const parsed = JSON.parse(detail);
        return normalizeWorkRequestDetailPayload(parsed);
      } catch {
        return null;
      }
    }
    if (detail && typeof detail === "object" && Object.prototype.hasOwnProperty.call(detail, "value")) {
      return normalizeWorkRequestDetailPayload(detail.value);
    }
    return detail && typeof detail === "object" ? detail : null;
  };

  const getWorkRequestAttachmentDebugSummary = (files = []) => (
    (Array.isArray(files) ? files : []).map((file) => {
      const dataUrl = String(file?.dataUrl || file?.dataURL || file?.dataurl || "");
      return {
        id: file?.id,
        name: file?.name,
        hasDataUrl: Boolean(dataUrl),
        hasPreviewUrl: Boolean(file?.previewUrl),
        mimeType: file?.mimeType || "",
        type: file?.type || "",
        isImage: Boolean(file?.isImage),
        dataUrlLength: dataUrl.length,
      };
    })
  );

  const cacheWorkRequestDetail = (detail) => {
    const normalizedDetail = normalizeWorkRequestDetailPayload(detail);
    const detailId = getAppDataItemId(normalizedDetail);
    if (!detailId) return;
    const summaryDetail = sanitizeWorkRequestForListSave(normalizedDetail);
    const detailAttachments = Array.isArray(normalizedDetail?.attachments) ? normalizedDetail.attachments : [];
    console.log("[work-request detail cache write]", {
      requestId: detailId,
      attachmentCount: detailAttachments.length,
      attachments: getWorkRequestAttachmentDebugSummary(detailAttachments),
    });

    setWorkRequestDetailsById((prev) => ({
      ...prev,
      [detailId]: normalizedDetail,
    }));
    setWorkRequests((prev) =>
      (Array.isArray(prev) ? prev : []).map((item) =>
        getAppDataItemId(item) === detailId
          ? sanitizeWorkRequestForListSave({ ...item, ...summaryDetail })
          : item
      )
    );
  };

  const loadWorkRequestDetail = async (requestId, options = {}) => {
    const context = options.context || "work-request";
    console.log(`[work-request ${context} detail fetch start]`, {
      requestId,
      detailKey: getWorkRequestDetailDataKey(requestId),
    });

    try {
      const rawDetail = await fetchAppDataItemDetail(WORK_REQUEST_APP_DATA_KEY, requestId);
      const detail = normalizeWorkRequestDetailPayload(rawDetail);
      const attachments = Array.isArray(detail?.attachments) ? detail.attachments : [];
      console.log(`[work-request ${context} detail fetch success]`, {
        requestId,
        detailFound: Boolean(detail),
        attachmentCount: attachments.length,
        attachments: getWorkRequestAttachmentDebugSummary(attachments),
      });
      if (detail) cacheWorkRequestDetail(detail);
      return detail;
    } catch (error) {
      console.error("업무요청 detail 조회 실패:", {
        requestId,
        detailKey: getWorkRequestDetailDataKey(requestId),
        errorMessage: error?.message || String(error),
      }, error);
      console.error(`[work-request ${context} detail fetch failed]`, {
        requestId,
        detailKey: getWorkRequestDetailDataKey(requestId),
        errorMessage: error?.message || String(error),
      }, error);
      return null;
    }
  };

  const getWorkRequestDetailFetchAttemptKey = (requestId, request = null) => {
    const id = String(requestId || "");
    const attachments = Array.isArray(request?.attachments) ? request.attachments : [];
    const attachmentSignature = attachments
      .map((file) => [
        file?.id || "",
        file?.name || "",
        file?.size || "",
        file?.mimeType || file?.type || "",
        file?.hasData ? "hasData" : "",
      ].join(":"))
      .join("|");
    return `${id}::${attachmentSignature}`;
  };

  const loadWorkRequestDetailOnce = async (requestId, options = {}) => {
    const id = String(requestId || "");
    if (!id) return null;

    const context = options.context || "work-request";
    const attemptKey = getWorkRequestDetailFetchAttemptKey(id, options.request || null);
    const cachedDetail = workRequestDetailsById[id];
    const cachedAttachments = Array.isArray(cachedDetail?.attachments) ? cachedDetail.attachments : [];

    if (workRequestAttachmentListHasPayload(cachedAttachments)) {
      console.log(`[work-request ${context} detail fetch skipped cache hit]`, {
        requestId: id,
        attachmentCount: cachedAttachments.length,
      });
      return cachedDetail;
    }

    if (workRequestDetailFetchInFlightRef.current.has(attemptKey)) {
      console.log(`[work-request ${context} detail fetch skipped in-flight]`, { requestId: id });
      return cachedDetail || null;
    }

    if (workRequestDetailFetchAttemptedRef.current.has(attemptKey)) {
      console.log(`[work-request ${context} detail fetch skipped already attempted]`, {
        requestId: id,
        attachmentCount: cachedAttachments.length,
        detailHasPayload: workRequestAttachmentListHasPayload(cachedAttachments),
      });
      return cachedDetail || null;
    }

    workRequestDetailFetchInFlightRef.current.add(attemptKey);
    try {
      const detail = await loadWorkRequestDetail(id, options);
      if (detail) workRequestDetailFetchAttemptedRef.current.add(attemptKey);
      return detail;
    } finally {
      workRequestDetailFetchInFlightRef.current.delete(attemptKey);
    }
  };

  const hydrateAppDataDetailsForItems = async (key, items, setValue) => {
    const targets = new Map(
      (Array.isArray(items) ? items : [])
        .filter(itemNeedsAttachmentDetail)
        .map((item) => [getAppDataItemId(item), item])
        .filter(([id]) => Boolean(id))
    );

    if (targets.size === 0) return;

    const targetIds = Array.from(targets.keys()).filter((itemId) => {
      if (key !== WORK_REQUEST_APP_DATA_KEY) return true;

      const id = String(itemId || "");
      const request = targets.get(itemId);
      const attemptKey = getWorkRequestDetailFetchAttemptKey(id, request);
      const cachedAttachments = workRequestDetailsById[id]?.attachments || [];
      if (workRequestAttachmentListHasPayload(cachedAttachments)) return false;
      if (workRequestDetailFetchInFlightRef.current.has(attemptKey)) return false;
      if (workRequestDetailFetchAttemptedRef.current.has(attemptKey)) return false;
      return true;
    });

    if (targetIds.length === 0) return;

    try {
      const details = await Promise.all(
        targetIds.map(async (itemId) => {
          const attemptKey = key === WORK_REQUEST_APP_DATA_KEY
            ? getWorkRequestDetailFetchAttemptKey(itemId, targets.get(itemId))
            : "";
          if (key === WORK_REQUEST_APP_DATA_KEY) {
            workRequestDetailFetchInFlightRef.current.add(attemptKey);
          }
          try {
            const detail = await fetchAppDataItemDetail(key, itemId);
            if (key === WORK_REQUEST_APP_DATA_KEY && detail) {
              workRequestDetailFetchAttemptedRef.current.add(attemptKey);
            }
            return detail;
          } catch (error) {
            console.warn("app-data detail hydration item failed:", { key, itemId, errorMessage: error?.message || String(error) });
            return null;
          } finally {
            if (key === WORK_REQUEST_APP_DATA_KEY) {
              workRequestDetailFetchInFlightRef.current.delete(attemptKey);
            }
          }
        })
      );
      const normalizedDetails = key === WORK_REQUEST_APP_DATA_KEY
        ? details.map(normalizeWorkRequestDetailPayload)
        : details;
      const usefulDetails = normalizedDetails.filter((detail) => detail && itemHasAttachmentPayload(detail));
      if (usefulDetails.length === 0) return;

      if (key === WORK_REQUEST_APP_DATA_KEY) {
        setWorkRequestDetailsById((prev) => {
          const next = { ...prev };
          usefulDetails.forEach((detail) => {
            const detailId = getAppDataItemId(detail);
            if (detailId) next[detailId] = detail;
          });
          return next;
        });
      }

      setValue((prev) =>
        usefulDetails.reduce((list, detail) => {
          if (key === WORK_REQUEST_APP_DATA_KEY) {
            const detailId = getAppDataItemId(detail);
            const summaryDetail = sanitizeWorkRequestForListSave(detail);
            return (Array.isArray(list) ? list : []).map((item) =>
              getAppDataItemId(item) === detailId
                ? sanitizeWorkRequestForListSave({ ...item, ...summaryDetail })
                : item
            );
          }
          return mergeDetailIntoListById(list, detail, key);
        }, prev)
      );
    } catch (error) {
      console.error("app-data detail hydration failed:", key, error);
    }
  };

  const openNoticeDetail = async (noticeId, mode = "staff") => {
    const isOpen = mode === "admin" ? openAdminNoticeId === noticeId : openNoticeId === noticeId;
    if (isOpen) {
      if (mode === "admin") setOpenAdminNoticeId(null);
      else setOpenNoticeId(null);
      return;
    }

    if (mode === "admin") setOpenAdminNoticeId(noticeId);
    else setOpenNoticeId(noticeId);

    try {
      const detail = await fetchAppDataItemDetail("rc_company_notices", noticeId);
      if (detail) setCompanyNotices((prev) => mergeDetailIntoListById(prev, detail, "rc_company_notices"));
    } catch (error) {
      console.error("공지 상세 조회 실패:", error);
    }
  };

  const openAdminWorkRequestDetail = async (requestId) => {
    if (openAdminWorkRequestId === requestId) {
      setOpenAdminWorkRequestId(null);
      return;
    }

    setOpenAdminWorkRequestId(requestId);

    try {
      const request = workRequests.find((item) => getAppDataItemId(item) === String(requestId));
      const cachedAttachments = workRequestDetailsById[String(requestId)]?.attachments || [];
      if (workRequestAttachmentListHasPayload(cachedAttachments)) {
        console.log("[work-request admin detail fetch skipped cache hit]", {
          requestId,
          attachmentCount: cachedAttachments.length,
          attachments: getWorkRequestAttachmentDebugSummary(cachedAttachments),
        });
      } else {
        await loadWorkRequestDetailOnce(requestId, { context: "admin", request });
      }
    } catch (error) {
      console.error("업무요청 상세 조회 실패:", error);
    }
  };

  const openAdminHandoverDetail = async (handoverId) => {
    if (openAdminHandoverId === handoverId) {
      setOpenAdminHandoverId(null);
      return;
    }

    setOpenAdminHandoverId(handoverId);

    try {
      const detail = await fetchAppDataItemDetail("rc_handovers", handoverId);
      if (detail) setHandovers((prev) => mergeDetailIntoListById(prev, detail, "rc_handovers"));
    } catch (error) {
      console.error("인수인계 상세 조회 실패:", error);
    }
  };

  const saveAppDataItem = useCallback(async (key, value) => {
    let payloadText = "";
    let responseStatus = "";
    let responseText = "";
    try {
      const isWorkRequestDetail = String(key || "").startsWith(`${WORK_REQUEST_APP_DATA_KEY}/detail/`);
      const isWorkRequestData = key === WORK_REQUEST_APP_DATA_KEY || isWorkRequestDetail;
      const saveUrl = isWorkRequestDetail
        ? `${APP_DATA_API_BASE}/item/${encodeURIComponent(key)}`
        : APP_DATA_API_BASE;
      const requestPayload = isWorkRequestDetail ? { value } : { key, value };
      payloadText = JSON.stringify(requestPayload);

      if (isWorkRequestData) {
        const attachments = Array.isArray(value?.attachments) ? value.attachments : [];
        console.log("[work-request app-data save request]", {
          key,
          kind: isWorkRequestDetail ? "detail" : "summary",
          payloadJsonLength: payloadText.length,
          requestCount: Array.isArray(value) ? value.length : undefined,
          attachmentCount: attachments.length,
          attachmentDataUrlLengths: attachments.map((file) =>
            String(file?.dataUrl || file?.dataURL || file?.dataurl || "").length
          ),
        });
      }

      const res = await fetch(saveUrl, {

        method: "POST",

        headers: { "Content-Type": "application/json" },

        body: payloadText,

      });

      responseStatus = res.status;
      if (!res.ok) {
        responseText = await res.text().catch(() => "");
        throw new Error(`app-data save failed (${res.status})`);
      }

      if (isWorkRequestData) {
        console.log("[work-request app-data save response]", {
          key,
          kind: isWorkRequestDetail ? "detail" : "summary",
          status: res.status,
          payloadJsonLength: payloadText.length,
        });
      }

      return true;

    } catch (error) {
      console.error("app-data item save failed:", {
        key,
        payloadBytes: payloadText ? payloadText.length : getJsonPayloadByteSize({ key, value }),
        status: responseStatus || "request-not-sent",
        errorMessage: error?.message || String(error),
        responseText: responseText ? responseText.slice(0, 500) : "",
      }, error);

      return false;

    }

  }, [APP_DATA_API_BASE]);

  const saveAppDataListPreservingDetails = async (key, nextList) => {
    const safeNextList = Array.isArray(nextList) ? nextList : [];

    try {
      const fullList = await fetchAppDataItemFull(key);
      if (!Array.isArray(fullList)) {
        return await saveAppDataItem(key, safeNextList);
      }

      const previousList = parseGranularAppDataListPayload(lastGranularAppDataPayloadRef.current[key]);
      const serverMergedMutationList = mergeGranularListMutationOntoLatest(fullList, previousList, safeNextList);
      const mergedList = mergeAppDataListPreservingDetails(key, fullList, serverMergedMutationList);

      return await saveAppDataItem(key, mergedList);
    } catch (error) {
      console.error("app-data 상세 보존 저장 실패. 일반 저장으로 재시도:", key, error);
      if (APP_DATA_DETAIL_PRESERVED_KEYS.has(key)) return false;
      return await saveAppDataItem(key, safeNextList);
    }
  };



  const PARTNERS_APP_DATA_KEY = "rc_partners";
  const [partners, setPartners] = useState(() => parseLocalStorageJson(PARTNERS_APP_DATA_KEY, defaultPartners));
  const [, setPartnersServerLoaded] = useState(false);
  const isDefaultPartnersList = (list) =>
    Array.isArray(list) &&
    list.length === 1 &&
    String(list[0]?.name || "") === "샘플 입점사";
  const [partnerFilter, setPartnerFilter] = useState({ type: "전체", keyword: "" });
  const [partnerAttachments, setPartnerAttachments] = useState([]);
  const [expandedPartners, setExpandedPartners] = useState({});
  const [partnerUploadSummary, setPartnerUploadSummary] = useState("");
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const partnerTypeRef = useRef(null);
  const partnerNameRef = useRef(null);
  const partnerBusinessNumberRef = useRef(null);
  const partnerCeoRef = useRef(null);
  const partnerAddressRef = useRef(null);
  const partnerContactRef = useRef(null);
  const partnerEmailRef = useRef(null);
  const partnerManagerRef = useRef(null);
  const partnerManagerContactRef = useRef(null);
  const partnerManagerEmailRef = useRef(null);
  const partnerNoteRef = useRef(null);
  const partnerFileRef = useRef(null);
  const partnerImportFileRef = useRef(null);
  const partnerKeywordRef = useRef(null);
  const editPartnerTypeRef = useRef(null);
  const editPartnerNameRef = useRef(null);
  const editPartnerBusinessNumberRef = useRef(null);
  const editPartnerCeoRef = useRef(null);
  const editPartnerAddressRef = useRef(null);
  const editPartnerContactRef = useRef(null);
  const editPartnerEmailRef = useRef(null);
  const editPartnerManagerRef = useRef(null);
  const editPartnerManagerContactRef = useRef(null);
  const editPartnerManagerEmailRef = useRef(null);
  const editPartnerNoteRef = useRef(null);
  const editPartnerFileRef = useRef(null);

  const adPeriodOptions = Array.from({ length: 12 }, (_, i) => `${i + 1}개월`);
  const adMediaOptions = ["외부", "매장", "POP", "온라인"];
  const defaultAds = [];
  const [ads, setAds] = useState([]);
  const [commonAppDataServerLoaded, setCommonAppDataServerLoaded] = useState(false);
  const isApplyingServerDataRef = useRef(false);
  const hasHydratedAppDataRef = useRef(false);
  const hasLoadedCommonAppDataFromServerRef = useRef(false);
  const lastCommonAppDataPayloadRef = useRef("");
  const lastGranularAppDataPayloadRef = useRef({});
  const lastMenuAppDataReloadAtRef = useRef(0);
  const COMMON_APP_DATA_SYNC_ITEMS = [

    { key: "rc_employees", setValue: setEmployees },

    { key: "rc_attendance", setValue: setAttendance },

    { key: "rc_work_requests", setValue: setWorkRequests },

    { key: "rc_supply_orders", setValue: setSupplyOrders },

    { key: "rc_supply_items", setValue: setSupplyItems },

    { key: "rc_product_list", setValue: setProductList },

    { key: PRODUCTION_APP_DATA_KEY, setValue: setProductionRecords },

    { key: PURCHASE_APP_DATA_KEY, setValue: setPurchaseRecords },

    { key: "rc_handovers", setValue: setHandovers },

    { key: "rc_company_notices", setValue: setCompanyNotices },

    { key: "rc_ads", setValue: setAds },

    { key: PARTNERS_APP_DATA_KEY, setValue: setPartners },

  ];



  const [adFilter, setAdFilter] = useState({ partnerId: "전체 업체", status: "진행중", store: "전체 매장", media: "전체", keyword: "" });
  const [openAdIds, setOpenAdIds] = useState({});
  const [adFiles, setAdFiles] = useState([]);
  const [editingAdId, setEditingAdId] = useState(null);
  const adPartnerRef = useRef(null);
  const adNameRef = useRef(null);
  const adEntryStatusRef = useRef(null);
  const adStoreRef = useRef(null);
  const [selectedAdMedia, setSelectedAdMedia] = useState(["매장"]);
  const adMediaRef = useRef(null);
  const adSpecRef = useRef(null);
  const adPeriodRef = useRef(null);
  const adStartDateRef = useRef(null);
  const adEndDateRef = useRef(null);
  const adTotalAdCostRef = useRef(null);
  const adTotalPaymentRef = useRef(null);
  const adNoteRef = useRef(null);
  const adFileRef = useRef(null);
  const adKeywordRef = useRef(null);
  const editAdPartnerRef = useRef(null);
  const editAdNameRef = useRef(null);
  const editAdEntryStatusRef = useRef(null);
  const editAdStoreRef = useRef(null);
  const [editAdMedia, setEditAdMedia] = useState(["매장"]);
  const editAdMediaRef = useRef(null);
  const editAdSpecRef = useRef(null);
  const editAdPeriodRef = useRef(null);
  const editAdStartDateRef = useRef(null);
  const editAdEndDateRef = useRef(null);
  const editAdTotalAdCostRef = useRef(null);
  const editAdTotalPaymentRef = useRef(null);
  const editAdNoteRef = useRef(null);
  const editAdFileRef = useRef(null);

  const salesChannels = ["전체", "OKPOS", "OrderQueen", "Cafe24", "KSNET", "수동", "기타"];
  const salesStatusOptions = ["전체", "정상", "수정됨", "누락", "수집실패", "확인필요", "재수집완료"];
  const defaultSalesRecords = [
    { id: 1, date: "2026-05-28", store: "강남점", channel: "OKPOS", grossSales: "1285000", netSales: "1285000", orderCount: "42", source: "server", status: "정상", collectedAt: "2026.05.29 09:00", updatedAt: "", memo: "샘플 POS 매출" },
    { id: 2, date: "2026-05-28", store: "강남점", channel: "OrderQueen", grossSales: "342000", netSales: "342000", orderCount: "18", source: "server", status: "정상", collectedAt: "2026.05.29 09:00", updatedAt: "", memo: "샘플 키오스크 매출" },
    { id: 3, date: "2026-05-28", store: "홍대입구역점", channel: "OKPOS", grossSales: "875000", netSales: "875000", orderCount: "31", source: "server", status: "정상", collectedAt: "2026.05.29 09:00", updatedAt: "", memo: "샘플 POS 매출" },
    { id: 4, date: "2026-05-28", store: "온라인몰", channel: "Cafe24", grossSales: "496000", netSales: "496000", orderCount: "12", source: "server", status: "정상", collectedAt: "2026.05.29 09:00", updatedAt: "", memo: "샘플 온라인몰 매출" },
    { id: 5, date: "2026-05-28", store: "이태원점", channel: "OrderQueen", grossSales: "", netSales: "", orderCount: "", source: "server", status: "누락", collectedAt: "2026.05.29 09:00", updatedAt: "", memo: "재수집 또는 수동 입력 필요" },
  ];
  const [salesRecords, setSalesRecords] = useState(() => parseLocalStorageJson("rc_sales_records", defaultSalesRecords));

  const getYesterdayInputDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().slice(0, 10);
  };

  const [salesFilter, setSalesFilter] = useState(() => {
    const yesterday = getYesterdayInputDate();
    return { start: yesterday, end: yesterday, store: "전체 매장", channel: "전체", status: "전체", keyword: "" };
  });

  const [isRecollectingSales, setIsRecollectingSales] = useState(false);
  const [isFullRecheckingSales, setIsFullRecheckingSales] = useState(false);
  const [salesRecheckSummary, setSalesRecheckSummary] = useState(null);
  const [isManualSalesFormOpen, setIsManualSalesFormOpen] = useState(false);
  const [isSavingManualSales, setIsSavingManualSales] = useState(false);
  const [manualSalesDuplicate, setManualSalesDuplicate] = useState(null);
  const [manualSalesPreview, setManualSalesPreview] = useState({ sales: 0, count: 0 });
  const [editingSalesId, setEditingSalesId] = useState(null);
  const salesStartRef = useRef(null);
  const salesEndRef = useRef(null);
  const salesStoreRef = useRef(null);
  const salesChannelRef = useRef(null);
  const salesStatusRef = useRef(null);
  const salesKeywordRef = useRef(null);
  const salesImportFileRef = useRef(null);
  const salesDateRef = useRef(null);
  const salesStoreAddRef = useRef(null);
  const salesChannelAddRef = useRef(null);
  const salesGrossRef = useRef(null);
  const salesNetRef = useRef(null);
  const salesOrderCountRef = useRef(null);
  const salesMemoRef = useRef(null);
  const salesStatusAddRef = useRef(null);
  const editSalesDateRef = useRef(null);
  const editSalesStoreRef = useRef(null);
  const editSalesChannelRef = useRef(null);
  const editSalesGrossRef = useRef(null);
  const editSalesNetRef = useRef(null);
  const editSalesOrderCountRef = useRef(null);
  const editSalesStatusRef = useRef(null);
  const editSalesMemoRef = useRef(null);

  const resetExpandedUiState = useCallback(() => {
    setOpenNoticeId(null);
    setOpenAdminNoticeId(null);
    setOpenAdminWorkRequestId(null);
    setOpenAdminHandoverId(null);
    setOpenAdminSupplyOrderId(null);
    setExpandedEmployeeIds({});
    setExpandedStoreIds([]);
    setExpandedPartners({});
    setOpenAdIds({});
    setOpenProductionRecordIds({});
    setOpenPurchaseRecordIds({});
  }, []);

  const changeStaffMenu = useCallback((menu, options = {}) => {
    resetExpandedUiState();
    scrollToPageTop();
    if (options.noticeFilter) setStaffNoticeCategoryFilter(options.noticeFilter);
    if (options.workRequestStatus) {
      setWorkRequestFilter({ type: "전체", status: options.workRequestStatus, start: "", end: "" });
    }
    if (options.handoverStatus) {
      setHandoverFilter({ store: "전체 매장", status: options.handoverStatus, start: "", end: "" });
      setStaffHandoverPage(1);
    }
    if (options.supplyOrderStatus) {
      setSupplyOrderFilter({ store: "전체 매장", status: options.supplyOrderStatus, start: "", end: "" });
    }
    if (options.expiryRange) {
      setExpiryRangeFilter(options.expiryRange);
      setExpiryPage(1);
    }
    setStaffMenu(menu);
    setMenuReloadTick((prev) => prev + 1);
    safeLocalStorageSetItem("rc_staff_menu", menu);
  }, [resetExpandedUiState]);

  const changeAdminMenu = useCallback((menu) => {
    resetExpandedUiState();
    scrollToPageTop();
    setAdminMenu(menu);
    setMenuReloadTick((prev) => prev + 1);
    safeLocalStorageSetItem("rc_admin_menu", menu);
  }, [resetExpandedUiState]);

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobileViewport());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    resetExpandedUiState();
  }, [staffMenu, adminMenu, resetExpandedUiState]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const isIOSDevice = isIOSLikeDevice();

    if (isIOSDevice) return;

    const pushDashboardGuard = () => {
      try {
        const url = window.location.href;

        window.history.pushState(
          { rcIntranetGuard: true, step: 1, time: Date.now() },
          "",
          url
        );

        window.history.pushState(
          { rcIntranetGuard: true, step: 2, time: Date.now() },
          "",
          url
        );

        window.history.pushState(
          { rcIntranetGuard: true, step: 3, time: Date.now() },
          "",
          url
        );
      } catch (error) {
        console.warn("history guard 설정 실패:", error);
      }
    };

    pushDashboardGuard();

    const handleBackButton = () => {
      if (userRole === "admin") {
        changeAdminMenu(DASHBOARD_MENU);
      } else {
        changeStaffMenu(DASHBOARD_MENU);
      }

      setMenuOpen(false);

      window.setTimeout(() => {
        pushDashboardGuard();
      }, 50);
    };

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [isLoggedIn, userRole, changeAdminMenu, changeStaffMenu]);

  useEffect(() => {
    loadStoresFromApi();
    loadStoreAliases();
    loadUnmappedStores();
  }, []);

  useEffect(() => {
    safeLocalStorageSetItem("rc_employees", JSON.stringify(employees));
  }, [employees]);

  // attendance는 서버 app-data(rc_attendance)를 단일 원본으로 사용한다.
  // localStorage에 남은 예전 근무 기록이 서버값을 다시 덮어쓰는 일을 막기 위해 저장하지 않는다.

  // localStorage 용량 초과 방지: 대용량/첨부 포함 데이터는 서버 DB만 사용
// useEffect(() => {
//   safeLocalStorageSetItem("rc_work_requests", JSON.stringify(workRequests));
// }, [workRequests]);

// useEffect(() => {
//   safeLocalStorageSetItem("rc_supply_orders", JSON.stringify(supplyOrders));
// }, [supplyOrders]);

// useEffect(() => {
//   safeLocalStorageSetItem("rc_supply_items", JSON.stringify(supplyItems));
// }, [supplyItems]);

// useEffect(() => {
//   safeLocalStorageSetItem("rc_product_list", JSON.stringify(productList));
// }, [productList]);

// useEffect(() => {
//   safeLocalStorageSetItem("rc_handovers", JSON.stringify(handovers));
// }, [handovers]);

// useEffect(() => {
//   safeLocalStorageSetItem("rc_company_notices", JSON.stringify(companyNotices));
// }, [companyNotices]);

  useEffect(() => {
    const syncPartnersFromServer = async () => {
      const applyLocalPartnersFallback = () => {
        const localPartners = parseLocalStorageJson(PARTNERS_APP_DATA_KEY, []);
        if (Array.isArray(localPartners) && localPartners.length > 0 && !isDefaultPartnersList(localPartners)) {
          setPartners(localPartners);
        }
      };

      try {
        const serverPartners = await fetchAppDataItem(PARTNERS_APP_DATA_KEY);

        if (serverPartners !== undefined) {
          const nextPartners = Array.isArray(serverPartners) ? serverPartners : [];
          setPartners(nextPartners);
          lastGranularAppDataPayloadRef.current[PARTNERS_APP_DATA_KEY] = JSON.stringify(nextPartners);
          safeLocalStorageSetItem(PARTNERS_APP_DATA_KEY, JSON.stringify(nextPartners));
          return;
        }

        applyLocalPartnersFallback();
      } catch (error) {
        console.error("거래처 서버 동기화 실패:", error);
        applyLocalPartnersFallback();
      } finally {
        setPartnersServerLoaded(true);
      }
    };

    syncPartnersFromServer();
  }, []);

  useEffect(() => {
    const syncEmployeesForLogin = async () => {
      if (isLoggedIn) return;

      try {
        const serverEmployees = await fetchAppDataItem("rc_employees");
        if (Array.isArray(serverEmployees) && serverEmployees.length > 0) {
          setEmployees(serverEmployees);
          safeLocalStorageSetItem("rc_employees", JSON.stringify(serverEmployees));
        }
      } catch (error) {
        console.error("로그인용 직원 데이터 조회 실패:", error);
      }
    };

    syncEmployeesForLogin();
  }, [isLoggedIn]);

  useEffect(() => {
    if (isDefaultPartnersList(partners)) return;
    safeLocalStorageSetItem(PARTNERS_APP_DATA_KEY, JSON.stringify(Array.isArray(partners) ? partners : []));
  }, [partners]);

  useEffect(() => {
    safeLocalStorageSetItem(PRODUCTION_APP_DATA_KEY, JSON.stringify(Array.isArray(productionRecords) ? productionRecords : []));
  }, [productionRecords]);

  useEffect(() => {
    safeLocalStorageSetItem(PURCHASE_APP_DATA_KEY, JSON.stringify(Array.isArray(purchaseRecords) ? purchaseRecords : []));
  }, [purchaseRecords]);

  // 광고 데이터도 서버 app-data(rc_ads)를 단일 원본으로 사용한다.

  useEffect(() => {
    safeLocalStorageSetItem("rc_sales_records", JSON.stringify(salesRecords));
  }, [salesRecords]);

  useEffect(() => {

    const syncCommonAppDataFromServer = async () => {
      setCommonAppDataServerLoaded(false);
      isApplyingServerDataRef.current = true;
      hasHydratedAppDataRef.current = false;
      hasLoadedCommonAppDataFromServerRef.current = false;
      lastCommonAppDataPayloadRef.current = "";
      lastGranularAppDataPayloadRef.current = {};
      try {

        const results = await Promise.all(
          COMMON_APP_DATA_SYNC_ITEMS
            .filter(({ key }) => isLoggedIn || key === "rc_employees")
            .map(async ({ key, setValue }) => ({
              key,
              setValue,
              value: await fetchAppDataItem(key),
            }))
        );

        results.forEach(({ key, setValue, value }) => {
          if (Array.isArray(value)) {
            lastGranularAppDataPayloadRef.current[key] = JSON.stringify(value);

            if (APP_DATA_DETAIL_PRESERVED_KEYS.has(key)) {
              setValue((prev) => mergeAppDataListPreservingDetails(key, prev, value));
            } else {
              setValue(value);
            }

            if (LOCAL_STORAGE_MIRRORED_COMMON_APP_DATA_KEYS.has(key)) {
              safeLocalStorageSetItem(key, JSON.stringify(value));
            }
          }
        });
        hasLoadedCommonAppDataFromServerRef.current = true;

      } catch (error) {

        console.error("공통 app-data 서버 읽기 실패:", error);

      } finally {
        setTimeout(() => {
          isApplyingServerDataRef.current = false;
          hasHydratedAppDataRef.current = true;
          setCommonAppDataServerLoaded(true);
        }, 0);
      }

    };

    if (!isLoggedIn) {
      isApplyingServerDataRef.current = false;
      hasLoadedCommonAppDataFromServerRef.current = false;
      lastCommonAppDataPayloadRef.current = "";
      lastGranularAppDataPayloadRef.current = {};
      setCommonAppDataServerLoaded(true);
      hasHydratedAppDataRef.current = true;
      return;
    }

    syncCommonAppDataFromServer();

  }, [isLoggedIn]);

  useEffect(() => {
    if (!commonAppDataServerLoaded) return;
    if (!isLoggedIn) return;
    if (!hasHydratedAppDataRef.current) return;
    if (!hasLoadedCommonAppDataFromServerRef.current) return;
    if (isApplyingServerDataRef.current) return;

    const granularItems = [
      { key: "rc_employees", value: employees },
    ];

    granularItems.forEach(({ key, value }) => {
      const safeValue = Array.isArray(value) ? value : [];
      const payloadText = JSON.stringify(safeValue);
      const previousPayloadText = lastGranularAppDataPayloadRef.current[key];

      if (previousPayloadText === payloadText) return;

      lastGranularAppDataPayloadRef.current[key] = payloadText;
      saveAppDataItem(key, safeValue).then((saved) => {
        if (!saved) {
          lastGranularAppDataPayloadRef.current[key] = previousPayloadText;
        }
      });
    });
  }, [commonAppDataServerLoaded, isLoggedIn, employees, saveAppDataItem]);

  const canPersistCommonAppData = useCallback(() => {
    return Boolean(
      isLoggedIn &&
      commonAppDataServerLoaded &&
      hasHydratedAppDataRef.current &&
      hasLoadedCommonAppDataFromServerRef.current &&
      !isApplyingServerDataRef.current
    );
  }, [commonAppDataServerLoaded, isLoggedIn]);

  const waitForCommonAppDataPersistReady = async () => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      if (canPersistCommonAppData()) return true;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return canPersistCommonAppData();
  };

  const saveGranularAppDataListNow = useCallback(async (key, nextList) => {
    const safeNextList = Array.isArray(nextList) ? nextList : [];
    const payloadText = JSON.stringify(safeNextList);
    const previousPayloadText = lastGranularAppDataPayloadRef.current[key];

    lastGranularAppDataPayloadRef.current[key] = payloadText;
    const saved = await saveAppDataItem(key, safeNextList);
    if (!saved) {
      lastGranularAppDataPayloadRef.current[key] = previousPayloadText;
    }

    return saved;
  }, [saveAppDataItem]);

  const saveGranularAppDataListPreservingServerChanges = useCallback(async (key, nextList) => {
    const safeNextList = Array.isArray(nextList) ? nextList : [];

    if (!STALE_SAFE_GRANULAR_APP_DATA_KEYS.has(key)) {
      return await saveGranularAppDataListNow(key, safeNextList);
    }

    let latestList = [];
    try {
      const latestValue = await fetchAppDataItemFull(key);
      latestList = Array.isArray(latestValue) ? latestValue : [];
    } catch (error) {
      console.error("app-data 최신값 조회 실패. 오래된 배열 저장을 중단합니다:", key, error);
      return false;
    }

    const previousList = parseGranularAppDataListPayload(lastGranularAppDataPayloadRef.current[key]);
    const mergedList = mergeGranularListMutationOntoLatest(latestList, previousList, safeNextList);
    const previousPayloadText = lastGranularAppDataPayloadRef.current[key];
    const mergedPayloadText = JSON.stringify(mergedList);

    lastGranularAppDataPayloadRef.current[key] = mergedPayloadText;
    const saved = await saveAppDataItem(key, mergedList);
    lastGranularAppDataPayloadRef.current[key] = previousPayloadText;

    return saved;
  }, [fetchAppDataItemFull, saveAppDataItem, saveGranularAppDataListNow]);

  const persistGranularAppDataList = useCallback(async (key, nextList) => {
    if (!canPersistCommonAppData()) return false;

    try {
      return Boolean(await saveGranularAppDataListPreservingServerChanges(key, nextList));
    } catch (error) {
      console.error("app-data 목록 저장 실패:", key, error);
      return false;
    }
  }, [canPersistCommonAppData, saveGranularAppDataListPreservingServerChanges]);

  const fetchLatestGranularAppDataListForMutation = async (key) => {
    let lastError = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const latestValue = await fetchAppDataItemFull(key);
        if (Array.isArray(latestValue)) return latestValue;
        if (latestValue === null || latestValue === undefined) {
          lastError = new Error(`app-data 최신값이 비어 있음 (${key})`);
          if (attempt === 0) {
            await new Promise((resolve) => setTimeout(resolve, 250));
            continue;
          }
        }
        return [];
      } catch (error) {
        lastError = error;
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 250));
        }
      }
    }
    throw lastError || new Error(`app-data 최신값 조회 실패 (${key})`);
  };

  const mutateGranularAppDataListFromServer = async (key, setValue, mutateList) => {
    if (!(await waitForCommonAppDataPersistReady())) return { saved: false, reason: "not-ready" };

    let serverList = [];
    try {
      serverList = await fetchLatestGranularAppDataListForMutation(key);
    } catch (error) {
      console.error("app-data 최신값 조회 실패. 오래된 화면 데이터 저장을 중단합니다:", key, error);
      return { saved: false, reason: "fetch-failed" };
    }

    const nextList = mutateList(serverList);
    const safeNextList = Array.isArray(nextList) ? nextList : serverList;
    const saved = await saveGranularAppDataListNow(key, safeNextList);

    if (!saved) return { saved: false, reason: "save-failed", latestList: serverList };

    setValue(safeNextList);
    return { saved: true, nextList: safeNextList };
  };



  useEffect(() => {
    if (!commonAppDataServerLoaded) return;
    if (!isLoggedIn) return;
    if (!hasHydratedAppDataRef.current) return;
    if (!hasLoadedCommonAppDataFromServerRef.current) return;
    if (isApplyingServerDataRef.current) return;


    const payloadData = {
      rc_employees: employees,
      rc_attendance: attendance,
      rc_work_requests: workRequests,
      rc_supply_orders: supplyOrders,
      rc_supply_items: supplyItems,
      rc_product_list: productList,
      rc_production_records: productionRecords,
      rc_purchase_records: purchaseRecords,
      rc_handovers: handovers,
      rc_company_notices: companyNotices,
      rc_ads: ads,
      rc_partners: partners,
    };

    const payloadText = JSON.stringify(payloadData);

    if (lastCommonAppDataPayloadRef.current === payloadText) return;

    lastCommonAppDataPayloadRef.current = payloadText;


    // EMERGENCY DISABLED: auto bulk save caused data overwrite

    return;



    fetch(`${APP_DATA_API_BASE}/bulk`, {

      method: "POST",

      headers: { "Content-Type": "application/json" },

      body: JSON.stringify({
        data: payloadData,

      }),
    }).catch((error) => {

      console.error("공통 app-data 서버 저장 실패:", error);

    });

  }, [

    commonAppDataServerLoaded,

    employees,

    attendance,

    workRequests,

    supplyOrders,

    supplyItems,

    productList,

    productionRecords,

    purchaseRecords,

    handovers,

    companyNotices,

    ads,

    partners,

  ]);



  useEffect(() => {
    if (staffMenu !== "업무 요청") {
      requestFileGenerationRef.current += 1;
      requestFilesReadingRef.current = false;
      setRequestFilesReading(false);
      setRequestFilesReadError(false);
      setProductResults([]);
      setProductResultVisibleCount(5);
      setProductDrafts({});
      setSelectedRequestProducts([]);
      setRequestAttachments([]);
      setLastProductSearchKeyword("");
      if (productKeywordRef.current) productKeywordRef.current.value = "";
      if (requestContentRef.current) requestContentRef.current.value = "";
      if (requestFileRef.current) requestFileRef.current.value = "";
      if (requestCustomerNameRef.current) requestCustomerNameRef.current.value = "";
      if (requestCustomerContactRef.current) requestCustomerContactRef.current.value = "";
      setRequestAddress({ postalCode: "", baseAddress: "", detailAddress: "" });
      if (asPurchaseStoreRef.current) asPurchaseStoreRef.current.value = "";
      if (asPurchaseDateRef.current) asPurchaseDateRef.current.value = "";
      if (asSymptomRef.current) asSymptomRef.current.value = "";
      if (asReceiveMethodRef.current) asReceiveMethodRef.current.value = "";
    }
  }, [staffMenu]);

  useEffect(() => {
    const handleStorageSync = (event) => {
      if (event.key === "rc_employees" && event.newValue) {
        setEmployees(JSON.parse(event.newValue));
      }
    };
    window.addEventListener("storage", handleStorageSync);
    return () => window.removeEventListener("storage", handleStorageSync);
  }, []);

  useEffect(() => {
    if (!isLoggedIn || userRole !== "staff" || !currentUser) return;
    const openWork = getOpenAttendanceRecordForUser(attendance, currentUser);

    if (openWork) {
      setIsWorking(true);
      setActiveStore(openWork.store);
      setSelectedStore(openWork.store);
      setWorkStartTime(`${openWork.date} ${openWork.start}`);
      setWorkEndTime(null);
    } else {
      setIsWorking(false);
      setWorkStartTime(null);
      setWorkEndTime(null);
    }
  }, [isLoggedIn, userRole, currentUser, attendance, getOpenAttendanceRecordForUser]);

  const resetTestData = () => {
    // localStorage 완전 초기화
    safeLocalStorageClear();

    // 상태 강제 초기화
    setEmployees([...defaultEmployees]);
    setAttendance([...defaultAttendance]);
    setIsWorking(false);
    setWorkStartTime(null);
    setWorkEndTime(null);

    // 화면까지 확실하게 초기화 (가장 확실한 방법)
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  const filteredEmployees = employees.filter((employee) => {
    const keyword = employeeSearch.trim().toLowerCase();
    const roleOk = employeeRoleFilter === "전체" || employee.role === employeeRoleFilter;
    const keywordOk = !keyword ||
      String(employee.name || "").toLowerCase().includes(keyword) ||
      String(employee.id || "").toLowerCase().includes(keyword);
    return roleOk && keywordOk;
  });

  const currentEmployee = employees.find((employee) => employee.name === currentUser) || employees[0];

  const handleLogin = () => {
    const enteredId = loginIdRef.current?.value?.trim() || "";
    const enteredPw = loginPwRef.current?.value || "";
    const employeeLoginUsers = employees.map((employee) => ({
      id: employee.id,
      pw: employee.tempPassword || "1111",
      role: employee.role === "관리자" ? "admin" : "staff",
      name: employee.name,
      status: employee.status,
    }));
    const employeeAccount = employeeLoginUsers.find((u) => u.id === enteredId);
    let user = null;

    if (employeeAccount) {
      user = employeeAccount.pw === enteredPw ? employeeAccount : null;
    } else {
      user = baseUsers.find((u) => u.id === enteredId && u.pw === enteredPw);
    }
    if (user?.status === "비활성화") {
      setConfirmModal({ type: "notice", title: "로그인 불가", message: "비활성화된 계정입니다. 관리자에게 문의해주세요." });
      return;
    }
    if (!user) {
      setConfirmModal({ type: "notice", title: "로그인 실패", message: "비밀번호가 일치하지 않습니다. 관리자에게 문의해주세요." });
      return;
    }

    const openWork = getOpenAttendanceRecordForUser(attendance, user.name);
    safeLocalStorageSetItem(LOGIN_AT_KEY, String(Date.now()));
    safeLocalStorageSetItem("rc_staff_menu", DASHBOARD_MENU);
    safeLocalStorageSetItem("rc_admin_menu", DASHBOARD_MENU);
    setUserRole(user.role);
    setCurrentUser(user.name);
    setIsLoggedIn(true);
    setStaffMenu(DASHBOARD_MENU);
    setAdminMenu(DASHBOARD_MENU);
    setOpenNoticeId(null);
    setOpenAdminNoticeId(null);
    setOpenAdminWorkRequestId(null);
    setOpenAdminHandoverId(null);

    if (openWork) {
      setIsWorking(true);
      setActiveStore(openWork.store);
      setSelectedStore(openWork.store);
      setWorkStartTime(`${openWork.date} ${openWork.start}`);
      setWorkEndTime(null);
    } else {
      const employee = employees.find((item) => item.name === user.name);
      const firstAllowedStore = employee?.stores === "전체" ? stores[0] : employee?.stores?.split(",")[0]?.trim() || stores[0];
      setIsWorking(false);
      setSelectedStore(firstAllowedStore);
      setActiveStore(firstAllowedStore);
      setWorkStartTime(null);
      setWorkEndTime(null);
    }
  };

  const handleLogout = useCallback(() => {
    setIsLoggedIn(false);
    setUserRole("staff");
    setCurrentUser("");
    clearStoredLoginSession();
    safeLocalStorageSetItem("rc_staff_menu", DASHBOARD_MENU);
    safeLocalStorageSetItem("rc_admin_menu", DASHBOARD_MENU);
    if (loginIdRef.current) loginIdRef.current.value = "";
    if (loginPwRef.current) loginPwRef.current.value = "";
    setStaffMenu(DASHBOARD_MENU);
    setAdminMenu(DASHBOARD_MENU);
    setOpenNoticeId(null);
    setOpenAdminNoticeId(null);
    setOpenAdminWorkRequestId(null);
    setOpenAdminHandoverId(null);
    setIsWorking(false);
    setWorkStartTime(null);
    setWorkEndTime(null);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    const loginAt = Number(safeLocalStorageGetItem(LOGIN_AT_KEY));
    const elapsed = Date.now() - loginAt;

    if (!Number.isFinite(loginAt) || loginAt <= 0 || elapsed >= LOGIN_TTL_MS) {
      handleLogout();
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      handleLogout();
    }, LOGIN_TTL_MS - elapsed);

    return () => window.clearTimeout(timeoutId);
  }, [isLoggedIn, handleLogout]);
  const reloadAppData = async (keys = null) => {
    if (!isLoggedIn) return false;
    if (!commonAppDataServerLoaded) return false;
    if (!hasHydratedAppDataRef.current) return false;
    if (!hasLoadedCommonAppDataFromServerRef.current) return false;
    if (isApplyingServerDataRef.current) return false;

    const requestedKeys = Array.isArray(keys) && keys.length > 0
      ? Array.from(new Set(keys))
      : COMMON_APP_DATA_SYNC_ITEMS.map(({ key }) => key);

    if (requestedKeys.length === 0) return false;

    isApplyingServerDataRef.current = true;

    try {
      const targetItems = COMMON_APP_DATA_SYNC_ITEMS.filter(({ key }) => requestedKeys.includes(key));

      const results = await Promise.all(
        targetItems.map(async ({ key, setValue }) => ({
          key,
          setValue,
          value: await fetchAppDataItem(key),
        }))
      );

      results.forEach(({ key, setValue, value }) => {
        if (Array.isArray(value)) {
          lastGranularAppDataPayloadRef.current[key] = JSON.stringify(value);

          if (APP_DATA_DETAIL_PRESERVED_KEYS.has(key)) {
            setValue((prev) => mergeAppDataListPreservingDetails(key, prev, value));
          } else {
            setValue(value);
          }

          if (LOCAL_STORAGE_MIRRORED_COMMON_APP_DATA_KEYS.has(key)) {
            safeLocalStorageSetItem(key, JSON.stringify(value));
          }
        }
      });
      return true;
    } catch (e) {
      console.error("메뉴 이동시 데이터 새로고침 실패", e);
      return false;
    } finally {
      setTimeout(() => {
        isApplyingServerDataRef.current = false;
      }, 0);
    }
  };


  const getAppDataKeysForMenu = (menu) => {
    const map = {
      "대시보드": ["rc_employees", "rc_attendance", "rc_company_notices", "rc_work_requests", "rc_supply_orders", "rc_handovers", "rc_product_list"],
      "공지사항": ["rc_company_notices", "rc_attendance"],
      "공지 작성 / 관리": ["rc_company_notices", "rc_employees"],
      "업무 요청": ["rc_work_requests", "rc_product_list", "rc_attendance"],
      "업무 요청 관리": ["rc_work_requests"],
      "인수인계": ["rc_handovers", "rc_attendance"],
      "인수인계 관리": ["rc_handovers"],
      "소모품 주문": ["rc_supply_orders", "rc_supply_items", "rc_attendance"],
      "소모품 주문 관리": ["rc_supply_orders"],
      "소모품 DB 관리": ["rc_supply_items"],
      "상품 DB 관리": ["rc_product_list"],
      "생산관리": [PRODUCTION_APP_DATA_KEY, "rc_product_list", PARTNERS_APP_DATA_KEY],
      "수입/구입 관리": [PURCHASE_APP_DATA_KEY, "rc_product_list", PARTNERS_APP_DATA_KEY],
      "유통기한 관리": ["rc_product_list"],
      "광고관리": ["rc_ads", PARTNERS_APP_DATA_KEY],
      "거래처관리": [PARTNERS_APP_DATA_KEY],
      "직원 근무일지": ["rc_attendance"],
      "내 근무 기록": ["rc_attendance"],
      "직원 계정 관리": ["rc_employees"],
    };
    return map[menu] || [];
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    const keys = userRole === "admin" ? getAppDataKeysForMenu(adminMenu) : getAppDataKeysForMenu(staffMenu);
    if (keys.length === 0) return;
    reloadAppData(keys);
  }, [isLoggedIn, userRole, staffMenu, adminMenu, menuReloadTick, commonAppDataServerLoaded]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    const reloadCurrentMenuAppData = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      if (productCsvImportInProgressRef.current) return;

      const now = Date.now();
      if (now - lastMenuAppDataReloadAtRef.current < 3000) return;

      const keys = userRole === "admin"
        ? getAppDataKeysForMenu(adminMenu)
        : getAppDataKeysForMenu(staffMenu);

      if (keys.length === 0) return;

      lastMenuAppDataReloadAtRef.current = now;
      reloadAppData(keys);
    };

    window.addEventListener("focus", reloadCurrentMenuAppData);
    document.addEventListener("visibilitychange", reloadCurrentMenuAppData);

    return () => {
      window.removeEventListener("focus", reloadCurrentMenuAppData);
      document.removeEventListener("visibilitychange", reloadCurrentMenuAppData);
    };
  }, [isLoggedIn, userRole, staffMenu, adminMenu, commonAppDataServerLoaded]);


  const handleLoginKeyDown = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  const formatNow = () => new Date().toLocaleString("ko-KR", { hour12: false });
  const formatTimeOnly = () => new Date().toLocaleTimeString("ko-KR", { hour12: false, hour: "2-digit", minute: "2-digit" });
  const formatDateOnly = () => new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" }).replace(/\. /g, ".").replace(/\.$/, "");
  const getAttendancePromptDateKey = () => formatDateOnly().replace(/\D/g, "");
  const normalizeAttendancePromptStore = (value) => String(value || "").trim();
  const isAllStoreTargetValue = (value) => {
    const text = normalizeAttendancePromptStore(value);
    return text === "전체 매장" || text === "전체";
  };
  const normalizeNoticeIdentityKey = (value) => String(value || "").trim().toLowerCase();
  const getEmployeeForNoticeIdentity = (userName = currentUser) => {
    const rawUserName = normalizeNoticeIdentityKey(userName);
    return employees.find((item) =>
      normalizeNoticeIdentityKey(item.name) === rawUserName ||
      normalizeNoticeIdentityKey(item.id) === rawUserName ||
      normalizeNoticeIdentityKey(item.employeeId) === rawUserName ||
      normalizeNoticeIdentityKey(item.userId) === rawUserName ||
      normalizeNoticeIdentityKey(item.userName) === rawUserName
    );
  };
  const getCurrentStaffIdentityValues = (userName = currentUser) => {
    const rawUserName = String(userName || "").trim();
    const employee = getEmployeeForNoticeIdentity(userName);
    return Array.from(new Set([
      rawUserName,
      employee?.id,
      employee?.employeeId,
      employee?.userId,
      employee?.name,
      employee?.userName,
    ].map((value) => String(value || "").trim()).filter(Boolean)));
  };
  const getNoticeIdentityKeysFromEntry = (entry) => {
    if (entry && typeof entry === "object") {
      return [entry.id, entry.employeeId, entry.userId, entry.name, entry.userName, entry.value]
        .map(normalizeNoticeIdentityKey)
        .filter(Boolean);
    }
    return [normalizeNoticeIdentityKey(entry)].filter(Boolean);
  };
  const getNoticeCheckedIdentitySet = (checkedValue) => {
    const values = Array.isArray(checkedValue) ? checkedValue : [checkedValue];
    return new Set(values.flatMap(getNoticeIdentityKeysFromEntry));
  };
  const isCheckedByCurrentStaff = (checkedValue, userName = currentUser) => {
    const identitySet = new Set(getCurrentStaffIdentityValues(userName).map(normalizeNoticeIdentityKey));
    const checkedIdentitySet = getNoticeCheckedIdentitySet(checkedValue);
    return [...identitySet].some((value) => checkedIdentitySet.has(value));
  };
  const buildNoticeCheckedEntry = (userName = currentUser) => {
    const employee = getEmployeeForNoticeIdentity(userName);
    return {
      id: employee?.id || "",
      employeeId: employee?.employeeId || employee?.id || "",
      userId: employee?.userId || employee?.id || "",
      name: employee?.name || userName || "",
      userName: employee?.userName || employee?.id || userName || "",
      store: activeStore || selectedStore || "",
      checkedAt: formatNow(),
    };
  };
  const addNoticeCheckedEntry = (notice, userName = currentUser) => {
    const checkedBy = Array.isArray(notice?.checkedBy) ? notice.checkedBy : [];
    if (isCheckedByCurrentStaff(checkedBy, userName)) return checkedBy;
    return [...checkedBy, buildNoticeCheckedEntry(userName)];
  };
  const isRecordTargetedToStore = (record, storeName) => {
    const currentStore = normalizeAttendancePromptStore(storeName);
    const targetValues = [record?.store, record?.target, record?.targetStore]
      .map(normalizeAttendancePromptStore)
      .filter(Boolean);
    if (targetValues.some(isAllStoreTargetValue)) return true;
    return Boolean(currentStore) && targetValues.some((value) => value === currentStore);
  };
  const getUncheckedStaffStartItems = (storeName = activeStore, options = {}) => {
    const noticeCount = typeof options.noticeCount === "number"
      ? options.noticeCount
      : getUncheckedVisibleStaffNoticeCount({
        categories: ["이벤트", "전달사항"],
        storeName,
        forceWorking: true,
        debugContext: "출근 후 안내 모달 공지",
      });
    const handoverCount = handovers.filter((item) =>
      item.status === "확인 필요" &&
      isRecordTargetedToStore(item, storeName) &&
      !isCheckedByCurrentStaff(item.checkedBy)
    ).length;
    return { noticeCount, handoverCount, total: noticeCount + handoverCount };
  };
  const getTodayAttendancePromptStorageKey = (userName = currentUser, dateKey = getAttendancePromptDateKey()) => {
    const identity = getCurrentStaffIdentityValues(userName)[0] || userName || "unknown";
    return `rc_today_notice_prompt_${encodeURIComponent(identity)}_${dateKey}`;
  };
  const hasUserAttendanceOnDate = (list, userName, dateText) => {
    const targetDate = String(dateText || "").slice(0, 10).replace(/\./g, "-");
    return (Array.isArray(list) ? list : []).some((row) =>
      isAttendanceForUser(row, userName) &&
      String(row?.date || "").slice(0, 10).replace(/\./g, "-") === targetDate
    );
  };
  const getTodayAttendancePromptModal = ({ userName = currentUser, storeName = activeStore, todayText = formatDateOnly(), hadAttendanceToday = false } = {}) => {
    if (userRole !== "staff" || !userName || hadAttendanceToday) return null;
    const storageKey = getTodayAttendancePromptStorageKey(userName, String(todayText || "").replace(/\D/g, ""));
    if (safeLocalStorageGetItem(storageKey) === "true") return null;
    const counts = getUncheckedStaffStartItems(storeName);
    if (counts.total <= 0) return null;
    safeLocalStorageSetItem(storageKey, "true");
    return {
      type: "attendanceStartPrompt",
      title: "확인이 필요한 항목이 있습니다.",
      noticeCount: counts.noticeCount,
      handoverCount: counts.handoverCount,
    };
  };
  const openAttendancePromptNotices = () => {
    setConfirmModal(null);
    changeStaffMenu("공지사항", { noticeFilter: "전체" });
  };
  const openAttendancePromptHandovers = () => {
    setConfirmModal(null);
    changeStaffMenu("인수인계", { handoverStatus: "확인 필요" });
  };

  const handleWorkStart = () => {
    const alreadyWorking = getOpenAttendanceRecordForUser(attendance, currentUser);
    if (alreadyWorking || isWorking) {
      setConfirmModal({
        type: "notice",
        title: "근무 시작 불가",
        message: `이미 ${alreadyWorking?.store || activeStore}에서 근무중입니다. 먼저 근무 종료를 진행해주세요.`,
      });
      return;
    }

    setConfirmModal({
      type: "start",
      title: "근무 시작 확인",
      message: `${selectedStore} 근무를 시작하시겠습니까?`,
    });
  };

  const handleWorkEnd = () => {
    const currentRecord = getOpenAttendanceRecordForUser(attendance, currentUser);
    if (!isWorking && !currentRecord) {
      setConfirmModal({
        type: "notice",
        title: "근무 종료 불가",
        message: "현재 근무중 상태가 아닙니다.",
      });
      return;
    }
    if (currentRecord?.status === "휴게중") {
      setConfirmModal({
        type: "notice",
        title: "근무 종료 불가",
        message: "휴게중에는 근무를 종료할 수 없습니다. 휴게 종료 후 근무 종료를 진행해주세요.",
      });
      return;
    }
    setConfirmModal({
      type: "end",
      title: "근무 종료 확인",
      message: `${activeStore} 근무를 종료하시겠습니까?`,
    });
  };

  const handleBreakStart = () => {
    const currentRecord = getOpenAttendanceRecordForUser(attendance, currentUser);
    if (!currentRecord) {
      setConfirmModal({ type: "notice", title: "휴게 시작 불가", message: "근무 시작 후 휴게를 시작할 수 있습니다." });
      return;
    }
    if (currentRecord.status === "휴게중") {
      setConfirmModal({ type: "notice", title: "휴게 시작 불가", message: "이미 휴게중입니다." });
      return;
    }
    if (currentRecord.breakStart && currentRecord.breakStart !== "-" && currentRecord.breakEnd && currentRecord.breakEnd !== "-") {
      setConfirmModal({ type: "notice", title: "휴게 시작 불가", message: "이미 휴게 기록이 완료되었습니다. 추가 휴게가 필요한 경우 관리자에게 요청해주세요." });
      return;
    }
    setConfirmModal({ type: "breakStart", title: "휴게 시작 확인", message: `${activeStore} 휴게를 시작하시겠습니까?` });
  };

  const handleBreakEnd = () => {
    const currentRecord = getOpenAttendanceRecordForUser(attendance, currentUser);
    if (!currentRecord || currentRecord.status !== "휴게중") {
      setConfirmModal({ type: "notice", title: "휴게 종료 불가", message: "현재 휴게중 상태가 아닙니다." });
      return;
    }
    setConfirmModal({ type: "breakEnd", title: "휴게 종료 확인", message: `${activeStore} 휴게를 종료하고 근무를 재개하시겠습니까?` });
  };

  const handleConfirmModal = async () => {
    if (confirmModal?.type === "start") {
      const nowFull = formatNow();
      const nowTime = formatTimeOnly();
      const today = formatDateOnly();
      const hadAttendanceToday = hasUserAttendanceOnDate(attendance, currentUser, today);
      setIsWorking(true);
      setActiveStore(selectedStore);
      setWorkStartTime(nowFull);
      setWorkEndTime(null);
      const nextAttendance = [
        ...attendance.filter((row) => !(isAttendanceForUser(row, currentUser) && isOpenAttendanceRecord(row))),
        {
          id: Date.now(),
          date: today,
          endDate: "-",
          name: currentUser,
          store: selectedStore,
          start: nowTime,
          breakStart: "-",
          breakEnd: "-",
          end: "-",
          status: "근무중",
        },
      ];
      setAttendance(nextAttendance);
      persistGranularAppDataList("rc_attendance", nextAttendance);
      const attendancePromptModal = getTodayAttendancePromptModal({
        userName: currentUser,
        storeName: selectedStore,
        todayText: today,
        hadAttendanceToday,
      });
      if (attendancePromptModal) {
        setConfirmModal(attendancePromptModal);
        return;
      }
    }
    if (confirmModal?.type === "breakStart") {
      const nowTime = formatTimeOnly();
      const nextAttendance = attendance.map((row) =>
          isAttendanceForUser(row, currentUser) && row.status === "근무중"
            ? { ...row, breakStart: nowTime, breakEnd: "-", status: "휴게중" }
            : row
      );
      setAttendance(nextAttendance);
      persistGranularAppDataList("rc_attendance", nextAttendance);
    }
    if (confirmModal?.type === "breakEnd") {
      const nowTime = formatTimeOnly();
      const nextAttendance = attendance.map((row) =>
          isAttendanceForUser(row, currentUser) && row.status === "휴게중"
            ? { ...row, breakEnd: nowTime, status: "근무중" }
            : row
      );
      setAttendance(nextAttendance);
      persistGranularAppDataList("rc_attendance", nextAttendance);
    }
    if (confirmModal?.type === "end") {
      const nowFull = formatNow();
      const nowTime = formatTimeOnly();
      const endDate = formatDateOnly();
      const endUser = currentUser;
      const latestAttendance = await getLatestAttendanceListForMutation();
      let closedCount = 0;
      const nextAttendance = latestAttendance.map((row) => {
        if (isAttendanceForUser(row, endUser) && isOpenAttendanceRecord(row)) {
          closedCount += 1;
          return { ...row, endDate, end: nowTime, status: "퇴근완료" };
        }

        return row;
      });

      if (closedCount === 0) {
        setAttendance(nextAttendance);
        setIsWorking(false);
        setWorkEndTime(nowFull);
        setConfirmModal(null);
        return;
      }

      const saved = await saveGranularAppDataListNow("rc_attendance", nextAttendance);
      if (!saved) {
        setConfirmModal({
          type: "notice",
          title: "근무 종료 저장 실패",
          message: "서버 저장에 실패했습니다. 새로고침하지 말고 네트워크 상태를 확인한 뒤 다시 근무 종료를 진행해주세요.",
        });
        return;
      }

      setAttendance(nextAttendance);
      setIsWorking(false);
      setWorkEndTime(nowFull);
    }
    if (confirmModal?.type === "changeStore") {
      setSelectedStore(pendingStore);
      setActiveStore(pendingStore);
      const nextAttendance = attendance.map((row) =>
          isAttendanceForUser(row, currentUser) && row.status === "근무중"
            ? { ...row, store: pendingStore }
            : row
      );
      setAttendance(nextAttendance);
      persistGranularAppDataList("rc_attendance", nextAttendance);
      setPendingStore(null);
    }
    setConfirmModal(null);
  };

  const getAllowedStores = () => {

    const fallbackStores = [

      "강남점",

      "가로수길점",

      "청담점",

      "명동점",

      "이태원점",

      "건대점",

      "연남점",

      "홍대역점",

      "홍대어울마당점",

      "홍대클럽거리점",

      "신림점",

      "부천점",

      "분당서현점",

      "동탄점",

      "정왕점",

    ];



    const apiStores = (storeRecords || [])

      .map((store) => store?.name)

      .filter((name) => name && !isSystemStoreName(name));



    const storeOptions = apiStores.length > 0 ? apiStores : fallbackStores;



    if (userRole === "admin") return storeOptions;



    const employee = employees.find((item) => item.name === currentUser || item.id === currentUser);



    if (!employee || employee.stores === "전체") return storeOptions;



    const allowedStores = employee.stores

      .split(",")

      .map((store) => store.trim())

      .filter(Boolean);



    return allowedStores.length > 0

      ? allowedStores.filter((store) => storeOptions.includes(store))

      : storeOptions;

  };






  const handleStoreSelect = (nextStore) => {
    const allowedStores = getAllowedStores();
    if (!allowedStores.includes(nextStore)) {
      setConfirmModal({
        type: "notice",
        title: "근무 매장 선택 불가",
        message: `${nextStore}은 현재 계정에 허용된 근무 매장이 아닙니다. 관리자에게 권한을 요청해주세요.`,
      });
      return;
    }
    if (isWorking) {
      setConfirmModal({
        type: "notice",
        title: "근무 매장 변경 불가",
        message: `이미 ${activeStore} 근무를 시작했습니다. 근무 매장 변경은 관리자에게 요청해주세요.`,
      });
      return;
    }
    setSelectedStore(nextStore);
  };

  const syncEmployeeFormFromInputs = () => {
    setEmployeeForm({
      name: employeeNameRef.current?.value || "",
      id: employeeIdRef.current?.value || "",
      contact: employeeContactRef.current?.value || "",
      address: employeeAddressRef.current?.value || "",
      hireDate: employeeHireDateRef.current?.value || "",
      role: employeeRoleRef.current?.value || "직원",
    });
  };

  const toggleAllEmployeeStores = (checked) => {
    syncEmployeeFormFromInputs();
    setSelectedEmployeeStores(checked ? [...stores] : []);
  };

  const toggleEmployeeStore = (store) => {
    syncEmployeeFormFromInputs();
    setSelectedEmployeeStores((prev) =>
      prev.includes(store)
        ? prev.filter((item) => item !== store)
        : [...prev, store]
    );
  };

  const generateEmployeePassword = () => String(Math.floor(1000 + Math.random() * 9000));

  const regenerateTempPassword = () => {
    setTempPassword(generateEmployeePassword());
  };

  const normalizeEmployeeStoreText = (value, role = "직원") => {
    const raw = String(value || "").trim();
    if (role === "관리자" || raw === "전체") return "전체";
    if (!raw) return "";
    const requestedStores = raw.split(/[\n,;/]+/).map((item) => item.trim()).filter(Boolean);
    const validStores = requestedStores.filter((store) => stores.includes(store));
    return Array.from(new Set(validStores)).join(", ");
  };

  const getEmployeeStoreSummary = (employee) => {
    if (!employee?.stores) return "미지정";
    if (employee.stores === "전체") return "전체";
    const list = employee.stores.split(",").map((store) => store.trim()).filter(Boolean);
    if (list.length === 0) return "미지정";
    if (list.length === stores.length) return `전체 ${list.length}개 매장`;
    if (list.length <= 3) return list.join(", ");
    return `${list.slice(0, 2).join(", ")} 외 ${list.length - 2}개`;
  };

  const toggleEmployeeExpanded = (employeeId) => {
    setExpandedEmployeeIds((prev) => ({ ...prev, [employeeId]: !prev[employeeId] }));
  };

  const handleAddEmployee = () => {
    const enteredName = employeeNameRef.current?.value?.trim() || "";
    const enteredId = employeeIdRef.current?.value?.trim() || "";
    const enteredContact = employeeContactRef.current?.value?.trim() || "";
    const enteredAddress = employeeAddressRef.current?.value?.trim() || "";
    const enteredHireDate = employeeHireDateRef.current?.value || "";
    const enteredRole = employeeRoleRef.current?.value || "직원";

    if (!enteredName) {
      setConfirmModal({ type: "notice", title: "직원 등록 불가", message: "직원명을 입력해주세요." });
      return;
    }
    if (!enteredId) {
      setConfirmModal({ type: "notice", title: "직원 등록 불가", message: "로그인 ID를 입력해주세요." });
      return;
    }
    if (employees.some((employee) => employee.id === enteredId)) {
      setConfirmModal({ type: "notice", title: "직원 등록 불가", message: "이미 사용 중인 로그인 ID입니다." });
      return;
    }
    if (selectedEmployeeStores.length === 0) {
      setConfirmModal({ type: "notice", title: "직원 등록 불가", message: "근무 가능 매장을 최소 1개 이상 선택해주세요." });
      return;
    }

    const newEmployee = {
      name: enteredName,
      id: enteredId,
      contact: enteredContact || "-",
      address: enteredAddress || "-",
      hireDate: enteredHireDate,
      stores: enteredRole === "관리자" || selectedEmployeeStores.length === stores.length ? "전체" : selectedEmployeeStores.join(", "),
      status: "활성화",
      role: enteredRole,
      tempPassword,
    };

    setEmployees((prev) => [...prev, newEmployee]);
    setEmployeeForm({ name: "", id: "", contact: "", address: "", hireDate: "", role: "직원" });
    if (employeeNameRef.current) employeeNameRef.current.value = "";
    if (employeeIdRef.current) employeeIdRef.current.value = "";
    if (employeeContactRef.current) employeeContactRef.current.value = "";
    if (employeeAddressRef.current) employeeAddressRef.current.value = "";
    if (employeeHireDateRef.current) employeeHireDateRef.current.value = "";
    if (employeeRoleRef.current) employeeRoleRef.current.value = "직원";
    setSelectedEmployeeStores(["강남점", "홍대입구역점", "이태원점"]);
    setTempPassword(generateEmployeePassword());
    setConfirmModal({ type: "notice", title: "직원 등록 완료", message: `${newEmployee.name} 직원 계정이 등록되었습니다. 초기 비밀번호는 ${newEmployee.tempPassword}입니다.` });
  };

  const handleToggleEmployeeStatus = (employeeId) => {
    setEmployees((prev) =>
      prev.map((employee) =>
        employee.id === employeeId
          ? { ...employee, status: employee.status === "활성화" ? "비활성화" : "활성화" }
          : employee
      )
    );
  };

  const handleDeleteEmployee = (employeeId) => {
    const target = employees.find((employee) => employee.id === employeeId);
    if (target?.role === "관리자" && employees.filter((employee) => employee.role === "관리자" && employee.status === "활성화").length <= 1) {
      setConfirmModal({ type: "notice", title: "삭제 불가", message: "활성 관리자 계정은 최소 1개 이상 필요합니다." });
      return;
    }
    if (!window.confirm(`${target?.name || employeeId} 직원을 삭제하시겠습니까? 테스트 계정 삭제용으로만 사용해주세요.`)) return;
    setEmployees((prev) => prev.filter((employee) => employee.id !== employeeId));
    if (editingEmployeeId === employeeId) setEditingEmployeeId(null);
    setExpandedEmployeeIds((prev) => {
      const next = { ...prev };
      delete next[employeeId];
      return next;
    });
  };

  const handleResetEmployeePassword = (employeeId) => {
    const nextPw = generateEmployeePassword();
    setEmployees((prev) => prev.map((employee) => employee.id === employeeId ? { ...employee, tempPassword: nextPw } : employee));
    setConfirmModal({ type: "notice", title: "비밀번호 초기화 완료", message: `임시 비밀번호가 ${nextPw}로 초기화되었습니다. 직원에게 전달해주세요.` });
  };

  const handleSetEmployeePassword = (employeeId) => {
    const target = employees.find((employee) => employee.id === employeeId);
    const nextPw = window.prompt(`${target?.name || employeeId} 계정의 새 비밀번호를 입력하세요.`, "1234");
    if (nextPw === null) return;
    const trimmedPw = String(nextPw).trim();
    if (trimmedPw.length < 4) {
      setConfirmModal({ type: "notice", title: "변경 불가", message: "비밀번호는 4자 이상으로 입력해주세요." });
      return;
    }
    setEmployees((prev) => prev.map((employee) => employee.id === employeeId ? { ...employee, tempPassword: trimmedPw } : employee));
    setConfirmModal({ type: "notice", title: "비밀번호 변경 완료", message: `${target?.name || employeeId} 계정의 비밀번호가 변경되었습니다.` });
  };

  const downloadEmployeeTemplate = () => {
    downloadCsv("직원계정_일괄등록양식.csv", [
      { 직원명: "홍길동", 로그인ID: "hong", 연락처: "010-1111-2222", 주소: "서울시 강남구", 입사일: "2026-06-01", 권한: "직원", 근무가능매장: "강남점, 청담점" },
      { 직원명: "김철수", 로그인ID: "kim", 연락처: "010-3333-4444", 주소: "서울시 마포구", 입사일: "2026-06-01", 권한: "직원", 근무가능매장: "전체" },
      { 직원명: "박관리", 로그인ID: "manager", 연락처: "010-5555-6666", 주소: "본사", 입사일: "2026-06-01", 권한: "관리자", 근무가능매장: "전체" },
    ]);
  };

  const handleImportEmployeesCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setConfirmModal({ type: "notice", title: "업로드 불가", message: "현재 버전에서는 CSV 파일만 업로드할 수 있습니다. 양식을 다운로드한 뒤 Excel에서 작성하고 CSV UTF-8 형식으로 저장해주세요." });
      if (employeeImportFileRef.current) employeeImportFileRef.current.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result;
      const decodeText = (encoding) => {
        try {
          return new TextDecoder(encoding).decode(buffer).replace(/^﻿/, "");
        } catch (error) {
          return "";
        }
      };
      const requiredHeaders = ["직원명", "로그인ID", "연락처", "주소", "입사일", "권한", "근무가능매장"];
      const decodedCandidates = [decodeText("utf-8"), decodeText("euc-kr")].filter(Boolean);
      let csvRows = [];
      let headers = [];

      for (const text of decodedCandidates) {
        const rows = parseCsvText(text);
        const candidateHeaders = rows[0]?.map((header) => header.replace(/^﻿/, "").trim()) || [];
        if (requiredHeaders.some((header) => candidateHeaders.includes(header))) {
          csvRows = rows;
          headers = candidateHeaders;
          break;
        }
      }

      if (csvRows.length < 2) {
        setConfirmModal({ type: "notice", title: "업로드 불가", message: "등록할 직원 데이터가 없거나 CSV 인코딩을 읽을 수 없습니다." });
        if (employeeImportFileRef.current) employeeImportFileRef.current.value = "";
        return;
      }

      const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
      if (missingHeaders.length > 0) {
        setConfirmModal({ type: "notice", title: "업로드 양식 오류", message: `필요한 열이 없습니다: ${missingHeaders.join(", ")}` });
        if (employeeImportFileRef.current) employeeImportFileRef.current.value = "";
        return;
      }

      const getCell = (row, headerName) => headers.includes(headerName) ? (row[headers.indexOf(headerName)]?.trim() || "") : "";
      const existingIds = new Set(employees.map((employee) => employee.id));
      const uploadIds = new Set();
      const nextEmployees = [];
      const resultRows = [];
      const invalidRows = [];
      const duplicateIds = [];

      csvRows.slice(1).forEach((row, index) => {
        const name = getCell(row, "직원명");
        const id = getCell(row, "로그인ID");
        const roleRaw = getCell(row, "권한") || "직원";
        const role = ["직원", "아르바이트", "관리자"].includes(roleRaw) ? roleRaw : "직원";
        const storesText = normalizeEmployeeStoreText(getCell(row, "근무가능매장"), role);
        if (!name || !id) {
          invalidRows.push(`${index + 2}행`);
          return;
        }
        if (existingIds.has(id) || uploadIds.has(id)) {
          duplicateIds.push(id);
          return;
        }
        if (role !== "관리자" && !storesText) {
          invalidRows.push(`${index + 2}행(매장명 확인 필요)`);
          return;
        }
        uploadIds.add(id);
        const tempPw = generateEmployeePassword();
        const employee = {
          name,
          id,
          contact: getCell(row, "연락처") || "-",
          address: getCell(row, "주소") || "-",
          hireDate: getCell(row, "입사일") || "",
          stores: role === "관리자" ? "전체" : storesText,
          status: "활성화",
          role,
          tempPassword: tempPw,
        };
        nextEmployees.push(employee);
        resultRows.push({ 직원명: name, 로그인ID: id, 임시비밀번호: tempPw, 권한: role, 근무가능매장: employee.stores, 등록상태: "등록완료" });
      });

      if (nextEmployees.length > 0) {
        setEmployees((prev) => [...prev, ...nextEmployees]);
        downloadCsv("직원계정_업로드결과_임시비밀번호.csv", resultRows);
      }
      const messages = [`${nextEmployees.length}명 등록 완료`];
      if (duplicateIds.length > 0) messages.push(`중복 ID 제외: ${duplicateIds.join(", ")}`);
      if (invalidRows.length > 0) messages.push(`제외 항목: ${invalidRows.slice(0, 10).join(", ")}${invalidRows.length > 10 ? " 외" : ""}`);
      setConfirmModal({ type: "notice", title: nextEmployees.length > 0 ? "직원 일괄 등록 완료" : "직원 일괄 등록 실패", message: messages.join(String.fromCharCode(10)) });
      if (employeeImportFileRef.current) employeeImportFileRef.current.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  const handleStartEditEmployee = (employee) => {
    setEditingEmployeeId(employee.id);
    setEditEmployeeStores(employee.stores === "전체" ? [...stores] : employee.stores.split(",").map((store) => store.trim()).filter(Boolean));
    setTimeout(() => {
      if (editEmployeeNameRef.current) editEmployeeNameRef.current.value = employee.name;
      if (editEmployeeContactRef.current) editEmployeeContactRef.current.value = employee.contact;
      if (editEmployeeAddressRef.current) editEmployeeAddressRef.current.value = employee.address;
      if (editEmployeeHireDateRef.current) editEmployeeHireDateRef.current.value = employee.hireDate || "";
      if (editEmployeeRoleRef.current) editEmployeeRoleRef.current.value = employee.role || "직원";
    }, 0);
  };

  const handleSaveEditEmployee = (employeeId) => {
    const nextName = editEmployeeNameRef.current?.value?.trim() || "";
    const nextContact = editEmployeeContactRef.current?.value?.trim() || "";
    const nextAddress = editEmployeeAddressRef.current?.value?.trim() || "";
    const nextHireDate = editEmployeeHireDateRef.current?.value || "";
    const nextRole = editEmployeeRoleRef.current?.value || "직원";
    const selectedOptions = editEmployeeStores;

    if (!nextName) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "직원명을 입력해주세요." });
      return;
    }
    if (nextRole !== "관리자" && selectedOptions.length === 0) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "근무 가능 매장을 최소 1개 이상 선택해주세요." });
      return;
    }

    setEmployees((prev) =>
      prev.map((employee) =>
        employee.id === employeeId
          ? { ...employee, name: nextName, contact: nextContact || "-", address: nextAddress || "-", hireDate: nextHireDate, role: nextRole, stores: nextRole === "관리자" || selectedOptions.length === stores.length ? "전체" : selectedOptions.join(", ") }
          : employee
      )
    );
    setEditingEmployeeId(null);
    setConfirmModal({ type: "notice", title: "수정 완료", message: "직원 정보와 근무 가능 매장이 수정되었습니다." });
  };

  const dateToInput = (dateText) => dateText?.replace(/\./g, "-") || "";
  const normalizeDate = (value) => value?.replace(/-/g, ".") || "";

  const dateTimeToDate = (dateText, timeText) => {
    if (!dateText || !timeText || timeText === "-") return null;
    const inputDate = dateToInput(dateText);
    const [year, month, day] = inputDate.split("-").map(Number);
    const [hour, minute] = timeText.split(":").map(Number);
    if (![year, month, day, hour, minute].every(Number.isFinite)) return null;
    return new Date(year, month - 1, day, hour, minute);
  };

  const addDaysToInputDate = (dateText, days) => {
    const date = dateTimeToDate(dateText, "00:00");
    if (!date) return "";
    date.setDate(date.getDate() + days);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getAutoAttendanceEndDateInput = (startDateText, startTime, endTime) => {
    if (!startDateText) return "";
    if (!startTime || !endTime || startTime === "-" || endTime === "-") return dateToInput(startDateText);
    const start = dateTimeToDate(startDateText, startTime);
    const end = dateTimeToDate(startDateText, endTime);
    if (start && end && end < start) return addDaysToInputDate(startDateText, 1);
    return dateToInput(startDateText);
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return "-";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h && m) return `${h}시간 ${m}분`;
    if (h) return `${h}시간`;
    return `${m}분`;
  };

  const getBreakMinutes = (row) => {
    const start = dateTimeToDate(row.date, row.breakStart);
    const end = dateTimeToDate(row.date, row.breakEnd);
    if (start === null || end === null) return 0;
    if (end < start) end.setDate(end.getDate() + 1);
    return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  };

  const getWorkMinutes = (row) => {
    const start = dateTimeToDate(row.date, row.start);
    const hasEndDate = row.endDate && row.endDate !== "-";
    const end = dateTimeToDate(hasEndDate ? row.endDate : row.date, row.end);
    if (start === null || end === null) return 0;
    if (end < start) end.setDate(end.getDate() + 1);
    return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000 - getBreakMinutes(row)));
  };

  const getWorkTimeStyle = (row) => {
    const minutes = getWorkMinutes(row);
    if (!minutes) return { fontWeight: "bold" };
    if (minutes < 470) return { color: "#2563eb", fontWeight: "bold", background: "#dbeafe", padding: "6px 8px", borderRadius: 10, display: "inline-block" };
    if (minutes > 510) return { color: "#b91c1c", fontWeight: "bold", background: "#fee2e2", padding: "6px 8px", borderRadius: 10, display: "inline-block" };
    return { color: "#111827", fontWeight: "bold" };
  };

  const getFilteredAttendance = (baseList, filter) => {
    return baseList.filter((row) => {
      const rowDate = dateToInput(row.date);
      const storeOk = !filter.store || filter.store === "전체 매장" || row.store === filter.store;
      const nameOk = !filter.name || row.name.includes(filter.name.trim());
      const statusOk = !filter.status || filter.status === "전체" || row.status === filter.status;
      const startOk = !filter.start || rowDate >= filter.start;
      const endOk = !filter.end || rowDate <= filter.end;
      return storeOk && nameOk && statusOk && startOk && endOk;
    });
  };

  const handleSearchStaffAttendance = () => {
    setStaffAttendanceFilter((prev) => ({ ...prev }));
  };

  const handleSearchAdminAttendance = () => {
    setAdminAttendanceFilter({
      store: adminAttendanceStoreRef.current?.value || "전체 매장",
      start: adminAttendanceStartRef.current?.value || "",
      end: adminAttendanceEndRef.current?.value || "",
      name: adminAttendanceNameRef.current?.value || "",
      status: adminAttendanceStatusRef.current?.value || "전체",
    });
    setAdminAttendancePage(1);
  };

  const handleResetStaffAttendance = () => {
    setStaffAttendanceFilter({ store: "전체 매장", start: "", end: "" });
  };

  const handleResetAdminAttendance = () => {
    const today = getTodayInputDate();
    if (adminAttendanceStoreRef.current) adminAttendanceStoreRef.current.value = "전체 매장";
    if (adminAttendanceStartRef.current) adminAttendanceStartRef.current.value = today;
    if (adminAttendanceEndRef.current) adminAttendanceEndRef.current.value = today;
    if (adminAttendanceNameRef.current) adminAttendanceNameRef.current.value = "";
    if (adminAttendanceStatusRef.current) adminAttendanceStatusRef.current.value = "전체";
    setAdminAttendanceFilter({ store: "전체 매장", start: today, end: today, name: "", status: "전체" });
    setShowUnclosedOnly(false);
    setAdminAttendancePage(1);
  };

  const handleAddAttendanceRecord = () => {
    const name = newAttendanceNameRef.current?.value || "";
    const store = newAttendanceStoreRef.current?.value || "";
    const date = normalizeDate(newAttendanceDateRef.current?.value || "");
    const endDateInput = normalizeDate(newAttendanceEndDateRef.current?.value || "");
    const start = newAttendanceStartRef.current?.value || "-";
    const breakStart = newAttendanceBreakStartRef.current?.value || "-";
    const breakEnd = newAttendanceBreakEndRef.current?.value || "-";
    const end = newAttendanceEndRef.current?.value || "-";
    const status = newAttendanceStatusRef.current?.value || "퇴근완료";

    if (!name || !store || !date || !start) {
      setConfirmModal({ type: "notice", title: "근무 기록 추가 불가", message: "직원명, 매장, 출근일, 출근시간은 필수입니다." });
      return;
    }
    if (status === "퇴근완료" && (!end || end === "-")) {
      setConfirmModal({ type: "notice", title: "근무 기록 추가 불가", message: "퇴근완료 상태는 퇴근시간을 입력해야 합니다." });
      return;
    }

    const newRecord = {
      id: Date.now(),
      date,
      endDate: status === "퇴근완료" ? (endDateInput || normalizeDate(getAutoAttendanceEndDateInput(date, start, end)) || date) : "-",
      name,
      store,
      start,
      breakStart: breakStart || "-",
      breakEnd: breakEnd || "-",
      end: status === "퇴근완료" ? end : "-",
      status,
    };

    const nextAttendance = [newRecord, ...attendance];
    setAttendance(nextAttendance);
    persistGranularAppDataList("rc_attendance", nextAttendance);
    [newAttendanceDateRef, newAttendanceEndDateRef, newAttendanceStartRef, newAttendanceBreakStartRef, newAttendanceBreakEndRef, newAttendanceEndRef].forEach((ref) => { if (ref.current) ref.current.value = ""; });
    if (newAttendanceStatusRef.current) newAttendanceStatusRef.current.value = "퇴근완료";
    setConfirmModal({ type: "notice", title: "근무 기록 추가 완료", message: `${name}님의 근무 기록이 추가되었습니다.` });
  };

  const handleStartEditAttendance = (row) => {
    setEditingAttendanceId(row.id);
    setAttendanceEditDraft({
      id: row.id,
      date: dateToInput(row.date),
      endDate: row.status === "퇴근완료" ? dateToInput(row.endDate && row.endDate !== "-" ? row.endDate : row.date) : "",
      name: row.name || "",
      store: row.store || "",
      start: row.start && row.start !== "-" ? row.start : "",
      breakStart: row.breakStart && row.breakStart !== "-" ? row.breakStart : "",
      breakEnd: row.breakEnd && row.breakEnd !== "-" ? row.breakEnd : "",
      end: row.end && row.end !== "-" ? row.end : "",
      status: row.status || "퇴근완료",
      memo: row.memo || "",
    });
  };

  const downloadAdminAttendanceList = () => {
    const rows = getFilteredAttendance(attendance, adminAttendanceFilter).filter((row) => !showUnclosedOnly || isOpenAttendanceRecord(row));
    downloadAttendanceList(rows);
  };

  const handleCancelAttendanceEdit = () => {
    setEditingAttendanceId(null);
    setAttendanceEditDraft(null);
  };

  const handleAttendanceEditDraftChange = (field, value) => {
    setAttendanceEditDraft((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  const getAttendanceEditPreviewRecord = () => {
    if (!attendanceEditDraft) return null;
    const status = attendanceEditDraft.status || "근무중";
    return {
      ...attendanceEditDraft,
      date: normalizeDate(attendanceEditDraft.date || ""),
      endDate: status === "퇴근완료" ? normalizeDate(attendanceEditDraft.endDate || getAutoAttendanceEndDateInput(attendanceEditDraft.date, attendanceEditDraft.start, attendanceEditDraft.end)) : "-",
      start: attendanceEditDraft.start || "-",
      breakStart: attendanceEditDraft.breakStart || "-",
      breakEnd: attendanceEditDraft.breakEnd || "-",
      end: status === "퇴근완료" ? (attendanceEditDraft.end || "-") : "-",
      status,
    };
  };

  const handleSaveAttendanceEdit = () => {
    if (!attendanceEditDraft) return;
    const attendanceId = attendanceEditDraft.id;
    const nextDate = normalizeDate(attendanceEditDraft.date || "");
    const nextName = attendanceEditDraft.name || "";
    const nextStore = attendanceEditDraft.store || "";
    const nextStart = attendanceEditDraft.start || "-";
    const nextEnd = attendanceEditDraft.end || "-";
    const nextBreakStart = attendanceEditDraft.breakStart || "-";
    const nextBreakEnd = attendanceEditDraft.breakEnd || "-";
    const nextStatus = attendanceEditDraft.status || "근무중";
    const explicitEndDate = normalizeDate(attendanceEditDraft.endDate || "");
    const autoEndDate = normalizeDate(getAutoAttendanceEndDateInput(attendanceEditDraft.date, nextStart, nextEnd));

    if (!nextDate || !nextName || !nextStore || !nextStart) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "출근일, 직원, 매장, 출근시간은 필수입니다." });
      return;
    }
    if (nextStatus === "퇴근완료" && (!nextEnd || nextEnd === "-")) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "퇴근완료 상태는 퇴근시간을 입력해야 합니다." });
      return;
    }

    const nextAttendance = attendance.map((row) =>
        row.id === attendanceId
          ? { ...row, date: nextDate, endDate: nextStatus === "퇴근완료" ? (explicitEndDate || autoEndDate || nextDate) : "-", name: nextName, store: nextStore, start: nextStart, breakStart: nextBreakStart, breakEnd: nextBreakEnd, end: nextStatus === "퇴근완료" ? nextEnd : "-", status: nextStatus, memo: attendanceEditDraft.memo || "" }
          : row
    );
    setAttendance(nextAttendance);
    persistGranularAppDataList("rc_attendance", nextAttendance);
    handleCancelAttendanceEdit();
    setConfirmModal({ type: "notice", title: "수정 완료", message: "출퇴근 기록이 수정되었습니다." });
  };

  const handleDeleteAttendanceRecord = (attendanceId) => {
    const target = attendance.find((row) => row.id === attendanceId);
    const label = target ? `${target.date} / ${target.name} / ${target.store}` : "선택한 근무 기록";
    if (!window.confirm(`${label} 근무 기록을 삭제하시겠습니까?\n삭제한 기록은 복구할 수 없습니다.`)) return;
    const nextAttendance = attendance.filter((row) => row.id !== attendanceId);
    setAttendance(nextAttendance);
    persistGranularAppDataList("rc_attendance", nextAttendance);
    if (editingAttendanceId === attendanceId) handleCancelAttendanceEdit();
    setConfirmModal({ type: "notice", title: "삭제 완료", message: "근무 기록이 삭제되었습니다." });
  };

  const getVisibleWorkRequestsForStaff = () => {
    const allowedStores = getAllowedStores();
    return workRequests.filter((request) => {
      const storeOk = allowedStores.includes(request.receiveStore) || allowedStores.includes(request.targetStore);
      const typeOk = workRequestFilter.type === "전체" || request.type === workRequestFilter.type;
      const statusOk = workRequestFilter.status === "전체" ||
        (workRequestFilter.status === "미처리" ? request.status !== "처리완료" : request.status === workRequestFilter.status);
      const requestDate = request.date?.slice(0, 10)?.replace(/\./g, "-") || "";
      const startOk = !workRequestFilter.start || requestDate >= workRequestFilter.start;
      const endOk = !workRequestFilter.end || requestDate <= workRequestFilter.end;
      return storeOk && typeOk && statusOk && startOk && endOk;
    });
  };

  const handleSearchProduct = async () => {
    const keyword = productKeywordRef.current?.value?.trim().toLowerCase() || "";
    setLastProductSearchKeyword(keyword);
    console.log("[work-request product search] start", {
      keyword,
      productListLength: productList.length,
      activeProductListLength: activeProductList.length,
    });
    if (!keyword) {
      setProductResults([]);
      setProductResultVisibleCount(5);
      setProductDrafts({});
      setConfirmModal({ type: "notice", title: "검색어 필요", message: "상품명 또는 상품코드를 입력해주세요." });
      return;
    }
    let searchableProducts = activeProductList;
    if (productList.length === 0) {
      try {
        const serverProducts = await fetchAppDataItem("rc_product_list");
        if (Array.isArray(serverProducts)) {
          setProductList(serverProducts);
          searchableProducts = serverProducts.filter((product) => product.status !== "비활성화");
          console.log("[work-request product search] fetched product list", {
            serverProductCount: serverProducts.length,
            activeServerProductCount: searchableProducts.length,
          });
        }
      } catch (error) {
        console.error("[work-request product search] product list fetch failed", {
          keyword,
          errorMessage: error?.message || String(error),
        }, error);
      }
    }

    const results = searchableProducts.filter((product) =>
      String(product.name || "").toLowerCase().includes(keyword) ||
      String(product.code || "").toLowerCase().includes(keyword) ||
      String(product.category || "").toLowerCase().includes(keyword) ||
      String(product.barcode || "").toLowerCase().includes(keyword)
    );
    console.log("[work-request product search] result", {
      keyword,
      sourceProductCount: searchableProducts.length,
      resultCount: results.length,
    });
    setProductResults(results);
    setProductResultVisibleCount(5);
    setProductDrafts((prev) => {
      const next = {};
      results.forEach((product) => {
        next[product.code] = prev[product.code] || { qty: "1", option: "" };
      });
      return next;
    });
  };

  const handleProductSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSearchProduct();
    }
  };

  const handleSearchWorkRequests = () => {
    setWorkRequestFilter({
      type: workRequestTypeFilterRef.current?.value || "전체",
      status: workRequestStatusFilterRef.current?.value || "전체",
      start: workRequestStartFilterRef.current?.value || "",
      end: workRequestEndFilterRef.current?.value || "",
    });
  };

  const handleResetWorkRequests = () => {
    if (workRequestTypeFilterRef.current) workRequestTypeFilterRef.current.value = "전체";
    if (workRequestStatusFilterRef.current) workRequestStatusFilterRef.current.value = "전체";
    if (workRequestStartFilterRef.current) workRequestStartFilterRef.current.value = "";
    if (workRequestEndFilterRef.current) workRequestEndFilterRef.current.value = "";

    setWorkRequestFilter({
      type: "전체",
      status: "전체",
      start: "",
      end: "",
    });
  };

  const handleProductDraftChange = (productCode, field, value) => {
    setProductDrafts((prev) => ({
      ...prev,
      [productCode]: {
        qty: prev[productCode]?.qty || "1",
        option: prev[productCode]?.option || "",
        [field]: value,
      },
    }));
  };

  const handleAddRequestProduct = (product) => {
    const draft = productDrafts[product.code] || { qty: "1", option: "" };
    const qty = String(draft.qty || "").trim() || "1";
    const option = String(draft.option || "").trim();

    setSelectedRequestProducts((prev) => [
      ...prev,
      { code: product.code, name: product.name, qty, option },
    ]);

    setProductDrafts((prev) => ({
      ...prev,
      [product.code]: { qty: "1", option: "" },
    }));
  };

  const formatWorkRequestAttachmentBytes = (size) => {
    const bytes = Number(size || 0);
    if (!bytes) return "0B";
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  };

  const getCleanWorkRequestAttachmentUrl = (...values) => (
    values
      .map((value) => String(value || "").trim())
      .find((value) => value && !value.startsWith("blob:")) || ""
  );

  const isWorkRequestAttachmentImage = (file, payloadUrl = "") => {
    const name = String(file?.name || "").toLowerCase();
    const type = String(file?.mimeType || file?.type || "").toLowerCase();
    const url = String(payloadUrl || "").toLowerCase();
    return Boolean(file?.isImage) ||
      type.startsWith("image/") ||
      url.startsWith("data:image/") ||
      /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name);
  };

  const sanitizeWorkRequestAttachmentForSave = (file, options = {}) => {
    if (!file || typeof file !== "object") return null;
    const compactDataUrl = options.compactDataUrl === true;

    const name = String(file.name || file.fileName || "").trim();
    const size = Number(file.size || 0);
    const rawType = String(file.type || file.mimeType || "").trim();
    const mimeType = rawType.includes("/") ? rawType : String(file.mimeType || "").trim();
    const payloadUrl = getCleanWorkRequestAttachmentUrl(
      file.dataUrl,
      file.dataURL,
      file.dataurl,
      file.previewUrl,
      file.url,
      file.downloadUrl
    );
    const thumbnail = getCleanWorkRequestAttachmentUrl(file.thumbnail, file.thumbUrl);
    const isImage = isWorkRequestAttachmentImage(file, payloadUrl || thumbnail);

    const cleanFile = {
      id: String(file.id || `${name || "attachment"}-${size}-${rawType || mimeType || "file"}`),
      name,
      size: Number.isFinite(size) ? size : 0,
      type: rawType || mimeType || "file",
      mimeType: mimeType || rawType || "",
      isImage,
    };

    if (payloadUrl) {
      if (payloadUrl.startsWith("data:")) {
        if (compactDataUrl) cleanFile.dataUrl = payloadUrl;
        else {
          cleanFile.previewUrl = payloadUrl;
          cleanFile.dataUrl = payloadUrl;
        }
      } else {
        cleanFile.previewUrl = payloadUrl;
        cleanFile.url = payloadUrl;
      }
    } else if (thumbnail) {
      cleanFile.previewUrl = thumbnail;
    }

    if (thumbnail && (!compactDataUrl || !thumbnail.startsWith("data:"))) cleanFile.thumbnail = thumbnail;

    return cleanFile;
  };

  const sanitizeWorkRequestAttachmentsForSave = (files, options = {}) => (
    Array.isArray(files)
      ? files.map((file) => sanitizeWorkRequestAttachmentForSave(file, options)).filter(Boolean)
      : []
  );

  const sanitizeWorkRequestAttachmentForDetailSave = (file) => {
    const cleanFile = sanitizeWorkRequestAttachmentForSave(file, { compactDataUrl: true });
    if (!cleanFile) return null;

    const payloadUrl = getCleanWorkRequestAttachmentUrl(
      file?.dataUrl,
      file?.dataURL,
      file?.dataurl,
      file?.previewUrl,
      file?.url,
      file?.downloadUrl
    );

    if (payloadUrl && payloadUrl.startsWith("data:")) {
      cleanFile.dataUrl = payloadUrl;
      delete cleanFile.previewUrl;
      delete cleanFile.url;
    }

    return cleanFile;
  };

  const sanitizeWorkRequestAttachmentsForDetailSave = (files) => (
    Array.isArray(files)
      ? files.map(sanitizeWorkRequestAttachmentForDetailSave).filter(Boolean)
      : []
  );

  const sanitizeWorkRequestForDetailSave = (request) => {
    if (!request || typeof request !== "object") return request;
    return {
      ...request,
      attachments: sanitizeWorkRequestAttachmentsForDetailSave(request.attachments),
    };
  };

  const stripWorkRequestAttachmentPayloadForList = (file) => {
    const cleanFile = sanitizeWorkRequestAttachmentForSave(file);
    if (!cleanFile) return null;

    const hasData = Boolean(
      cleanFile.previewUrl ||
      cleanFile.dataUrl ||
      cleanFile.url ||
      file?.base64 ||
      file?.dataURL ||
      file?.dataurl ||
      file?.file ||
      file?.rawFile ||
      file?.originFileObj ||
      file?.blob
    );

    return {
      id: cleanFile.id,
      name: cleanFile.name,
      size: cleanFile.size,
      type: cleanFile.type,
      mimeType: cleanFile.mimeType,
      isImage: cleanFile.isImage,
      hasData,
    };
  };

  const sanitizeWorkRequestAttachmentsForListSave = (files) => (
    Array.isArray(files)
      ? files.map(stripWorkRequestAttachmentPayloadForList).filter(Boolean)
      : []
  );

  const sanitizeWorkRequestForSave = (request, options = {}) => {
    if (!request || typeof request !== "object") return request;
    if (!Array.isArray(request.attachments)) return request;
    return {
      ...request,
      attachments: sanitizeWorkRequestAttachmentsForSave(request.attachments, options),
    };
  };

  const sanitizeWorkRequestForListSave = (request) => {
    if (!request || typeof request !== "object") return request;
    const requestId = getAppDataItemId(request);
    const {
      previewUrl,
      dataUrl,
      dataURL,
      dataurl,
      base64,
      file,
      rawFile,
      originFileObj,
      blob,
      ...safeRequest
    } = request;
    return {
      ...safeRequest,
      detailKey: requestId ? getWorkRequestDetailDataKey(requestId) : request.detailKey,
      attachments: sanitizeWorkRequestAttachmentsForListSave(request.attachments),
    };
  };

  const sanitizeWorkRequestsForSave = (requests) => (
    Array.isArray(requests) ? requests.map(sanitizeWorkRequestForListSave) : []
  );

  const workRequestListHasAttachmentPayload = (requests) => {
    const safeRequests = Array.isArray(requests) ? requests : [];
    return safeRequests.some((request) =>
      Array.isArray(request?.attachments) &&
      request.attachments.some((file) => Boolean(
        file?.previewUrl ||
        file?.dataUrl ||
        file?.dataURL ||
        file?.dataurl ||
        file?.base64 ||
        file?.file ||
        file?.rawFile ||
        file?.originFileObj ||
        file?.blob ||
        (typeof file?.previewUrl === "string" && file.previewUrl.startsWith("blob:")) ||
        (typeof file?.url === "string" && file.url.startsWith("data:")) ||
        (typeof file?.thumbnail === "string" && file.thumbnail.startsWith("data:"))
      ))
    );
  };

  const WORK_REQUEST_PAYLOAD_LOG_MAX_CHARS = 10000;

  const summarizeWorkRequestPayloadAttachmentFields = (requests) => {
    const safeRequests = Array.isArray(requests) ? requests : [];
    return safeRequests.slice(0, 30).map((request) => ({
      requestId: getAppDataItemId(request),
      attachmentCount: Array.isArray(request?.attachments) ? request.attachments.length : 0,
      attachments: (Array.isArray(request?.attachments) ? request.attachments : []).slice(0, 10).map((file) => ({
        id: file?.id,
        name: file?.name,
        hasDataUrl: Object.prototype.hasOwnProperty.call(file || {}, "dataUrl"),
        hasDataURL: Object.prototype.hasOwnProperty.call(file || {}, "dataURL"),
        hasDataurl: Object.prototype.hasOwnProperty.call(file || {}, "dataurl"),
        hasPreviewUrl: Object.prototype.hasOwnProperty.call(file || {}, "previewUrl"),
        hasUrl: Object.prototype.hasOwnProperty.call(file || {}, "url"),
        hasBase64: Object.prototype.hasOwnProperty.call(file || {}, "base64"),
        hasBlob: Object.prototype.hasOwnProperty.call(file || {}, "blob"),
        hasFile: Object.prototype.hasOwnProperty.call(file || {}, "file"),
        hasRawFile: Object.prototype.hasOwnProperty.call(file || {}, "rawFile"),
        hasOriginFileObj: Object.prototype.hasOwnProperty.call(file || {}, "originFileObj"),
        previewUrlKind: typeof file?.previewUrl === "string"
          ? (file.previewUrl.startsWith("data:") ? "data" : file.previewUrl.startsWith("blob:") ? "blob" : "string")
          : typeof file?.previewUrl,
        urlKind: typeof file?.url === "string"
          ? (file.url.startsWith("data:") ? "data" : file.url.startsWith("blob:") ? "blob" : "string")
          : typeof file?.url,
        fileConstructor: file?.file?.constructor?.name || "",
        rawFileConstructor: file?.rawFile?.constructor?.name || "",
        originFileObjConstructor: file?.originFileObj?.constructor?.name || "",
        blobConstructor: file?.blob?.constructor?.name || "",
      })),
    }));
  };

  const logWorkRequestPayloadJson = (label, payload) => {
    try {
      console.log(label, JSON.stringify(payload).slice(0, WORK_REQUEST_PAYLOAD_LOG_MAX_CHARS));
    } catch (error) {
      console.error(`${label} stringify failed`, {
        errorMessage: error?.message || String(error),
      }, error);
    }
    console.log(`${label} attachment audit`, summarizeWorkRequestPayloadAttachmentFields(payload));
  };

  const getWorkRequestDetailLogPayload = (detail) => ({
    ...detail,
    attachments: (Array.isArray(detail?.attachments) ? detail.attachments : []).map((file) => ({
      ...file,
      dataUrl: file?.dataUrl ? `[dataUrl length ${String(file.dataUrl).length}]` : file?.dataUrl,
      dataURL: file?.dataURL ? `[dataURL length ${String(file.dataURL).length}]` : file?.dataURL,
      dataurl: file?.dataurl ? `[dataurl length ${String(file.dataurl).length}]` : file?.dataurl,
      previewUrl: file?.previewUrl ? `[previewUrl length ${String(file.previewUrl).length}]` : file?.previewUrl,
    })),
  });

  const logWorkRequestDetailPayload = (label, detail) => {
    const attachments = Array.isArray(detail?.attachments) ? detail.attachments : [];
    try {
      console.log(label, JSON.stringify(getWorkRequestDetailLogPayload(detail)).slice(0, WORK_REQUEST_PAYLOAD_LOG_MAX_CHARS));
    } catch (error) {
      console.error(`${label} stringify failed`, {
        errorMessage: error?.message || String(error),
      }, error);
    }
    console.log(`${label} attachment audit`, {
      requestId: getAppDataItemId(detail),
      payloadBytes: getJsonPayloadByteSize(detail),
      attachmentCount: attachments.length,
      attachments: getWorkRequestAttachmentDebugSummary(attachments),
    });
  };

  const getWorkRequestAttachmentStateAudit = (files = []) => (
    (Array.isArray(files) ? files : []).map((file) => ({
      id: file?.id,
      name: file?.name,
      size: file?.size,
      type: file?.type,
      mimeType: file?.mimeType,
      isImage: file?.isImage,
      hasDataUrl: Object.prototype.hasOwnProperty.call(file || {}, "dataUrl"),
      hasDataURL: Object.prototype.hasOwnProperty.call(file || {}, "dataURL"),
      hasDataurl: Object.prototype.hasOwnProperty.call(file || {}, "dataurl"),
      hasPreviewUrl: Object.prototype.hasOwnProperty.call(file || {}, "previewUrl"),
      hasBase64: Object.prototype.hasOwnProperty.call(file || {}, "base64"),
      hasFile: Object.prototype.hasOwnProperty.call(file || {}, "file"),
      hasRawFile: Object.prototype.hasOwnProperty.call(file || {}, "rawFile"),
      hasOriginFileObj: Object.prototype.hasOwnProperty.call(file || {}, "originFileObj"),
      hasBlob: Object.prototype.hasOwnProperty.call(file || {}, "blob"),
      previewUrlKind: typeof file?.previewUrl === "string"
        ? (file.previewUrl.startsWith("data:") ? "data" : file.previewUrl.startsWith("blob:") ? "blob" : "string")
        : typeof file?.previewUrl,
      dataUrlKind: typeof file?.dataUrl === "string"
        ? (file.dataUrl.startsWith("data:") ? "data" : file.dataUrl.startsWith("blob:") ? "blob" : "string")
        : typeof file?.dataUrl,
      fileConstructor: file?.file?.constructor?.name || "",
      rawFileConstructor: file?.rawFile?.constructor?.name || "",
      originFileObjConstructor: file?.originFileObj?.constructor?.name || "",
      blobConstructor: file?.blob?.constructor?.name || "",
    }))
  );

  const logWorkRequestAttachmentState = (label, type, files) => {
    const safeFiles = Array.isArray(files) ? files : [];
    console.log(label, {
      type,
      attachmentCount: safeFiles.length,
      attachments: getWorkRequestAttachmentStateAudit(safeFiles),
    });
  };

  const clearWorkRequestAttachmentsState = (reason) => {
    requestFileGenerationRef.current += 1;
    requestFilesReadingRef.current = false;
    setRequestFilesReading(false);
    setRequestFilesReadError(false);
    setRequestAttachments((prev) => {
      logWorkRequestAttachmentState(`[work-request attachments clear before] ${reason}`, requestType, prev);
      return [];
    });
    if (requestFileRef.current) requestFileRef.current.value = "";
    console.log(`[work-request attachments clear requested] ${reason}`, {
      type: requestType,
      attachmentCount: 0,
    });
  };

  const resetWorkRequestFormDraft = (reason, options = {}) => {
    const { nextType = "", resetType = false, resetStores = false } = options;
    console.log("[work-request form reset]", {
      reason,
      currentType: requestType,
      nextType,
      resetType,
      resetStores,
    });

    setSelectedRequestProducts([]);
    setProductResults([]);
    setProductResultVisibleCount(5);
    setProductDrafts({});
    setLastProductSearchKeyword("");
    clearWorkRequestAttachmentsState(reason);

    if (resetType) setRequestType(requestTypes[0] || requestType);
    if (resetStores) {
      setRequestReceiveStore(selectedStore);
      setRequestTargetStore("본사");
    }

    if (productKeywordRef.current) productKeywordRef.current.value = "";
    if (requestContentRef.current) requestContentRef.current.value = "";
    if (requestFileRef.current) requestFileRef.current.value = "";
    if (requestCustomerNameRef.current) requestCustomerNameRef.current.value = "";
    if (requestCustomerContactRef.current) requestCustomerContactRef.current.value = "";
    setRequestAddress({ postalCode: "", baseAddress: "", detailAddress: "" });
    if (asPurchaseStoreRef.current) asPurchaseStoreRef.current.value = "";
    if (asPurchaseDateRef.current) asPurchaseDateRef.current.value = "";
    if (asSymptomRef.current) asSymptomRef.current.value = "";
    if (asReceiveMethodRef.current) asReceiveMethodRef.current.value = "";
  };

  const getWorkRequestAttachmentSizeError = (newFiles = [], existingFiles = []) => {
    const safeNewFiles = Array.from(newFiles || []);
    const safeExistingFiles = Array.isArray(existingFiles) ? existingFiles : [];
    const allFiles = [...safeExistingFiles, ...safeNewFiles];
    const oversizedFile = allFiles.find((file) => Number(file?.size || 0) > WORK_REQUEST_ATTACHMENT_MAX_BYTES);

    if (oversizedFile) {
      return `"${oversizedFile.name || "첨부파일"}" 용량이 ${formatWorkRequestAttachmentBytes(WORK_REQUEST_ATTACHMENT_MAX_BYTES)}를 초과했습니다. 더 작은 파일로 다시 첨부해주세요.`;
    }

    const totalSize = allFiles.reduce((sum, file) => sum + Number(file?.size || 0), 0);
    if (totalSize > WORK_REQUEST_ATTACHMENT_TOTAL_MAX_BYTES) {
      return `업무요청 첨부파일 총 용량이 ${formatWorkRequestAttachmentBytes(WORK_REQUEST_ATTACHMENT_TOTAL_MAX_BYTES)}를 초과했습니다. 일부 파일을 제거한 뒤 다시 등록해주세요.`;
    }

    return "";
  };

  const showWorkRequestAttachmentSizeError = (message) => {
    setConfirmModal({
      type: "notice",
      title: "파일 용량 초과",
      message,
    });
  };

  const workRequestAttachmentListHasPayload = (files) => (
    Array.isArray(files) && files.some((file) => hasAttachmentPayload(file))
  );

  const getWorkRequestDisplayAttachments = (request) => {
    const requestId = getAppDataItemId(request);
    const detail = requestId ? workRequestDetailsById[requestId] : null;
    const detailAttachments = Array.isArray(detail?.attachments) ? detail.attachments : [];
    const listAttachments = Array.isArray(request?.attachments) ? request.attachments : [];

    if (detailAttachments.length > 0) return detailAttachments;
    return listAttachments;
  };

  const buildWorkRequestDetailPayload = async (request, options = {}) => {
    const requestId = getAppDataItemId(request);
    if (!requestId) return sanitizeWorkRequestForSave(request);

    let existingDetail = null;
    if (options.loadExistingDetail !== false) {
      try {
        existingDetail = await withTimeout(
          fetchAppDataExactKeyValue(getWorkRequestDetailDataKey(requestId)),
          WORK_REQUEST_SAVE_TIMEOUT_MS,
          "work-request-detail-fetch"
        );
      } catch (error) {
        console.warn("업무요청 기존 detail 조회 실패, 현재 요청 데이터로 detail을 저장합니다.", {
          requestId,
          errorMessage: error?.message || String(error),
        });
        existingDetail = null;
      }
    }

    const requestHasPayload = itemHasAttachmentPayload(request);
    const mergedDetail = existingDetail && typeof existingDetail === "object"
      ? {
        ...existingDetail,
        ...request,
        attachments: requestHasPayload ? request.attachments : (existingDetail.attachments || request.attachments || []),
      }
      : request;

    console.log("[work-request detail build source]", {
      requestId,
      requestHasPayload,
      requestAttachmentCount: Array.isArray(request?.attachments) ? request.attachments.length : 0,
      requestAttachments: getWorkRequestAttachmentDebugSummary(request?.attachments || []),
      existingAttachmentCount: Array.isArray(existingDetail?.attachments) ? existingDetail.attachments.length : 0,
      existingAttachments: getWorkRequestAttachmentDebugSummary(existingDetail?.attachments || []),
    });

    const detailPayload = sanitizeWorkRequestForDetailSave({
      ...mergedDetail,
      detailKey: getWorkRequestDetailDataKey(requestId),
    });

    logWorkRequestDetailPayload("[work-request detail save payload before save]", detailPayload);

    return detailPayload;
  };

  const saveWorkRequestDetailData = async (request, options = {}) => {
    const requestId = getAppDataItemId(request);
    if (!requestId) return false;

    console.log("[work-request detail save stage] detail payload build start", {
      stage: "detail payload build",
      requestId,
      attachmentCount: Array.isArray(request?.attachments) ? request.attachments.length : 0,
      attachments: getWorkRequestAttachmentDebugSummary(request?.attachments || []),
    });
    let detail = null;
    try {
      detail = await buildWorkRequestDetailPayload(request, options);
      console.log("[work-request detail save stage] detail payload build success", {
        stage: "detail payload build",
        requestId,
        payloadBytes: getJsonPayloadByteSize(detail),
        attachmentCount: Array.isArray(detail?.attachments) ? detail.attachments.length : 0,
        attachments: getWorkRequestAttachmentDebugSummary(detail?.attachments || []),
      });
    } catch (error) {
      console.error("[work-request detail save stage] detail payload build failed", {
        stage: "detail payload build",
        requestId,
        errorMessage: error?.message || String(error),
        attachmentCount: Array.isArray(request?.attachments) ? request.attachments.length : 0,
        attachments: getWorkRequestAttachmentDebugSummary(request?.attachments || []),
      }, error);
      return false;
    }
    let saved = false;

    try {
      console.log("[work-request detail save stage] detail save start", {
        stage: "detail save",
        key: getWorkRequestDetailDataKey(requestId),
        requestId,
        payloadBytes: getJsonPayloadByteSize(detail),
        attachmentCount: Array.isArray(detail?.attachments) ? detail.attachments.length : 0,
        attachments: getWorkRequestAttachmentDebugSummary(detail?.attachments || []),
      });
      saved = await withTimeout(
        saveAppDataItem(getWorkRequestDetailDataKey(requestId), detail),
        WORK_REQUEST_SAVE_TIMEOUT_MS,
        "work-request-detail-save"
      );
      console.log("[work-request detail save stage] detail save result", {
        stage: "detail save",
        requestId,
        saved,
      });
    } catch (error) {
      console.error("업무요청 detail 저장 중단:", {
        key: getWorkRequestDetailDataKey(requestId),
        requestId,
        payloadBytes: getJsonPayloadByteSize(detail),
        errorMessage: error?.message || String(error),
      }, error);
      return false;
    }

    if (!saved) {
      console.error("업무요청 detail 저장 실패:", {
        key: getWorkRequestDetailDataKey(requestId),
        requestId,
        payloadBytes: getJsonPayloadByteSize(detail),
      });
      return false;
    }

    void (async () => {
      try {
        const savedDetail = normalizeWorkRequestDetailPayload(
          await withTimeout(
            fetchAppDataExactKeyValue(getWorkRequestDetailDataKey(requestId)),
            WORK_REQUEST_SAVE_TIMEOUT_MS,
            "work-request-detail-verify-fetch"
          )
        );
        logWorkRequestDetailPayload("[work-request detail save verify fetch]", savedDetail);
      } catch (error) {
        console.error("[work-request detail save verify fetch failed]", {
          stage: "detail verify fetch",
          requestId,
          detailKey: getWorkRequestDetailDataKey(requestId),
          errorMessage: error?.message || String(error),
        }, error);
      }
    })();

    return saved;
  };

  const clearWorkRequestDetailData = async (requestId) => {
    if (requestId === null || requestId === undefined || requestId === "") return true;
    return await saveAppDataItem(getWorkRequestDetailDataKey(requestId), null);
  };

  const cleanupFailedWorkRequestSubmit = async (requestId, reason) => {
    console.warn("[work-request submit cleanup]", { requestId, reason });
    resetWorkRequestFormDraft(`submit-failed:${reason}`, { resetType: true });
    setWorkRequestDetailsById((prev) => {
      if (!requestId || !prev[requestId]) return prev;
      const next = { ...prev };
      delete next[requestId];
      return next;
    });

    if (requestId) {
      try {
        await clearWorkRequestDetailData(requestId);
      } catch (error) {
        console.error("[work-request submit cleanup detail clear failed]", {
          requestId,
          detailKey: getWorkRequestDetailDataKey(requestId),
          errorMessage: error?.message || String(error),
        }, error);
      }
    }
  };

  const saveWorkRequestsSummaryList = async (nextList, options = {}) => {
    const explicitDetailItems = Array.isArray(options.detailItemsToSave) ? options.detailItemsToSave : [];
    const detailItemsToSave = explicitDetailItems.filter((request) => getAppDataItemId(request) && itemHasAttachmentPayload(request));

    console.log("[업무요청 저장 단계] before detail save", {
      detailSaveCount: detailItemsToSave.length,
      requestCount: Array.isArray(nextList) ? nextList.length : 0,
    });

    for (const request of detailItemsToSave) {
      const detailSaved = await saveWorkRequestDetailData(request, options.detailSaveOptions || {});
      if (!detailSaved) {
        console.error("업무요청 detail 선저장 실패로 목록 저장 중단:", {
          key: getWorkRequestDetailDataKey(getAppDataItemId(request)),
          requestId: getAppDataItemId(request),
        });
        return false;
      }
    }

    console.log("[업무요청 저장 단계] after detail save", {
      detailSaveCount: detailItemsToSave.length,
    });

    const safeNextList = sanitizeWorkRequestsForSave(nextList);
    const listPayloadBytes = getJsonPayloadByteSize(safeNextList);
    const listHasAttachmentPayload = workRequestListHasAttachmentPayload(safeNextList);
    logWorkRequestPayloadJson("[work-request summary sanitized input payload JSON]", safeNextList);

    console.log("[업무요청 저장 진단]", {
      label: options.debugLabel || "summary-list-save",
      requestCount: safeNextList.length,
      detailSaveCount: detailItemsToSave.length,
      listPayloadBytes,
      listHasAttachmentPayload,
    });

    if (listHasAttachmentPayload) {
      console.error("업무요청 목록 저장 payload에 첨부 원본 데이터가 남아 있어 저장을 중단합니다.", {
        key: WORK_REQUEST_APP_DATA_KEY,
        requestCount: safeNextList.length,
        payloadBytes: listPayloadBytes,
      });
      return false;
    }

    try {
      console.log("[업무요청 저장 단계] before list fetch", { key: WORK_REQUEST_APP_DATA_KEY });
      const fullList = await withTimeout(
        fetchAppDataItem(WORK_REQUEST_APP_DATA_KEY),
        WORK_REQUEST_SAVE_TIMEOUT_MS,
        "work-request-list-fetch"
      );
      console.log("[업무요청 저장 단계] after list fetch", {
        isArray: Array.isArray(fullList),
        count: Array.isArray(fullList) ? fullList.length : 0,
      });
      if (!Array.isArray(fullList)) {
        console.log("[업무요청 저장 단계] before list save", {
          key: WORK_REQUEST_APP_DATA_KEY,
          requestCount: safeNextList.length,
          mode: "replace",
        });
        logWorkRequestPayloadJson("[work-request summary save final payload JSON]", safeNextList);
        const saved = await withTimeout(
          saveAppDataItem(WORK_REQUEST_APP_DATA_KEY, safeNextList),
          WORK_REQUEST_SAVE_TIMEOUT_MS,
          "work-request-list-save"
        );
        console.log("[업무요청 저장 단계] after list save", { saved, mode: "replace" });
        return saved;
      }

      const lightServerList = sanitizeWorkRequestsForSave(fullList);
      const previousList = sanitizeWorkRequestsForSave(parseGranularAppDataListPayload(lastGranularAppDataPayloadRef.current[WORK_REQUEST_APP_DATA_KEY]));
      const serverMergedMutationList = mergeGranularListMutationOntoLatest(lightServerList, previousList, safeNextList);
      const mergedList = mergeAppDataListPreservingDetails(WORK_REQUEST_APP_DATA_KEY, lightServerList, serverMergedMutationList);

      const finalList = sanitizeWorkRequestsForSave(mergedList);
      const finalListHasAttachmentPayload = workRequestListHasAttachmentPayload(finalList);
      if (finalListHasAttachmentPayload) {
        console.error("업무요청 최종 목록 payload에 첨부 원본 데이터가 남아 있어 저장을 중단합니다.", {
          key: WORK_REQUEST_APP_DATA_KEY,
          requestCount: finalList.length,
          payloadBytes: getJsonPayloadByteSize(finalList),
        });
        return false;
      }
      console.log("[업무요청 저장 단계] before list save", {
        key: WORK_REQUEST_APP_DATA_KEY,
        requestCount: finalList.length,
        mode: "merge",
      });
      logWorkRequestPayloadJson("[work-request summary save final payload JSON]", finalList);
      const saved = await withTimeout(
        saveAppDataItem(WORK_REQUEST_APP_DATA_KEY, finalList),
        WORK_REQUEST_SAVE_TIMEOUT_MS,
        "work-request-list-save"
      );
      console.log("[업무요청 저장 단계] after list save", { saved, mode: "merge" });
      return saved;
    } catch (error) {
      console.error("업무요청 목록 저장 실패:", {
        key: WORK_REQUEST_APP_DATA_KEY,
        stage: "summary save",
        requestCount: safeNextList.length,
        payloadBytes: listPayloadBytes,
        errorMessage: error?.message || String(error),
      }, error);
      return false;
    }
  };

  const handleRequestFileChange = async (e) => {
    if (requestFilesReadingRef.current || workRequestSubmittingRef.current) return;
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    logWorkRequestAttachmentState("[work-request attachment select current state]", requestType, requestAttachments);
    const sizeError = getWorkRequestAttachmentSizeError(selectedFiles, requestAttachments);
    if (sizeError) {
      showWorkRequestAttachmentSizeError(sizeError);
      if (requestFileRef.current) requestFileRef.current.value = "";
      return;
    }

    const generation = ++requestFileGenerationRef.current;
    requestFilesReadingRef.current = true;
    setRequestFilesReading(true);
    setRequestFilesReadError(false);
    let timer;
    try {
      const prepared = await Promise.race([
        makePersistentAttachments(selectedFiles),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("file_read_timeout")), 15000); }),
      ]);
      if (generation !== requestFileGenerationRef.current) return;
      const files = sanitizeWorkRequestAttachmentsForSave(prepared);
      if (files.length !== selectedFiles.length || !files.every(hasUsableRequestAttachment)) throw new Error("file_read_failed");
      setRequestAttachments((prev) => {
      const merged = [...prev];
      files.forEach((file) => {
        if (!merged.some((item) => item.id === file.id)) merged.push(file);
      });
      const next = sanitizeWorkRequestAttachmentsForSave(merged);
      logWorkRequestAttachmentState("[work-request attachment select next state]", requestType, next);
      return next;
      });
    } catch (error) {
      if (generation !== requestFileGenerationRef.current) return;
      setRequestFilesReadError(true);
      setConfirmModal({ type: "notice", title: "첨부 읽기 실패", message: "파일을 읽지 못했습니다. 파일을 다시 선택해주세요." });
    } finally {
      clearTimeout(timer);
      if (generation === requestFileGenerationRef.current) {
        requestFilesReadingRef.current = false;
        setRequestFilesReading(false);
        if (requestFileRef.current) requestFileRef.current.value = "";
      }
    }
  };

  const handleRemoveRequestAttachment = (fileId) => {
    setRequestAttachments((prev) => {
      const next = sanitizeWorkRequestAttachmentsForSave(prev.filter((file) => file.id !== fileId));
      logWorkRequestAttachmentState("[work-request attachment remove before state]", requestType, prev);
      logWorkRequestAttachmentState("[work-request attachment remove after state]", requestType, next);
      return next;
    });
    if (requestFileRef.current) requestFileRef.current.value = "";
  };

  const formatFileSize = (size) => {
    if (!size) return "-";
    if (size < 1024) return `${size}B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)}KB`;
    return `${(size / 1024 / 1024).toFixed(1)}MB`;
  };

  const isImageFile = (file) => {
    const name = String(file?.name || "").toLowerCase();
    const type = String(file?.type || "").toLowerCase();
    return Boolean(file?.isImage) || type.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name);
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () => resolve("");
      reader.readAsDataURL(file);
    });

  const makePersistentAttachments = async (fileList) => {
    const rawFiles = Array.from(fileList || []);
    return Promise.all(rawFiles.map(async (file) => {
      const previewUrl = await readFileAsDataUrl(file);
      return {
        id: `${file.name}-${file.size}-${file.lastModified}`,
        name: file.name,
        size: file.size,
        type: file.type || "파일",
        isImage: file.type?.startsWith("image/") || false,
        previewUrl,
      };
    }));
  };

  const getFileOpenUrl = (file) => file?.dataUrl || file?.dataURL || file?.dataurl || file?.previewUrl || file?.url || file?.downloadUrl || "";



  const openAttachmentFile = (file) => {
    const fileUrl = getFileOpenUrl(file);

    if (!fileUrl) return;

    if (isImageFile(file)) {
      const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${file.name || "첨부 이미지"}</title>
        </head>
        <body style="margin:0;background:#111;display:flex;align-items:center;justify-content:center;min-height:100vh;">
          <img src="${fileUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;" />
        </body>
      </html>
    `;

      const blob = new Blob([html], { type: "text/html" });
      const blobUrl = URL.createObjectURL(blob);

      window.open(blobUrl, "_blank");
      return;
    }

    window.open(fileUrl, "_blank");
  };

  const handleRemoveRequestProduct = (index) => {
    setSelectedRequestProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const resetWorkRequestDraftForTypeChange = (nextType) => {
    resetWorkRequestFormDraft(`type-change:${requestType}->${nextType}`, { nextType });
  };

  const handleChangeWorkRequestType = (type) => {
    if (isSubmittingWorkRequest) return;
    if (type === requestType) return;
    resetWorkRequestDraftForTypeChange(type);
    setRequestType(type);
  };

  const handleSubmitWorkRequest = async () => {
    if (workRequestSubmittingRef.current) return;
    console.log("[업무요청 저장 단계] submit start");
    logWorkRequestAttachmentState("[work-request submit start attachment state]", requestType, requestAttachments);
    workRequestSubmittingRef.current = true;
    setIsSubmittingWorkRequest(true);
    const timing = createSaveTimingLogger("업무요청 저장 시간");
    let submitSaved = false;
    let saveStage = "start";
    let pendingWorkRequestId = null;
    let shouldCleanupFailedSubmit = false;
    try {
    const content = requestContentRef.current?.value?.trim() || "";
    const customerName = requestCustomerNameRef.current?.value?.trim() || "";
    const customerContact = formatRequestPhone(requestCustomerContactRef.current?.value || "");
    const asCustomerAddress = requestAddressText(requestAddress).trim();
    const asPurchaseStore = asPurchaseStoreRef.current?.value || "";
    const asPurchaseDate = asPurchaseDateRef.current?.value || "";
    const asSymptom = asSymptomRef.current?.value || "";
    const asReceiveMethod = asReceiveMethodRef.current?.value || "";

    if (!requestReceiveStore) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "접수 매장을 선택해주세요." });
      return;
    }
    if (requestType !== "이슈보고" && selectedRequestProducts.length === 0) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "상품을 검색해 1개 이상 추가해주세요." });
      return;
    }
    if (requestType !== "배송 요청" && !content) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "업무 요청 내용을 입력해주세요." });
      return;
    }
    if (requestType === "A/S 요청" && (!customerName || !customerContact || !asCustomerAddress || !asPurchaseStore || !asPurchaseDate || !asSymptom || !asReceiveMethod)) {
      setConfirmModal({ type: "notice", title: "A/S 정보 필요", message: "A/S 요청은 고객명, 연락처, 주소, 구매지점, 구매일, 증상, 수령방법을 모두 입력해야 합니다." });
      return;
    }

    const requiresCustomerAttachment = requestType === "A/S 요청" || requestType === "배송 요청";
    if (requestType === "배송 요청" && (!customerName || !customerContact || !/^\d{5}$/.test(requestAddress.postalCode) || !requestAddress.baseAddress.trim())) {
      setConfirmModal({ type: "notice", title: "배송 정보 필요", message: "고객명, 연락처, 우편번호, 기본주소를 입력해주세요." });
      return;
    }
    if (requiresCustomerAttachment && !isValidRequestPhone(customerContact)) {
      setConfirmModal({ type: "notice", title: "연락처 확인", message: "국내 휴대전화 또는 일반전화 번호를 정확히 입력해주세요. 초과한 숫자는 자동으로 잘라 저장하지 않습니다." });
      return;
    }

    saveStage = "file prepare";
    const attachmentsForSave = sanitizeWorkRequestAttachmentsForSave(requestAttachments);
    if (requiresCustomerAttachment && (requestFilesReadingRef.current || requestFilesReadError || !attachmentsForSave.some(hasUsableRequestAttachment))) {
      setConfirmModal({ type: "notice", title: "첨부파일 필요", message: "읽기가 완료된 유효한 첨부파일을 1개 이상 추가해주세요. 읽기에 실패한 파일은 다시 선택해주세요." });
      return;
    }
    const attachmentSizeError = getWorkRequestAttachmentSizeError([], attachmentsForSave);
    if (attachmentSizeError) {
      showWorkRequestAttachmentSizeError(attachmentSizeError);
      return;
    }
    timing.step("file prepare", {
      attachmentCount: attachmentsForSave.length,
      attachmentBytes: attachmentsForSave.reduce((sum, file) => sum + Number(file?.size || 0), 0),
    });

    const newRequest = {
      id: Date.now(),
      type: requestType,
      receiveStore: requestReceiveStore,
      targetStore: requestType === "이슈보고" ? requestReceiveStore : requestTargetStore,
      products: selectedRequestProducts,
      attachments: attachmentsForSave,
      customerName,
      customerContact,
      asInfo: requestType === "A/S 요청" ? {
        ...requestAddress,
        customerAddress: asCustomerAddress,
        purchaseStore: asPurchaseStore,
        purchaseDate: asPurchaseDate,
        symptom: asSymptom,
        receiveMethod: asReceiveMethod,
        receptionManager: currentUser,
        receptionStore: requestReceiveStore,
      } : null,
      ...(requestType === "배송 요청" ? { deliveryInfo: { ...requestAddress, customerAddress: requestAddressText(requestAddress) } } : {}),
      content,
      status: "접수",
      writer: currentUser,
      date: formatNow(),
      adminMemo: "",
    };
    pendingWorkRequestId = newRequest.id;
    shouldCleanupFailedSubmit = true;
    console.log("[work-request submit new request detail source]", {
      requestId: newRequest.id,
      type: newRequest.type,
      attachmentCount: attachmentsForSave.length,
      attachments: getWorkRequestAttachmentDebugSummary(attachmentsForSave),
    });

    const nextWorkRequests = [newRequest, ...workRequests];
    saveStage = "summary save";
    let saved = false;
    try {
      saved = await withTimeoutTask(
        () => saveWorkRequestsSummaryList(nextWorkRequests, {
          debugLabel: "create work request",
          detailItemsToSave: [newRequest],
          detailSaveOptions: { loadExistingDetail: false },
        }),
        WORK_REQUEST_SAVE_TIMEOUT_MS,
        "work-request-submit-save"
      );
    } catch (error) {
      console.error("[업무요청 저장 단계] submit catch", {
        stage: saveStage,
        requestCount: nextWorkRequests.length,
        payloadBytes: getJsonPayloadByteSize(sanitizeWorkRequestsForSave(nextWorkRequests)),
        errorMessage: error?.message || String(error),
      }, error);
      throw error;
    }
    submitSaved = saved;
    timing.step("summary save", { saved, count: nextWorkRequests.length });
    if (!saved) {
      console.error("업무요청 등록 저장 실패:", {
        stage: saveStage,
        requestCount: nextWorkRequests.length,
        payloadBytes: getJsonPayloadByteSize(sanitizeWorkRequestsForSave(nextWorkRequests)),
        hasAttachmentDataInSummary: workRequestListHasAttachmentPayload(sanitizeWorkRequestsForSave(nextWorkRequests)),
        hasAttachments: attachmentsForSave.length > 0,
      });
      setConfirmModal({
        type: "notice",
        title: "서버 저장 실패",
        message: "업무 요청을 서버에 저장하지 못했습니다. 네트워크와 FastAPI 서버를 확인한 뒤 다시 시도해주세요.",
      });
      await cleanupFailedWorkRequestSubmit(pendingWorkRequestId, "summary-save-false");
      return;
    }

    console.log("[업무요청 저장 단계] submit success");
    saveStage = "state update";
    const summaryWorkRequests = sanitizeWorkRequestsForSave(nextWorkRequests);
    if (itemHasAttachmentPayload(newRequest)) {
      setWorkRequestDetailsById((prev) => ({
        ...prev,
        [newRequest.id]: newRequest,
      }));
    }
    setWorkRequests(summaryWorkRequests);
    timing.step("state update", { count: summaryWorkRequests.length });
    resetWorkRequestFormDraft("submit-success", { resetType: true });
    setConfirmModal({ type: "notice", title: "업무 요청 등록 완료", message: "업무 요청이 접수되었습니다. 진행 상태는 업무 요청 메뉴에서 확인할 수 있습니다." });
    } catch (error) {
      console.error("업무 요청 등록 실패:", error);
      if (shouldCleanupFailedSubmit) {
        await cleanupFailedWorkRequestSubmit(pendingWorkRequestId, `catch:${saveStage}`);
      }
      setConfirmModal({
        type: "notice",
        title: "업무 요청 등록 실패",
        message: "업무 요청 등록 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
      });
    } finally {
      console.log("[업무요청 저장 단계] submit finally", { saved: submitSaved, stage: saveStage });
      timing.total({ saved: submitSaved });
      workRequestSubmittingRef.current = false;
      setIsSubmittingWorkRequest(false);
    }
  };

  const handleUpdateWorkRequestStatus = async (requestId, nextStatus) => {
    const currentRequest = workRequests.find((request) => request.id === requestId);
    const memo = (adminWorkRequestMemos[requestId] || currentRequest?.adminMemo || "").trim();

    if (nextStatus === "처리완료" && !memo) {
      setConfirmModal({
        type: "notice",
        title: "처리 메모 필요",
        message: "처리완료로 변경하려면 처리 메모를 입력해주세요.",
      });
      return;
    }

    const nextWorkRequests = workRequests.map((request) =>
      request.id === requestId
        ? { ...request, status: nextStatus, adminMemo: memo || request.adminMemo }
        : request
    );
    const updatedRequest = nextWorkRequests.find((request) => request.id === requestId);
    if (updatedRequest) await saveWorkRequestDetailData(updatedRequest);

    const summaryWorkRequests = sanitizeWorkRequestsForSave(nextWorkRequests);
    const saved = await saveWorkRequestsSummaryList(summaryWorkRequests, { debugLabel: "update work request status" });
    if (!saved) {
      console.error("업무요청 상태 변경 저장 실패:", {
        stage: "status summary save",
        requestId,
        requestCount: summaryWorkRequests.length,
        payloadBytes: getJsonPayloadByteSize(summaryWorkRequests),
        hasAttachmentDataInSummary: workRequestListHasAttachmentPayload(summaryWorkRequests),
      });
      setConfirmModal({
        type: "notice",
        title: "서버 저장 실패",
        message: "업무 요청 상태를 서버에 저장하지 못했습니다. 첨부파일 정보를 확인한 뒤 다시 시도해주세요.",
      });
      return;
    }

    setWorkRequests(summaryWorkRequests);
    if (updatedRequest) {
      setWorkRequestDetailsById((prev) => {
        const detail = prev[requestId];
        if (!detail) return prev;
        return {
          ...prev,
          [requestId]: {
            ...detail,
            status: nextStatus,
            adminMemo: memo || detail.adminMemo,
          },
        };
      });
    }

    setConfirmModal({
      type: "notice",
      title: "상태 변경 완료",
      message: "업무 요청 상태가 변경되었습니다.",
    });
  };

  const handleDeleteWorkRequest = async (requestId) => {
    if (!window.confirm("이 업무 요청을 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.")) return;

    const nextWorkRequests = workRequests.filter((request) => request.id !== requestId);
    const summaryWorkRequests = sanitizeWorkRequestsForSave(nextWorkRequests);
    logWorkRequestPayloadJson("[work-request delete raw payload JSON]", nextWorkRequests);
    logWorkRequestPayloadJson("[work-request delete sanitized payload JSON]", summaryWorkRequests);
    const saved = await saveWorkRequestsSummaryList(summaryWorkRequests, { debugLabel: "delete work request" });
    if (!saved) {
      console.error("업무요청 삭제 저장 실패:", {
        stage: "delete summary save",
        requestId,
        requestCount: summaryWorkRequests.length,
        payloadBytes: getJsonPayloadByteSize(summaryWorkRequests),
        hasAttachmentDataInSummary: workRequestListHasAttachmentPayload(summaryWorkRequests),
      });
      setConfirmModal({
        type: "notice",
        title: "서버 저장 실패",
        message: "업무 요청 삭제 정보를 서버에 저장하지 못했습니다. 다시 시도해주세요.",
      });
      return;
    }

    const detailCleared = await clearWorkRequestDetailData(requestId);
    if (!detailCleared) {
      console.error("업무요청 detail 삭제 정리 실패:", {
        key: getWorkRequestDetailDataKey(requestId),
        requestId,
      });
    }

    setWorkRequests(summaryWorkRequests);
    setWorkRequestDetailsById((prev) => {
      if (!prev[requestId]) return prev;
      const next = { ...prev };
      delete next[requestId];
      return next;
    });

    if (openAdminWorkRequestId === requestId) {
      setOpenAdminWorkRequestId(null);
    }

    setConfirmModal({
      type: "notice",
      title: "삭제 완료",
      message: "업무 요청이 삭제되었습니다.",
    });
  };

  const isIncompleteAdminWorkRequest = (request) => String(request?.status || "").trim() !== "처리완료";
  const getAdminIncompleteWorkRequests = () => workRequests.filter(isIncompleteAdminWorkRequest);

  const getFilteredAdminWorkRequests = () => {
    return workRequests.filter((request) => {
      const requestDate = request.date?.slice(0, 10)?.replace(/\./g, "-") || "";
      const typeOk = adminWorkRequestFilter.type === "전체" || request.type === adminWorkRequestFilter.type;
      const statusOk = adminWorkRequestFilter.status === "전체" ||
        (adminWorkRequestFilter.status === "미처리" ? isIncompleteAdminWorkRequest(request) : request.status === adminWorkRequestFilter.status);
      const storeOk = adminWorkRequestFilter.store === "전체 매장" || request.receiveStore === adminWorkRequestFilter.store || request.targetStore === adminWorkRequestFilter.store;
      const startOk = !adminWorkRequestFilter.start || requestDate >= adminWorkRequestFilter.start;
      const endOk = !adminWorkRequestFilter.end || requestDate <= adminWorkRequestFilter.end;
      const keyword = adminWorkRequestFilter.keyword.trim();
      const keywordOk = !keyword || request.content.includes(keyword) || request.writer.includes(keyword) || request.products?.some((product) => product.name.includes(keyword) || product.code.includes(keyword));
      return typeOk && statusOk && storeOk && startOk && endOk && keywordOk;
    });
  };

  const handleResetAdminWorkRequestFilter = () => {
    setAdminWorkRequestFilter({ type: "전체", status: "전체", store: "전체 매장", start: "", end: "", keyword: "" });
  };

  const handleHandoverFileChange = async (e) => {
    const files = await makePersistentAttachments(e.target.files);
    setHandoverAttachments(files);
  };

  const handleRemoveHandoverAttachment = (fileId) => {
    setHandoverAttachments((prev) => prev.filter((file) => file.id !== fileId));
    if (handoverFileRef.current) handoverFileRef.current.value = "";
  };

  const handleSubmitHandover = async () => {
    if (handoverSubmittingRef.current) return;
    handoverSubmittingRef.current = true;
    setIsSubmittingHandover(true);
    try {
    const title = handoverTitleRef.current?.value?.trim() || "";
    const content = handoverContentRef.current?.value?.trim() || "";
    const important = handoverImportantRef.current?.checked || false;
    const needCheck = handoverNeedCheckRef.current?.checked || false;

    if (!title) {
      setConfirmModal({ type: "notice", title: "저장 불가", message: "인수인계 제목을 입력해주세요." });
      return;
    }
    if (!content) {
      setConfirmModal({ type: "notice", title: "저장 불가", message: "인수인계 내용을 입력해주세요." });
      return;
    }

    const newHandover = {
      id: Date.now(),
      date: formatNow(),
      store: activeStore || selectedStore,
      writer: currentUser,
      title,
      content,
      important,
      needCheck,
      status: "확인 필요",
      checkedBy: "",
      checkedAt: "",
      attachments: handoverAttachments,
    };

    const nextHandovers = [newHandover, ...handovers];
    const saved = await saveAppDataListPreservingDetails("rc_handovers", nextHandovers);
    if (!saved) {
      setConfirmModal({
        type: "notice",
        title: "인수인계 저장 실패",
        message: "인수인계를 서버에 저장하지 못했습니다. 잠시 후 다시 시도해주세요.",
      });
      return;
    }

    setHandovers(nextHandovers);
    if (handoverTitleRef.current) handoverTitleRef.current.value = "";
    if (handoverContentRef.current) handoverContentRef.current.value = "";
    if (handoverImportantRef.current) handoverImportantRef.current.checked = false;
    if (handoverNeedCheckRef.current) handoverNeedCheckRef.current.checked = true;
    if (handoverFileRef.current) handoverFileRef.current.value = "";
    setHandoverAttachments([]);
    setConfirmModal({ type: "notice", title: "인수인계 저장 완료", message: "인수인계 내용이 저장되었습니다." });
    } catch (error) {
      console.error("인수인계 저장 실패:", error);
      setConfirmModal({
        type: "notice",
        title: "인수인계 저장 실패",
        message: "인수인계 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
      });
    } finally {
      handoverSubmittingRef.current = false;
      setIsSubmittingHandover(false);
    }
  };

  const handleConfirmHandover = (handoverId) => {
    const nextHandovers = handovers.map((item) => item.id === handoverId ? { ...item, status: "처리완료", checkedBy: currentUser, checkedAt: formatNow() } : item);
    setHandovers(nextHandovers);
    saveAppDataListPreservingDetails("rc_handovers", nextHandovers);
  };

  const getFilteredHandovers = () => {
    const allowedStores = getAllowedStores();
    return handovers.filter((item) => {
      const itemDate = item.date?.slice(0, 10)?.replace(/\./g, "-") || "";
      const storeOk = handoverFilter.store === "전체 매장" ? allowedStores.includes(item.store) : item.store === handoverFilter.store;
      const statusOk = handoverFilter.status === "전체" || item.status === handoverFilter.status;
      const startOk = !handoverFilter.start || itemDate >= handoverFilter.start;
      const endOk = !handoverFilter.end || itemDate <= handoverFilter.end;
      return storeOk && statusOk && startOk && endOk;
    });
  };

  const getFilteredAdminHandovers = () => {
    return handovers.filter((item) => {
      const itemDate = item.date?.slice(0, 10)?.replace(/\./g, "-") || "";
      const storeOk = adminHandoverFilter.store === "전체 매장" || item.store === adminHandoverFilter.store;
      const statusOk = adminHandoverFilter.status === "전체" || item.status === adminHandoverFilter.status;
      const startOk = !adminHandoverFilter.start || itemDate >= adminHandoverFilter.start;
      const endOk = !adminHandoverFilter.end || itemDate <= adminHandoverFilter.end;
      const keyword = adminHandoverFilter.keyword.trim();
      const keywordOk = !keyword || item.title.includes(keyword) || item.content.includes(keyword) || item.writer.includes(keyword) || item.checkedBy?.includes(keyword);
      const importantOk = !adminHandoverFilter.importantOnly || item.important;
      return storeOk && statusOk && startOk && endOk && keywordOk && importantOk;
    });
  };

  const handleResetAdminHandoverFilter = () => {
    setAdminHandoverFilter({ store: "전체 매장", status: "전체", start: "", end: "", keyword: "", importantOnly: false });
  };

  const handleAdminCompleteHandover = (handoverId) => {
    const nextHandovers = handovers.map((item) => item.id === handoverId ? { ...item, status: "처리완료", checkedBy: item.checkedBy || currentUser, checkedAt: item.checkedAt || formatNow() } : item);
    setHandovers(nextHandovers);
    saveAppDataListPreservingDetails("rc_handovers", nextHandovers);
    setConfirmModal({ type: "notice", title: "처리 완료", message: "인수인계가 처리완료 상태로 변경되었습니다." });
  };
  const handleDeleteHandover = (handoverId) => {
    if (!window.confirm("이 인수인계를 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.")) return;

    const nextHandovers = handovers.filter((item) => item.id !== handoverId);
    setHandovers(nextHandovers);
    saveAppDataListPreservingDetails("rc_handovers", nextHandovers);

    if (openAdminHandoverId === handoverId) {
      setOpenAdminHandoverId(null);
    }

    setConfirmModal({
      type: "notice",
      title: "삭제 완료",
      message: "인수인계가 삭제되었습니다.",
    });
  };
  const getPendingHandovers = () => {
    const allowedStores = getAllowedStores();
    return handovers.filter((item) =>
      item.status === "확인 필요" &&
      allowedStores.includes(item.store)
    );
  };

  useEffect(() => {
    if (!isLoggedIn || userRole !== "staff" || staffMenu !== "업무 요청") return;
    hydrateAppDataDetailsForItems("rc_work_requests", getVisibleWorkRequestsForStaff(), setWorkRequests);
  }, [isLoggedIn, userRole, staffMenu, workRequests, workRequestFilter, currentUser, selectedStore, activeStore, storeRecords]);

  useEffect(() => {
    if (!isLoggedIn || userRole !== "admin" || !openAdminWorkRequestId) return;
    const request = workRequests.find((item) => getAppDataItemId(item) === String(openAdminWorkRequestId));
    if (!request || !itemNeedsAttachmentDetail(request)) return;

    loadWorkRequestDetailOnce(openAdminWorkRequestId, { context: "admin", request }).then((detail) => {
      console.log("[work-request admin detail load]", {
        requestId: openAdminWorkRequestId,
        detailFound: Boolean(detail),
        detailAttachmentCount: Array.isArray(detail?.attachments) ? detail.attachments.length : 0,
        detailHasPayload: workRequestAttachmentListHasPayload(detail?.attachments || []),
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, userRole, openAdminWorkRequestId, workRequests]);

  useEffect(() => {
    if (!isLoggedIn || userRole !== "staff" || staffMenu !== "인수인계") return;
    const visibleById = new Map(
      [...getPendingHandovers(), ...getFilteredHandovers()]
        .map((item) => [getAppDataItemId(item), item])
        .filter(([id]) => Boolean(id))
    );
    hydrateAppDataDetailsForItems("rc_handovers", Array.from(visibleById.values()), setHandovers);
  }, [isLoggedIn, userRole, staffMenu, handovers, handoverFilter, currentUser, selectedStore, activeStore, storeRecords]);

  useEffect(() => {
    setStaffHandoverPage(1);
  }, [handoverFilter, selectedStore, activeStore, currentUser]);

  const handleSupplyQtyChange = (itemName, delta) => {
    setSupplyCart((prev) => {
      const found = prev.find((item) => item.name === itemName);
      if (!found) {
        if (delta <= 0) return prev;
        const base = activeSupplyItems.find((item) => item.name === itemName) || supplyItems.find((item) => item.name === itemName);
        return [...prev, { name: itemName, qty: 1, unit: base?.unit || "" }];
      }
      const nextQty = Math.max(0, Number(found.qty || 0) + delta);
      if (nextQty === 0) return prev.filter((item) => item.name !== itemName);
      return prev.map((item) => item.name === itemName ? { ...item, qty: nextQty } : item);
    });
  };

  const getSupplyCartQty = (itemName) => {
    return supplyCart.find((item) => item.name === itemName)?.qty || 0;
  };

  const handleAddOtherSupplyRow = () => {
    setOtherSupplyRows((prev) => [...prev, { id: Date.now(), name: "", qty: "1", link: "" }]);
  };

  const handleChangeOtherSupplyRow = (rowId, field, value) => {
    setOtherSupplyRows((prev) => prev.map((row) => row.id === rowId ? { ...row, [field]: value } : row));
  };

  const handleRemoveOtherSupplyRow = (rowId) => {
    setOtherSupplyRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const getCurrentOpenWorkRecord = () => getOpenAttendanceRecordForUser(attendance, currentUser);

  const getCurrentWorkStoreForSupply = () => getCurrentOpenWorkRecord()?.store || "";

  const handleSubmitSupplyOrder = async () => {
    if (supplyOrderSubmittingRef.current) return;
    supplyOrderSubmittingRef.current = true;
    setIsSubmittingSupplyOrder(true);
    try {
    const currentWork = getCurrentOpenWorkRecord();
    if (!currentWork) {
      setConfirmModal({ type: "notice", title: "소모품 주문 불가", message: "근무중에만 사용 가능합니다. 출근 후 이용해주세요." });
      return;
    }

    const validOtherItems = otherSupplyRows
      .filter((row) => row.name.trim())
      .map((row) => ({ name: row.name.trim(), qty: row.qty || "1", unit: "기타", link: row.link || "" }));
    const allItems = [...supplyCart, ...validOtherItems];

    if (allItems.length === 0) {
      setConfirmModal({ type: "notice", title: "주문 불가", message: "주문할 소모품을 1개 이상 추가해주세요." });
      return;
    }
    if (otherSupplyRows.length > 0 && validOtherItems.length !== otherSupplyRows.length) {
      setConfirmModal({ type: "notice", title: "주문 불가", message: "기타 소모품의 품목명을 입력하거나 빈 항목을 삭제해주세요." });
      return;
    }

    const newOrder = {
      id: Date.now(),
      date: formatNow(),
      store: currentWork.store,
      writer: currentUser,
      items: allItems,
      reason: supplyOrderReason.trim() || "-",
      status: "접수",
      adminMemo: "",
    };

    const result = await mutateGranularAppDataListFromServer(
      "rc_supply_orders",
      setSupplyOrders,
      (latestOrders) => [newOrder, ...latestOrders.filter((order) => order.id !== newOrder.id)]
    );

    if (!result.saved) {
      setConfirmModal({
        type: "notice",
        title: "소모품 주문 저장 실패",
        message: "서버 최신 데이터를 확인하지 못해 주문 저장을 중단했습니다. 잠시 후 다시 시도해주세요.",
      });
      return;
    }

    setSupplyCart([]);
    setOtherSupplyRows([]);
    setSupplyOrderReason("");
    setConfirmModal({ type: "notice", title: "소모품 주문 접수 완료", message: "소모품 주문 요청이 접수되었습니다." });
    } catch (error) {
      console.error("소모품 주문 저장 실패:", error);
      setConfirmModal({
        type: "notice",
        title: "소모품 주문 저장 실패",
        message: "소모품 주문 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
      });
    } finally {
      supplyOrderSubmittingRef.current = false;
      setIsSubmittingSupplyOrder(false);
    }
  };

  const getFilteredSupplyOrders = () => {
    const currentWorkStore = getCurrentWorkStoreForSupply();
    if (!currentWorkStore) return [];

    return supplyOrders.filter((order) => {
      const orderDate = order.date?.slice(0, 10)?.replace(/\./g, "-") || "";
      const storeOk = order.store === currentWorkStore;
      const statusOk = supplyOrderFilter.status === "전체" ||
        (supplyOrderFilter.status === "미처리" ? order.status !== "처리완료" : order.status === supplyOrderFilter.status);
      const startOk = !supplyOrderFilter.start || orderDate >= supplyOrderFilter.start;
      const endOk = !supplyOrderFilter.end || orderDate <= supplyOrderFilter.end;
      return storeOk && statusOk && startOk && endOk;
    });
  };

  const getFilteredAdminSupplyOrders = () => {
    return supplyOrders.filter((order) => {
      const orderDate = order.date?.slice(0, 10)?.replace(/\./g, "-") || "";
      const storeOk = adminSupplyOrderFilter.store === "전체 매장" || order.store === adminSupplyOrderFilter.store;
      const statusOk = adminSupplyOrderFilter.status === "전체" ||
        (adminSupplyOrderFilter.status === "미처리" ? order.status !== "처리완료" : order.status === adminSupplyOrderFilter.status);
      const startOk = !adminSupplyOrderFilter.start || orderDate >= adminSupplyOrderFilter.start;
      const endOk = !adminSupplyOrderFilter.end || orderDate <= adminSupplyOrderFilter.end;
      const keyword = adminSupplyOrderFilter.keyword.trim();
      const keywordOk = !keyword || order.writer.includes(keyword) || order.reason?.includes(keyword) || order.items?.some((item) => item.name.includes(keyword) || item.link?.includes(keyword));
      return storeOk && statusOk && startOk && endOk && keywordOk;
    });
  };

  const handleResetAdminSupplyOrderFilter = () => {
    setAdminSupplyOrderFilter({ store: "전체 매장", status: "전체", start: "", end: "", keyword: "" });
  };

  const handleUpdateSupplyOrderStatus = async (orderId, nextStatus) => {
    const currentOrder = supplyOrders.find((order) => order.id === orderId);
    const memo = (adminSupplyOrderMemos[orderId] || currentOrder?.adminMemo || "").trim();

    let foundOrder = false;
    const result = await mutateGranularAppDataListFromServer(
      "rc_supply_orders",
      setSupplyOrders,
      (latestOrders) => latestOrders.map((order) => {
        if (order.id !== orderId) return order;
        foundOrder = true;
        return { ...order, status: nextStatus, adminMemo: memo || order.adminMemo || "" };
      })
    );

    if (!result.saved) {
      setConfirmModal({
        type: "notice",
        title: "상태 변경 실패",
        message: "서버 최신 데이터를 확인하지 못해 상태 변경을 중단했습니다. 잠시 후 다시 시도해주세요.",
      });
      return;
    }

    if (!foundOrder) {
      setConfirmModal({ type: "notice", title: "상태 변경 불가", message: "이미 삭제되었거나 최신 목록에 없는 주문입니다." });
      return;
    }

    setConfirmModal({ type: "notice", title: "상태 변경 완료", message: "소모품 주문 상태가 변경되었습니다." });
  };
  const handleDeleteSupplyOrder = async (orderId) => {
    if (!window.confirm("이 소모품 주문을 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.")) return;

    let foundOrder = false;
    const result = await mutateGranularAppDataListFromServer(
      "rc_supply_orders",
      setSupplyOrders,
      (latestOrders) => latestOrders.filter((order) => {
        if (order.id === orderId) {
          foundOrder = true;
          return false;
        }
        return true;
      })
    );

    if (!result.saved) {
      setConfirmModal({
        type: "notice",
        title: "삭제 실패",
        message: "서버 최신 데이터를 확인하지 못해 삭제를 중단했습니다. 잠시 후 다시 시도해주세요.",
      });
      return;
    }

    if (openAdminSupplyOrderId === orderId) {
      setOpenAdminSupplyOrderId(null);
    }

    setConfirmModal({
      type: "notice",
      title: foundOrder ? "삭제 완료" : "삭제 대상 없음",
      message: foundOrder ? "소모품 주문이 삭제되었습니다." : "이미 삭제되었거나 최신 목록에 없는 주문입니다.",
    });
  };


  const filteredSupplyItems = supplyItems.filter((item) => {
    const keyword = supplyItemSearch.trim().toLowerCase();
    const categoryOk = supplyItemCategoryFilter === "전체" || (item.category || "기타") === supplyItemCategoryFilter;
    const keywordOk = !keyword ||
      String(item.name || "").toLowerCase().includes(keyword) ||
      String(item.unit || "").toLowerCase().includes(keyword) ||
      (item.category || "기타").toLowerCase().includes(keyword) ||
      (item.memo || "").toLowerCase().includes(keyword);
    return categoryOk && keywordOk;
  });
  const supplyItemTotalPages = Math.max(1, Math.ceil(filteredSupplyItems.length / supplyItemPageSize));
  const safeSupplyItemPage = Math.min(supplyItemPage, supplyItemTotalPages);
  const pagedSupplyItems = filteredSupplyItems.slice((safeSupplyItemPage - 1) * supplyItemPageSize, safeSupplyItemPage * supplyItemPageSize);
  const supplyItemPageNumbers = Array.from({ length: supplyItemTotalPages }, (_, index) => index + 1).filter((page) => {
    if (supplyItemTotalPages <= 7) return true;
    return page === 1 || page === supplyItemTotalPages || Math.abs(page - safeSupplyItemPage) <= 2;
  });

  const handleAddSupplyItem = () => {
    const name = supplyItemNameRef.current?.value?.trim() || "";
    const unit = supplyItemUnitRef.current?.value?.trim() || "";
    const category = supplyItemCategoryRef.current?.value?.trim() || "";
    const memo = supplyItemMemoRef.current?.value?.trim() || "";

    if (!name) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "소모품명을 입력해주세요." });
      return;
    }
    if (supplyItems.some((item) => item.name === name)) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "이미 등록된 소모품명입니다." });
      return;
    }

    const nextSupplyItems = [...supplyItems, { id: Date.now(), name, unit: unit || "1개", category: category || "기타", memo, status: "활성화" }];
    setSupplyItems(nextSupplyItems);
    persistGranularAppDataList("rc_supply_items", nextSupplyItems);
    if (supplyItemNameRef.current) supplyItemNameRef.current.value = "";
    if (supplyItemUnitRef.current) supplyItemUnitRef.current.value = "";
    if (supplyItemCategoryRef.current) supplyItemCategoryRef.current.value = "";
    if (supplyItemMemoRef.current) supplyItemMemoRef.current.value = "";
    setConfirmModal({ type: "notice", title: "등록 완료", message: `${name} 소모품이 등록되었습니다.` });
  };

  const handleToggleSupplyItemStatus = (itemId) => {
    const nextSupplyItems = supplyItems.map((item) => item.id === itemId ? { ...item, status: item.status === "활성화" ? "비활성화" : "활성화" } : item);
    setSupplyItems(nextSupplyItems);
    persistGranularAppDataList("rc_supply_items", nextSupplyItems);
  };

  const handleDeleteSupplyItem = (itemId) => {
    const nextSupplyItems = supplyItems.filter((item) => item.id !== itemId);
    setSupplyItems(nextSupplyItems);
    persistGranularAppDataList("rc_supply_items", nextSupplyItems);
    if (editingSupplyItemId === itemId) setEditingSupplyItemId(null);
  };

  const handleStartEditSupplyItem = (item) => {
    setEditingSupplyItemId(item.id);
    setTimeout(() => {
      if (editSupplyItemNameRef.current) editSupplyItemNameRef.current.value = item.name;
      if (editSupplyItemUnitRef.current) editSupplyItemUnitRef.current.value = item.unit;
      if (editSupplyItemCategoryRef.current) editSupplyItemCategoryRef.current.value = item.category;
      if (editSupplyItemMemoRef.current) editSupplyItemMemoRef.current.value = item.memo || "";
    }, 0);
  };

  const handleSaveEditSupplyItem = (itemId) => {
    const name = editSupplyItemNameRef.current?.value?.trim() || "";
    const unit = editSupplyItemUnitRef.current?.value?.trim() || "";
    const category = editSupplyItemCategoryRef.current?.value?.trim() || "";
    const memo = editSupplyItemMemoRef.current?.value?.trim() || "";

    if (!name) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "소모품명을 입력해주세요." });
      return;
    }
    if (supplyItems.some((item) => item.id !== itemId && item.name === name)) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "이미 등록된 소모품명입니다." });
      return;
    }

    const nextSupplyItems = supplyItems.map((item) => item.id === itemId ? { ...item, name, unit: unit || "1개", category: category || "기타", memo } : item);
    setSupplyItems(nextSupplyItems);
    persistGranularAppDataList("rc_supply_items", nextSupplyItems);
    setEditingSupplyItemId(null);
    setConfirmModal({ type: "notice", title: "수정 완료", message: "소모품 정보가 수정되었습니다." });
  };

  const handleResetSupplyItemSearch = () => {
    if (supplyItemSearchRef.current) supplyItemSearchRef.current.value = "";
    setSupplyItemSearch("");
    setSupplyItemCategoryFilter("전체");
    setSupplyItemPage(1);
  };

  const handleAddProductDbItem = () => {
    const code = productCodeRef.current?.value?.trim() || "";
    const name = productNameRef.current?.value?.trim() || "";
    const category = productCategoryRef.current?.value || "기타";
    const barcode = productBarcodeRef.current?.value?.trim() || "";
    const salePrice = productSalePriceRef.current?.value?.trim() || "";
    const wholesalePrice = productWholesalePriceRef.current?.value?.trim() || "";
    const ctQuantity = productCtQuantityRef.current?.value?.trim() || "";
    const productionDate = productProductionDateRef.current?.value || "";
    const expiry = productExpiryRef.current?.value || "";
    const note = productNoteRef.current?.value?.trim() || "";
    const imageUrl = productImageUrlRef.current?.value?.trim() || "";

    if (!code || !name) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "상품코드와 상품명을 입력해주세요." });
      return;
    }
    if (productList.some((product) => product.code === code)) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "이미 등록된 상품코드입니다." });
      return;
    }

    const nextProductList = [...productList, { code, barcode, name, category, salePrice, wholesalePrice, ctQuantity, productionDate, expiry, note, imageUrl, status: "활성화" }];
    setProductList(nextProductList);
    persistGranularAppDataList("rc_product_list", nextProductList);
    if (productCodeRef.current) productCodeRef.current.value = "";
    if (productNameRef.current) productNameRef.current.value = "";
    if (productCategoryRef.current) productCategoryRef.current.value = "콘돔";
    if (productBarcodeRef.current) productBarcodeRef.current.value = "";
    if (productSalePriceRef.current) productSalePriceRef.current.value = "";
    if (productWholesalePriceRef.current) productWholesalePriceRef.current.value = "";
    if (productCtQuantityRef.current) productCtQuantityRef.current.value = "";
    if (productProductionDateRef.current) productProductionDateRef.current.value = "";
    if (productExpiryRef.current) productExpiryRef.current.value = "";
    if (productNoteRef.current) productNoteRef.current.value = "";
    if (productImageUrlRef.current) productImageUrlRef.current.value = "";
    setProductDbPage(1);
    setConfirmModal({ type: "notice", title: "등록 완료", message: `${name} 상품이 등록되었습니다.` });
  };

  const handleToggleProductDbStatus = (productCode) => {
    const nextProductList = productList.map((product) => product.code === productCode ? { ...product, status: product.status === "비활성화" ? "활성화" : "비활성화" } : product);
    setProductList(nextProductList);
    persistGranularAppDataList("rc_product_list", nextProductList);
  };

  const handleDeleteProductDbItem = (productCode) => {
    const target = productList.find((product) => product.code === productCode);
    const ok = window.confirm(`${target?.name || productCode} 상품을 삭제하시겠습니까? 삭제하면 유통기한 관리에서도 함께 사라집니다.`);
    if (!ok) return;
    const nextProductList = productList.filter((product) => product.code !== productCode);
    setProductList(nextProductList);
    persistGranularAppDataList("rc_product_list", nextProductList);
    if (editingProductCode === productCode) setEditingProductCode(null);
    setProductDbPage((prev) => Math.max(1, prev));
  };

  const handleStartEditProductDb = (product) => {
    setEditingProductCode(product.code);
    setTimeout(() => {
      if (editProductCodeRef.current) editProductCodeRef.current.value = product.code;
      if (editProductNameRef.current) editProductNameRef.current.value = product.name;
      if (editProductCategoryRef.current) editProductCategoryRef.current.value = product.category || "기타";
      if (editProductBarcodeRef.current) editProductBarcodeRef.current.value = product.barcode || "";
      if (editProductSalePriceRef.current) editProductSalePriceRef.current.value = product.salePrice || "";
      if (editProductWholesalePriceRef.current) editProductWholesalePriceRef.current.value = product.wholesalePrice || "";
      if (editProductCtQuantityRef.current) editProductCtQuantityRef.current.value = getProductCtQuantity(product);
      if (editProductProductionDateRef.current) editProductProductionDateRef.current.value = product.productionDate || "";
      if (editProductExpiryRef.current) editProductExpiryRef.current.value = product.expiry || "";
      if (editProductNoteRef.current) editProductNoteRef.current.value = product.note || "";
      if (editProductImageUrlRef.current) editProductImageUrlRef.current.value = product.imageUrl || "";
    }, 0);
  };

  const handleSaveEditProductDb = (originCode) => {
    const code = editProductCodeRef.current?.value?.trim() || "";
    const name = editProductNameRef.current?.value?.trim() || "";
    const category = editProductCategoryRef.current?.value || "기타";
    const barcode = editProductBarcodeRef.current?.value?.trim() || "";
    const salePrice = editProductSalePriceRef.current?.value?.trim() || "";
    const wholesalePrice = editProductWholesalePriceRef.current?.value?.trim() || "";
    const ctQuantity = editProductCtQuantityRef.current?.value?.trim() || "";
    const productionDate = editProductProductionDateRef.current?.value || "";
    const expiry = editProductExpiryRef.current?.value || "";
    const note = editProductNoteRef.current?.value?.trim() || "";
    const imageUrl = editProductImageUrlRef.current?.value?.trim() || "";

    if (!code || !name) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "상품코드와 상품명을 입력해주세요." });
      return;
    }
    if (productList.some((product) => product.code !== originCode && product.code === code)) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "이미 등록된 상품코드입니다." });
      return;
    }

    const nextProductList = productList.map((product) => product.code === originCode ? { ...product, code, name, category, barcode, salePrice, wholesalePrice, ctQuantity, productionDate, expiry, note, imageUrl } : product);
    setProductList(nextProductList);
    persistGranularAppDataList("rc_product_list", nextProductList);
    setEditingProductCode(null);
    setConfirmModal({ type: "notice", title: "수정 완료", message: "상품 정보가 수정되었습니다." });
  };

  const handleResetProductDbSearch = () => {
    if (productDbSearchRef.current) productDbSearchRef.current.value = "";
    setProductDbSearch("");
    setProductDbCategoryFilter("전체");
    setProductDbExpiryFilter("전체");
    setProductDbPage(1);
  };

  const handleSearchProductDb = () => {
    setProductDbSearch(productDbSearchRef.current?.value || "");
    setProductDbPage(1);
  };

  const downloadFilteredProductDbList = () => {
    const rows = filteredProductDbItems.map((product) => ({
      카테고리: product.category || "기타",
      상품명: product.name,
      상품코드: product.code,
      바코드: product.barcode || "",
      이미지경로: product.imageUrl || "",
      판매가: product.salePrice || "",
      도매가: product.wholesalePrice || "",
      CT입수량: getProductCtQuantity(product),
      제조일자: product.productionDate || "",
      유통기한: product.expiry || "",
      기타메시지: product.note || "",
      상태: product.status || "활성화",
    }));
    downloadCsv("상품DB_검색결과.csv", rows);
  };

  const downloadProductTemplate = () => {
    downloadCsv("상품DB_업로드양식.csv", [
      { 카테고리: "콘돔", 상품명: "예시 상품명", 상품코드: "RC-EX-001", 바코드: "8800000000000", 이미지경로: "https://example.com/product-image.jpg", 판매가: "10000", 도매가: "5000", CT입수량: "24", 제조일자: "2026-01-01", 유통기한: "2028-01-01", 기타메시지: "선진열 권장" },
    ]);
  };

  const parseCsvText = (text) => {
    const lineBreakIndexR = text.indexOf(String.fromCharCode(13));
    const lineBreakIndexN = text.indexOf(String.fromCharCode(10));
    let firstLineEnd = -1;
    if (lineBreakIndexR >= 0 && lineBreakIndexN >= 0) firstLineEnd = Math.min(lineBreakIndexR, lineBreakIndexN);
    else firstLineEnd = Math.max(lineBreakIndexR, lineBreakIndexN);

    const firstLine = firstLineEnd >= 0 ? text.slice(0, firstLineEnd) : text;
    const tabChar = String.fromCharCode(9);
    const delimiter = firstLine.includes(tabChar) ? tabChar : firstLine.includes(";") && !firstLine.includes(",") ? ";" : ",";
    const rows = [];
    let row = [];
    let value = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      const next = text[i + 1];
      const code = char.charCodeAt(0);
      const nextCode = next ? next.charCodeAt(0) : 0;

      if (char === '"' && inQuotes && next === '"') {
        value += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        row.push(value.trim());
        value = "";
      } else if ((code === 10 || code === 13) && !inQuotes) {
        if (code === 13 && nextCode === 10) i += 1;
        row.push(value.trim());
        if (row.some((cell) => cell !== "")) rows.push(row);
        row = [];
        value = "";
      } else {
        value += char;
      }
    }

    row.push(value.trim());
    if (row.some((cell) => cell !== "")) rows.push(row);
    return rows;
  };

  const finishProductCsvImport = () => {
    productCsvImportInProgressRef.current = false;
    productCsvImportStartedRef.current = false;
    setIsImportingProductCsv(false);
    if (productImportFileRef.current) productImportFileRef.current.value = "";
  };

  const handleProductCsvFileDialogOpen = () => {
    if (productCsvImportInProgressRef.current) return;

    productCsvImportInProgressRef.current = true;
    productCsvImportStartedRef.current = false;

    const releaseImportGuardAfterCancel = () => {
      window.setTimeout(() => {
        if (!productCsvImportStartedRef.current) finishProductCsvImport();
      }, 500);
    };

    window.addEventListener("focus", releaseImportGuardAfterCancel, { once: true });
    window.setTimeout(() => {
      if (!productCsvImportStartedRef.current && !isImportingProductCsv) finishProductCsvImport();
    }, 60000);
  };

  const handleImportProductCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      finishProductCsvImport();
      return;
    }
    if (productCsvImportStartedRef.current) return;

    productCsvImportStartedRef.current = true;
    productCsvImportInProgressRef.current = true;
    setIsImportingProductCsv(true);

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setConfirmModal({ type: "notice", title: "업로드 불가", message: "현재 테스트 버전에서는 CSV 파일만 업로드할 수 있습니다. 양식을 다운로드한 뒤 Excel에서 작성하고 CSV UTF-8 형식으로 저장해주세요." });
      finishProductCsvImport();
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      let stage = "csv-parse";
      try {
        const buffer = reader.result;
        const decodeText = (encoding) => {
          try {
            return new TextDecoder(encoding).decode(buffer).replace(/^﻿/, "");
          } catch (error) {
            return "";
          }
        };

        const requiredHeaders = ["카테고리", "상품명", "상품코드", "바코드", "판매가", "도매가", "제조일자", "유통기한", "기타메시지"];
        const decodedCandidates = [decodeText("utf-8"), decodeText("euc-kr")].filter(Boolean);
        let csvRows = [];
        let headers = [];

        for (const text of decodedCandidates) {
          const rows = parseCsvText(text);
          const candidateHeaders = rows[0]?.map((header) => header.replace(/^﻿/, "").trim()) || [];
          const hasRequiredHeader = requiredHeaders.some((header) => candidateHeaders.includes(header));
          if (hasRequiredHeader) {
            csvRows = rows;
            headers = candidateHeaders;
            break;
          }
        }

        if (csvRows.length < 2) {
          setConfirmModal({ type: "notice", title: "업로드 불가", message: "등록할 상품 데이터가 없거나 CSV 인코딩을 읽을 수 없습니다. Excel에서 'CSV UTF-8' 형식으로 다시 저장한 뒤 업로드해주세요." });
          return;
        }

        const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
        if (missingHeaders.length > 0) {
          setConfirmModal({ type: "notice", title: "업로드 양식 오류", message: `양식에 필요한 열이 없습니다: ${missingHeaders.join(", ")} / 현재 읽힌 첫 줄: ${headers.join(" / ") || "없음"}` });
          return;
        }

        stage = "server-list-fetch";
        for (let attempt = 0; attempt < 100 && !canPersistCommonAppData(); attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 100));
        }
        if (!canPersistCommonAppData()) throw new Error("상품 DB 서버 동기화가 완료되지 않았습니다.");

        const serverProductList = await fetchAppDataItemFull("rc_product_list");
        if (!Array.isArray(serverProductList)) throw new Error("서버 상품 목록을 확인할 수 없습니다.");

        const getCell = (row, headerName) => headers.includes(headerName) ? (row[headers.indexOf(headerName)]?.trim() || "") : "";
        const normalizeProductCode = (value) => String(value || "").trim();
        const existingCodes = new Set(serverProductList.map((product) => normalizeProductCode(product.code)).filter(Boolean));
        const uploadCodes = new Set();
        const duplicateCodes = [];
        const invalidRows = [];
        const nextProducts = [];

        csvRows.slice(1).forEach((row) => {
          const category = getCell(row, "카테고리") || "기타";
          const name = getCell(row, "상품명");
          const code = normalizeProductCode(getCell(row, "상품코드"));
          const barcode = getCell(row, "바코드");
          const imageUrl = getCell(row, "이미지경로");
          const salePrice = getCell(row, "판매가");
          const wholesalePrice = getCell(row, "도매가");
          const ctQuantity = getCell(row, "CT입수량");
          const productionDate = getCell(row, "제조일자");
          const expiry = getCell(row, "유통기한");
          const note = getCell(row, "기타메시지");
          if (!code || !name) {
            invalidRows.push(`필수값 누락(${code || "코드 없음"})`);
            return;
          }
          if (existingCodes.has(code) || uploadCodes.has(code)) {
            duplicateCodes.push(code);
            return;
          }
          uploadCodes.add(code);
          nextProducts.push({ code, barcode, name, category, salePrice, wholesalePrice, ctQuantity, productionDate, expiry, note, imageUrl, status: "활성화" });
        });

        const resultMessages = [];
        if (nextProducts.length === 0) {
          resultMessages.push("신규 등록 0개");
          if (duplicateCodes.length > 0) resultMessages.push(`기존/파일 내 중복 ${duplicateCodes.length}개: ${duplicateCodes.join(", ")}`);
          if (invalidRows.length > 0) resultMessages.push(`등록 제외 항목: ${invalidRows.slice(0, 8).join(" / ")}${invalidRows.length > 8 ? " 외" : ""}`);
          setProductList(serverProductList);
          lastGranularAppDataPayloadRef.current.rc_product_list = JSON.stringify(serverProductList);
          setConfirmModal({ type: "notice", title: "업로드 처리 완료", message: resultMessages.join(String.fromCharCode(10)) });
          return;
        }

        stage = "server-save";
        const nextProductList = [...serverProductList, ...nextProducts];
        const saved = await persistGranularAppDataList("rc_product_list", nextProductList);
        if (!saved) throw new Error("상품 목록 서버 저장이 완료되지 않았습니다.");

        stage = "server-verify";
        const verifiedProductList = await fetchAppDataItemFull("rc_product_list");
        if (!Array.isArray(verifiedProductList)) throw new Error("저장 후 서버 상품 목록을 확인할 수 없습니다.");

        const verifiedCodes = new Set(verifiedProductList.map((product) => normalizeProductCode(product.code)).filter(Boolean));
        const verifiedNewProducts = nextProducts.filter((product) => verifiedCodes.has(product.code));
        if (verifiedNewProducts.length !== nextProducts.length) {
          const missingCodes = nextProducts.filter((product) => !verifiedCodes.has(product.code)).map((product) => product.code);
          throw new Error(`저장 검증 실패: ${missingCodes.slice(0, 10).join(", ")}${missingCodes.length > 10 ? " 외" : ""}`);
        }

        setProductList(verifiedProductList);
        lastGranularAppDataPayloadRef.current.rc_product_list = JSON.stringify(verifiedProductList);
        setProductDbPage(1);
        resultMessages.push(`${verifiedNewProducts.length}개 상품이 등록되었습니다.`);
        if (duplicateCodes.length > 0) resultMessages.push(`기존/파일 내 중복 ${duplicateCodes.length}개 제외: ${duplicateCodes.join(", ")}`);
        if (invalidRows.length > 0) resultMessages.push(`등록 제외 항목: ${invalidRows.slice(0, 8).join(" / ")}${invalidRows.length > 8 ? " 외" : ""}`);
        setConfirmModal({ type: "notice", title: "업로드 처리 완료", message: resultMessages.join(String.fromCharCode(10)) });
      } catch (error) {
        console.error("상품 DB CSV 업로드 실패:", { stage, errorMessage: error?.message || String(error) }, error);
        try {
          const latestProductList = await fetchAppDataItemFull("rc_product_list");
          if (Array.isArray(latestProductList)) {
            setProductList(latestProductList);
            lastGranularAppDataPayloadRef.current.rc_product_list = JSON.stringify(latestProductList);
          }
        } catch (reloadError) {
          console.error("상품 DB CSV 업로드 실패 후 최신 목록 복구 실패:", reloadError);
        }
        setConfirmModal({ type: "notice", title: "업로드 실패", message: "상품 저장에 실패했습니다. 다시 시도해주세요." });
      } finally {
        finishProductCsvImport();
      }
    };
    reader.onerror = () => {
      console.error("상품 DB CSV 파일 읽기 실패:", reader.error);
      setConfirmModal({ type: "notice", title: "업로드 실패", message: "CSV 파일을 읽지 못했습니다. 파일을 확인한 뒤 다시 시도해주세요." });
      finishProductCsvImport();
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSaveMyInfo = async () => {
    if (myPageSavingRef.current) return;
    myPageSavingRef.current = true;
    setIsSavingMyPage(true);
    try {
    const nextContact = myContactRef.current?.value?.trim() || "";
    const nextAddress = myAddressRef.current?.value?.trim() || "";
    const nextEmployees = employees.map((employee) =>
        employee.name === currentUser
          ? { ...employee, contact: nextContact || "-", address: nextAddress || "-" }
          : employee
    );
    const saved = await saveGranularAppDataListNow("rc_employees", nextEmployees);
    if (!saved) {
      setConfirmModal({ type: "notice", title: "저장 실패", message: "연락처와 주소를 서버에 저장하지 못했습니다. 잠시 후 다시 시도해주세요." });
      return;
    }
    setEmployees(nextEmployees);
    setConfirmModal({ type: "notice", title: "저장 완료", message: "연락처와 주소가 저장되었습니다." });
    } catch (error) {
      console.error("마이페이지 정보 저장 실패:", error);
      setConfirmModal({ type: "notice", title: "저장 실패", message: "연락처와 주소 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요." });
    } finally {
      myPageSavingRef.current = false;
      setIsSavingMyPage(false);
    }
  };

  const handleChangePassword = async () => {
    if (myPageSavingRef.current) return;
    myPageSavingRef.current = true;
    setIsSavingMyPage(true);
    try {
    const currentPw = currentPwRef.current?.value || "";
    const newPw = newPwRef.current?.value || "";
    const newPwConfirm = newPwConfirmRef.current?.value || "";
    const employee = employees.find((item) => item.name === currentUser);
    const savedPw = employee?.tempPassword || (currentUser === "관리자" ? "1234" : "1111");

    if (currentPw !== savedPw) {
      setConfirmModal({ type: "notice", title: "변경 불가", message: "현재 비밀번호가 일치하지 않습니다." });
      return;
    }
    if (!newPw || newPw.length < 4) {
      setConfirmModal({ type: "notice", title: "변경 불가", message: "새 비밀번호는 4자 이상 입력해주세요." });
      return;
    }
    if (newPw !== newPwConfirm) {
      setConfirmModal({ type: "notice", title: "변경 불가", message: "새 비밀번호 확인이 일치하지 않습니다." });
      return;
    }

    const nextEmployees = employees.map((item) =>
        item.name === currentUser ? { ...item, tempPassword: newPw } : item
    );
    const saved = await saveGranularAppDataListNow("rc_employees", nextEmployees);
    if (!saved) {
      setConfirmModal({ type: "notice", title: "변경 실패", message: "비밀번호를 서버에 저장하지 못했습니다. 잠시 후 다시 시도해주세요." });
      return;
    }
    setEmployees(nextEmployees);
    if (currentPwRef.current) currentPwRef.current.value = "";
    if (newPwRef.current) newPwRef.current.value = "";
    if (newPwConfirmRef.current) newPwConfirmRef.current.value = "";
    setConfirmModal({ type: "notice", title: "변경 완료", message: "비밀번호가 변경되었습니다. 다음 로그인부터 새 비밀번호를 사용해주세요." });
    } catch (error) {
      console.error("비밀번호 변경 실패:", error);
      setConfirmModal({ type: "notice", title: "변경 실패", message: "비밀번호 변경 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요." });
    } finally {
      myPageSavingRef.current = false;
      setIsSavingMyPage(false);
    }
  };

  const downloadCsv = (filename, rows) => {
    if (!rows || rows.length === 0) {
      setConfirmModal({ type: "notice", title: "다운로드 불가", message: "다운로드할 데이터가 없습니다." });
      return;
    }

    const headers = Object.keys(rows[0]);
    const csvRows = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((header) => `"${String(row[header] ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ];

    const blob = new Blob(["﻿" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadEmployeeList = () => {
    const rows = filteredEmployees.map((employee) => ({
      이름: employee.name,
      ID: employee.id,
      연락처: employee.contact,
      주소: employee.address,
      입사일: employee.hireDate || "",
      권한: employee.role,
      근무가능매장: employee.stores,
      상태: employee.status,
    }));
    downloadCsv("직원계정관리.csv", rows);
  };

  const parseStoreMemo = (memo) => {
    if (memo === null || memo === undefined || memo === "") return {};
    if (typeof memo === "object" && !Array.isArray(memo)) return memo;

    const rawMemo = String(memo || "");
    try {
      const parsed = JSON.parse(rawMemo);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : { memoText: rawMemo };
    } catch (error) {
      return { memoText: rawMemo };
    }
  };

  const getPlainStoreMemo = (store, extra) => {
    if (Object.prototype.hasOwnProperty.call(extra || {}, "memoText")) {
      return String(extra.memoText || "");
    }
    const rawMemo = store?.memo;
    if (typeof rawMemo === "object") return "";
    const rawText = String(rawMemo || "");
    const trimmed = rawText.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      return "";
    }
    return rawText;
  };

  const normalizeStoreRecord = (store) => {
    const extra = parseStoreMemo(store.memo);
    const isActive = store.isActive ?? store.is_active ?? store.active;
    return {
      id: store.id,
      name: store.name || store.storeName || store.store_name || "",
      address: store.address || "-",
      owner: store.owner || store.managerName || store.manager_name || extra.owner || "-",
      businessNumber: store.businessNumber || store.business_number || extra.businessNumber || "-",
      contact: store.contact || store.phone || extra.contact || "-",
      extension: store.extension || extra.extension || "-",
      leaseStart: store.leaseStart || store.lease_start || extra.leaseStart || "",
      leaseEnd: store.leaseEnd || store.lease_end || extra.leaseEnd || "",
      rentPayDay: store.rentPayDay || store.rent_pay_day || extra.rentPayDay || "-",
      bank1: store.bank1 || extra.bank1 || "",
      bank2: store.bank2 || extra.bank2 || "",
      attachments: store.attachments || extra.attachments || [],
      status: isActive === false ? "비활성화" : "활성화",
      memo: getPlainStoreMemo(store, extra),
      createdAt: store.createdAt || store.created_at || "",
      updatedAt: store.updatedAt || store.updated_at || "",
    };
  };

  const buildStoreApiPayload = (store) => ({
    name: store.name,
    address: store.address === "-" ? "" : store.address,
    phone: store.contact === "-" ? "" : store.contact,
    managerName: store.owner === "-" ? "" : store.owner,
    isActive: store.status !== "비활성화",
    memo: JSON.stringify({
      memoText: String(store.memo || ""),
      owner: store.owner === "-" ? "" : store.owner,
      businessNumber: store.businessNumber === "-" ? "" : store.businessNumber,
      contact: store.contact === "-" ? "" : store.contact,
      extension: store.extension === "-" ? "" : store.extension,
      leaseStart: store.leaseStart || "",
      leaseEnd: store.leaseEnd || "",
      rentPayDay: store.rentPayDay === "-" ? "" : store.rentPayDay,
      bank1: store.bank1 || "",
      bank2: store.bank2 || "",
      attachments: store.attachments || [],
    }),
  });

  const loadStoresFromApi = async () => {
    try {
      setStoreLoading(true);
      setStoreError("");
      const res = await fetch(STORE_API_BASE);
      if (!res.ok) throw new Error(`매장 API 조회 실패 (${res.status})`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.stores || data.items || [];
      setStoreRecords(rows.map(normalizeStoreRecord));
    } catch (error) {
      setStoreError(`stores API 연결에 실패했습니다. 호출 주소: ${STORE_API_BASE}`);
      setStoreRecords([]);
    } finally {
      setStoreLoading(false);
    }
  };

  const requestStoreApi = async (url, options, fallbackMethod = null) => {
    const res = await fetch(url, options);
    if (res.ok) return res;
    if (fallbackMethod && (res.status === 404 || res.status === 405 || res.status === 422)) {
      return await fetch(url, { ...options, method: fallbackMethod });
    }
    return res;
  };

  const normalizeStoreAlias = (alias) => ({
    id: alias.id,
    storeId: alias.storeId ?? alias.store_id,
    storeName: alias.storeName || alias.store_name || "",
    aliasName: alias.aliasName || alias.alias_name || "",
    source: alias.source || "",
    createdAt: alias.createdAt || alias.created_at || "",
  });

  const loadStoreAliases = async () => {
    try {
      setStoreAliasLoading(true);
      setStoreAliasError("");
      const res = await fetch(STORE_ALIAS_API_BASE);
      if (!res.ok) throw new Error(`매장 매핑 API 조회 실패 (${res.status})`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.items || [];
      setStoreAliases(rows.map(normalizeStoreAlias).filter((alias) => !isSystemStoreName(alias.aliasName) && !isSystemStoreName(alias.storeName)));
    } catch (error) {
      setStoreAliasError("매장 매핑 API 연결에 실패했습니다.");
      setStoreAliases([]);
    } finally {
      setStoreAliasLoading(false);
    }
  };

  const loadUnmappedStores = async () => {
    try {
      const res = await fetch(`${STORE_ALIAS_API_BASE}/unmapped`);
      if (!res.ok) throw new Error("미매핑 매장 조회 실패");
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.items || [];
      setUnmappedStores(rows.map((row) => row.storeName || row.store_name || row.name || row).filter(Boolean).filter((name) => !isSystemStoreName(name)));
    } catch (error) {
      setUnmappedStores([]);
    }
  };

  const reloadStoreAliasData = async () => {
    await loadStoreAliases();
    await loadUnmappedStores();
  };

  const createStoreAlias = async ({ aliasName, storeId, source = "manual" }) => {
    const trimmedAlias = String(aliasName || "").trim();
    if (!trimmedAlias) {
      setConfirmModal({ type: "notice", title: "매핑 등록 불가", message: "매핑할 원본 매장명을 입력해주세요." });
      return false;
    }
    if (isSystemStoreName(trimmedAlias)) {
      setConfirmModal({ type: "notice", title: "매핑 등록 제외", message: `${trimmedAlias}은 매장이 아니라 시스템 채널이므로 매핑하지 않습니다.` });
      return false;
    }
    if (!storeId) {
      setConfirmModal({ type: "notice", title: "매핑 등록 불가", message: "표준 매장을 선택해주세요." });
      return false;
    }
    const targetStore = storeRecords.find((store) => String(store.id) === String(storeId));
    if (isSystemStoreName(targetStore?.name)) {
      setConfirmModal({ type: "notice", title: "매핑 등록 제외", message: "온라인몰, 인형뽑기, 본사 등 시스템 채널은 표준 매장으로 사용할 수 없습니다." });
      return false;
    }
    try {
      const res = await fetch(STORE_ALIAS_API_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aliasName: trimmedAlias, storeId: Number(storeId), source }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `매장 매핑 등록 실패 (${res.status})`);
      }
      await reloadStoreAliasData();
      setConfirmModal({ type: "notice", title: "매장 매핑 등록 완료", message: `${trimmedAlias} 매장명을 표준 매장에 연결했습니다.` });
      return true;
    } catch (error) {
      setConfirmModal({ type: "notice", title: "매장 매핑 등록 실패", message: error.message || "store_aliases API 등록에 실패했습니다." });
      return false;
    }
  };

  const handleAddStoreAlias = async () => {
    const ok = await createStoreAlias({
      aliasName: aliasNameRef.current?.value || "",
      storeId: aliasStoreRef.current?.value || "",
      source: aliasSourceRef.current?.value || "manual",
    });
    if (ok) {
      if (aliasNameRef.current) aliasNameRef.current.value = "";
      if (aliasSourceRef.current) aliasSourceRef.current.value = "manual";
    }
  };

  const handleMapUnmappedStore = async (aliasName) => {
    const selectedStoreId = unmappedDrafts[aliasName];
    await createStoreAlias({ aliasName, storeId: selectedStoreId, source: "sales" });
  };

  const handleDeleteStoreAlias = async (aliasId) => {
    try {
      const res = await fetch(`${STORE_ALIAS_API_BASE}/${aliasId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`삭제 실패 (${res.status})`);
      await reloadStoreAliasData();
    } catch (error) {
      setConfirmModal({ type: "notice", title: "매핑 삭제 실패", message: "매장 매핑 삭제에 실패했습니다." });
    }
  };

  const getMappedStoreName = (storeName) => {
    const raw = String(storeName || "").trim();
    if (!raw) return "기타";
    const directStore = storeRecords.find((store) => store.name === raw);
    if (directStore) return directStore.name;
    const alias = storeAliases.find((item) => item.aliasName === raw);
    return alias?.storeName || raw;
  };

  const filteredStoreRecords = storeRecords.filter((store) => {
    if (isSystemStoreName(store.name)) return false;
    const keyword = storeSearch.trim();
    if (!keyword) return true;
    return store.name.includes(keyword);
  });

  const getDaysUntilRentPay = (rentPayDay) => {
    const day = Number(String(rentPayDay || "").replace(/[^0-9]/g, ""));
    if (!day) return null;
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const year = todayDate.getFullYear();
    const month = todayDate.getMonth();
    const thisMonthLastDay = new Date(year, month + 1, 0).getDate();
    let payDate = new Date(year, month, Math.min(day, thisMonthLastDay));
    payDate.setHours(0, 0, 0, 0);
    if (payDate < todayDate) {
      const nextMonthLastDay = new Date(year, month + 2, 0).getDate();
      payDate = new Date(year, month + 1, Math.min(day, nextMonthLastDay));
      payDate.setHours(0, 0, 0, 0);
    }
    return Math.ceil((payDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const rentDueSoonStores = storeRecords
    .filter((store) => store.status === "활성화" && !isSystemStoreName(store.name))
    .map((store) => ({ ...store, rentDaysLeft: getDaysUntilRentPay(store.rentPayDay) }))
    .filter((store) => store.rentDaysLeft !== null && store.rentDaysLeft >= 0 && store.rentDaysLeft <= 5)
    .sort((a, b) => a.rentDaysLeft - b.rentDaysLeft);

  const toggleStoreExpanded = (storeId) => {
    setExpandedStoreIds((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId]
    );
  };

  const handleStoreFileChange = async (e, mode = "new") => {
    const files = await makePersistentAttachments(e.target.files);

    if (mode === "edit" && editingStoreId) {
      setStoreRecords((prev) => prev.map((store) => {
        if (store.id !== editingStoreId) return store;
        const merged = [...(store.attachments || [])];
        files.forEach((file) => {
          if (!merged.some((item) => item.id === file.id)) merged.push(file);
        });
        return { ...store, attachments: merged };
      }));
      if (editStoreFileRef.current) editStoreFileRef.current.value = "";
      return;
    }

    setStoreAttachments((prev) => {
      const merged = [...prev];
      files.forEach((file) => {
        if (!merged.some((item) => item.id === file.id)) merged.push(file);
      });
      return merged;
    });
    if (storeFileRef.current) storeFileRef.current.value = "";
  };

  const handleRemoveStoreAttachment = (fileId, mode = "new", storeId = null) => {
    if (mode === "edit" && storeId) {
      setStoreRecords((prev) => prev.map((store) => store.id === storeId ? { ...store, attachments: (store.attachments || []).filter((file) => file.id !== fileId) } : store));
      if (editStoreFileRef.current) editStoreFileRef.current.value = "";
      return;
    }
    setStoreAttachments((prev) => prev.filter((file) => file.id !== fileId));
    if (storeFileRef.current) storeFileRef.current.value = "";
  };

  const handleAddStore = async () => {
    const name = storeNameRef.current?.value?.trim() || "";
    const address = storeAddressRef.current?.value?.trim() || "";
    const owner = storeOwnerRef.current?.value?.trim() || "";
    const businessNumber = storeBusinessNumberRef.current?.value?.trim() || "";
    const contact = storeContactRef.current?.value?.trim() || "";
    const extension = storeExtensionRef.current?.value?.trim() || "";
    const leaseStart = storeLeaseStartRef.current?.value || "";
    const leaseEnd = storeLeaseEndRef.current?.value || "";
    const rentPayDay = storeRentPayDayRef.current?.value?.trim() || "";
    const memo = storeMemoRef.current?.value?.trim() || "";

    if (!name) {
      setConfirmModal({ type: "notice", title: "매장 등록 불가", message: "매장명을 입력해주세요." });
      return;
    }
    if (storeRecords.some((store) => store.name === name)) {
      setConfirmModal({ type: "notice", title: "매장 등록 불가", message: "이미 등록된 매장명입니다." });
      return;
    }

    const nextStore = {
      name,
      address: address || "-",
      owner: owner || "-",
      businessNumber: businessNumber || "-",
      contact: contact || "-",
      extension: extension || "-",
      leaseStart,
      leaseEnd,
      rentPayDay: rentPayDay || "-",
      bank1: storeBank1Ref.current?.value?.trim() || "",
      bank2: storeBank2Ref.current?.value?.trim() || "",
      memo,
      attachments: storeAttachments,
      status: "활성화",
    };

    try {
      const res = await fetch(STORE_API_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildStoreApiPayload(nextStore)),
      });
      if (!res.ok) throw new Error(`매장 등록 API 실패 (${res.status})`);
      await loadStoresFromApi();
      [storeNameRef, storeAddressRef, storeOwnerRef, storeBusinessNumberRef, storeContactRef, storeExtensionRef, storeLeaseStartRef, storeLeaseEndRef, storeRentPayDayRef, storeBank1Ref, storeBank2Ref, storeMemoRef].forEach((ref) => {
        if (ref.current) ref.current.value = "";
      });
      setStoreAttachments([]);
      if (storeFileRef.current) storeFileRef.current.value = "";
      setConfirmModal({ type: "notice", title: "매장 등록 완료", message: `${name} 매장이 서버 DB에 등록되었습니다.` });
    } catch (error) {
      setConfirmModal({ type: "notice", title: "매장 등록 실패", message: "stores API 등록에 실패했습니다. FastAPI 서버와 stores API를 확인해주세요." });
    }
  };

  const handleToggleStoreStatus = async (storeId) => {
    const target = storeRecords.find((store) => store.id === storeId);
    if (!target) return;
    const nextStore = { ...target, status: target.status === "활성화" ? "비활성화" : "활성화" };

    try {
      const res = await requestStoreApi(`${STORE_API_BASE}/${storeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildStoreApiPayload(nextStore)),
      }, "PATCH");
      if (!res.ok) throw new Error(`매장 상태 변경 API 실패 (${res.status})`);
      await loadStoresFromApi();
    } catch (error) {
      setConfirmModal({ type: "notice", title: "상태 변경 실패", message: "stores API 상태 변경에 실패했습니다." });
    }
  };

  const handleDeleteStore = async (storeId) => {
    const target = storeRecords.find((store) => store.id === storeId);
    if (!target) return;
    const confirmed = window.confirm(`${target.name} 매장을 삭제하시겠습니까? 삭제 후에는 목록에서 제거됩니다.`);
    if (!confirmed) return;

    try {
      const res = await fetch(`${STORE_API_BASE}/${storeId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`매장 삭제 API 실패 (${res.status})`);
      await loadStoresFromApi();
      if (editingStoreId === storeId) setEditingStoreId(null);
      setConfirmModal({ type: "notice", title: "삭제 완료", message: `${target.name} 매장이 삭제되었습니다.` });
    } catch (error) {
      setConfirmModal({ type: "notice", title: "삭제 실패", message: "stores API 삭제에 실패했습니다. 백엔드에 DELETE /api/stores/{id} 지원 여부를 확인해주세요." });
    }
  };

  const handleStartEditStore = (store) => {
    setEditingStoreId(store.id);
    setTimeout(() => {
      if (editStoreNameRef.current) editStoreNameRef.current.value = store.name;
      if (editStoreAddressRef.current) editStoreAddressRef.current.value = store.address === "-" ? "" : store.address;
      if (editStoreOwnerRef.current) editStoreOwnerRef.current.value = store.owner === "-" ? "" : store.owner;
      if (editStoreBusinessNumberRef.current) editStoreBusinessNumberRef.current.value = store.businessNumber === "-" ? "" : store.businessNumber;
      if (editStoreContactRef.current) editStoreContactRef.current.value = store.contact === "-" ? "" : store.contact;
      if (editStoreExtensionRef.current) editStoreExtensionRef.current.value = store.extension === "-" ? "" : store.extension;
      if (editStoreLeaseStartRef.current) editStoreLeaseStartRef.current.value = store.leaseStart || "";
      if (editStoreLeaseEndRef.current) editStoreLeaseEndRef.current.value = store.leaseEnd || "";
      if (editStoreRentPayDayRef.current) editStoreRentPayDayRef.current.value = store.rentPayDay === "-" ? "" : String(store.rentPayDay || "").replace(/[^0-9]/g, "");
      if (editStoreBank1Ref.current) editStoreBank1Ref.current.value = store.bank1 || "";
      if (editStoreBank2Ref.current) editStoreBank2Ref.current.value = store.bank2 || "";
      if (editStoreMemoRef.current) editStoreMemoRef.current.value = store.memo || "";
    }, 0);
  };

  const handleSaveEditStore = async (storeId) => {
    const name = editStoreNameRef.current?.value?.trim() || "";
    if (!name) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "매장명을 입력해주세요." });
      return;
    }
    if (storeRecords.some((store) => store.id !== storeId && store.name === name)) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "이미 등록된 매장명입니다." });
      return;
    }

    const current = storeRecords.find((store) => store.id === storeId) || {};
    const nextStore = {
      ...current,
      name,
      address: editStoreAddressRef.current?.value?.trim() || "-",
      owner: editStoreOwnerRef.current?.value?.trim() || "-",
      businessNumber: editStoreBusinessNumberRef.current?.value?.trim() || "-",
      contact: editStoreContactRef.current?.value?.trim() || "-",
      extension: editStoreExtensionRef.current?.value?.trim() || "-",
      leaseStart: editStoreLeaseStartRef.current?.value || "",
      leaseEnd: editStoreLeaseEndRef.current?.value || "",
      rentPayDay: editStoreRentPayDayRef.current?.value || "-",
      bank1: editStoreBank1Ref.current?.value?.trim() || "",
      bank2: editStoreBank2Ref.current?.value?.trim() || "",
      memo: editStoreMemoRef.current?.value?.trim() || "",
    };

    try {
      const res = await requestStoreApi(`${STORE_API_BASE}/${storeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildStoreApiPayload(nextStore)),
      }, "PATCH");
      if (!res.ok) throw new Error(`매장 수정 API 실패 (${res.status})`);
      await loadStoresFromApi();
      setEditingStoreId(null);
      setConfirmModal({ type: "notice", title: "수정 완료", message: "매장 정보가 서버 DB에 수정되었습니다." });
    } catch (error) {
      setConfirmModal({ type: "notice", title: "수정 실패", message: "stores API 수정에 실패했습니다." });
    }
  };

  const downloadStoreList = () => {
    const rows = filteredStoreRecords.map((store) => ({
      매장명: store.name,
      주소: store.address,
      대표자: store.owner,
      사업자등록번호: store.businessNumber,
      연락처: store.contact,
      내선번호: store.extension,
      임대계약일: store.leaseStart,
      임대종료일: store.leaseEnd,
      임대료납부일: store.rentPayDay,
      계좌정보1: store.bank1 || "",
      계좌정보2: store.bank2 || "",
      첨부파일: (store.attachments || []).map((file) => file.name).join(" / "),
      상태: store.status,
    }));
    downloadCsv("매장관리.csv", rows);
  };

  const downloadAttendanceList = (rows) => {
    const exportRows = rows.map((row) => ({
      출근일: row.date,
      퇴근일: row.endDate || "-",
      직원명: row.name,
      근무매장: row.store,
      출근시간: row.start,
      휴게시작: row.breakStart || "-",
      휴게종료: row.breakEnd || "-",
      휴게시간: formatDuration(getBreakMinutes(row)),
      퇴근시간: row.end,
      총근무시간: formatDuration(getWorkMinutes(row)),
      상태: row.status,
    }));
    downloadCsv("출근기록.csv", exportRows);
  };

  const handlePartnerFileChange = async (e, mode = "new") => {
    const files = await makePersistentAttachments(e.target.files);

    if (mode === "edit" && editingPartnerId) {
      setPartners((prev) => prev.map((partner) => {
        if (partner.id !== editingPartnerId) return partner;
        const merged = [...(partner.attachments || [])];
        files.forEach((file) => {
          if (!merged.some((item) => item.id === file.id)) merged.push(file);
        });
        return { ...partner, attachments: merged };
      }));
      if (editPartnerFileRef.current) editPartnerFileRef.current.value = "";
      return;
    }

    setPartnerAttachments((prev) => {
      const merged = [...prev];
      files.forEach((file) => {
        if (!merged.some((item) => item.id === file.id)) merged.push(file);
      });
      return merged;
    });
    if (partnerFileRef.current) partnerFileRef.current.value = "";
  };

  const handleRemovePartnerAttachment = (fileId, mode = "new", partnerId = null) => {
    if (mode === "edit" && partnerId) {
      setPartners((prev) => prev.map((partner) => partner.id === partnerId ? { ...partner, attachments: (partner.attachments || []).filter((file) => file.id !== fileId) } : partner));
      if (editPartnerFileRef.current) editPartnerFileRef.current.value = "";
      return;
    }
    setPartnerAttachments((prev) => prev.filter((file) => file.id !== fileId));
    if (partnerFileRef.current) partnerFileRef.current.value = "";
  };

  const handleAddPartner = async () => {
    const type = partnerTypeRef.current?.value || "기타";
    const name = partnerNameRef.current?.value?.trim() || "";
    const businessNumber = partnerBusinessNumberRef.current?.value?.trim() || "";
    const ceo = partnerCeoRef.current?.value?.trim() || "";
    const address = partnerAddressRef.current?.value?.trim() || "";
    const contact = partnerContactRef.current?.value?.trim() || "";
    const email = partnerEmailRef.current?.value?.trim() || "";
    const manager = partnerManagerRef.current?.value?.trim() || "";
    const managerContact = partnerManagerContactRef.current?.value?.trim() || "";
    const managerEmail = partnerManagerEmailRef.current?.value?.trim() || "";
    const note = partnerNoteRef.current?.value?.trim() || "";

    if (!name) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "거래처명을 입력해주세요." });
      return;
    }

    const newPartner = { id: Date.now(), type, name, businessNumber, ceo, address, contact, email, manager, managerContact, managerEmail, note, attachments: partnerAttachments };
    const result = await mutateGranularAppDataListFromServer(
      PARTNERS_APP_DATA_KEY,
      setPartners,
      (serverPartners) => [newPartner, ...serverPartners]
    );

    if (!result.saved) {
      setConfirmModal({ type: "notice", title: "거래처 저장 실패", message: "서버 최신 거래처 데이터를 확인하지 못해 등록을 중단했습니다. 잠시 후 다시 시도해주세요." });
      return;
    }

    [partnerNameRef, partnerBusinessNumberRef, partnerCeoRef, partnerAddressRef, partnerContactRef, partnerEmailRef, partnerManagerRef, partnerManagerContactRef, partnerManagerEmailRef, partnerNoteRef].forEach((ref) => {
      if (ref.current) ref.current.value = "";
    });
    if (partnerTypeRef.current) partnerTypeRef.current.value = "입점사";
    if (partnerFileRef.current) partnerFileRef.current.value = "";
    setPartnerAttachments([]);
    setConfirmModal({ type: "notice", title: "거래처 등록 완료", message: `${name} 거래처가 등록되었습니다.` });
  };


  const togglePartnerExpanded = (partnerId) => {
    setExpandedPartners((prev) => ({ ...prev, [partnerId]: !prev[partnerId] }));
  };

  const downloadPartnerTemplate = () => {
    downloadCsv("거래처_업로드양식.csv", [
      {
        구분: "입점사",
        거래처명: "예시 거래처",
        사업자등록번호: "000-00-00000",
        대표자: "홍길동",
        주소: "서울시 강남구",
        연락처: "02-0000-0000",
        이메일: "sample@partner.com",
        담당자: "김담당",
        담당자연락처: "010-0000-0000",
        담당자이메일: "manager@partner.com",
        기타: "비고 입력",
      },
    ]);
  };

  const handleImportPartnerCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setConfirmModal({ type: "notice", title: "업로드 불가", message: "현재 버전에서는 CSV 파일만 업로드할 수 있습니다. 업로드 양식을 받은 뒤 CSV UTF-8 형식으로 저장해주세요." });
      if (partnerImportFileRef.current) partnerImportFileRef.current.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const buffer = reader.result;
      const decodeText = (encoding) => {
        try {
          return new TextDecoder(encoding).decode(buffer).replace(/^﻿/, "");
        } catch (error) {
          return "";
        }
      };

      const requiredHeaders = ["구분", "거래처명", "사업자등록번호", "대표자", "주소", "연락처", "이메일", "담당자", "담당자연락처", "담당자이메일", "기타"];
      const decodedCandidates = [decodeText("utf-8"), decodeText("euc-kr")].filter(Boolean);
      let csvRows = [];
      let headers = [];

      for (const csvText of decodedCandidates) {
        const rows = parseCsvText(csvText);
        const candidateHeaders = rows[0]?.map((header) => header.replace(/^﻿/, "").trim()) || [];
        const hasNameHeader = candidateHeaders.includes("거래처명");
        if (hasNameHeader) {
          csvRows = rows;
          headers = candidateHeaders;
          break;
        }
      }

      if (csvRows.length < 2) {
        setConfirmModal({ type: "notice", title: "업로드 불가", message: "등록할 거래처 데이터가 없거나 CSV 인코딩을 읽을 수 없습니다." });
        if (partnerImportFileRef.current) partnerImportFileRef.current.value = "";
        return;
      }

      const missingHeaders = ["거래처명"].filter((header) => !headers.includes(header));
      if (missingHeaders.length > 0) {
        setConfirmModal({ type: "notice", title: "업로드 양식 오류", message: `필수 열이 없습니다: ${missingHeaders.join(", ")}` });
        if (partnerImportFileRef.current) partnerImportFileRef.current.value = "";
        return;
      }

      const getCell = (row, headerName) => headers.includes(headerName) ? (row[headers.indexOf(headerName)]?.trim() || "") : "";
      const uploadNames = new Set();
      const incoming = [];
      const duplicateNames = [];
      const invalidRows = [];

      csvRows.slice(1).forEach((row, index) => {
        const name = getCell(row, "거래처명");
        if (!name) {
          invalidRows.push(`${index + 2}행`);
          return;
        }
        if (uploadNames.has(name)) {
          duplicateNames.push(name);
          return;
        }
        uploadNames.add(name);
        const rawType = getCell(row, "구분") || "기타";
        const type = partnerTypes.includes(rawType) && rawType !== "전체" ? rawType : "기타";
        incoming.push({
          id: Date.now() + index,
          type,
          name,
          businessNumber: getCell(row, "사업자등록번호"),
          ceo: getCell(row, "대표자"),
          address: getCell(row, "주소"),
          contact: getCell(row, "연락처"),
          email: getCell(row, "이메일"),
          manager: getCell(row, "담당자"),
          managerContact: getCell(row, "담당자연락처"),
          managerEmail: getCell(row, "담당자이메일"),
          note: getCell(row, "기타"),
          attachments: [],
        });
      });

      let savedIncoming = [];
      if (incoming.length > 0) {
        const result = await mutateGranularAppDataListFromServer(
          PARTNERS_APP_DATA_KEY,
          setPartners,
          (serverPartners) => {
            const existingNames = new Set(serverPartners.map((partner) => String(partner.name || "").trim()).filter(Boolean));
            savedIncoming = incoming.filter((partner) => {
              const normalizedName = String(partner.name || "").trim();
              if (existingNames.has(normalizedName)) {
                duplicateNames.push(normalizedName);
                return false;
              }
              existingNames.add(normalizedName);
              return true;
            });

            return savedIncoming.length > 0 ? [...savedIncoming, ...serverPartners] : serverPartners;
          }
        );

        if (!result.saved) {
          setConfirmModal({ type: "notice", title: "거래처 업로드 실패", message: "서버 최신 거래처 데이터를 확인하지 못해 업로드를 중단했습니다. 잠시 후 다시 시도해주세요." });
          if (partnerImportFileRef.current) partnerImportFileRef.current.value = "";
          return;
        }
      }

      const messages = [`${savedIncoming.length}개 거래처가 등록되었습니다.`];
      if (duplicateNames.length) messages.push(`중복 제외: ${duplicateNames.slice(0, 8).join(", ")}${duplicateNames.length > 8 ? " 외" : ""}`);
      if (invalidRows.length) messages.push(`거래처명 누락 제외: ${invalidRows.slice(0, 8).join(", ")}${invalidRows.length > 8 ? " 외" : ""}`);
      setPartnerUploadSummary(messages.join(" / "));
      setConfirmModal({ type: "notice", title: savedIncoming.length > 0 ? "거래처 업로드 완료" : "거래처 업로드 결과", message: messages.join(String.fromCharCode(10)) });
      if (partnerImportFileRef.current) partnerImportFileRef.current.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  const filteredPartners = partners.filter((partner) => {
    const typeOk = partnerFilter.type === "전체" || partner.type === partnerFilter.type;
    const keyword = partnerFilter.keyword.trim();
    const keywordOk = !keyword || partner.name.includes(keyword) || partner.businessNumber?.includes(keyword) || partner.ceo?.includes(keyword) || partner.contact?.includes(keyword) || partner.email?.includes(keyword) || partner.manager?.includes(keyword) || partner.managerContact?.includes(keyword) || partner.managerEmail?.includes(keyword) || partner.note?.includes(keyword);
    return typeOk && keywordOk;
  });

  const handleStartEditPartner = (partner) => {
    setEditingPartnerId(partner.id);
    setTimeout(() => {
      if (editPartnerTypeRef.current) editPartnerTypeRef.current.value = partner.type || "기타";
      if (editPartnerNameRef.current) editPartnerNameRef.current.value = partner.name || "";
      if (editPartnerBusinessNumberRef.current) editPartnerBusinessNumberRef.current.value = partner.businessNumber || "";
      if (editPartnerCeoRef.current) editPartnerCeoRef.current.value = partner.ceo || "";
      if (editPartnerAddressRef.current) editPartnerAddressRef.current.value = partner.address || "";
      if (editPartnerContactRef.current) editPartnerContactRef.current.value = partner.contact || "";
      if (editPartnerEmailRef.current) editPartnerEmailRef.current.value = partner.email || "";
      if (editPartnerManagerRef.current) editPartnerManagerRef.current.value = partner.manager || "";
      if (editPartnerManagerContactRef.current) editPartnerManagerContactRef.current.value = partner.managerContact || "";
      if (editPartnerManagerEmailRef.current) editPartnerManagerEmailRef.current.value = partner.managerEmail || "";
      if (editPartnerNoteRef.current) editPartnerNoteRef.current.value = partner.note || "";
    }, 0);
  };

  const handleSaveEditPartner = async (partnerId) => {
    const name = editPartnerNameRef.current?.value?.trim() || "";
    if (!name) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "거래처명을 입력해주세요." });
      return;
    }

    const editedPartner = {
      ...(partners.find((partner) => partner.id === partnerId) || { id: partnerId }),
      type: editPartnerTypeRef.current?.value || "기타",
      name,
      businessNumber: editPartnerBusinessNumberRef.current?.value?.trim() || "",
      ceo: editPartnerCeoRef.current?.value?.trim() || "",
      address: editPartnerAddressRef.current?.value?.trim() || "",
      contact: editPartnerContactRef.current?.value?.trim() || "",
      email: editPartnerEmailRef.current?.value?.trim() || "",
      manager: editPartnerManagerRef.current?.value?.trim() || "",
      managerContact: editPartnerManagerContactRef.current?.value?.trim() || "",
      managerEmail: editPartnerManagerEmailRef.current?.value?.trim() || "",
      note: editPartnerNoteRef.current?.value?.trim() || "",
    };
    let replaced = false;
    const result = await mutateGranularAppDataListFromServer(
      PARTNERS_APP_DATA_KEY,
      setPartners,
      (serverPartners) => serverPartners.map((partner) => {
        if (String(partner.id) !== String(partnerId)) return partner;
        replaced = true;
        return { ...partner, ...editedPartner };
      })
    );

    if (!result.saved) {
      setConfirmModal({ type: "notice", title: "거래처 저장 실패", message: "서버 최신 거래처 데이터를 확인하지 못해 수정을 중단했습니다. 잠시 후 다시 시도해주세요." });
      return;
    }

    setEditingPartnerId(null);
    setConfirmModal({
      type: "notice",
      title: replaced ? "수정 완료" : "수정 대상 없음",
      message: replaced ? "거래처 정보가 수정되었습니다." : "이미 삭제되었거나 서버 최신 목록에 없는 거래처입니다.",
    });
  };

  const handleDeletePartner = async (partnerId) => {
    const result = await mutateGranularAppDataListFromServer(
      PARTNERS_APP_DATA_KEY,
      setPartners,
      (serverPartners) => serverPartners.filter((partner) => String(partner.id) !== String(partnerId))
    );

    if (!result.saved) {
      setConfirmModal({ type: "notice", title: "거래처 삭제 실패", message: "서버 최신 거래처 데이터를 확인하지 못해 삭제를 중단했습니다. 잠시 후 다시 시도해주세요." });
      return;
    }

    if (editingPartnerId === partnerId) setEditingPartnerId(null);
  };

  const downloadPartnerList = () => {
    const rows = filteredPartners.map((partner) => ({
      구분: partner.type,
      거래처명: partner.name,
      사업자등록번호: partner.businessNumber,
      대표자: partner.ceo,
      주소: partner.address,
      연락처: partner.contact,
      이메일: partner.email,
      담당자: partner.manager,
      담당자연락처: partner.managerContact,
      담당자이메일: partner.managerEmail,
      기타: partner.note,
      첨부파일: (partner.attachments || []).map((file) => file.name).join(" / "),
    }));
    downloadCsv("거래처관리.csv", rows);
  };

  const getAdPartnerName = (partnerId, fallbackName = "") => {
    const partner = partners.find((item) => String(item.id) === String(partnerId));
    return partner?.name || fallbackName || "-";
  };

  const getAdDaysLeft = (ad) => {
    if (!ad.endDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(ad.endDate);
    end.setHours(0, 0, 0, 0);
    return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getAdStatus = (ad) => {
    const daysLeft = getAdDaysLeft(ad);
    if (daysLeft === null) return "진행중";
    return daysLeft < 0 ? "종료" : "진행중";
  };

  const isAdEndingSoon = (ad) => {
    const daysLeft = getAdDaysLeft(ad);
    return daysLeft !== null && daysLeft >= 0 && daysLeft <= 45;
  };

  const formatWon = (value) => {
    const raw = String(value || "").replace(/[^0-9]/g, "");
    if (!raw) return "-";
    return `${Number(raw).toLocaleString("ko-KR")}원`;
  };

  const formatProductPrice = (value, withWon = false) => {
    const raw = String(value ?? "").trim();
    const numeric = raw.replace(/[^0-9]/g, "");
    if (!numeric) return "-";
    const formatted = Number(numeric).toLocaleString("ko-KR");
    return withWon ? `${formatted}원` : formatted;
  };

  function getProductCtQuantity(product) {
    return String(product?.ctQuantity ?? product?.ctQty ?? product?.ctCount ?? "").trim();
  }

  const getProductImageUrl = (product) => String(product?.imageUrl || product?.image || product?.image_url || product?.productImage || "").trim();

  const getExpiryDaysLeft = (expiryDate) => {
    if (!expiryDate) return null;
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    if (Number.isNaN(expiry.getTime())) return null;
    expiry.setHours(0, 0, 0, 0);
    return Math.ceil((expiry.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const isWithinExpiryRange = (expiryDate, months) => {
    const daysLeft = getExpiryDaysLeft(expiryDate);
    if (daysLeft === null) return false;
    return daysLeft >= 0 && daysLeft <= months * 31;
  };

  const getExpiryRangeCount = (items, months) => items.filter((item) => isWithinExpiryRange(item.expiry, months)).length;

  const getExpiryBadgeInfo = (expiryDate) => {
    const daysLeft = getExpiryDaysLeft(expiryDate);
    if (daysLeft === null) return { label: "정상", tone: "blue", urgent: false };
    if (daysLeft < 0) return { label: "기간 종료", tone: "red", urgent: true };
    if (daysLeft <= 31) return { label: "1개월 이내", tone: "red", urgent: true };
    if (daysLeft <= 93) return { label: "3개월 이내", tone: "orange", urgent: true };
    if (daysLeft <= 186) return { label: "6개월 이내", tone: "yellow", urgent: true };
    return { label: "정상", tone: "blue", urgent: false };
  };

  const filteredProductDbItems = productList.filter((product) => {
    const keyword = productDbSearch.trim().toLowerCase();
    const categoryOk = productDbCategoryFilter === "전체" || (product.category || "기타") === productDbCategoryFilter;
    const expiryOk = productDbExpiryFilter === "전체" || isWithinExpiryRange(product.expiry, Number(productDbExpiryFilter));
    const keywordOk = !keyword ||
      String(product.code || "").toLowerCase().includes(keyword) ||
      String(product.name || "").toLowerCase().includes(keyword) ||
      String(product.category || "").toLowerCase().includes(keyword) ||
      String(product.barcode || "").toLowerCase().includes(keyword) ||
      String(getProductCtQuantity(product)).toLowerCase().includes(keyword) ||
      String(product.imageUrl || "").toLowerCase().includes(keyword);
    return categoryOk && expiryOk && keywordOk;
  });
  const productDbTotalPages = Math.max(1, Math.ceil(filteredProductDbItems.length / productDbPageSize));
  const safeProductDbPage = Math.min(productDbPage, productDbTotalPages);
  const pagedProductDbItems = filteredProductDbItems.slice((safeProductDbPage - 1) * productDbPageSize, safeProductDbPage * productDbPageSize);
  const productDbPageNumbers = Array.from({ length: productDbTotalPages }, (_, index) => index + 1).filter((page) => {
    if (productDbTotalPages <= 7) return true;
    return page === 1 || page === productDbTotalPages || Math.abs(page - safeProductDbPage) <= 2;
  });

  const expiryFilterButtonStyle = (active, tone = "red") => ({
    ...(active ? redButton : darkButton),
    background: active ? (tone === "orange" ? "#f97316" : tone === "yellow" ? "#f59e0b" : "#dc2626") : "#111827",
    width: "100%",
  });

  const handleAdFileChange = async (e, mode = "new") => {
    const files = await makePersistentAttachments(e.target.files);

    if (mode === "edit" && editingAdId) {
      const nextAds = ads.map((ad) => {
        if (ad.id !== editingAdId) return ad;
        const merged = [...(ad.files || [])];
        files.forEach((file) => {
          if (!merged.some((item) => item.id === file.id)) merged.push(file);
        });
        return { ...ad, files: merged };
      });
      setAds(nextAds);
      persistGranularAppDataList("rc_ads", nextAds);
      if (editAdFileRef.current) editAdFileRef.current.value = "";
      return;
    }

    setAdFiles((prev) => {
      const merged = [...prev];
      files.forEach((file) => {
        if (!merged.some((item) => item.id === file.id)) merged.push(file);
      });
      return merged;
    });
    if (adFileRef.current) adFileRef.current.value = "";
  };

  const handleRemoveAdFile = (fileId, mode = "new", adId = null) => {
    if (mode === "edit" && adId) {
      const nextAds = ads.map((ad) => ad.id === adId ? { ...ad, files: (ad.files || []).filter((file) => file.id !== fileId) } : ad);
      setAds(nextAds);
      persistGranularAppDataList("rc_ads", nextAds);
      if (editAdFileRef.current) editAdFileRef.current.value = "";
      return;
    }
    setAdFiles((prev) => prev.filter((file) => file.id !== fileId));
    if (adFileRef.current) adFileRef.current.value = "";
  };

  const toggleAdMedia = (media, mode = "new") => {
    if (mode === "edit") {
      setEditAdMedia((prev) => prev.includes(media) ? prev.filter((item) => item !== media) : [...prev, media]);
      return;
    }
    setSelectedAdMedia((prev) => prev.includes(media) ? prev.filter((item) => item !== media) : [...prev, media]);
  };

  const normalizeMediaValue = (mediaList) => {
    const next = Array.isArray(mediaList) ? mediaList.filter(Boolean) : [];
    return next.length > 0 ? next : ["매장"];
  };

  const handleAddAd = () => {
    const partnerId = adPartnerRef.current?.value || "";
    const partnerName = getAdPartnerName(partnerId);
    const adName = adNameRef.current?.value?.trim() || "";
    const entryStatus = adEntryStatusRef.current?.value?.trim() || "";
    const store = adStoreRef.current?.value || "";
    const media = normalizeMediaValue(selectedAdMedia);
    const spec = adSpecRef.current?.value?.trim() || "";
    const period = adPeriodRef.current?.value || "1개월";
    const startDate = adStartDateRef.current?.value || "";
    const endDate = adEndDateRef.current?.value || "";
    const totalAdCost = adTotalAdCostRef.current?.value?.trim() || "";
    const totalPayment = adTotalPaymentRef.current?.value?.trim() || "";
    const note = adNoteRef.current?.value?.trim() || "";

    if (!partnerId) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "업체를 선택해주세요. 거래처관리에 등록된 거래처가 업체 목록으로 표시됩니다." });
      return;
    }
    if (!adName) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "광고명을 입력해주세요." });
      return;
    }
    if (!store) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "광고 지점을 선택해주세요." });
      return;
    }

    const nextAds = [{ id: Date.now(), partnerId, partnerName, adName, entryStatus, files: adFiles, store, media, spec, period, startDate, endDate, totalAdCost, totalPayment, note }, ...ads];
    setAds(nextAds);
    persistGranularAppDataList("rc_ads", nextAds);
    [adNameRef, adEntryStatusRef, adSpecRef, adStartDateRef, adEndDateRef, adTotalAdCostRef, adTotalPaymentRef, adNoteRef].forEach((ref) => {
      if (ref.current) ref.current.value = "";
    });
    if (adPartnerRef.current) adPartnerRef.current.value = "";
    if (adStoreRef.current) adStoreRef.current.value = stores[0] || "";
    setSelectedAdMedia(["매장"]);
    if (adPeriodRef.current) adPeriodRef.current.value = "1개월";
    if (adFileRef.current) adFileRef.current.value = "";
    setAdFiles([]);
    setConfirmModal({ type: "notice", title: "광고 등록 완료", message: `${partnerName} / ${adName} 광고가 등록되었습니다.` });
  };

  const filteredAds = ads.filter((ad) => {
    const status = getAdStatus(ad);
    const partnerOk = adFilter.partnerId === "전체 업체" || String(ad.partnerId) === String(adFilter.partnerId);
    const statusOk = adFilter.status === "전체" || status === adFilter.status || (adFilter.status === "45일 이내 종료" && isAdEndingSoon(ad));
    const storeOk = adFilter.store === "전체 매장" || ad.store === adFilter.store;
    const mediaOk = adFilter.media === "전체" || (ad.media || []).includes(adFilter.media);
    const keyword = adFilter.keyword.trim();
    const partnerName = getAdPartnerName(ad.partnerId, ad.partnerName);
    const keywordOk = !keyword || partnerName.includes(keyword) || ad.adName?.includes(keyword) || ad.entryStatus?.includes(keyword) || ad.spec?.includes(keyword) || ad.note?.includes(keyword);
    return partnerOk && statusOk && storeOk && mediaOk && keywordOk;
  });

  const handleStartEditAd = (ad) => {
    setEditingAdId(ad.id);
    setTimeout(() => {
      if (editAdPartnerRef.current) editAdPartnerRef.current.value = ad.partnerId || "";
      if (editAdNameRef.current) editAdNameRef.current.value = ad.adName || "";
      if (editAdEntryStatusRef.current) editAdEntryStatusRef.current.value = ad.entryStatus || "";
      if (editAdStoreRef.current) editAdStoreRef.current.value = ad.store || "";
      setEditAdMedia(normalizeMediaValue(ad.media || ["매장"]));
      if (editAdSpecRef.current) editAdSpecRef.current.value = ad.spec || "";
      if (editAdPeriodRef.current) editAdPeriodRef.current.value = ad.period || "1개월";
      if (editAdStartDateRef.current) editAdStartDateRef.current.value = ad.startDate || "";
      if (editAdEndDateRef.current) editAdEndDateRef.current.value = ad.endDate || "";
      if (editAdTotalAdCostRef.current) editAdTotalAdCostRef.current.value = ad.totalAdCost || "";
      if (editAdTotalPaymentRef.current) editAdTotalPaymentRef.current.value = ad.totalPayment || "";
      if (editAdNoteRef.current) editAdNoteRef.current.value = ad.note || "";
    }, 0);
  };

  const handleSaveEditAd = (adId) => {
    const partnerId = editAdPartnerRef.current?.value || "";
    const adName = editAdNameRef.current?.value?.trim() || "";
    const store = editAdStoreRef.current?.value || "";
    if (!partnerId || !adName || !store) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "업체, 광고명, 광고 지점은 필수입니다." });
      return;
    }

    const nextAds = ads.map((ad) => ad.id === adId ? {
      ...ad,
      partnerId,
      partnerName: getAdPartnerName(partnerId),
      adName,
      entryStatus: editAdEntryStatusRef.current?.value?.trim() || "",
      store,
      media: normalizeMediaValue(editAdMedia),
      spec: editAdSpecRef.current?.value?.trim() || "",
      period: editAdPeriodRef.current?.value || "1개월",
      startDate: editAdStartDateRef.current?.value || "",
      endDate: editAdEndDateRef.current?.value || "",
      totalAdCost: editAdTotalAdCostRef.current?.value?.trim() || "",
      totalPayment: editAdTotalPaymentRef.current?.value?.trim() || "",
      note: editAdNoteRef.current?.value?.trim() || "",
    } : ad);
    setAds(nextAds);
    persistGranularAppDataList("rc_ads", nextAds);
    setEditingAdId(null);
    setConfirmModal({ type: "notice", title: "수정 완료", message: "광고 정보가 수정되었습니다." });
  };

  const handleDeleteAd = (adId) => {
    const nextAds = ads.filter((ad) => ad.id !== adId);
    setAds(nextAds);
    persistGranularAppDataList("rc_ads", nextAds);
    if (editingAdId === adId) setEditingAdId(null);
  };

  const downloadAdList = () => {
    const rows = filteredAds.map((ad) => ({
      업체: getAdPartnerName(ad.partnerId, ad.partnerName),
      광고명: ad.adName,
      입점여부: ad.entryStatus,
      광고지점: ad.store,
      진행매체: (ad.media || []).join(" / "),
      규격: ad.spec,
      광고기간: ad.period,
      광고시작일: ad.startDate,
      광고종료일: ad.endDate,
      잔여일수: getAdDaysLeft(ad) === null ? "-" : `${getAdDaysLeft(ad)}일`,
      상태: getAdStatus(ad),
      총광고비: ad.totalAdCost,
      총결제금액: ad.totalPayment,
      비고: ad.note,
      첨부파일: (ad.files || []).map((file) => file.name).join(" / "),
    }));
    downloadCsv("광고관리.csv", rows);
  };

  const salesStoreOptions = ["전체 매장", ...stores, "온라인몰", "기타"];
  const salesInputStoreOptions = [...stores, "온라인몰", "기타"];
  const SALES_API_BASE = "/api";


  const toNumber = (value) => Number(String(value ?? "").replace(/[^0-9.-]/g, "")) || 0;
  const formatNumber = (value) => Number(value || 0).toLocaleString("ko-KR");
  const formatSalesWon = (value) => `${formatNumber(toNumber(value))}원`;

  const purchaseCurrencyOptions = ["KRW", "CNY", "USD", "JPY", "EUR", "기타"];
  const formatCostWon = (value) => `${Math.round(Number(value) || 0).toLocaleString("ko-KR")}원`;
  const getManagementProductText = (product) => [
    product?.name,
    product?.code,
    product?.barcode,
    product?.category,
    getProductCtQuantity(product),
  ].map((item) => String(item || "").toLowerCase()).join(" ");
  const searchManagementProducts = (keyword) => {
    const normalizedKeyword = String(keyword || "").trim().toLowerCase();
    if (!normalizedKeyword) return [];
    return activeProductList.filter((product) => getManagementProductText(product).includes(normalizedKeyword)).slice(0, 12);
  };
  const toProductSnapshot = (product) => ({
    code: product?.code || "",
    name: product?.name || "",
    barcode: product?.barcode || "",
    category: product?.category || "",
    salePrice: product?.salePrice || "",
    wholesalePrice: product?.wholesalePrice || "",
    ctQuantity: getProductCtQuantity(product),
    imageUrl: getProductImageUrl(product),
  });
  const getManagementPageNumbers = (totalPages, safePage) => Array.from({ length: totalPages }, (_, index) => index + 1).filter((page) => {
    if (totalPages <= 7) return true;
    return page === 1 || page === totalPages || Math.abs(page - safePage) <= 2;
  });
  const getProductionCostSummary = (quantity, materials, otherCost) => {
    const safeMaterials = Array.isArray(materials) ? materials : [];
    const safeQuantity = toNumber(quantity);
    const safeOtherCost = toNumber(otherCost);
    const totalMaterialCost = safeMaterials.reduce((sum, item) => {
      const amount = toNumber(item.amount);
      return sum + (amount || toNumber(item.qty) * toNumber(item.unitPrice));
    }, 0);
    const materialUnitCost = safeMaterials.reduce((sum, item) => sum + toNumber(item.unitPrice), 0);
    const otherCostPerUnit = safeQuantity > 0 ? safeOtherCost / safeQuantity : 0;
    const totalCost = totalMaterialCost + safeOtherCost;
    const unitCost = materialUnitCost + otherCostPerUnit;
    return { totalMaterialCost, totalCost, unitCost, materialUnitCost, otherCostPerUnit };
  };
  const getProductionRecordCostSummary = (record) => {
    const materials = Array.isArray(record?.materials) ? record.materials : [];
    const summary = getProductionCostSummary(record?.quantity, materials, record?.otherCost);
    const hasCostInputs = materials.length > 0 || toNumber(record?.otherCost) > 0;
    if (hasCostInputs) return summary;
    return {
      totalMaterialCost: toNumber(record?.totalMaterialCost),
      totalCost: toNumber(record?.totalCost),
      unitCost: toNumber(record?.unitCost),
      materialUnitCost: toNumber(record?.unitCost),
      otherCostPerUnit: 0,
    };
  };
  const getPurchaseCostSummary = (quantity, currency, foreignUnitPrice, exchangeRate) => {
    const safeQuantity = toNumber(quantity);
    const safeUnitPrice = toNumber(foreignUnitPrice);
    const foreignTotal = safeQuantity * safeUnitPrice;
    const safeExchangeRate = currency === "KRW" && !toNumber(exchangeRate) ? 1 : toNumber(exchangeRate);
    const krwAmount = foreignTotal * safeExchangeRate;
    const totalCost = krwAmount;
    const unitCost = safeQuantity > 0 ? totalCost / safeQuantity : 0;
    return { foreignTotal, exchangeRate: safeExchangeRate, krwAmount, totalCost, unitCost };
  };
  const getPurchaseRecordCostSummary = (record) => getPurchaseCostSummary(
    record?.quantity || "",
    record?.currency || "KRW",
    record?.foreignUnitPrice || "",
    record?.exchangeRate || ""
  );
  const getFirstImageAttachment = (files = []) => (files || []).find((file) => isImageFile(file) && getFileOpenUrl(file));
  const getManagementRecordImageUrl = (record) => {
    const recordProduct = record?.product || {};
    const currentProduct = productList.find((product) => String(product.code || "") === String(recordProduct.code || ""));
    const productImage = getProductImageUrl(currentProduct || recordProduct);
    if (productImage) return productImage;
    const imageAttachment = getFirstImageAttachment(record?.attachments || []);
    return getFileOpenUrl(imageAttachment);
  };
  const renderManagementAttachmentDrafts = (files, onRemove) => (
    files.length > 0 ? (
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        {files.map((file) => {
          const imageFile = isImageFile(file) && getFileOpenUrl(file);
          return (
            <div key={file.id || file.name} style={{ display: "grid", gridTemplateColumns: imageFile ? "72px 1fr auto" : "1fr auto", gap: 8, alignItems: "center", fontSize: 13 }}>
              {imageFile && <img src={getFileOpenUrl(file)} alt={file.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb" }} />}
              <span style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} ({formatFileSize(file.size)})</span>
              <button style={grayButton} onClick={() => onRemove(file.id)}>삭제</button>
            </div>
          );
        })}
      </div>
    ) : null
  );
  const getManagementRecordSortTime = (record) => {
    const raw = record?.createdAt || record?.registeredAt || record?.updatedAt || record?.id || "";
    if (typeof raw === "number") return raw;
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return parsed;
    const numeric = Number(raw);
    return Number.isNaN(numeric) ? 0 : numeric;
  };
  const getThisMonthPrefix = () => new Date().toISOString().slice(0, 7);
  const getRecordDateValue = (record) => String(record?.date || "");
  const matchesManagementDateRange = (date, start, end) => (!start || date >= start) && (!end || date <= end);
  const getProductionMaterialSupplierName = (item) => String(item?.supplierName || item?.partnerName || "").trim();
  const getProductionMaterialRowsForSave = () => productionMaterialRows
    .map((row) => {
      const qty = toNumber(row.qty);
      const unitPrice = toNumber(row.unitPrice);
      const supplierName = getProductionMaterialSupplierName(row);
      return {
        id: row.id,
        name: String(row.name || "").trim(),
        supplierName,
        qty: row.qty || "",
        unitPrice: row.unitPrice || "",
        amount: qty * unitPrice,
      };
    })
    .filter((row) => row.name || row.supplierName || toNumber(row.qty) || toNumber(row.unitPrice));
  const resetProductionForm = () => {
    setEditingProductionId(null);
    setSelectedProductionProduct(null);
    setProductionProductResults([]);
    setProductionMaterialRows([createProductionMaterialRow()]);
    setProductionAttachments([]);
    [productionProductKeywordRef, productionDateRef, productionExpiryRef, productionQtyRef, productionOtherCostRef, productionMemoRef, productionFileRef].forEach((ref) => {
      if (ref.current) ref.current.value = "";
    });
    setProductionCalcTick((prev) => prev + 1);
  };
  const resetPurchaseForm = () => {
    setEditingPurchaseId(null);
    setSelectedPurchaseProduct(null);
    setSelectedPurchasePartner(null);
    setPurchaseProductResults([]);
    setPurchasePartnerResults([]);
    setPurchaseAttachments([]);
    [purchaseProductKeywordRef, purchasePartnerKeywordRef, purchaseDateRef, purchasePartnerNameRef, purchaseQtyRef, purchaseForeignUnitPriceRef, purchaseExchangeRateRef, purchaseMemoRef, purchaseFileRef].forEach((ref) => {
      if (ref.current) ref.current.value = "";
    });
    if (purchaseTypeRef.current) purchaseTypeRef.current.value = "수입";
    if (purchasePartnerRef.current) purchasePartnerRef.current.value = "";
    if (purchaseCurrencyRef.current) purchaseCurrencyRef.current.value = "KRW";
    if (purchaseExchangeRateRef.current) purchaseExchangeRateRef.current.value = "";
    setPurchaseCalcTick((prev) => prev + 1);
  };
  const handleProductionProductSearch = () => {
    const keyword = productionProductKeywordRef.current?.value || "";
    const results = searchManagementProducts(keyword);
    setProductionProductResults(results);
    if (!keyword.trim()) {
      setConfirmModal({ type: "notice", title: "검색어 필요", message: "상품명, 상품코드 또는 바코드를 입력해주세요." });
    }
  };
  const handlePurchaseProductSearch = () => {
    const keyword = purchaseProductKeywordRef.current?.value || "";
    const results = searchManagementProducts(keyword);
    setPurchaseProductResults(results);
    if (!keyword.trim()) {
      setConfirmModal({ type: "notice", title: "검색어 필요", message: "상품명, 상품코드 또는 바코드를 입력해주세요." });
    }
  };
  const handleSelectProductionProduct = (product) => {
    setSelectedProductionProduct(toProductSnapshot(product));
    setProductionProductResults([]);
    if (productionProductKeywordRef.current) productionProductKeywordRef.current.value = product.name || "";
  };
  const handleSelectPurchaseProduct = (product) => {
    setSelectedPurchaseProduct(toProductSnapshot(product));
    setPurchaseProductResults([]);
    if (purchaseProductKeywordRef.current) purchaseProductKeywordRef.current.value = product.name || "";
  };
  const handlePurchasePartnerSearch = () => {
    const keyword = String(purchasePartnerKeywordRef.current?.value || "").trim().toLowerCase();
    if (!keyword) {
      setPurchasePartnerResults([]);
      setConfirmModal({ type: "notice", title: "검색어 필요", message: "거래처명을 입력해주세요." });
      return;
    }
    setPurchasePartnerResults(partners.filter((partner) => String(partner.name || "").toLowerCase().includes(keyword)).slice(0, 8));
  };
  const handleSelectPurchasePartner = (partner) => {
    setSelectedPurchasePartner({ id: partner.id, name: partner.name, type: partner.type || "" });
    setPurchasePartnerResults([]);
    if (purchasePartnerKeywordRef.current) purchasePartnerKeywordRef.current.value = partner.name || "";
    if (purchasePartnerNameRef.current) purchasePartnerNameRef.current.value = "";
  };
  const handleClearPurchasePartner = () => {
    setSelectedPurchasePartner(null);
    setPurchasePartnerResults([]);
    if (purchasePartnerKeywordRef.current) purchasePartnerKeywordRef.current.value = "";
  };
  const handleProductionMaterialChange = (rowId, field, value) => {
    setProductionMaterialRows((prev) => prev.map((row) => row.id === rowId ? { ...row, [field]: value } : row));
  };
  const handleProductionMaterialSupplierChange = (rowId, value) => {
    const keyword = String(value || "").trim().toLowerCase();
    const supplierResults = keyword
      ? partners.filter((partner) => String(partner.name || "").toLowerCase().includes(keyword)).slice(0, 6)
      : [];
    setProductionMaterialRows((prev) => prev.map((row) =>
      row.id === rowId ? { ...row, supplierName: value, supplierResults } : row
    ));
  };
  const handleSelectProductionMaterialSupplier = (rowId, partner) => {
    setProductionMaterialRows((prev) => prev.map((row) =>
      row.id === rowId ? { ...row, supplierName: partner.name || "", supplierResults: [] } : row
    ));
  };
  const handleAddProductionMaterialRow = () => setProductionMaterialRows((prev) => [...prev, createProductionMaterialRow()]);
  const handleRemoveProductionMaterialRow = (rowId) => setProductionMaterialRows((prev) => prev.length <= 1 ? [createProductionMaterialRow()] : prev.filter((row) => row.id !== rowId));
  const handleProductionFileChange = async (e) => {
    const files = await makePersistentAttachments(e.target.files);
    setProductionAttachments((prev) => {
      const merged = [...prev];
      files.forEach((file) => {
        if (!merged.some((item) => item.id === file.id)) merged.push(file);
      });
      return merged;
    });
    if (productionFileRef.current) productionFileRef.current.value = "";
  };
  const handlePurchaseFileChange = async (e) => {
    const files = await makePersistentAttachments(e.target.files);
    setPurchaseAttachments((prev) => {
      const merged = [...prev];
      files.forEach((file) => {
        if (!merged.some((item) => item.id === file.id)) merged.push(file);
      });
      return merged;
    });
    if (purchaseFileRef.current) purchaseFileRef.current.value = "";
  };
  const handleRemoveProductionAttachment = (fileId) => setProductionAttachments((prev) => prev.filter((file) => file.id !== fileId));
  const handleRemovePurchaseAttachment = (fileId) => setPurchaseAttachments((prev) => prev.filter((file) => file.id !== fileId));
  const buildProductionRecordFromForm = () => {
    if (!selectedProductionProduct) {
      setConfirmModal({ type: "notice", title: "상품 선택 필요", message: "상품 DB에서 생산 상품을 검색해 선택해주세요." });
      return null;
    }
    const quantity = productionQtyRef.current?.value || "";
    if (toNumber(quantity) <= 0) {
      setConfirmModal({ type: "notice", title: "수량 확인", message: "생산수량을 입력해주세요." });
      return null;
    }
    const materials = getProductionMaterialRowsForSave();
    const otherCost = productionOtherCostRef.current?.value?.trim() || "";
    const summary = getProductionCostSummary(quantity, materials, otherCost);
    const previousRecord = productionRecords.find((record) => String(record.id) === String(editingProductionId));
    const now = new Date().toISOString();
    return {
      id: editingProductionId || Date.now(),
      date: productionDateRef.current?.value || new Date().toISOString().slice(0, 10),
      productionExpiryDate: productionExpiryRef.current?.value || "",
      product: selectedProductionProduct,
      quantity,
      materials,
      otherCost,
      totalMaterialCost: summary.totalMaterialCost,
      totalCost: summary.totalCost,
      unitCost: summary.unitCost,
      memo: productionMemoRef.current?.value?.trim() || "",
      attachments: productionAttachments,
      createdAt: previousRecord?.createdAt || previousRecord?.registeredAt || now,
      updatedAt: now,
    };
  };
  const buildPurchaseRecordFromForm = () => {
    if (!selectedPurchaseProduct) {
      setConfirmModal({ type: "notice", title: "상품 선택 필요", message: "상품 DB에서 구입 상품을 검색해 선택해주세요." });
      return null;
    }
    const quantity = purchaseQtyRef.current?.value || "";
    if (toNumber(quantity) <= 0) {
      setConfirmModal({ type: "notice", title: "수량 확인", message: "구입수량을 입력해주세요." });
      return null;
    }
    const partnerId = selectedPurchasePartner?.id ? String(selectedPurchasePartner.id) : "";
    const partnerName = selectedPurchasePartner?.name || purchasePartnerNameRef.current?.value?.trim() || "";
    const currency = purchaseCurrencyRef.current?.value || "KRW";
    const exchangeRate = currency === "KRW" && !purchaseExchangeRateRef.current?.value ? "1" : purchaseExchangeRateRef.current?.value || "";
    const summary = getPurchaseCostSummary(
      quantity,
      currency,
      purchaseForeignUnitPriceRef.current?.value || "",
      exchangeRate
    );
    const previousRecord = purchaseRecords.find((record) => String(record.id) === String(editingPurchaseId));
    const now = new Date().toISOString();
    return {
      id: editingPurchaseId || Date.now(),
      date: purchaseDateRef.current?.value || new Date().toISOString().slice(0, 10),
      type: purchaseTypeRef.current?.value || "수입",
      product: selectedPurchaseProduct,
      partnerId,
      partnerName,
      quantity,
      currency,
      foreignUnitPrice: purchaseForeignUnitPriceRef.current?.value?.trim() || "",
      foreignTotal: summary.foreignTotal,
      exchangeRate: String(summary.exchangeRate || exchangeRate || ""),
      krwAmount: summary.krwAmount,
      totalCost: summary.totalCost,
      unitCost: summary.unitCost,
      memo: purchaseMemoRef.current?.value?.trim() || "",
      attachments: purchaseAttachments,
      createdAt: previousRecord?.createdAt || previousRecord?.registeredAt || now,
      updatedAt: now,
    };
  };
  const handleSaveProductionRecord = async () => {
    const record = buildProductionRecordFromForm();
    if (!record) return;
    let replaced = false;
    const result = await mutateGranularAppDataListFromServer(
      PRODUCTION_APP_DATA_KEY,
      setProductionRecords,
      (serverRecords) => editingProductionId
        ? serverRecords.map((item) => {
          if (String(item.id) !== String(editingProductionId)) return item;
          replaced = true;
          return { ...item, ...record };
        })
        : [record, ...serverRecords]
    );
    if (!result.saved) {
      setConfirmModal({ type: "notice", title: "생산기록 저장 실패", message: "서버 최신 생산관리 데이터를 확인하지 못해 저장을 중단했습니다." });
      return;
    }
    if (editingProductionId && !replaced) {
      setConfirmModal({ type: "notice", title: "수정 대상 없음", message: "이미 삭제되었거나 서버 최신 목록에 없는 생산기록입니다." });
    } else {
      setConfirmModal({ type: "notice", title: editingProductionId ? "수정 완료" : "등록 완료", message: "생산기록이 저장되었습니다." });
    }
    resetProductionForm();
    setProductionPage(1);
  };
  const handleSavePurchaseRecord = async () => {
    const record = buildPurchaseRecordFromForm();
    if (!record) return;
    let replaced = false;
    const result = await mutateGranularAppDataListFromServer(
      PURCHASE_APP_DATA_KEY,
      setPurchaseRecords,
      (serverRecords) => editingPurchaseId
        ? serverRecords.map((item) => {
          if (String(item.id) !== String(editingPurchaseId)) return item;
          replaced = true;
          return { ...item, ...record };
        })
        : [record, ...serverRecords]
    );
    if (!result.saved) {
      setConfirmModal({ type: "notice", title: "구입기록 저장 실패", message: "서버 최신 수입/구입 데이터를 확인하지 못해 저장을 중단했습니다." });
      return;
    }
    if (editingPurchaseId && !replaced) {
      setConfirmModal({ type: "notice", title: "수정 대상 없음", message: "이미 삭제되었거나 서버 최신 목록에 없는 구입기록입니다." });
    } else {
      setConfirmModal({ type: "notice", title: editingPurchaseId ? "수정 완료" : "등록 완료", message: "수입/구입 기록이 저장되었습니다." });
    }
    resetPurchaseForm();
    setPurchasePage(1);
  };
  const handleStartEditProductionRecord = (record) => {
    setEditingProductionId(record.id);
    setSelectedProductionProduct(record.product || null);
    setProductionMaterialRows(record.materials?.length ? record.materials.map((item) => ({ ...createProductionMaterialRow(), ...item, supplierName: getProductionMaterialSupplierName(item), supplierResults: [] })) : [createProductionMaterialRow()]);
    setProductionAttachments(record.attachments || []);
    setTimeout(() => {
      if (productionProductKeywordRef.current) productionProductKeywordRef.current.value = record.product?.name || "";
      if (productionDateRef.current) productionDateRef.current.value = record.date || "";
      if (productionExpiryRef.current) productionExpiryRef.current.value = record.productionExpiryDate || record.expiryDate || "";
      if (productionQtyRef.current) productionQtyRef.current.value = record.quantity || "";
      if (productionOtherCostRef.current) productionOtherCostRef.current.value = record.otherCost || "";
      if (productionMemoRef.current) productionMemoRef.current.value = record.memo || "";
      setProductionCalcTick((prev) => prev + 1);
      scrollToPageTop();
    }, 0);
  };
  const handleStartEditPurchaseRecord = (record) => {
    setEditingPurchaseId(record.id);
    setSelectedPurchaseProduct(record.product || null);
    const matchedPartner = partners.find((partner) => String(partner.id) === String(record.partnerId));
    setSelectedPurchasePartner(matchedPartner ? { id: matchedPartner.id, name: matchedPartner.name, type: matchedPartner.type || "" } : null);
    setPurchaseAttachments(record.attachments || []);
    setTimeout(() => {
      if (purchaseProductKeywordRef.current) purchaseProductKeywordRef.current.value = record.product?.name || "";
      if (purchasePartnerKeywordRef.current) purchasePartnerKeywordRef.current.value = matchedPartner?.name || "";
      if (purchaseDateRef.current) purchaseDateRef.current.value = record.date || "";
      if (purchaseTypeRef.current) purchaseTypeRef.current.value = record.type || "수입";
      if (purchasePartnerRef.current) purchasePartnerRef.current.value = record.partnerId || "";
      if (purchasePartnerNameRef.current) purchasePartnerNameRef.current.value = matchedPartner ? "" : record.partnerName || "";
      if (purchaseQtyRef.current) purchaseQtyRef.current.value = record.quantity || "";
      if (purchaseCurrencyRef.current) purchaseCurrencyRef.current.value = record.currency || "KRW";
      if (purchaseForeignUnitPriceRef.current) purchaseForeignUnitPriceRef.current.value = record.foreignUnitPrice || "";
      if (purchaseExchangeRateRef.current) {
        const savedExchangeRate = String(record.exchangeRate || "");
        purchaseExchangeRateRef.current.value = (record.currency || "KRW") === "KRW" && savedExchangeRate === "1" ? "" : savedExchangeRate;
      }
      if (purchaseMemoRef.current) purchaseMemoRef.current.value = record.memo || "";
      setPurchaseCalcTick((prev) => prev + 1);
      scrollToPageTop();
    }, 0);
  };
  const deleteManagementRecordFromLatestServer = async (key, setValue, recordId) => {
    if (!(await waitForCommonAppDataPersistReady())) return { saved: false, reason: "not-ready" };

    let serverRecords = [];
    try {
      serverRecords = await fetchLatestGranularAppDataListForMutation(key);
    } catch (error) {
      console.error("app-data 최신값 조회 실패. 삭제를 중단합니다.", key, error);
      return { saved: false, reason: "fetch-failed" };
    }

    const nextRecords = serverRecords.filter((record) => String(record.id) !== String(recordId));
    const saved = await saveGranularAppDataListNow(key, nextRecords);
    if (!saved) return { saved: false, reason: "save-failed", latestList: serverRecords };

    setValue(nextRecords);
    return { saved: true, nextList: nextRecords };
  };
  const handleDeleteProductionRecord = async (recordId) => {
    if (!window.confirm("선택한 생산기록을 삭제하시겠습니까?")) return;
    const result = await deleteManagementRecordFromLatestServer(
      PRODUCTION_APP_DATA_KEY,
      setProductionRecords,
      recordId
    );
    if (!result.saved) {
      setConfirmModal({ type: "notice", title: "삭제 실패", message: "서버 최신 생산관리 데이터를 확인하지 못해 삭제를 중단했습니다." });
      return;
    }
    if (editingProductionId === recordId) resetProductionForm();
  };
  const handleDeletePurchaseRecord = async (recordId) => {
    if (!window.confirm("선택한 수입/구입 기록을 삭제하시겠습니까?")) return;
    const result = await deleteManagementRecordFromLatestServer(
      PURCHASE_APP_DATA_KEY,
      setPurchaseRecords,
      recordId
    );
    if (!result.saved) {
      setConfirmModal({ type: "notice", title: "삭제 실패", message: "서버 최신 수입/구입 데이터를 확인하지 못해 삭제를 중단했습니다." });
      return;
    }
    if (editingPurchaseId === recordId) resetPurchaseForm();
  };
  const filteredProductionRecords = [...productionRecords].filter((record) => {
    const date = getRecordDateValue(record);
    const keyword = productionFilter.keyword.trim().toLowerCase();
    const product = record.product || {};
    const keywordOk = !keyword ||
      getManagementProductText(product).includes(keyword) ||
      String(record.memo || "").toLowerCase().includes(keyword);
    return matchesManagementDateRange(date, productionFilter.start, productionFilter.end) && keywordOk;
  }).sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || getManagementRecordSortTime(b) - getManagementRecordSortTime(a));
  const filteredPurchaseRecords = [...purchaseRecords].filter((record) => {
    const date = getRecordDateValue(record);
    const keyword = purchaseFilter.keyword.trim().toLowerCase();
    const product = record.product || {};
    const typeOk = purchaseFilter.type === "전체" || record.type === purchaseFilter.type;
    const currencyOk = purchaseFilter.currency === "전체" || record.currency === purchaseFilter.currency;
    const recordPartnerKey = partners.some((partner) => String(partner.id) === String(record.partnerId))
      ? String(record.partnerId)
      : String(record.partnerName || record.partnerId || "");
    const partnerOk = purchaseFilter.partner === "전체" || recordPartnerKey === String(purchaseFilter.partner);
    const keywordOk = !keyword ||
      getManagementProductText(product).includes(keyword) ||
      String(record.partnerName || "").toLowerCase().includes(keyword) ||
      String(record.memo || "").toLowerCase().includes(keyword);
    return typeOk && currencyOk && partnerOk && matchesManagementDateRange(date, purchaseFilter.start, purchaseFilter.end) && keywordOk;
  }).sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || getManagementRecordSortTime(b) - getManagementRecordSortTime(a));
  const productionTotalPages = Math.max(1, Math.ceil(filteredProductionRecords.length / productionPageSize));
  const safeProductionPage = Math.min(productionPage, productionTotalPages);
  const pagedProductionRecords = filteredProductionRecords.slice((safeProductionPage - 1) * productionPageSize, safeProductionPage * productionPageSize);
  const productionPageNumbers = getManagementPageNumbers(productionTotalPages, safeProductionPage);
  const purchaseTotalPages = Math.max(1, Math.ceil(filteredPurchaseRecords.length / purchasePageSize));
  const safePurchasePage = Math.min(purchasePage, purchaseTotalPages);
  const pagedPurchaseRecords = filteredPurchaseRecords.slice((safePurchasePage - 1) * purchasePageSize, safePurchasePage * purchasePageSize);
  const purchasePageNumbers = getManagementPageNumbers(purchaseTotalPages, safePurchasePage);
  const thisMonthPrefix = getThisMonthPrefix();
  const productionSummary = productionRecords.reduce((acc, record) => {
    const summary = getProductionRecordCostSummary(record);
    return {
      count: acc.count + 1,
      monthCount: acc.monthCount + (String(record.date || "").startsWith(thisMonthPrefix) ? 1 : 0),
      quantity: acc.quantity + toNumber(record.quantity),
      totalCost: acc.totalCost + summary.totalCost,
    };
  }, { count: 0, monthCount: 0, quantity: 0, totalCost: 0 });
  const purchaseSummary = purchaseRecords.reduce((acc, record) => ({
    count: acc.count + 1,
    monthCount: acc.monthCount + (String(record.date || "").startsWith(thisMonthPrefix) ? 1 : 0),
    quantity: acc.quantity + toNumber(record.quantity),
    totalCost: acc.totalCost + getPurchaseRecordCostSummary(record).totalCost,
  }), { count: 0, monthCount: 0, quantity: 0, totalCost: 0 });
  const downloadProductionRecordsCsv = () => {
    downloadCsv("생산관리.csv", filteredProductionRecords.map((record) => {
      const summary = getProductionRecordCostSummary(record);
      return {
        생산일: record.date,
        유통기한: record.productionExpiryDate || record.expiryDate || "",
        상품명: record.product?.name,
        상품코드: record.product?.code,
        바코드: record.product?.barcode,
        생산수량: record.quantity,
        부자재비: summary.totalMaterialCost,
        기타비용: record.otherCost,
        총생산원가: summary.totalCost,
        개당생산원가: Math.round(toNumber(summary.unitCost)),
        부자재항목: (record.materials || []).map((item) => `${item.name}${getProductionMaterialSupplierName(item) ? ` @ ${getProductionMaterialSupplierName(item)}` : ""} ${item.qty} x ${item.unitPrice}`).join(" / "),
        "생산처/구입처": (record.materials || []).map((item) => getProductionMaterialSupplierName(item)).join(" / "),
        메모: record.memo,
        첨부파일: (record.attachments || []).map((file) => file.name).join(" / "),
      };
    }));
  };
  const downloadPurchaseRecordsCsv = () => {
    downloadCsv("수입구입관리.csv", filteredPurchaseRecords.map((record) => {
      const summary = getPurchaseRecordCostSummary(record);
      return {
        구입일: record.date,
        구분: record.type,
        상품명: record.product?.name,
        상품코드: record.product?.code,
        바코드: record.product?.barcode,
        거래처: record.partnerName,
        구입수량: record.quantity,
        화폐: record.currency,
        외화단가: record.foreignUnitPrice,
        외화총액: summary.foreignTotal,
        환율: summary.exchangeRate,
        원화환산액: summary.krwAmount,
        개당구입원가: Math.round(toNumber(summary.unitCost)),
        메모: record.memo,
        첨부파일: (record.attachments || []).map((file) => file.name).join(" / "),
      };
    }));
  };

  const readManagementCsvRows = (file, requiredHeader, onReady, inputRef) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setConfirmModal({ type: "notice", title: "업로드 불가", message: "현재 버전에서는 CSV 파일만 업로드할 수 있습니다. 양식을 다운로드한 뒤 Excel에서 작성하고 CSV UTF-8 형식으로 저장해주세요." });
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result;
      const decodeText = (encoding) => {
        try {
          return new TextDecoder(encoding).decode(buffer).replace(/^﻿/, "");
        } catch (error) {
          return "";
        }
      };

      const decodedCandidates = [decodeText("utf-8"), decodeText("euc-kr")].filter(Boolean);
      let csvRows = [];
      let headers = [];

      const requiredHeaders = Array.isArray(requiredHeader) ? requiredHeader : [requiredHeader];
      for (const csvText of decodedCandidates) {
        const rows = parseCsvText(csvText);
        const candidateHeaders = rows[0]?.map((header) => header.replace(/^﻿/, "").trim()) || [];
        if (requiredHeaders.some((header) => candidateHeaders.includes(header))) {
          csvRows = rows;
          headers = candidateHeaders;
          break;
        }
      }

      if (csvRows.length < 2) {
        setConfirmModal({ type: "notice", title: "업로드 불가", message: "등록할 데이터가 없거나 CSV 인코딩을 읽을 수 없습니다." });
        if (inputRef.current) inputRef.current.value = "";
        return;
      }

      Promise.resolve(onReady(csvRows, headers)).catch((error) => {
        console.error("관리 데이터 CSV 업로드 처리 실패:", error);
        setConfirmModal({ type: "notice", title: "업로드 실패", message: "CSV 처리 중 오류가 발생했습니다. 양식을 확인한 뒤 다시 시도해주세요." });
      });
      if (inputRef.current) inputRef.current.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  const getCsvCell = (row, headers, aliases) => {
    const targetHeaders = Array.isArray(aliases) ? aliases : [aliases];
    const header = targetHeaders.find((item) => headers.includes(item));
    return header ? (row[headers.indexOf(header)]?.trim() || "") : "";
  };

  const normalizeManagementCsvDate = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const normalized = raw.replace(/[./]/g, "-");
    const parts = normalized.split("-").map((part) => part.padStart(2, "0"));
    if (parts.length >= 3) return `${parts[0]}-${parts[1]}-${parts[2]}`;
    return raw;
  };

  const getManagementCsvProductSnapshot = ({ name, code, barcode }) => {
    const product = activeProductList.find((item) =>
      (code && String(item.code || "") === String(code)) ||
      (barcode && String(item.barcode || "") === String(barcode)) ||
      (!code && name && String(item.name || "").trim() === String(name).trim())
    );
    return product ? toProductSnapshot(product) : { code: code || "", name: name || "", barcode: barcode || "", category: "", salePrice: "", wholesalePrice: "", imageUrl: "" };
  };

  const getProductionDuplicateKey = (record) => [
    record.date || "",
    record.product?.code || record.product?.name || "",
    record.quantity || "",
    Math.round(toNumber(record.totalCost)),
  ].map((item) => String(item).trim()).join("|");

  const getPurchaseDuplicateKey = (record) => [
    record.date || "",
    record.product?.code || record.product?.name || "",
    record.partnerName || "",
    record.quantity || "",
    record.currency || "",
    record.foreignUnitPrice || "",
  ].map((item) => String(item).trim()).join("|");

  const parseProductionMaterialsFromCsv = (text) => String(text || "").split(/[\/;\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((rawItem, index) => {
      const pipeParts = rawItem.split("|").map((part) => part.trim());
      if (pipeParts.length >= 4) {
        const qty = pipeParts[2] || "";
        const unitPrice = pipeParts[3] || "";
        return {
          id: `${Date.now()}-csv-${index}`,
          name: pipeParts[0] || "",
          supplierName: pipeParts[1] || "",
          qty,
          unitPrice,
          amount: toNumber(qty) * toNumber(unitPrice),
        };
      }

      const match = rawItem.match(/^(.*?)\s+([0-9,.-]+)\s*x\s*([0-9,.-]+)$/i);
      const nameAndSupplier = (match ? match[1] : rawItem).trim();
      const supplierParts = nameAndSupplier.split("@").map((part) => part.trim());
      const name = supplierParts[0] || nameAndSupplier;
      const supplierName = supplierParts.slice(1).join(" @ ");
      const qty = match?.[2] || "";
      const unitPrice = match?.[3] || "";
      return {
        id: `${Date.now()}-csv-${index}`,
        name,
        supplierName,
        qty,
        unitPrice,
        amount: toNumber(qty) * toNumber(unitPrice),
      };
    });

  const downloadProductionUploadTemplate = () => {
    downloadCsv("생산관리_업로드양식.csv", [{
      생산일: "2026-06-16",
      유통기한: "2027-06-16",
      상품명: "예시 상품",
      상품코드: "RC-EX-001",
      바코드: "8800000000000",
      생산수량: "100",
      부자재내역: "원재료A @ 토이테크 2 x 1000 / 포장재B @ 박스업체 100 x 50",
      "생산처/구입처": "토이테크 / 박스업체",
      기타비용: "10000",
      총생산원가: "150000",
      개당생산원가: "1500",
      메모: "CSV 등록 예시",
    }]);
  };

  const downloadPurchaseUploadTemplate = () => {
    downloadCsv("수입구입관리_업로드양식.csv", [{
      구입일: "2026-06-16",
      구분: "수입",
      상품명: "예시 상품",
      상품코드: "RC-EX-001",
      바코드: "8800000000000",
      거래처: "예시 거래처",
      수량: "100",
      화폐: "USD",
      외화단가: "2.5",
      외화총액: "250",
      환율: "1350",
      원화환산액: "337500",
      개당구입원가: "3375",
      메모: "CSV 등록 예시",
    }]);
  };

  const handleImportProductionCsv = (e) => {
    const file = e.target.files?.[0];
    readManagementCsvRows(file, "생산일", async (csvRows, headers) => {
      const incoming = [];
      const invalidRows = [];
      const uploadKeys = new Set();

      csvRows.slice(1).forEach((row, index) => {
        const date = normalizeManagementCsvDate(getCsvCell(row, headers, ["생산일", "생산일자"]));
        const name = getCsvCell(row, headers, "상품명");
        const code = getCsvCell(row, headers, "상품코드");
        const barcode = getCsvCell(row, headers, "바코드");
        const quantity = getCsvCell(row, headers, ["생산수량", "수량"]);
        if (!date || (!name && !code) || toNumber(quantity) <= 0) {
          invalidRows.push(`${index + 2}행`);
          return;
        }

        const materials = parseProductionMaterialsFromCsv(getCsvCell(row, headers, ["부자재내역", "부자재항목"]));
        const materialSuppliers = getCsvCell(row, headers, ["생산처/구입처", "부자재생산처", "부자재거래처"])
          .split(/[\/;\n]/)
          .map((item) => item.trim())
          .filter(Boolean);
        const materialsWithSuppliers = materials.map((item, materialIndex) => ({
          ...item,
          supplierName: getProductionMaterialSupplierName(item) || materialSuppliers[materialIndex] || "",
        }));
        const otherCost = getCsvCell(row, headers, "기타비용");
        const totalCostInput = getCsvCell(row, headers, "총생산원가");
        const fallbackSummary = getProductionCostSummary(quantity, materialsWithSuppliers, otherCost);
        const totalCost = totalCostInput ? toNumber(totalCostInput) : fallbackSummary.totalCost;
        const unitCost = fallbackSummary.unitCost;
        const record = {
          id: Date.now() + index,
          date,
          productionExpiryDate: normalizeManagementCsvDate(getCsvCell(row, headers, "유통기한")),
          product: getManagementCsvProductSnapshot({ name, code, barcode }),
          quantity,
          materials: materialsWithSuppliers,
          otherCost,
          totalMaterialCost: fallbackSummary.totalMaterialCost,
          totalCost,
          unitCost,
          memo: getCsvCell(row, headers, "메모"),
          attachments: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const duplicateKey = getProductionDuplicateKey(record);
        if (uploadKeys.has(duplicateKey)) return;
        uploadKeys.add(duplicateKey);
        incoming.push(record);
      });

      let savedCount = 0;
      let duplicateCount = 0;
      if (incoming.length > 0) {
        const result = await mutateGranularAppDataListFromServer(PRODUCTION_APP_DATA_KEY, setProductionRecords, (serverRecords) => {
          const existingKeys = new Set(serverRecords.map(getProductionDuplicateKey));
          const uniqueIncoming = incoming.filter((record) => {
            const key = getProductionDuplicateKey(record);
            if (existingKeys.has(key)) {
              duplicateCount += 1;
              return false;
            }
            existingKeys.add(key);
            return true;
          });
          savedCount = uniqueIncoming.length;
          return [...uniqueIncoming, ...serverRecords];
        });
        if (!result.saved) {
          setConfirmModal({ type: "notice", title: "업로드 실패", message: "서버 최신 생산관리 데이터를 확인하지 못해 업로드를 중단했습니다." });
          return;
        }
      }

      setProductionPage(1);
      setConfirmModal({ type: "notice", title: "생산관리 업로드 완료", message: `등록 ${savedCount}건, 중복 제외 ${duplicateCount}건, 오류 ${invalidRows.length}건` });
    }, productionImportFileRef);
  };

  const handleImportPurchaseCsv = (e) => {
    const file = e.target.files?.[0];
    readManagementCsvRows(file, ["구입일", "수입일", "구입일/수입일"], async (csvRows, headers) => {
      const incoming = [];
      const invalidRows = [];
      const uploadKeys = new Set();

      csvRows.slice(1).forEach((row, index) => {
        const date = normalizeManagementCsvDate(getCsvCell(row, headers, ["구입일", "수입일", "구입일/수입일"]));
        const name = getCsvCell(row, headers, "상품명");
        const code = getCsvCell(row, headers, "상품코드");
        const barcode = getCsvCell(row, headers, "바코드");
        const quantity = getCsvCell(row, headers, ["수량", "구입수량"]);
        const currency = getCsvCell(row, headers, "화폐") || "KRW";
        const foreignUnitPrice = getCsvCell(row, headers, "외화단가");
        if (!date || (!name && !code) || toNumber(quantity) <= 0) {
          invalidRows.push(`${index + 2}행`);
          return;
        }

        const exchangeRate = getCsvCell(row, headers, "환율");
        const summary = getPurchaseCostSummary(quantity, currency, foreignUnitPrice, exchangeRate);
        const record = {
          id: Date.now() + index,
          date,
          type: getCsvCell(row, headers, "구분") || "국내구입",
          product: getManagementCsvProductSnapshot({ name, code, barcode }),
          partnerId: "",
          partnerName: getCsvCell(row, headers, "거래처"),
          quantity,
          currency,
          foreignUnitPrice,
          foreignTotal: toNumber(getCsvCell(row, headers, "외화총액")) || summary.foreignTotal,
          exchangeRate: String(summary.exchangeRate || exchangeRate || ""),
          krwAmount: toNumber(getCsvCell(row, headers, "원화환산액")) || summary.krwAmount,
          totalCost: toNumber(getCsvCell(row, headers, "원화환산액")) || summary.totalCost,
          unitCost: toNumber(getCsvCell(row, headers, "개당구입원가")) || summary.unitCost,
          memo: getCsvCell(row, headers, "메모"),
          attachments: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const duplicateKey = getPurchaseDuplicateKey(record);
        if (uploadKeys.has(duplicateKey)) return;
        uploadKeys.add(duplicateKey);
        incoming.push(record);
      });

      let savedCount = 0;
      let duplicateCount = 0;
      if (incoming.length > 0) {
        const result = await mutateGranularAppDataListFromServer(PURCHASE_APP_DATA_KEY, setPurchaseRecords, (serverRecords) => {
          const existingKeys = new Set(serverRecords.map(getPurchaseDuplicateKey));
          const uniqueIncoming = incoming.filter((record) => {
            const key = getPurchaseDuplicateKey(record);
            if (existingKeys.has(key)) {
              duplicateCount += 1;
              return false;
            }
            existingKeys.add(key);
            return true;
          });
          savedCount = uniqueIncoming.length;
          return [...uniqueIncoming, ...serverRecords];
        });
        if (!result.saved) {
          setConfirmModal({ type: "notice", title: "업로드 실패", message: "서버 최신 수입/구입 데이터를 확인하지 못해 업로드를 중단했습니다." });
          return;
        }
      }

      setPurchasePage(1);
      setConfirmModal({ type: "notice", title: "수입/구입관리 업로드 완료", message: `등록 ${savedCount}건, 중복 제외 ${duplicateCount}건, 오류 ${invalidRows.length}건` });
    }, purchaseImportFileRef);
  };

  const getSalesDateValue = (record) => String(record?.date || record?.salesDate || record?.sales_date || "");
  const getSalesStoreValue = (record) => getMappedStoreName(record?.store || record?.storeName || record?.store_name || "기타");
  const getSalesChannelValue = (record) => String(record?.channel || "기타");
  const getSalesGrossValue = (record) => record?.grossSales ?? record?.gross_sales ?? record?.gross ?? 0;
  const getSalesNetValue = (record) => record?.netSales ?? record?.net_sales ?? record?.net ?? record?.grossSales ?? record?.gross_sales ?? 0;
  const getSalesOrderCountValue = (record) => record?.orderCount ?? record?.order_count ?? record?.orders ?? 0;
  const getSalesStatusValue = (record) => String(record?.status || "정상");
  const getSalesSourceValue = (record) => String(record?.source || "server");
  const getSalesMemoValue = (record) => String(record?.memo || "");
  const normalizeChannelKey = (value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9가-힣]/g, "");
  const isOfflineSalesChannel = (value) => {
    const key = normalizeChannelKey(value);
    return key === "okpos" || key === "orderqueen" || key.includes("orderqueen");
  };
  const isCafe24SalesChannel = (value) => normalizeChannelKey(value).includes("cafe24");
  const isKsnetSalesChannel = (value) => normalizeChannelKey(value).includes("ksnet");
  const isSalesIssueStatus = (value) => {
    const status = getSalesStatusValue({ status: value });
    return status === "누락" || status === "수집실패" || status === "확인필요";
  };
  const normalizeServerSalesRecord = (record, index = 0) => ({
    id: record?.id ?? `${getSalesDateValue(record)}-${getSalesStoreValue(record)}-${getSalesChannelValue(record)}-${index}`,
    date: getSalesDateValue(record),
    store: getSalesStoreValue(record),
    channel: getSalesChannelValue(record),
    grossSales: String(getSalesGrossValue(record) ?? ""),
    netSales: String(getSalesNetValue(record) ?? ""),
    orderCount: String(getSalesOrderCountValue(record) ?? ""),
    source: getSalesSourceValue(record),
    status: getSalesStatusValue(record),
    collectedAt: record?.collectedAt || record?.collected_at || "",
    updatedAt: record?.updatedAt || record?.updated_at || "",
    memo: getSalesMemoValue(record),
  });
  const getSalesAvgTicket = (record) => {
    const sales = toNumber(record.netSales || record.grossSales);
    const count = toNumber(record.orderCount);
    if (!sales || !count) return 0;
    return Math.round(sales / count);
  };

  const getSalesRecordKey = (record) => `${record.date}__${record.store}__${record.channel}`;

  const filteredSalesRecords = salesRecords.map((record, index) => normalizeServerSalesRecord(record, index)).filter((record) => {
    if (isSalesExcluded(record)) return false;
    const recordDate = getSalesDateValue(record);
    const recordStore = getSalesStoreValue(record);
    const recordChannel = getSalesChannelValue(record);
    const recordStatus = getSalesStatusValue(record);
    const startOk = !salesFilter.start || recordDate >= salesFilter.start;
    const endOk = !salesFilter.end || recordDate <= salesFilter.end;
    const storeOk = salesFilter.store === "전체 매장" || recordStore === salesFilter.store;
    const channelOk = salesFilter.channel === "전체" || recordChannel === salesFilter.channel;
    const statusOk = salesFilter.status === "전체" || recordStatus === salesFilter.status;
    const keyword = salesFilter.keyword.trim();
    const keywordOk = !keyword || recordStore.includes(keyword) || recordChannel.includes(keyword) || getSalesMemoValue(record).includes(keyword) || getSalesSourceValue(record).includes(keyword) || recordStatus.includes(keyword);
    return startOk && endOk && storeOk && channelOk && statusOk && keywordOk;
  });

  const salesSummary = filteredSalesRecords.reduce((acc, record) => {
    const sales = toNumber(getSalesNetValue(record));
    const count = toNumber(getSalesOrderCountValue(record));
    acc.totalSales += sales;
    acc.totalCount += count;
    if (isSalesIssueStatus(getSalesStatusValue(record))) acc.issueCount += 1;
    if (getSalesStatusValue(record) === "수정됨") acc.modifiedCount += 1;
    return acc;
  }, { totalSales: 0, totalCount: 0, issueCount: 0, modifiedCount: 0 });

  const salesByStore = Object.values(filteredSalesRecords.reduce((acc, record) => {
    const key = getSalesStoreValue(record) || "기타";
    if (!acc[key]) acc[key] = { store: key, totalSales: 0, totalCount: 0, issueCount: 0 };
    acc[key].totalSales += toNumber(getSalesNetValue(record));
    acc[key].totalCount += toNumber(getSalesOrderCountValue(record));
    if (isSalesIssueStatus(getSalesStatusValue(record))) acc[key].issueCount += 1;
    return acc;
  }, {})).sort((a, b) => b.totalSales - a.totalSales);

  const salesByChannel = Object.values(filteredSalesRecords.reduce((acc, record) => {
    const key = getSalesChannelValue(record) || "기타";
    if (!acc[key]) acc[key] = { channel: key, totalSales: 0, totalCount: 0, issueCount: 0 };
    acc[key].totalSales += toNumber(getSalesNetValue(record));
    acc[key].totalCount += toNumber(getSalesOrderCountValue(record));
    if (isSalesIssueStatus(getSalesStatusValue(record))) acc[key].issueCount += 1;
    return acc;
  }, {})).sort((a, b) => b.totalSales - a.totalSales);

  const offlineSalesTableRows = Object.values(filteredSalesRecords.reduce((acc, record) => {
    const channel = getSalesChannelValue(record);
    if (!isOfflineSalesChannel(channel)) return acc;
    const key = getSalesStoreValue(record) || "기타";
    if (!acc[key]) {
      acc[key] = {
        store: key,
        posSales: 0,
        posCount: 0,
        kioskSales: 0,
        kioskCount: 0,
        hasPosRecord: false,
        hasKioskRecord: false,
        posIssue: false,
        kioskIssue: false,
        issueCount: 0,
      };
    }
    const sales = toNumber(getSalesNetValue(record));
    const count = toNumber(getSalesOrderCountValue(record));
    const channelKey = normalizeChannelKey(channel);
    if (channelKey === "okpos") {
      acc[key].hasPosRecord = true;
      acc[key].posSales += sales;
      acc[key].posCount += count;
      if (isSalesIssueStatus(getSalesStatusValue(record))) acc[key].posIssue = true;
    }
    if (channelKey === "orderqueen" || channelKey.includes("orderqueen")) {
      acc[key].hasKioskRecord = true;
      acc[key].kioskSales += sales;
      acc[key].kioskCount += count;
      if (isSalesIssueStatus(getSalesStatusValue(record))) acc[key].kioskIssue = true;
    }
    return acc;
  }, {})).map((row) => ({
    ...row,
    issueCount:
      (!row.hasPosRecord || row.posIssue ? 1 : 0) +
      (!row.hasKioskRecord || row.kioskIssue ? 1 : 0),
    totalSales: row.posSales + row.kioskSales,
    totalCount: row.posCount + row.kioskCount,
  })).sort((a, b) => b.totalSales - a.totalSales);

  const externalSalesSummary = filteredSalesRecords.reduce((acc, record) => {
    const channel = getSalesChannelValue(record);
    const sales = toNumber(getSalesNetValue(record));
    const count = toNumber(getSalesOrderCountValue(record));
    const issue = isSalesIssueStatus(getSalesStatusValue(record));

    if (isCafe24SalesChannel(channel)) {
      acc.cafe24.totalSales += sales;
      acc.cafe24.totalCount += count;
      if (issue) acc.cafe24.issueCount += 1;
    }

    if (isKsnetSalesChannel(channel)) {
      acc.ksnet.totalSales += sales;
      acc.ksnet.totalCount += count;
      if (issue) acc.ksnet.issueCount += 1;
    }

    return acc;
  }, {
    cafe24: { label: "온라인몰(Cafe24)", totalSales: 0, totalCount: 0, issueCount: 0 },
    ksnet: { label: "인형뽑기(KSNET)", totalSales: 0, totalCount: 0, issueCount: 0 },
  });

  const handleSearchSales = async () => {
    setSalesFilter({
      start: salesStartRef.current?.value || "",
      end: salesEndRef.current?.value || "",
      store: salesStoreRef.current?.value || "전체 매장",
      channel: salesChannelRef.current?.value || "전체",
      status: salesStatusRef.current?.value || "전체",
      keyword: salesKeywordRef.current?.value || "",
    });
    await handleFetchSalesFromServer();
  };

  const handleResetSalesFilter = () => {
    const yesterday = getYesterdayInputDate();
    if (salesStartRef.current) salesStartRef.current.value = yesterday;
    if (salesEndRef.current) salesEndRef.current.value = yesterday;
    if (salesKeywordRef.current) salesKeywordRef.current.value = "";
    if (salesStoreRef.current) salesStoreRef.current.value = "전체 매장";
    if (salesChannelRef.current) salesChannelRef.current.value = "전체";
    if (salesStatusRef.current) salesStatusRef.current.value = "전체";
    setSalesFilter({ start: yesterday, end: yesterday, store: "전체 매장", channel: "전체", status: "전체", keyword: "" });
  };

  const resetManualSalesForm = () => {
    [salesGrossRef, salesNetRef, salesOrderCountRef, salesMemoRef].forEach((ref) => {
      if (ref.current) ref.current.value = "";
    });
    if (salesDateRef.current) salesDateRef.current.value = getYesterdayInputDate();
    if (salesChannelAddRef.current) salesChannelAddRef.current.value = "OKPOS";
    if (salesStoreAddRef.current) salesStoreAddRef.current.value = salesInputStoreOptions[0] || "";
    if (salesStatusAddRef.current) salesStatusAddRef.current.value = "정상";
    setManualSalesPreview({ sales: 0, count: 0 });
    setManualSalesDuplicate(null);
  };

  const updateManualSalesPreview = () => {
    setManualSalesPreview({
      sales: toNumber(salesNetRef.current?.value || salesGrossRef.current?.value || 0),
      count: toNumber(salesOrderCountRef.current?.value || 0),
    });
  };

  const handleAddSalesRecord = async (options = {}) => {
    if (isSavingManualSales) return;
    const overwrite = options?.overwrite === true;
    const date = salesDateRef.current?.value || "";
    const store = salesStoreAddRef.current?.value || "";
    const channel = salesChannelAddRef.current?.value || "OKPOS";
    const netSales = salesNetRef.current?.value?.trim() || "";
    const grossSales = salesGrossRef.current?.value?.trim() || netSales;
    const orderCount = salesOrderCountRef.current?.value?.trim() || "";
    const memo = salesMemoRef.current?.value?.trim() || "";
    const status = salesStatusAddRef.current?.value || "정상";

    if (!date || !store || !channel || netSales === "" || orderCount === "") {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "날짜, 매장, 채널, 실매출, 건수는 필수입니다." });
      return;
    }
    if (toNumber(netSales) < 0 || toNumber(orderCount) < 0) {
      setConfirmModal({ type: "notice", title: "등록 불가", message: "실매출과 건수는 0 이상이어야 합니다." });
      return;
    }

    const payload = {
      date,
      store,
      channel,
      grossSales,
      netSales,
      orderCount,
      status,
      memo: memo || "관리자 수동 등록",
      overwrite,
      editor: currentUser || "admin",
    };

    setIsSavingManualSales(true);
    try {
      const res = await fetch(`${SALES_API_BASE}/sales`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const responseData = await res.json().catch(() => ({}));
      if (res.status === 409) {
        const duplicate = responseData?.detail?.existing || null;
        setManualSalesDuplicate(duplicate);
        setConfirmModal({
          type: "notice",
          title: "기존 매출 데이터 확인",
          message: "같은 날짜/매장/채널 데이터가 있습니다. 기존 행을 수정하거나 수동 값으로 덮어쓰기를 선택해주세요.",
        });
        return;
      }
      if (!res.ok) {
        throw new Error(responseData?.detail || `수동 매출 저장 실패 (${res.status})`);
      }
      const saved = normalizeServerSalesRecord(responseData, 0);
      setSalesRecords((prev) => {
        const savedKey = getSalesRecordKey(saved);
        const exists = prev.some((record) => getSalesRecordKey(record) === savedKey);
        return exists
          ? prev.map((record) => getSalesRecordKey(record) === savedKey ? saved : record)
          : [saved, ...prev];
      });
      resetManualSalesForm();
      setIsManualSalesFormOpen(false);
      setConfirmModal({
        type: "notice",
        title: overwrite ? "수동 매출 덮어쓰기 완료" : "수동 매출 추가 완료",
        message: `${date} ${store} ${channel} 매출이 source=manual로 저장되었습니다.`,
      });
    } catch (error) {
      setConfirmModal({
        type: "notice",
        title: "수동 매출 저장 실패",
        message: error?.message || "서버에 수동 매출을 저장하지 못했습니다.",
      });
    } finally {
      setIsSavingManualSales(false);
    }
  };

  const handleEditExistingManualSales = () => {
    if (!manualSalesDuplicate) return;
    setSalesRecords((prev) => {
      const key = getSalesRecordKey(manualSalesDuplicate);
      return prev.some((record) => getSalesRecordKey(record) === key)
        ? prev
        : [normalizeServerSalesRecord(manualSalesDuplicate, 0), ...prev];
    });
    setIsManualSalesFormOpen(false);
    setTimeout(() => handleStartEditSales(normalizeServerSalesRecord(manualSalesDuplicate, 0)), 0);
  };

  const handleStartEditSales = (record) => {
    setEditingSalesId(record.id);
    setTimeout(() => {
      if (editSalesDateRef.current) editSalesDateRef.current.value = record.date || "";
      if (editSalesStoreRef.current) editSalesStoreRef.current.value = record.store || "";
      if (editSalesChannelRef.current) editSalesChannelRef.current.value = record.channel || "수동";
      if (editSalesGrossRef.current) editSalesGrossRef.current.value = record.grossSales || "";
      if (editSalesNetRef.current) editSalesNetRef.current.value = record.netSales || "";
      if (editSalesOrderCountRef.current) editSalesOrderCountRef.current.value = record.orderCount || "";
      if (editSalesStatusRef.current) editSalesStatusRef.current.value = record.status || "정상";
      if (editSalesMemoRef.current) editSalesMemoRef.current.value = record.memo || "";
    }, 0);
  };

  const handleSaveEditSales = async (recordId) => {
    const date = editSalesDateRef.current?.value || "";
    const store = editSalesStoreRef.current?.value || "";
    const channel = editSalesChannelRef.current?.value || "수동";
    if (!date || !store || !channel) {
      setConfirmModal({ type: "notice", title: "수정 불가", message: "날짜, 매장, 채널은 필수입니다." });
      return;
    }

    const payload = {
      date,
      store,
      channel,
      grossSales: editSalesGrossRef.current?.value?.trim() || "",
      netSales: editSalesNetRef.current?.value?.trim() || "",
      orderCount: editSalesOrderCountRef.current?.value?.trim() || "",
      status: editSalesStatusRef.current?.value || "정상",
      memo: editSalesMemoRef.current?.value?.trim() || "",
    };

    try {
      const res = await fetch(`${SALES_API_BASE}/sales/${recordId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("API 수정 실패");
      const saved = await res.json();
      setSalesRecords((prev) => prev.map((record) => record.id === recordId ? saved : record));
      setEditingSalesId(null);
      setConfirmModal({ type: "notice", title: "수정 완료", message: "서버 DB의 매출 데이터가 수정되었습니다." });
      return;
    } catch (error) {
      setSalesRecords((prev) => prev.map((record) => record.id === recordId ? {
        ...record,
        ...payload,
        source: record.source === "server" ? "server+manual" : record.source || "manual",
        updatedAt: formatNow(),
      } : record));
      setEditingSalesId(null);
      setConfirmModal({ type: "notice", title: "로컬 수정 완료", message: "서버 연결에 실패해 현재 브라우저 화면에서만 수정했습니다. FastAPI 서버 실행 여부를 확인해주세요." });
    }
  };

  const handleDeleteSalesRecord = async (recordId) => {
    try {
      const res = await fetch(`${SALES_API_BASE}/sales/${recordId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("API 삭제 실패");
    } catch (error) {
      // 서버 미실행 상태에서도 화면 테스트는 가능하도록 로컬 삭제를 진행합니다.
    }
    setSalesRecords((prev) => prev.filter((record) => record.id !== recordId));
    if (editingSalesId === recordId) setEditingSalesId(null);
  };

  const buildMockSalesRecords = (dateText) => {
    const targetStores = stores.slice(0, 6);
    const channels = ["OKPOS", "OrderQueen"];
    const baseSeed = Number((dateText || "").replace(/[^0-9]/g, "").slice(-4)) || 528;
    const rows = [];
    targetStores.forEach((store, storeIndex) => {
      channels.forEach((channel, channelIndex) => {
        const seed = baseSeed + storeIndex * 137 + channelIndex * 71;
        const count = 12 + (seed % 36);
        const sales = (count * (18000 + (seed % 22000))) + (storeIndex * 10000);
        rows.push({
          id: Date.now() + rows.length,
          date: dateText,
          store,
          channel,
          grossSales: String(sales),
          netSales: String(sales),
          orderCount: String(count),
          source: "server",
          status: "재수집완료",
          collectedAt: formatNow(),
          updatedAt: "",
          memo: "서버 연계 전 화면 테스트용 재수집 데이터",
        });
      });
    });
    rows.push({
      id: Date.now() + rows.length,
      date: dateText,
      store: "온라인몰",
      channel: "Cafe24",
      grossSales: String(250000 + baseSeed * 300),
      netSales: String(250000 + baseSeed * 300),
      orderCount: String(8 + (baseSeed % 12)),
      source: "server",
      status: "재수집완료",
      collectedAt: formatNow(),
      updatedAt: "",
      memo: "서버 연계 전 화면 테스트용 온라인몰 데이터",
    });
    return rows;
  };

  const handleFetchSalesFromServer = async (options = {}) => {
    const silent = Boolean(options.silent);
    const targetStart = salesStartRef.current?.value || salesFilter.start || salesFilter.end || getYesterdayInputDate();
    const targetEnd = salesEndRef.current?.value || salesFilter.end || targetStart;
    const params = new URLSearchParams({ start: targetStart, end: targetEnd });
    const storeValue = salesStoreRef.current?.value || salesFilter.store;
    const channelValue = salesChannelRef.current?.value || salesFilter.channel;
    const statusValue = salesStatusRef.current?.value || salesFilter.status;
    const keywordValue = salesKeywordRef.current?.value || salesFilter.keyword;
    if (storeValue && storeValue !== "전체 매장") params.append("store", storeValue);
    if (channelValue && channelValue !== "전체") params.append("channel", channelValue);
    if (statusValue && statusValue !== "전체") params.append("status", statusValue);
    if (keywordValue) params.append("keyword", keywordValue);

    try {
      const res = await fetch(`${SALES_API_BASE}/sales?${params.toString()}`);
      if (!res.ok) throw new Error("API 조회 실패");
      const rows = await res.json();
      setSalesRecords(Array.isArray(rows) ? rows.map((row, index) => normalizeServerSalesRecord(row, index)) : []);
      if (!silent) {
        setConfirmModal({ type: "notice", title: "서버 데이터 조회 완료", message: `${targetStart} ~ ${targetEnd} 기준 매출 데이터 ${rows.length}건을 불러왔습니다.` });
      }
    } catch (error) {
      const nextRows = []; // 서버 실패 시 샘플 매출 생성 금지
      setSalesRecords((prev) => {
        const incomingKeys = new Set(nextRows.map(getSalesRecordKey));
        return [...nextRows, ...prev.filter((record) => !incomingKeys.has(getSalesRecordKey(record)))];
      });
      if (!silent) {
        setConfirmModal({ type: "notice", title: "서버 연결 실패", message: "FastAPI 서버에 연결하지 못했습니다. 화면 확인용 샘플 데이터를 대신 불러왔습니다. 백엔드 실행 후 다시 눌러주세요." });
      }
    }
  };

  useEffect(() => {
    if (!isLoggedIn || userRole !== "admin" || adminMenu !== "매출조회") return;
    const timer = setTimeout(() => {
      handleFetchSalesFromServer({ silent: true });
    }, 0);
    return () => clearTimeout(timer);
  }, [isLoggedIn, userRole, adminMenu]);

  const handleRecollectSales = async () => {
    if (isRecollectingSales) return;
    setIsRecollectingSales(true);
    const targetDate = salesEndRef.current?.value || salesFilter.end || salesStartRef.current?.value || salesFilter.start || getYesterdayInputDate();
    try {
      const res = await fetch(`${SALES_API_BASE}/sales/recollect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: targetDate }),
      });
      if (!res.ok) throw new Error("재수집 API 실패");
      const data = await res.json();
      if (Array.isArray(data.records) && data.records.length > 0) {
        setSalesRecords(data.records.map((row, index) => normalizeServerSalesRecord(row, index)));
      }
      setIsRecollectingSales(false);
      setConfirmModal({ type: "notice", title: "재수집 요청 완료", message: data.message || `${targetDate} 매출 재수집을 완료했습니다.` });
      return;
    } catch (error) {
      setIsRecollectingSales(false);
      setConfirmModal({ type: "notice", title: "재수집 실패", message: "서버 재수집 API에 연결하지 못했습니다. 운영 매출값은 변경하지 않았습니다." });
    }
  };

  const waitForSalesRecheck = (milliseconds) => new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });

  const pollSalesRecheckStatus = async (targetDate) => {
    for (let attempt = 0; attempt < 72; attempt += 1) {
      const res = await fetch(`${SALES_API_BASE}/sales/recollect/status?date=${encodeURIComponent(targetDate)}&_=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) throw new Error(`재검수 상태 조회 실패 (${res.status})`);
      const data = await res.json();
      if (data.status === "completed" || data.status === "failed") return data;
      await waitForSalesRecheck(5000);
    }
    throw new Error("전체 재검수 결과 대기 시간이 초과되었습니다.");
  };

  const handleRecheckYesterdaySales = async () => {
    if (isFullRecheckingSales || isRecollectingSales) return;
    const targetDate = getYesterdayInputDate();
    setIsFullRecheckingSales(true);
    setSalesRecheckSummary(null);
    try {
      const res = await fetch(`${SALES_API_BASE}/sales/recollect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: targetDate, full: true }),
      });
      const startData = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(startData?.detail || `전체 재검수 요청 실패 (${res.status})`);
      if (startData.alreadyRunning) {
        throw new Error(startData.message || "다른 매출 재수집이 이미 진행 중입니다.");
      }
      const result = await pollSalesRecheckStatus(targetDate);
      if (result.status !== "completed") {
        throw new Error(result.message || result.error || "전체 재검수 실패");
      }
      const summary = result.summary || {};
      setSalesRecheckSummary(summary);
      if (salesStartRef.current) salesStartRef.current.value = targetDate;
      if (salesEndRef.current) salesEndRef.current.value = targetDate;
      setSalesFilter((prev) => ({ ...prev, start: targetDate, end: targetDate }));
      await handleFetchSalesFromServer({ silent: true });
      setConfirmModal({
        type: "notice",
        title: "어제 전체 재검수 완료",
        message: [
          `대상 ${summary.targetStoreCount || 0}개 매장`,
          `OKPOS 성공 ${summary.okposSuccess || 0} / 실패 ${summary.okposFailed || 0}`,
          `OrderQueen 성공 ${summary.orderqueenSuccess || 0} / 실패 ${summary.orderqueenFailed || 0}`,
          `신규 ${summary.created || 0} · 갱신 ${summary.updated || 0} · 유지 ${summary.maintained || 0}`,
          `수동 보호 ${summary.manualProtected || 0} · 확인 필요 ${summary.needsCheck || 0}`,
        ].join("\n"),
      });
    } catch (error) {
      setConfirmModal({
        type: "notice",
        title: "어제 전체 재검수 실패",
        message: `${error?.message || "재검수 중 오류가 발생했습니다."} 기존 정상/수동 매출은 변경하지 않았습니다.`,
      });
    } finally {
      setIsFullRecheckingSales(false);
    }
  };
  const downloadSalesTemplate = () => {
    downloadCsv("매출조회_업로드양식.csv", [
      { 날짜: "2026-05-28", 매장명: "강남점", 채널: "OKPOS", 총매출: "1000000", 실매출: "1000000", 건수: "30", 수집상태: "정상", 메모: "예시 데이터" },
    ]);
  };

  const downloadFilteredSalesList = () => {
    const rows = filteredSalesRecords.map((record) => ({
      날짜: record.date,
      매장명: record.store,
      채널: record.channel,
      총매출: record.grossSales || "",
      실매출: record.netSales || "",
      건수: record.orderCount || "",
      객단가: getSalesAvgTicket(record) || "",
      출처: record.source || "",
      수집상태: record.status || "",
      수집일시: record.collectedAt || "",
      수정일시: record.updatedAt || "",
      메모: record.memo || "",
    }));
    downloadCsv("매출조회_검색결과.csv", rows);
  };

  const handleImportSalesCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setConfirmModal({ type: "notice", title: "업로드 불가", message: "현재 버전에서는 CSV 파일만 업로드할 수 있습니다. 양식을 다운로드한 뒤 CSV UTF-8 형식으로 저장해주세요." });
      if (salesImportFileRef.current) salesImportFileRef.current.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result;
      const decodeText = (encoding) => {
        try {
          return new TextDecoder(encoding).decode(buffer).replace(/^﻿/, "");
        } catch (error) {
          return "";
        }
      };
      const requiredHeaders = ["날짜", "매장명", "채널", "총매출", "실매출", "건수", "수집상태", "메모"];
      const decodedCandidates = [decodeText("utf-8"), decodeText("euc-kr")].filter(Boolean);
      let csvRows = [];
      let headers = [];

      for (const csvText of decodedCandidates) {
        const rows = parseCsvText(csvText);
        const candidateHeaders = rows[0]?.map((header) => header.replace(/^﻿/, "").trim()) || [];
        const hasRequiredHeader = requiredHeaders.some((header) => candidateHeaders.includes(header));
        if (hasRequiredHeader) {
          csvRows = rows;
          headers = candidateHeaders;
          break;
        }
      }

      if (csvRows.length < 2) {
        setConfirmModal({ type: "notice", title: "업로드 불가", message: "등록할 매출 데이터가 없거나 CSV 인코딩을 읽을 수 없습니다." });
        if (salesImportFileRef.current) salesImportFileRef.current.value = "";
        return;
      }

      const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
      if (missingHeaders.length > 0) {
        setConfirmModal({ type: "notice", title: "업로드 양식 오류", message: `필요한 열이 없습니다: ${missingHeaders.join(", ")}` });
        if (salesImportFileRef.current) salesImportFileRef.current.value = "";
        return;
      }

      const getCell = (row, headerName) => headers.includes(headerName) ? (row[headers.indexOf(headerName)]?.trim() || "") : "";
      const incoming = [];
      const invalidRows = [];

      csvRows.slice(1).forEach((row, index) => {
        const date = getCell(row, "날짜");
        const store = getCell(row, "매장명");
        const channel = getCell(row, "채널") || "기타";
        if (!date || !store || !channel) {
          invalidRows.push(`${index + 2}행`);
          return;
        }
        incoming.push({
          id: Date.now() + index,
          date,
          store,
          channel,
          grossSales: getCell(row, "총매출"),
          netSales: getCell(row, "실매출") || getCell(row, "총매출"),
          orderCount: getCell(row, "건수"),
          source: "csv",
          status: getCell(row, "수집상태") || "정상",
          collectedAt: "",
          updatedAt: formatNow(),
          memo: getCell(row, "메모"),
        });
      });

      setSalesRecords((prev) => {
        const incomingKeys = new Set(incoming.map(getSalesRecordKey));
        return [...incoming, ...prev.filter((record) => !incomingKeys.has(getSalesRecordKey(record)))];
      });
      setConfirmModal({ type: "notice", title: "업로드 처리 완료", message: `${incoming.length}건이 등록되었습니다.${invalidRows.length ? ` / 제외: ${invalidRows.join(", ")}` : ""}` });
      if (salesImportFileRef.current) salesImportFileRef.current.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  const SectionTitle = ({ title, desc }) => (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ margin: 0, fontSize: 26 }}>{title}</h2>
      {desc && <div style={{ fontSize: 14, color: "#6b7280", marginTop: 6 }}>{desc}</div>}
    </div>
  );

  const StatusBadge = ({ children, tone = "red" }) => (
    <span style={{ background: tone === "blue" ? "#dbeafe" : tone === "orange" ? "#ffedd5" : tone === "yellow" ? "#fef3c7" : "#fee2e2", color: tone === "blue" ? "#1d4ed8" : tone === "orange" ? "#c2410c" : tone === "yellow" ? "#92400e" : "#b91c1c", padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: "bold" }}>{children}</span>
  );

  const AttachmentList = ({ files = [], title = "첨부파일" }) => {
    if (!files || files.length === 0) return null;
    return (
      <div style={{ marginTop: 12, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, fontSize: 13 }}>
        <b>{title}</b>
        <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
          {files.map((file) => {
            const fileUrl = getFileOpenUrl(file);
            const canOpen = Boolean(fileUrl);
            const imageFile = isImageFile(file);
            const fileLabel = `${file.name} (${formatFileSize(file.size)})`;
            return (
              <div key={file.id || file.name} style={{ display: "grid", gridTemplateColumns: imageFile && fileUrl ? "88px 1fr" : "1fr", gap: 10, alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                {imageFile && fileUrl && (
                  <button type="button" onClick={() => openAttachmentFile(file)} title="이미지 크게 보기" style={{ padding: 0, border: "none", background: "transparent", cursor: "pointer" }}>
                    <img src={fileUrl} alt={file.name} style={{ width: 88, height: 88, objectFit: "cover", borderRadius: 10, display: "block", border: "1px solid #e5e7eb" }} />
                  </button>
                )}
                {canOpen ? (
                  <button type="button" onClick={() => openAttachmentFile(file)} style={{ color: "#2563eb", fontWeight: "bold", wordBreak: "break-all", overflowWrap: "anywhere", textDecoration: "underline", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }}>
                    {fileLabel}
                  </button>
                ) : (
                  <span style={{ color: "#374151", wordBreak: "break-all", overflowWrap: "anywhere" }}>{fileLabel}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const ConfirmModal = () => {
    if (!confirmModal) return null;

    if (confirmModal.type === "attendanceStartPrompt") {
      return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 24, padding: 28, width: "100%", maxWidth: 460, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}>
            <h2 style={{ marginTop: 0 }}>확인이 필요한 항목이 있습니다.</h2>
            <div style={{ color: "#374151", lineHeight: 1.8, fontSize: 15 }}>
              <div>미확인 공지사항: <b>{confirmModal.noticeCount || 0}건</b></div>
              <div>미확인 업무인수인계: <b>{confirmModal.handoverCount || 0}건</b></div>
              <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: "bold" }}>업무 시작 전 반드시 확인해 주세요.</div>
            </div>
            <div style={{ display: "grid", gap: 10, marginTop: 24 }}>
              <button style={{ ...redButton, width: "100%" }} onClick={openAttendancePromptNotices}>공지사항 확인하기</button>
              <button style={{ ...orangeButton, width: "100%" }} onClick={openAttendancePromptHandovers}>업무인수인계 확인하기</button>
              <button style={{ ...grayButton, width: "100%" }} onClick={() => setConfirmModal(null)}>나중에 보기</button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 24, padding: 28, width: "100%", maxWidth: 430, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}>
          <h2 style={{ marginTop: 0 }}>{confirmModal.title}</h2>
          <p style={{ color: "#374151", lineHeight: 1.7 }}>{confirmModal.message}</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
            {confirmModal.type !== "notice" && (
              <button style={grayButton} onClick={() => setConfirmModal(null)}>취소</button>
            )}
            <button style={redButton} onClick={handleConfirmModal}>
              {confirmModal.type === "notice" ? "확인" : "확정"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const LoginPage = () => (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#111827,#000)", display: "flex", justifyContent: "center", alignItems: "center", padding: isMobile ? 14 : 30 }}>
      <ConfirmModal />
      <div style={{ width: "100%", maxWidth: 1000, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr", background: "#fff", borderRadius: isMobile ? 24 : 32, overflow: "hidden", boxShadow: "0 20px 40px rgba(0,0,0,0.35)" }}>
        <div style={{ background: "linear-gradient(135deg,#7f1d1d,#dc2626)", color: "#fff", padding: isMobile ? 28 : 60 }}>
          <div style={{ fontSize: 13, letterSpacing: 1, opacity: 0.85 }}>RED CONTAINER STAFF SYSTEM</div>
          <h1 style={{ fontSize: isMobile ? 30 : 46, lineHeight: 1.18, margin: "14px 0 12px" }}>레드컨테이너<br />직원 운영 시스템</h1>
          <p style={{ fontSize: 14, lineHeight: 1.7, opacity: 0.92, marginBottom: 0 }}>공지사항 · 인수인계 · 이슈보고 · 재고이동 · 소모품 주문 · 근무관리를 하나로 통합합니다.</p>
        </div>
        <div style={{ padding: isMobile ? 26 : "60px 50px", background: "#f9fafb", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <h2 style={{ fontSize: isMobile ? 28 : 32, margin: 0 }}>로그인</h2>
          <input
            ref={loginIdRef}
            placeholder="아이디"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            onKeyDown={handleLoginKeyDown}
            style={{ ...input, width: "100%", marginTop: 24, marginBottom: 12 }}
          />
          <input
            ref={loginPwRef}
            type="password"
            placeholder="비밀번호"
            autoComplete="current-password"
            onKeyDown={handleLoginKeyDown}
            style={{ ...input, width: "100%", marginBottom: 12 }}
          />
          <button type="button" style={{ ...redButton, width: "100%", padding: 16 }} onClick={handleLogin}>로그인</button>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginTop: 14 }}>
            <button style={{ ...darkButton, width: "100%" }} onClick={() => window.open("https://cosmostoy.co.kr", "_blank")}>코스모스 쇼핑몰</button>
            <button style={{ ...darkButton, width: "100%" }} onClick={() => window.open("https://redctn.co.kr", "_blank")}>레드컨테이너 쇼핑몰</button>
          </div>
          <a
            href="/manual/staff/index.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...darkButton, display: "block", width: "100%", boxSizing: "border-box", marginTop: 10, padding: 14, textAlign: "center", textDecoration: "none", background: "#374151" }}
          >
            직원 사용 매뉴얼
          </a>
        </div>
      </div>
    </div>
  );

  const normalizeNoticeCategory = (category) => category === "업무요청" ? "업무 요청" : (category || "전달사항");
  const isNoticeCategory = (notice, category) => normalizeNoticeCategory(notice.category) === normalizeNoticeCategory(category);

  const NoticePage = () => {
    const eventNotices = getVisibleCompanyNotices("이벤트");
    const infoNotices = getVisibleCompanyNotices("전달사항");
    const taskNotices = getVisibleCompanyNotices("업무 요청");

    const smallNoticeButton = {
      ...darkButton,
      padding: "8px 10px",
      fontSize: 12,
      borderRadius: 10,
      whiteSpace: "nowrap",
    };
    const staffNoticeSummaryCards = [
      { key: "전체", label: "전체", count: eventNotices.length + infoNotices.length + taskNotices.length, background: "#f9fafb", border: "#e5e7eb", color: "#111827" },
      { key: "이벤트", label: "진행중 이벤트", count: eventNotices.filter(isEventOngoing).length, background: "#fff7ed", border: "#fed7aa", color: "#c2410c" },
      { key: "전달사항", label: "미확인 전달사항", count: getUncheckedNoticeCount("전달사항"), background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
      { key: "업무 요청", label: "미처리 업무 요청", count: getIncompleteTaskCount(), background: "#fff5f5", border: "#fecaca", color: "#b91c1c" },
    ];
    const getStaffNoticeSummaryStyle = (item) => ({
      ...clickableCardStyle,
      background: item.background,
      border: normalizeNoticeCategory(staffNoticeCategoryFilter) === normalizeNoticeCategory(item.key) ? "2px solid #dc2626" : `1px solid ${item.border}`,
      borderRadius: 16,
      padding: 14,
    });
    const showStaffNoticeSection = (category) => staffNoticeCategoryFilter === "전체" || normalizeNoticeCategory(staffNoticeCategoryFilter) === normalizeNoticeCategory(category);

    const NoticeDetail = ({ notice }) => {
      const checked = isNoticeCheckedByMe(notice);
      const isTask = isNoticeCategory(notice, "업무 요청");
      const taskFiles = noticeTaskFiles[notice.id] || [];
      const noticeSaving = isNoticeActionSaving(notice.id);

      return (
        <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
          <div style={{ lineHeight: 1.7, fontSize: 14 }}>{notice.content}</div>

          <AttachmentList files={notice.attachments || []} title="첨부파일" />

          {notice.products?.length > 0 && (
            <div style={{ marginTop: 12, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: 10, fontSize: 13 }}>
              <b>관련 상품</b>
              {notice.products.map((product) => (
                <div key={product.code} style={{ marginTop: 6, wordBreak: "break-all", overflowWrap: "anywhere" }}>
                  - {product.name} ({product.code})
                </div>
              ))}
            </div>
          )}

          {!isTask && (
            <button
              style={{ ...(checked || noticeSaving ? grayButton : redButton), width: "100%", marginTop: 12 }}
              onClick={() => handleCheckCompanyNotice(notice.id)}
              disabled={checked || noticeSaving}
            >
              {noticeSaving ? "저장중입니다..." : checked ? "확인완료" : "확인"}
            </button>
          )}

          {isTask && (
            <div style={{ marginTop: 12 }}>
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, fontSize: 13, lineHeight: 1.7 }}>
                <b>업무 처리 현황</b><br />
                상태: {notice.taskStatus || "미확인"}<br />
                확인자: {notice.taskCheckedBy || "-"}{notice.taskCheckedAt ? ` · ${notice.taskCheckedAt}` : ""}<br />
                처리자: {notice.taskCompletedBy || "-"}{notice.taskCompletedAt ? ` · ${notice.taskCompletedAt}` : ""}
                {notice.taskMemo && <><br />처리 메모: {notice.taskMemo}</>}
              </div>

              {notice.taskStatus !== "처리완료" && (
                <>
                  <button
                    style={{ ...(noticeSaving ? grayButton : orangeButton), width: "100%", marginTop: 10 }}
                    onClick={() => handleStartCompanyTask(notice.id)}
                    disabled={noticeSaving}
                  >
                    {noticeSaving ? "처리중입니다..." : "확인중으로 변경"}
                  </button>
                  <textarea
                    value={noticeTaskMemos[notice.id] || ""}
                    onChange={(e) => setNoticeTaskMemos((prev) => ({ ...prev, [notice.id]: e.target.value }))}
                    placeholder="처리 메모를 입력하세요. 예: POP 교체 완료, 진열대 정리 완료"
                    style={{ ...input, width: "100%", minHeight: 80, marginTop: 10 }}
                  />
                  <input
                    ref={(el) => { noticeTaskFileRefs.current[notice.id] = el; }}
                    type="file"
                    multiple
                    onChange={(e) => handleNoticeTaskFileChange(notice.id, e)}
                    style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }}
                  />
                  {taskFiles.length > 0 && (
                    <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                      {taskFiles.map((file) => (
                        <div key={file.id} style={{ display: "grid", gridTemplateColumns: file.isImage ? "64px 1fr" : "1fr", gap: 8, alignItems: "center", background: "#f9fafb", borderRadius: 12, padding: 8 }}>
                          {isImageFile(file) && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 10 }} />}
                          <span style={{ fontSize: 13, wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} ({formatFileSize(file.size)})</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    style={{ ...(noticeSaving ? grayButton : redButton), width: "100%", marginTop: 10 }}
                    onClick={() => handleCompleteCompanyTask(notice.id)}
                    disabled={noticeSaving}
                  >
                    {noticeSaving ? "저장중입니다..." : "처리완료"}
                  </button>
                </>
              )}

              {notice.taskFiles?.length > 0 && (
                <div style={{ marginTop: 10, background: "#f9fafb", borderRadius: 12, padding: 10, fontSize: 13 }}>
                  <b>완료 첨부파일</b>
                  {notice.taskFiles.map((file) => (
                    <div key={file.id || file.name} style={{ marginTop: 6, wordBreak: "break-all", overflowWrap: "anywhere" }}>
                      {isImageFile(file) && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 10, display: "block", marginBottom: 4 }} />}
                      {getFileOpenUrl(file) ? (
                        <button type="button" onClick={() => openAttachmentFile(file)} style={{ color: "#2563eb", fontWeight: "bold", wordBreak: "break-all", overflowWrap: "anywhere", textDecoration: "underline", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }}>
                          - {file.name} ({formatFileSize(file.size)})
                        </button>
                      ) : (
                        <>- {file.name} ({formatFileSize(file.size)})</>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      );
    };

    const NoticeCard = ({ notice }) => {
      const open = openNoticeId === notice.id;
      const checked = isNoticeCheckedByMe(notice);
      const isTask = isNoticeCategory(notice, "업무 요청");
      const eventOngoing = isEventOngoing(notice);
      const noticeCategory = normalizeNoticeCategory(notice.category);
      const badgeText = noticeCategory === "이벤트" ? (eventOngoing ? "진행중" : "종료") : isTask ? (notice.taskStatus || "미확인") : (checked ? "확인완료" : "미확인");
      const badgeTone = noticeCategory === "이벤트" ? (eventOngoing ? "yellow" : "blue") : isTask ? (notice.taskStatus === "처리완료" ? "blue" : notice.taskStatus === "확인중" ? "yellow" : "red") : (checked ? "blue" : "red");

      return (
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderLeft: noticeCategory === "업무 요청" ? "4px solid #f97316" : noticeCategory === "이벤트" ? "4px solid #dc2626" : "4px solid #2563eb", borderRadius: 16, padding: 14, marginBottom: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "start" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: "bold", lineHeight: 1.4, wordBreak: "keep-all" }}>{notice.title}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, lineHeight: 1.5 }}>
                {notice.target} · {noticeDateLabel(notice)}
              </div>
              <div style={{ marginTop: 8 }}><StatusBadge tone={badgeTone}>{badgeText}</StatusBadge></div>
            </div>
            <button style={smallNoticeButton} onClick={() => openNoticeDetail(notice.id, "staff")}>
              {open ? "접기" : "내용보기"}
            </button>
          </div>
          {open && <NoticeDetail notice={notice} />}
        </div>
      );
    };

    const NoticeSection = ({ title, countText, children }) => (
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 21 }}>{title}</h3>
          <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 999, padding: "6px 10px", fontSize: 12, fontWeight: "bold" }}>{countText}</span>
        </div>
        {children}
      </div>
    );

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="공지사항" desc={isWorking ? `${activeStore} 근무 기준으로 확인 가능한 공지입니다.` : "근무 시작 전에는 전체 매장 공지만 볼 수 있고, 확인 처리는 근무 시작 후 가능합니다."} />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 22 }}>
          {staffNoticeSummaryCards.map((item) => (
            <button key={item.key} type="button" style={getStaffNoticeSummaryStyle(item)} onClick={() => { setStaffNoticeCategoryFilter(item.key); setOpenNoticeId(null); }} onMouseEnter={handleClickableCardMouseEnter} onMouseLeave={handleClickableCardMouseLeave}>
              <b>{item.label}</b><br /><span style={{ fontSize: 24, fontWeight: "bold", color: item.color }}>{item.count}건</span>
            </button>
          ))}
        </div>

        {showStaffNoticeSection("이벤트") && (
          <NoticeSection title="이벤트" countText={`미확인 ${getUncheckedNoticeCount("이벤트")}건`}>
            {eventNotices.length === 0 ? <div style={{ color: "#6b7280", background: "#f9fafb", borderRadius: 14, padding: 16 }}>등록된 이벤트가 없습니다.</div> : eventNotices.map((notice) => <NoticeCard key={notice.id} notice={notice} />)}
          </NoticeSection>
        )}

        {showStaffNoticeSection("전달사항") && (
          <NoticeSection title="전달사항" countText={`미확인 ${getUncheckedNoticeCount("전달사항")}건`}>
            {infoNotices.length === 0 ? <div style={{ color: "#6b7280", background: "#f9fafb", borderRadius: 14, padding: 16 }}>등록된 전달사항이 없습니다.</div> : infoNotices.map((notice) => <NoticeCard key={notice.id} notice={notice} />)}
          </NoticeSection>
        )}

        {showStaffNoticeSection("업무 요청") && (
          <NoticeSection title="업무 요청" countText={`미처리 ${getIncompleteTaskCount()}건`}>
            {taskNotices.length === 0 ? <div style={{ color: "#6b7280", background: "#f9fafb", borderRadius: 14, padding: 16 }}>등록된 업무 요청이 없습니다.</div> : taskNotices.map((notice) => <NoticeCard key={notice.id} notice={notice} />)}
          </NoticeSection>
        )}
      </div>
    );
  };

  const noticeDateLabel = (notice) => {
    if (isNoticeCategory(notice, "이벤트")) return `${notice.startDate || "-"} ~ ${notice.endDate || "-"}`;
    if (notice.endDate) return `${notice.startDate || "-"} ~ ${notice.endDate}`;
    return notice.startDate || "상시";
  };

  const getNoticeCompareDate = (value) => String(value || "").trim().slice(0, 10).replace(/[./]/g, "-");
  const getTodayNoticeCompareDate = () => formatDateOnly().replace(/\./g, "-");
  const isNoticeActiveForToday = (notice) => {
    const today = getTodayNoticeCompareDate();
    const startDate = getNoticeCompareDate(notice?.startDate);
    const endDate = getNoticeCompareDate(notice?.endDate);
    return (!startDate || startDate <= today) && (!endDate || endDate >= today);
  };
  const isAdminNoticeCurrentlyVisible = (notice) => {
    if (!notice) return false;
    if (notice.hidden) return false;
    if (notice.disabled || notice.active === false || notice.isActive === false || String(notice.status || "").trim() === "비활성화") return false;
    return isNoticeActiveForToday(notice);
  };
  const getAdminVisibleNotices = () => companyNotices.filter(isAdminNoticeCurrentlyVisible);
  const getStaffNoticeTargetResult = (notice, { storeName = activeStore, forceWorking = isWorking } = {}) => {
    const targetValues = [notice?.target, notice?.store, notice?.targetStore]
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    const targetStore = String(storeName || "").trim();
    if (targetValues.some((value) => value === "전체 매장" || value === "전체")) {
      return { ok: true, reason: "전체 매장 대상" };
    }
    if (!forceWorking) return { ok: false, reason: "근무 시작 전 매장 대상 공지 제외" };
    if (!targetStore) return { ok: false, reason: "현재 근무 매장 없음" };
    if (targetValues.some((value) => value === targetStore)) return { ok: true, reason: `${targetStore} 대상` };
    return { ok: false, reason: `현재 근무 매장(${targetStore}) 대상 아님` };
  };
  const getStaffVisibleNoticeDecision = (notice, category, options = {}) => {
    const includeReasons = [];
    const excludeReasons = [];
    if (category && !isNoticeCategory(notice, category)) excludeReasons.push(`카테고리 불일치(${normalizeNoticeCategory(notice?.category)})`);
    else if (category) includeReasons.push(`${normalizeNoticeCategory(category)} 카테고리`);

    if (notice?.hidden) excludeReasons.push("숨김 처리됨");
    else includeReasons.push("숨김 아님");

    if (notice?.disabled || notice?.active === false || notice?.isActive === false || String(notice?.status || "").trim() === "비활성화") {
      excludeReasons.push("비활성 공지");
    } else {
      includeReasons.push("활성 공지");
    }

    if (isNoticeActiveForToday(notice)) includeReasons.push("노출기간 안");
    else excludeReasons.push("노출기간 밖");

    const targetResult = getStaffNoticeTargetResult(notice, options);
    if (targetResult.ok) includeReasons.push(targetResult.reason);
    else excludeReasons.push(targetResult.reason);

    return {
      include: excludeReasons.length === 0,
      includeReasons,
      excludeReasons,
    };
  };

  const isEventOngoing = (notice) => {
    if (!isNoticeCategory(notice, "이벤트")) return false;
    return isNoticeActiveForToday(notice);
  };

  const isNoticeCheckedByMe = (notice) => {
    return isCheckedByCurrentStaff(notice?.checkedBy);
  };

  const isAllStoreNotice = (notice) => {
    const targetValues = [notice?.store, notice?.target, notice?.targetStore]
      .map((value) => String(value || "").trim());
    return targetValues.some((value) => value === "전체 매장" || value === "전체");
  };

  const getNoticeActiveStaffEmployees = () => employees.filter((employee) => {
    const status = String(employee?.status ?? "활성화").trim();
    const role = String(employee?.role || "").trim().toLowerCase();
    if (status === "비활성화" || employee?.disabled || employee?.isActive === false || employee?.active === false) return false;
    if (role === "관리자" || role === "admin" || role === "manager") return false;
    return Boolean(employee?.name || employee?.id);
  });

  const getNoticeEmployeeIdentityKeys = (employee) => [
    employee?.id,
    employee?.employeeId,
    employee?.userId,
    employee?.name,
    employee?.userName,
  ].map(normalizeNoticeIdentityKey).filter(Boolean);

  const getNoticeCheckedIdentityKeys = (notice) => {
    return getNoticeCheckedIdentitySet(notice?.checkedBy);
  };

  const getAdminNoticeConfirmationSummary = (notice) => {
    const checkedBy = Array.isArray(notice?.checkedBy) ? notice.checkedBy : [];
    const seenCheckedKeys = new Set();
    const checkedNames = checkedBy.reduce((names, entry) => {
      const identityKeys = getNoticeIdentityKeysFromEntry(entry);
      const dedupeKey = identityKeys[0] || normalizeNoticeIdentityKey(entry);
      if (dedupeKey && seenCheckedKeys.has(dedupeKey)) return names;
      if (dedupeKey) seenCheckedKeys.add(dedupeKey);
      const displayName = entry && typeof entry === "object" ? entry.name || entry.userName || entry.id || entry.userId || "" : entry;
      const trimmedName = String(displayName || "").trim();
      if (trimmedName) names.push(trimmedName);
      return names;
    }, []);

    if (!isAllStoreNotice(notice)) {
      return { isAllStore: false, checkedCount: checkedNames.length, checkedNames, uncheckedEmployees: [] };
    }

    const checkedIdentityKeys = getNoticeCheckedIdentityKeys(notice);
    const activeStaffEmployees = getNoticeActiveStaffEmployees();
    const uncheckedEmployees = activeStaffEmployees.filter((employee) =>
      !getNoticeEmployeeIdentityKeys(employee).some((key) => checkedIdentityKeys.has(key))
    );

    return {
      isAllStore: true,
      checkedCount: checkedNames.length,
      checkedNames,
      uncheckedEmployees,
    };
  };

  const getVisibleCompanyNotices = (category) => {
    return companyNotices.filter((notice) => getStaffVisibleNoticeDecision(notice, category).include);
  };

  const getUncheckedVisibleStaffNoticeItems = ({ categories = ["이벤트", "전달사항"], storeName = activeStore, forceWorking = isWorking, debugContext = "공지 미확인 카운트" } = {}) => {
    const normalizedCategories = categories.map(normalizeNoticeCategory);
    return companyNotices.filter((notice) => {
      const matchedCategory = normalizedCategories.find((category) => isNoticeCategory(notice, category));
      const decision = matchedCategory
        ? getStaffVisibleNoticeDecision(notice, matchedCategory, { storeName, forceWorking })
        : { include: false, includeReasons: [], excludeReasons: [`카테고리 제외(${normalizeNoticeCategory(notice?.category)})`] };
      const checked = isCheckedByCurrentStaff(notice?.checkedBy);
      const include = decision.include && !checked;
      const includeReasons = include ? [...decision.includeReasons, "미확인"] : decision.includeReasons;
      const excludeReasons = checked ? [...decision.excludeReasons, "이미 확인 완료"] : decision.excludeReasons;
      console.log("[공지 미확인 카운트]", {
        context: debugContext,
        noticeId: notice?.id,
        title: notice?.title,
        included: include,
        includeReasons,
        excludeReasons,
      });
      return include;
    });
  };

  const getUncheckedVisibleStaffNoticeCount = (options = {}) => {
    return getUncheckedVisibleStaffNoticeItems(options).length;
  };

  const getUncheckedNoticeCount = (category) => {
    return getUncheckedVisibleStaffNoticeCount({
      categories: [category],
      debugContext: `공지 페이지 ${normalizeNoticeCategory(category)} 미확인`,
    });
  };

  const getIncompleteTaskCount = () => {
    return getVisibleCompanyNotices("업무 요청").filter((notice) => notice.taskStatus !== "처리완료").length;
  };

  const canHandleStaffNoticeAction = () => {
    const currentRecord = getOpenAttendanceRecordForUser(attendance, currentUser);
    const canAction = isWorking && currentRecord && currentRecord.status === "근무중" && currentRecord.store === activeStore;

    if (!canAction) {
      setConfirmModal({
        type: "notice",
        title: "근무 시작 필요",
        message: "공지 확인과 업무 처리는 근무 시작 후에만 가능합니다. 먼저 대시보드에서 오늘 근무 매장을 선택하고 근무 시작을 진행해주세요.",
      });
      return false;
    }
    return true;
  };

  const setNoticeActionSaving = (noticeId, saving) => {
    if (saving) noticeActionSavingRefs.current[noticeId] = true;
    else delete noticeActionSavingRefs.current[noticeId];
    setNoticeSavingIds((prev) => {
      const next = { ...prev };
      if (saving) next[noticeId] = true;
      else delete next[noticeId];
      return next;
    });
  };

  const isNoticeActionSaving = (noticeId) => Boolean(noticeActionSavingRefs.current[noticeId] || noticeSavingIds[noticeId]);

  const mutateCompanyNoticeFromLatest = async (noticeId, mutateNotice, options = {}) => {
    const logStep = typeof options.logStep === "function" ? options.logStep : () => {};
    try {
      const latestNotices = await fetchAppDataItemFull("rc_company_notices");
      if (!Array.isArray(latestNotices)) throw new Error("rc_company_notices 최신값이 배열이 아닙니다.");

      let mutated = false;
      const nextNotices = latestNotices.map((notice) => {
        if (notice.id !== noticeId) return notice;
        mutated = true;
        return mutateNotice(notice);
      });

      if (!mutated) throw new Error(`공지 id를 찾지 못했습니다: ${noticeId}`);

      logStep("payload prepare", { count: nextNotices.length });
      const saved = await saveAppDataListPreservingDetails("rc_company_notices", nextNotices);
      logStep("server save", { saved });
      if (saved) {
        setCompanyNotices((prev) => mergeAppDataListPreservingDetails("rc_company_notices", prev, nextNotices));
      }
      logStep("reload/apply", { applied: saved });
      return saved;
    } catch (error) {
      console.error("공지 최신값 기준 저장 실패:", { noticeId, errorMessage: error?.message || String(error) }, error);
      return false;
    }
  };

  const handleCheckCompanyNotice = async (noticeId) => {
    if (!canHandleStaffNoticeAction()) return;
    if (isNoticeActionSaving(noticeId)) return;

    const timing = createSaveTimingLogger("공지 저장 시간");
    setNoticeActionSaving(noticeId, true);
    let saved = false;
    try {
      saved = await mutateCompanyNoticeFromLatest(noticeId, (notice) => ({
        ...notice,
        checkedBy: addNoticeCheckedEntry(notice),
      }), { logStep: timing.step });
    } finally {
      setNoticeActionSaving(noticeId, false);
      timing.total({ action: "staff notice confirm", saved });
    }

    setConfirmModal(saved
      ? { type: "notice", title: "확인 완료", message: "공지 확인 상태가 서버에 저장되었습니다." }
      : { type: "notice", title: "저장 실패", message: "공지 확인 상태를 서버에 저장하지 못했습니다. 잠시 후 다시 시도해주세요." });
  };

  const handleStartCompanyTask = async (noticeId) => {
    if (!canHandleStaffNoticeAction()) return;
    if (isNoticeActionSaving(noticeId)) return;

    setNoticeActionSaving(noticeId, true);
    let saved = false;
    try {
      saved = await mutateCompanyNoticeFromLatest(noticeId, (notice) => ({
      ...notice,
      checkedBy: addNoticeCheckedEntry(notice),
      taskStatus: notice.taskStatus === "처리완료" ? "처리완료" : "확인중",
      taskCheckedBy: notice.taskCheckedBy || currentUser,
      taskCheckedAt: notice.taskCheckedAt || formatNow(),
    }));
    } finally {
      setNoticeActionSaving(noticeId, false);
    }

    if (!saved) {
      setConfirmModal({ type: "notice", title: "저장 실패", message: "업무 요청 공지 확인 상태를 저장하지 못했습니다. 다시 시도해주세요." });
    }
  };

  const handleNoticeTaskFileChange = async (noticeId, e) => {
    const files = await makePersistentAttachments(e.target.files);
    setNoticeTaskFiles((prev) => ({ ...prev, [noticeId]: files }));
  };

  const handleAdminNoticeFileChange = async (e) => {
    const files = await makePersistentAttachments(e.target.files);
    setAdminNoticeAttachments((prev) => {
      const merged = [...prev];
      files.forEach((file) => {
        if (!merged.some((item) => item.id === file.id)) merged.push(file);
      });
      return merged;
    });
    if (adminNoticeFileRef.current) adminNoticeFileRef.current.value = "";
  };

  const handleRemoveAdminNoticeFile = (fileId) => {
    setAdminNoticeAttachments((prev) => prev.filter((file) => file.id !== fileId));
    if (adminNoticeFileRef.current) adminNoticeFileRef.current.value = "";
  };

  const resetAdminNoticeForm = () => {
    if (adminNoticeTitleRef.current) adminNoticeTitleRef.current.value = "";
    if (adminNoticeContentRef.current) adminNoticeContentRef.current.value = "";
    if (adminNoticeCategoryRef.current) adminNoticeCategoryRef.current.value = "이벤트";
    if (adminNoticeTargetRef.current) adminNoticeTargetRef.current.value = "전체 매장";
    if (adminNoticeStartRef.current) adminNoticeStartRef.current.value = "";
    if (adminNoticeEndRef.current) adminNoticeEndRef.current.value = "";
    if (adminNoticeFileRef.current) adminNoticeFileRef.current.value = "";
    if (adminNoticeProductKeywordRef.current) adminNoticeProductKeywordRef.current.value = "";
    setAdminNoticeAttachments([]);
    setAdminNoticeProductResults([]);
    setAdminNoticeSelectedProducts([]);
    setAdminNoticeProductVisibleCount(5);
    setEditingNoticeId(null);
  };

  const handleAdminNoticeProductSearch = () => {
    const keyword = adminNoticeProductKeywordRef.current?.value?.trim().toLowerCase() || "";
    if (!keyword) {
      setAdminNoticeProductResults([]);
      setAdminNoticeProductVisibleCount(5);
      setConfirmModal({ type: "notice", title: "검색어 필요", message: "상품명 또는 상품코드를 입력해주세요." });
      return;
    }
    const results = activeProductList.filter((product) => product.name.toLowerCase().includes(keyword) || product.code.toLowerCase().includes(keyword) || (product.category || "").toLowerCase().includes(keyword) || (product.barcode || "").toLowerCase().includes(keyword));
    setAdminNoticeProductResults(results);
    setAdminNoticeProductVisibleCount(5);
  };

  const handleAdminNoticeProductSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdminNoticeProductSearch();
    }
  };

  const handleAddAdminNoticeProduct = (product) => {
    setAdminNoticeSelectedProducts((prev) => {
      if (prev.some((item) => item.code === product.code)) return prev;
      return [...prev, { code: product.code, name: product.name }];
    });
  };

  const handleRemoveAdminNoticeProduct = (productCode) => {
    setAdminNoticeSelectedProducts((prev) => prev.filter((product) => product.code !== productCode));
  };

  const handleSubmitAdminNotice = async () => {
    if (adminNoticeSavingRef.current || adminNoticeSaving) return;
    const category = normalizeNoticeCategory(adminNoticeCategoryRef.current?.value || "공지");

    const title = adminNoticeTitleRef.current?.value.trim() || "";

    const target = adminNoticeTargetRef.current?.value || "전체 매장";

    const startDate = adminNoticeStartRef.current?.value || "";

    const endDate = adminNoticeEndRef.current?.value || "";

    const content = adminNoticeContentRef.current?.value.trim() || "";



    if (!title) {

      setConfirmModal({ type: "notice", title: "입력 필요", message: "제목을 입력해주세요." });

      return;

    }

    if (!content) {

      setConfirmModal({ type: "notice", title: "입력 필요", message: "내용을 입력해주세요." });

      return;

    }

    if ((category === "이벤트" || category === "업무 요청") && (!startDate || !endDate)) {

      setConfirmModal({ type: "notice", title: "입력 필요", message: "이벤트/업무요청은 시작일과 종료일을 입력해주세요." });

      return;

    }

    const noticeSaveAction = editingNoticeId ? "edit notice" : "create notice";
    const timing = createSaveTimingLogger("공지 저장 시간");
    adminNoticeSavingRef.current = true;
    setAdminNoticeSaving(true);

    try {

    let nextNotices;



    if (editingNoticeId) {

      nextNotices = companyNotices.map((notice) => notice.id === editingNoticeId ? {

        ...notice,

        category,

        title,

        target,

        startDate,

        endDate,

        content,

        attachments: adminNoticeAttachments,

        products: adminNoticeSelectedProducts,

        hidden: notice.hidden || false,

        taskStatus: category === "업무 요청" ? (notice.taskStatus || "접수") : undefined,

      } : notice);

      timing.step("payload prepare", { action: noticeSaveAction, count: nextNotices.length });
      const saved = await saveAppDataListPreservingDetails("rc_company_notices", nextNotices);
      timing.step("server save", { action: noticeSaveAction, saved });
      if (!saved) throw new Error("공지 수정 저장 실패");

      setCompanyNotices(nextNotices);
      timing.step("reload/apply", { action: noticeSaveAction });

      setConfirmModal({ type: "notice", title: "수정 완료", message: "공지 내용이 저장되었습니다." });

    } else {

      const newNotice = {

        id: Date.now(),

        category,

        title,

        target,

        startDate,

        endDate,

        content,

        attachments: adminNoticeAttachments,

        products: adminNoticeSelectedProducts,

        hidden: false,

        checkedBy: [],

        ...(category === "업무 요청" ? { taskStatus: "접수", taskCheckedBy: "", taskCheckedAt: "", taskCompletedBy: "", taskCompletedAt: "", taskMemo: "", taskFiles: [] } : {}),

      };

      nextNotices = [newNotice, ...companyNotices];

      timing.step("payload prepare", { action: noticeSaveAction, count: nextNotices.length });
      const saved = await saveAppDataListPreservingDetails("rc_company_notices", nextNotices);
      timing.step("server save", { action: noticeSaveAction, saved });
      if (!saved) throw new Error("공지 등록 저장 실패");

      setCompanyNotices(nextNotices);
      timing.step("reload/apply", { action: noticeSaveAction });

      setConfirmModal({ type: "notice", title: "등록 완료", message: "공지사항이 저장되었습니다." });

    }

    resetAdminNoticeForm();

    } catch (error) {
      console.error("공지 저장 실패:", error);
      setConfirmModal({
        type: "notice",
        title: "저장 실패",
        message: "공지사항을 서버에 저장하지 못했습니다. 잠시 후 다시 시도해주세요.",
      });
    } finally {
      timing.total({ action: noticeSaveAction });
      adminNoticeSavingRef.current = false;
      setAdminNoticeSaving(false);
    }

  };



  const handleEditAdminNotice = async (notice) => {
    let fullNotice = notice;

    try {
      const detail = await fetchAppDataItemDetail("rc_company_notices", notice.id);
      if (detail) {
        fullNotice = { ...notice, ...detail };
        setCompanyNotices((prev) => mergeDetailIntoListById(prev, fullNotice, "rc_company_notices"));
      }
    } catch (error) {
      console.error("공지 수정용 상세 조회 실패:", error);
    }

    setEditingNoticeId(fullNotice.id);
    setAdminNoticeAttachments(fullNotice.attachments || []);
    setAdminNoticeSelectedProducts(fullNotice.products || []);
    setAdminNoticeProductResults([]);
    setAdminNoticeProductVisibleCount(5);
    setTimeout(() => {
      if (adminNoticeCategoryRef.current) adminNoticeCategoryRef.current.value = fullNotice.category;
      if (adminNoticeTitleRef.current) adminNoticeTitleRef.current.value = fullNotice.title;
      if (adminNoticeTargetRef.current) adminNoticeTargetRef.current.value = fullNotice.target;
      if (adminNoticeStartRef.current) adminNoticeStartRef.current.value = fullNotice.startDate || "";
      if (adminNoticeEndRef.current) adminNoticeEndRef.current.value = fullNotice.endDate || "";
      if (adminNoticeContentRef.current) adminNoticeContentRef.current.value = fullNotice.content;
    }, 0);
  };

  const handleDeleteAdminNotice = async (noticeId) => {
    if (adminNoticeSavingRef.current || adminNoticeSaving) return;
    const timing = createSaveTimingLogger("공지 저장 시간");
    adminNoticeSavingRef.current = true;
    setAdminNoticeSaving(true);
    try {
    const nextNotices = companyNotices.filter((notice) => notice.id !== noticeId);
      timing.step("payload prepare", { action: "delete notice", count: nextNotices.length });
      const saved = await saveAppDataListPreservingDetails("rc_company_notices", nextNotices);
      timing.step("server save", { action: "delete notice", saved });
      if (!saved) throw new Error("공지 삭제 저장 실패");
    setCompanyNotices(nextNotices);
    if (editingNoticeId === noticeId) resetAdminNoticeForm();
      timing.step("reload/apply", { action: "delete notice" });
      setConfirmModal({ type: "notice", title: "삭제 완료", message: "공지사항이 삭제되었습니다." });
    } catch (error) {
      console.error("공지 삭제 실패:", error);
      setConfirmModal({
        type: "notice",
        title: "삭제 실패",
        message: "공지사항 삭제 내용을 서버에 저장하지 못했습니다. 잠시 후 다시 시도해주세요.",
      });
    } finally {
      timing.total({ action: "delete notice" });
      adminNoticeSavingRef.current = false;
      setAdminNoticeSaving(false);
    }
  };

  const handleToggleAdminNoticeHidden = async (noticeId) => {
    if (adminNoticeSavingRef.current || adminNoticeSaving) return;
    const timing = createSaveTimingLogger("공지 저장 시간");
    adminNoticeSavingRef.current = true;
    setAdminNoticeSaving(true);
    try {
      const currentNotice = companyNotices.find((notice) => notice.id === noticeId);
    const nextNotices = companyNotices.map((notice) => notice.id === noticeId ? { ...notice, hidden: !notice.hidden } : notice);
      timing.step("payload prepare", { action: "toggle notice visibility", count: nextNotices.length });
      const saved = await saveAppDataListPreservingDetails("rc_company_notices", nextNotices);
      timing.step("server save", { action: "toggle notice visibility", saved });
      if (!saved) throw new Error("공지 노출 상태 저장 실패");
    setCompanyNotices(nextNotices);
      timing.step("reload/apply", { action: "toggle notice visibility" });
      setConfirmModal({
        type: "notice",
        title: "처리 완료",
        message: currentNotice?.hidden ? "공지사항이 다시 노출됩니다." : "공지사항이 비노출 처리되었습니다.",
      });
    } catch (error) {
      console.error("공지 노출 상태 변경 실패:", error);
      setConfirmModal({
        type: "notice",
        title: "처리 실패",
        message: "공지사항 노출 상태를 서버에 저장하지 못했습니다. 잠시 후 다시 시도해주세요.",
      });
    } finally {
      timing.total({ action: "toggle notice visibility" });
      adminNoticeSavingRef.current = false;
      setAdminNoticeSaving(false);
    }
  };

  const getFilteredAdminNotices = () => companyNotices.filter((notice) => {
    const categoryOk = adminNoticeFilter.category === "전체" || isNoticeCategory(notice, adminNoticeFilter.category);
    const targetOk = adminNoticeFilter.target === "전체" || notice.target === adminNoticeFilter.target;
    const keyword = adminNoticeFilter.keyword.trim();
    const keywordOk = !keyword || notice.title.includes(keyword) || notice.content.includes(keyword);
    const visibleOk = !adminNoticeFilter.visibleOnly || isAdminNoticeCurrentlyVisible(notice);
    return categoryOk && targetOk && keywordOk && visibleOk;
  });

  const handleCompleteCompanyTask = async (noticeId) => {
    if (!canHandleStaffNoticeAction()) return;
    if (isNoticeActionSaving(noticeId)) return;
    const memo = noticeTaskMemos[noticeId]?.trim() || "";
    const files = noticeTaskFiles[noticeId] || [];
    if (!memo && files.length === 0) {
      setConfirmModal({ type: "notice", title: "처리완료 불가", message: "처리 메모를 입력하거나 완료 사진/파일을 첨부해주세요." });
      return;
    }

    setNoticeActionSaving(noticeId, true);
    let saved = false;
    try {
      saved = await mutateCompanyNoticeFromLatest(noticeId, (notice) => ({
      ...notice,
      checkedBy: addNoticeCheckedEntry(notice),
      taskStatus: "처리완료",
      taskCheckedBy: notice.taskCheckedBy || currentUser,
      taskCheckedAt: notice.taskCheckedAt || formatNow(),
      taskCompletedBy: currentUser,
      taskCompletedAt: formatNow(),
      taskMemo: memo,
      taskFiles: files,
    }));
    } finally {
      setNoticeActionSaving(noticeId, false);
    }

    if (!saved) {
      setConfirmModal({ type: "notice", title: "저장 실패", message: "업무 요청 공지 처리완료 상태를 저장하지 못했습니다. 다시 시도해주세요." });
      return;
    }

    setNoticeTaskMemos((prev) => ({ ...prev, [noticeId]: "" }));
    setNoticeTaskFiles((prev) => ({ ...prev, [noticeId]: [] }));
    if (noticeTaskFileRefs.current[noticeId]) noticeTaskFileRefs.current[noticeId].value = "";
  };

  const HandoverPage = () => {
    const visibleHandovers = getFilteredHandovers();
    const pendingHandovers = getPendingHandovers();
    const staffHandoverTotalPages = Math.max(1, Math.ceil(visibleHandovers.length / staffHandoverPageSize));
    const safeStaffHandoverPage = Math.min(staffHandoverPage, staffHandoverTotalPages);
    const pagedVisibleHandovers = visibleHandovers.slice(
      (safeStaffHandoverPage - 1) * staffHandoverPageSize,
      safeStaffHandoverPage * staffHandoverPageSize
    );
    const changeStaffHandoverPage = (nextPage) => {
      resetExpandedUiState();
      setStaffHandoverPage(Math.min(staffHandoverTotalPages, Math.max(1, nextPage)));
    };

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="인수인계" desc="퇴근자가 남기고, 다음 출근자가 처리완료하면 완료됩니다." />

        <button
          style={{
            width: "100%",
            border: "1px solid #f59e0b",
            background: "#fef3c7",
            color: "#92400e",
            borderRadius: 18,
            padding: 16,
            marginBottom: 16,
            fontWeight: "bold",
            fontSize: 16,
            textAlign: "left",
            cursor: "pointer",
          }}
          onClick={() => setHandoverFilter({ store: "전체 매장", status: "확인 필요", start: "", end: "" })}
        >
          미확인 인수인계 {pendingHandovers.length}건
          <div style={{ fontSize: 12, marginTop: 6, fontWeight: "normal" }}>누르면 확인이 필요한 인수인계만 표시됩니다.</div>
        </button>

        <div style={{ background: "#111827", color: "#fff", borderRadius: 18, padding: 16, marginBottom: 16 }}>
          <b>{activeStore || selectedStore} 인수인계 내용입니다.</b>
          <div style={{ fontSize: 13, color: "#d1d5db", marginTop: 6 }}>작성자: {currentUser} · 작성 매장: {activeStore || selectedStore}</div>
        </div>

        <input ref={handoverTitleRef} placeholder="인수인계 제목" style={{ ...input, width: "100%", marginBottom: 10 }} />
        <textarea ref={handoverContentRef} placeholder="다음 근무자가 확인해야 할 내용을 입력하세요" style={{ ...input, width: "100%", minHeight: 110 }} />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginTop: 12 }}>
          <label style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, lineHeight: 1.5 }}>
            <input ref={handoverImportantRef} type="checkbox" /> 중요 인수인계 표시
          </label>
          <label style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, lineHeight: 1.5 }}>
            <input ref={handoverNeedCheckRef} type="checkbox" checked readOnly /> 다음 근무자 확인 필수
          </label>
        </div>

        <div style={{ marginTop: 14, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8, fontWeight: "bold" }}>파일 첨부</div>
          <input ref={handoverFileRef} type="file" multiple onChange={handleHandoverFileChange} style={{ ...input, width: "100%", background: "#fff" }} />
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>사진, 동영상, PDF 등 인수인계에 필요한 파일을 첨부할 수 있습니다.</div>
          {handoverAttachments.length > 0 && (
            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {handoverAttachments.map((file) => (
                <div key={file.id} style={{ display: "grid", gridTemplateColumns: isImageFile(file) ? "72px 1fr auto" : "1fr auto", gap: 10, alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                  {isImageFile(file) && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb" }} />}
                  <span style={{ fontSize: 13, minWidth: 0, wordBreak: "break-all", overflowWrap: "anywhere" }}><b>{file.name}</b><br /><small>{formatFileSize(file.size)}</small></span>
                  <button style={grayButton} onClick={() => handleRemoveHandoverAttachment(file.id)}>삭제</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          style={{ ...(isSubmittingHandover ? grayButton : redButton), width: "100%", padding: 14, marginTop: 14 }}
          onClick={handleSubmitHandover}
          disabled={isSubmittingHandover}
        >
          {isSubmittingHandover ? "저장중입니다..." : "저장"}
        </button>

        <div style={{ marginTop: 28 }}>
          <SectionTitle title="미확인 인수인계" desc="내가 확인해야 할 인수인계를 먼저 처리합니다." />

          {pendingHandovers.length === 0 ? (
            <div style={{ background: "#f9fafb", borderRadius: 18, padding: 18, color: "#6b7280", marginBottom: 18 }}>현재 확인해야 할 인수인계가 없습니다.</div>
          ) : pendingHandovers.map((item) => (
            <div key={`pending-${item.id}`} style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 18, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <b>{item.store} · {item.title}</b>
                <StatusBadge>{item.status}</StatusBadge>
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{item.date} · 작성자 {item.writer}</div>
              <div style={{ marginTop: 10, lineHeight: 1.7 }}>{item.content}</div>
              {item.attachments?.length > 0 && (
                <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>
                  <b>첨부파일</b>
                  {item.attachments.map((file) => (
                    <div key={file.id || file.name} style={{ marginTop: 6, wordBreak: "break-all", overflowWrap: "anywhere" }}>
                      {isImageFile(file) && file.previewUrl && (
                        <button type="button" onClick={() => openAttachmentFile(file)} title="이미지 크게 보기" style={{ padding: 0, border: "none", background: "transparent", cursor: "pointer", display: "block", marginBottom: 6 }}>
                          <img src={file.previewUrl} alt={file.name} style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 10, display: "block" }} />
                        </button>
                      )}
                      {getFileOpenUrl(file) ? (
                        <button type="button" onClick={() => openAttachmentFile(file)} style={{ color: "#2563eb", fontWeight: "bold", wordBreak: "break-all", overflowWrap: "anywhere", textDecoration: "underline", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }}>
                          - {file.name} ({formatFileSize(file.size)})
                        </button>
                      ) : (
                        <>- {file.name} ({formatFileSize(file.size)})</>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button style={{ ...redButton, width: "100%", marginTop: 12 }} onClick={() => handleConfirmHandover(item.id)}>처리완료</button>
            </div>
          ))}

          <SectionTitle title="인수인계 리스트" desc="누적된 인수인계와 처리 상태를 조회합니다." />

          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 14, marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr", gap: 8 }}>
              <select value={handoverFilter.store} onChange={(e) => setHandoverFilter((prev) => ({ ...prev, store: e.target.value }))} style={{ ...input, width: "100%" }}>
                <option>전체 매장</option>
                {getAllowedStores().map((store) => <option key={store}>{store}</option>)}
              </select>
              <select value={handoverFilter.status} onChange={(e) => setHandoverFilter((prev) => ({ ...prev, status: e.target.value }))} style={{ ...input, width: "100%" }}>
                <option>전체</option>
                <option>확인 필요</option>
                <option>처리완료</option>
              </select>
              <input value={handoverFilter.start} onChange={(e) => setHandoverFilter((prev) => ({ ...prev, start: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
              <input value={handoverFilter.end} onChange={(e) => setHandoverFilter((prev) => ({ ...prev, end: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
            </div>
            <button style={{ ...grayButton, width: "100%", marginTop: 8 }} onClick={() => setHandoverFilter({ store: "전체 매장", status: "전체", start: "", end: "" })}>검색조건 초기화</button>
          </div>

          {visibleHandovers.length === 0 ? (
            <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 인수인계가 없습니다.</div>
          ) : pagedVisibleHandovers.map((item) => (
            <div key={item.id} style={{ background: item.important ? "#fff5f5" : "#f9fafb", border: item.important ? "1px solid #fecaca" : "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <b>{item.store} · {item.title}</b>
                <StatusBadge tone={item.status === "처리완료" ? "blue" : "red"}>{item.status}</StatusBadge>
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{item.date} · 작성자 {item.writer}</div>
              <div style={{ marginTop: 10, lineHeight: 1.7 }}>{item.content}</div>
              {item.attachments?.length > 0 && (
                <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>
                  <b>첨부파일</b>
                  {item.attachments.map((file) => (
                    <div key={file.id || file.name} style={{ marginTop: 6, wordBreak: "break-all", overflowWrap: "anywhere" }}>
                      {isImageFile(file) && file.previewUrl && (
                        <button type="button" onClick={() => openAttachmentFile(file)} title="이미지 크게 보기" style={{ padding: 0, border: "none", background: "transparent", cursor: "pointer", display: "block", marginBottom: 6 }}>
                          <img src={file.previewUrl} alt={file.name} style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 10, display: "block" }} />
                        </button>
                      )}
                      {getFileOpenUrl(file) ? (
                        <button type="button" onClick={() => openAttachmentFile(file)} style={{ color: "#2563eb", fontWeight: "bold", wordBreak: "break-all", overflowWrap: "anywhere", textDecoration: "underline", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }}>
                          - {file.name} ({formatFileSize(file.size)})
                        </button>
                      ) : (
                        <>- {file.name} ({formatFileSize(file.size)})</>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 10, fontSize: 13, color: "#374151" }}>
                처리자: {item.checkedBy || "-"}{item.checkedAt ? ` · 처리일시 ${item.checkedAt}` : ""}
              </div>
              {item.needCheck && item.status !== "처리완료" && (
                <button style={{ ...redButton, width: "100%", marginTop: 12 }} onClick={() => handleConfirmHandover(item.id)}>처리완료</button>
              )}
            </div>
          ))}
          {staffHandoverTotalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
              <button style={safeStaffHandoverPage === 1 ? grayButton : darkButton} disabled={safeStaffHandoverPage === 1} onClick={() => changeStaffHandoverPage(safeStaffHandoverPage - 1)}>이전</button>
              <span style={{ padding: "10px 12px", fontSize: 13, color: "#374151" }}>{safeStaffHandoverPage} / {staffHandoverTotalPages}</span>
              <button style={safeStaffHandoverPage === staffHandoverTotalPages ? grayButton : darkButton} disabled={safeStaffHandoverPage === staffHandoverTotalPages} onClick={() => changeStaffHandoverPage(safeStaffHandoverPage + 1)}>다음</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const WorkRequestPage = () => {
    const visibleRequests = getVisibleWorkRequestsForStaff();

    return (
      <div style={card}>
        <SectionTitle title="업무 요청 등록" desc="상품을 검색해 추가하고, 매장 요청을 접수합니다." />

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8, fontWeight: "bold" }}>1. 접수 유형</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(6, 1fr)", gap: 8 }}>
            {requestTypes.map((type) => (
              <button
                key={type}
                style={isSubmittingWorkRequest ? grayButton : requestType === type ? redButton : darkButton}
                disabled={isSubmittingWorkRequest}
                onClick={() => handleChangeWorkRequestType(type)}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8, fontWeight: "bold" }}>2. 매장 선택</div>
          <div style={{ display: "grid", gridTemplateColumns: requestType === "이슈보고" || isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
            <select value={requestReceiveStore} onChange={(e) => setRequestReceiveStore(e.target.value)} style={{ ...input, width: "100%" }}>
              <option value="">접수 매장 선택</option>
              {getAllowedStores().map((store) => <option key={store}>{store}</option>)}
            </select>
            {requestType !== "이슈보고" && (
              <select value={requestTargetStore} onChange={(e) => setRequestTargetStore(e.target.value)} style={{ ...input, width: "100%" }}>
                {locationOptions.map((loc) => <option key={loc}>{loc}</option>)}
              </select>
            )}
          </div>
          {requestType === "이슈보고" && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
              이슈보고는 접수 매장만 선택합니다. 처리 지점은 본사에서 확인 후 배정합니다.
            </div>
          )}
        </div>

        {requestType !== "이슈보고" && (
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8, fontWeight: "bold" }}>3. 상품 검색 후 추가</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 10 }}>
              <input
                ref={productKeywordRef}
                onKeyDown={handleProductSearchKeyDown}
                placeholder="상품명 또는 상품코드 검색"
                autoComplete="off"
                style={{ ...input, width: "100%" }}
              />
              <button style={darkButton} onClick={handleSearchProduct}>검색</button>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
              먼저 상품을 검색한 뒤, 검색 결과 안에서 수량과 옵션을 입력하고 추가해주세요.
            </div>

            {productResults.length > 0 && (
              <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                {productResults.slice(0, productResultVisibleCount).map((product) => {
                  const draft = productDrafts[product.code] || { qty: "1", option: "" };
                  const productImageUrl = getProductImageUrl(product);
                  return (
                    <div key={product.code} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 10, alignItems: "center", marginBottom: 10 }}>
                        <button
                          type="button"
                          onClick={() => handleAddRequestProduct(product)}
                          title="상품 추가"
                          style={{ position: "relative", width: 56, height: 56, borderRadius: 12, border: "1px solid #e5e7eb", background: "#f9fafb", padding: 0, overflow: "hidden", cursor: "pointer" }}
                        >
                          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 6, color: "#9ca3af", fontSize: 11, lineHeight: 1.2, textAlign: "center" }}>이미지 없음</span>
                          {productImageUrl && (
                            <img
                              src={productImageUrl}
                              alt={product.name}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          )}
                        </button>
                        <div style={{ minWidth: 0 }}>
                          <b style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>{product.name}</b><br />
                          <small style={{ color: "#6b7280", wordBreak: "break-word", overflowWrap: "anywhere" }}>{product.code} · {product.category || "기타"}</small>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            판매가 {formatProductPrice(product.salePrice, true)} · 도매가 {formatProductPrice(product.wholesalePrice, true)} · CT입수량 {getProductCtQuantity(product) || "-"}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "0.6fr 1fr auto", gap: 8, alignItems: "center" }}>
                        <input
                          defaultValue={draft.qty}
                          onChange={(e) => handleProductDraftChange(product.code, "qty", e.target.value)}
                          placeholder="수량"
                          inputMode="numeric"
                          style={{ ...input, width: "100%" }}
                        />
                        <input
                          defaultValue={draft.option}
                          onChange={(e) => handleProductDraftChange(product.code, "option", e.target.value)}
                          placeholder="색상/옵션"
                          style={{ ...input, width: "100%" }}
                        />
                        <button style={darkButton} onClick={() => handleAddRequestProduct(product)}>추가</button>
                      </div>
                    </div>
                  );
                })}
                {productResults.length > productResultVisibleCount && (
                  <button style={{ ...grayButton, width: "100%" }} onClick={() => setProductResultVisibleCount((prev) => prev + 5)}>
                    더보기 ({productResultVisibleCount}/{productResults.length})
                  </button>
                )}
              </div>
            )}

            {lastProductSearchKeyword && productResults.length === 0 && (
              <div style={{ marginTop: 10, background: "#fff", border: "1px dashed #d1d5db", borderRadius: 14, padding: 14, color: "#6b7280", fontSize: 13 }}>
                검색 결과가 없습니다. 상품명 또는 상품코드를 다시 확인해주세요.
              </div>
            )}

            {selectedRequestProducts.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <b>추가된 상품</b>
                {selectedRequestProducts.map((product, index) => (
                  <div key={`${product.code}-${index}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, marginTop: 8 }}>
                    <div><b>{product.name}</b><br /><small>{product.code} · 수량 {product.qty || "1"}{product.option ? ` · ${product.option}` : ""}</small></div>
                    <button style={grayButton} onClick={() => handleRemoveRequestProduct(index)}>삭제</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {requestType === "이슈보고" && (
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18, color: "#6b7280", fontSize: 13, lineHeight: 1.6 }}>
            이슈보고는 상품 선택 없이 접수할 수 있습니다. 매장을 선택한 뒤 요청 내용과 필요한 파일만 첨부해주세요.
          </div>
        )}

        {requestType !== "배송 요청" && <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8, fontWeight: "bold" }}>{requestType === "이슈보고" ? "3" : "4"}. 요청 내용</div>
          <textarea ref={requestContentRef} placeholder="요청 내용을 구체적으로 작성해주세요." style={{ ...input, width: "100%", minHeight: 110 }} />
        </div>}

        <div style={{ marginBottom: 18, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8, fontWeight: "bold" }}>5. 파일 첨부{["A/S 요청", "배송 요청"].includes(requestType) ? " (필수)" : ""}</div>
          <input ref={requestFileRef} type="file" multiple disabled={requestFilesReading || isSubmittingWorkRequest} onChange={handleRequestFileChange} style={{ ...input, width: "100%", background: "#fff" }} />
          {requestFilesReading && <div role="status">파일을 읽는 중입니다...</div>}
          {requestFilesReadError && <div role="alert">첨부파일을 다시 선택해주세요.</div>}
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
            사진, 동영상, PDF, 엑셀 등 요청 확인에 필요한 파일을 첨부할 수 있습니다.
          </div>
          <div style={{ fontSize: 12, color: "#9a3412", marginTop: 6 }}>
            파일당 {formatFileSize(WORK_REQUEST_ATTACHMENT_MAX_BYTES)}, 총 {formatFileSize(WORK_REQUEST_ATTACHMENT_TOTAL_MAX_BYTES)}까지 등록할 수 있습니다.
          </div>
          {requestAttachments.length > 0 && (
            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {requestAttachments.map((file) => (
                <div key={file.id} style={{ display: "grid", gridTemplateColumns: isImageFile(file) ? "72px 1fr auto" : "1fr auto", gap: 10, alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                  {file.isImage && (
                    <img src={file.previewUrl} alt={file.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb" }} />
                  )}
                  <span style={{ fontSize: 13, minWidth: 0, wordBreak: "break-all", overflowWrap: "anywhere" }}><b>{file.name}</b><br /><small>{formatFileSize(file.size)}</small></span>
                  <button style={grayButton} disabled={requestFilesReading || isSubmittingWorkRequest} onClick={() => handleRemoveRequestAttachment(file.id)}>삭제</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {requestType === "A/S 요청" && (
          <div style={{ marginBottom: 18, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 18, padding: 16 }}>
            <div style={{ fontSize: 13, color: "#9a3412", marginBottom: 8, fontWeight: "bold" }}>6. A/S 접수 정보 입력 필수</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <input ref={requestCustomerNameRef} placeholder="고객명" style={{ ...input, width: "100%" }} />
              <RequestPhoneInput ref={requestCustomerContactRef} disabled={isSubmittingWorkRequest} style={{ ...input, width: "100%" }} />
              <RequestAddressFields key={requestType} value={requestAddress} onChange={setRequestAddress} disabled={isSubmittingWorkRequest} />
              <select ref={asPurchaseStoreRef} defaultValue="" style={{ ...input, width: "100%" }}>
                <option value="">구매 지점 선택</option>
                {locationOptions.map((store) => <option key={store}>{store}</option>)}
              </select>
              <input ref={asPurchaseDateRef} type="date" placeholder="구매일" style={{ ...input, width: "100%" }} />
              <select ref={asSymptomRef} defaultValue="" style={{ ...input, width: "100%" }}>
                <option value="">증상 선택</option>
                <option>충전불량</option>
                <option>작동불량</option>
              </select>
              <select ref={asReceiveMethodRef} defaultValue="" style={{ ...input, width: "100%" }}>
                <option value="">수령 방법 선택</option>
                <option>매장수령</option>
                <option>택배수령</option>
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginTop: 10 }}>
              <div style={{ background: "#fff", border: "1px solid #fed7aa", borderRadius: 12, padding: 12, fontSize: 13 }}>
                <small>접수 담당자</small><br /><b>{currentUser}</b>
              </div>
              <div style={{ background: "#fff", border: "1px solid #fed7aa", borderRadius: 12, padding: 12, fontSize: 13 }}>
                <small>접수 지점</small><br /><b>{requestReceiveStore || "-"}</b>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#9a3412", marginTop: 8, lineHeight: 1.6 }}>
              증빙 사진이나 고객 전달 자료는 위 파일 첨부 영역에 추가해주세요.
            </div>
          </div>
        )}

        {requestType === "배송 요청" && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>배송 고객정보 · 주소 (필수)</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <input ref={requestCustomerNameRef} placeholder="고객명" disabled={isSubmittingWorkRequest} style={{ ...input, width: "100%", fontSize: 16 }} />
              <RequestPhoneInput ref={requestCustomerContactRef} disabled={isSubmittingWorkRequest} style={{ ...input, width: "100%" }} />
            </div>
            <RequestAddressFields key={requestType} value={requestAddress} onChange={setRequestAddress} disabled={isSubmittingWorkRequest} />
            <div style={{ fontWeight: 800, marginTop: 14, marginBottom: 8 }}>추가 전달 사항 (선택)</div>
            <textarea ref={requestContentRef} placeholder="추가 전달 사항 (선택)" disabled={isSubmittingWorkRequest} style={{ ...input, width: "100%", minHeight: 110, fontSize: 16 }} />
          </div>
        )}

        <button
          style={{ ...(isSubmittingWorkRequest ? grayButton : redButton), width: "100%", padding: 14 }}
          onClick={handleSubmitWorkRequest}
          disabled={isSubmittingWorkRequest || requestFilesReading}
        >
          {isSubmittingWorkRequest ? "등록중입니다..." : requestFilesReading ? "파일을 읽는 중입니다..." : "업무 요청 등록"}
        </button>

        <div style={{ marginTop: 28 }}>
          <SectionTitle title="우리 매장 업무 요청 현황" desc="내가 근무 가능한 매장의 요청 진행 상태를 확인합니다." />

          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 14, marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr", gap: 8 }}>
              <select ref={workRequestTypeFilterRef} defaultValue={workRequestFilter.type} style={{ ...input, width: "100%" }}>
                <option>전체</option>
                {requestTypes.map((type) => <option key={type}>{type}</option>)}
              </select>
              <select ref={workRequestStatusFilterRef} defaultValue={workRequestFilter.status} style={{ ...input, width: "100%" }}>
                <option>전체</option>
                <option>미처리</option>
                <option>접수</option>
                <option>본사 확인</option>
                <option>처리완료</option>
              </select>
              <input ref={workRequestStartFilterRef} defaultValue={workRequestFilter.start} type="date" style={{ ...input, width: "100%" }} />
              <input ref={workRequestEndFilterRef} defaultValue={workRequestFilter.end} type="date" style={{ ...input, width: "100%" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <button style={darkButton} onClick={handleSearchWorkRequests}>검색</button>
              <button style={grayButton} onClick={handleResetWorkRequests}>초기화</button>
            </div>
          </div>

          {visibleRequests.length === 0 ? (
            <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>등록된 업무 요청이 없습니다.</div>
          ) : visibleRequests.map((request) => (
            <div key={request.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                <b>{request.type} · {request.receiveStore}</b>
                <StatusBadge tone={request.status === "처리완료" ? "blue" : request.status === "본사 확인" ? "yellow" : "red"}>{request.status}</StatusBadge>
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{request.date} · {request.writer} · 처리지점: {request.targetStore}</div>
              <div style={{ marginTop: 10, lineHeight: 1.6 }}>{request.content}</div>
              <div style={{ marginTop: 10, fontSize: 13 }}>
                {request.products.map((product, idx) => (
                  <div key={idx}>- {product.name} ({product.code}) / 수량 {product.qty || "1"}{product.option ? ` / ${product.option}` : ""}</div>
                ))}
              </div>
              {(request.customerName || request.customerContact) && <div style={{ marginTop: 10, fontSize: 13, overflowWrap: "anywhere" }}>고객정보: {request.customerName || "-"} / {request.customerContact || "-"}</div>}
              <AttachmentList files={getWorkRequestDisplayAttachments(request)} title="첨부파일" />
              {request.asInfo && (
                <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13, lineHeight: 1.7 }}>
                  <b>A/S 접수 정보</b><br />
                  고객 주소: {request.asInfo.customerAddress || "-"}<br />
                  구매 지점: {request.asInfo.purchaseStore || "-"} / 구매일: {request.asInfo.purchaseDate || "-"}<br />
                  증상: {request.asInfo.symptom || "-"} / 수령방법: {request.asInfo.receiveMethod || "-"}<br />
                  접수 담당자: {request.asInfo.receptionManager || "-"} / 접수 지점: {request.asInfo.receptionStore || "-"}
                </div>
              )}
              {request.deliveryInfo && <div style={{ marginTop: 10, fontSize: 13, overflowWrap: "anywhere" }}>배송 주소: {requestAddressText(request.deliveryInfo) || "-"}</div>}
              {request.adminMemo && <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>본사 처리 메모: {request.adminMemo}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const SupplyOrderPage = () => {
    const currentWork = getCurrentOpenWorkRecord();
    const currentWorkStore = currentWork?.store || "";
    const visibleSupplyOrders = getFilteredSupplyOrders();
    const staffSupplyKeyword = staffSupplyItemSearch.trim().toLowerCase();
    const filteredActiveSupplyItems = activeSupplyItems.filter((item) => {
      const categoryOk = staffSupplyItemCategoryFilter === "전체" || (item.category || "기타") === staffSupplyItemCategoryFilter;
      const keywordOk = !staffSupplyKeyword ||
        String(item.name || "").toLowerCase().includes(staffSupplyKeyword) ||
        String(item.unit || "").toLowerCase().includes(staffSupplyKeyword) ||
        (item.category || "기타").toLowerCase().includes(staffSupplyKeyword);
      return categoryOk && keywordOk;
    });
    const staffSupplyItemTotalPages = Math.max(1, Math.ceil(filteredActiveSupplyItems.length / staffSupplyItemPageSize));
    const safeStaffSupplyItemPage = Math.min(staffSupplyItemPage, staffSupplyItemTotalPages);
    const pagedActiveSupplyItems = filteredActiveSupplyItems.slice(
      (safeStaffSupplyItemPage - 1) * staffSupplyItemPageSize,
      safeStaffSupplyItemPage * staffSupplyItemPageSize
    );
    const staffSupplyItemPageNumbers = Array.from({ length: staffSupplyItemTotalPages }, (_, index) => index + 1).filter((page) => {
      if (staffSupplyItemTotalPages <= 7) return true;
      return page === 1 || page === staffSupplyItemTotalPages || Math.abs(page - safeStaffSupplyItemPage) <= 2;
    });

    if (!currentWork) {
      return (
        <div>
          <SectionTitle title="소모품 주문 신청" desc="소모품 주문은 현재 출근한 매장 기준으로만 사용할 수 있습니다." />
          <div style={{ ...card, border: "1px solid #fecaca", background: "#fff1f2" }}>
            <h3 style={{ marginTop: 0, color: "#b91c1c" }}>근무중에만 사용 가능합니다.</h3>
            <p style={{ color: "#7f1d1d", lineHeight: 1.7, marginBottom: 0 }}>먼저 대시보드에서 출근을 누른 뒤 소모품 주문을 이용해주세요.</p>
          </div>
        </div>
      );
    }

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="소모품 주문 신청" desc="자주 주문하는 소모품을 선택하고, 기타 품목도 추가 요청할 수 있습니다." />
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 16, padding: 14, marginBottom: 16, color: "#1e40af", fontWeight: "bold" }}>
          현재 근무 매장: {currentWorkStore}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
            <small>주문 매장</small><br />
            <b>{activeStore || selectedStore}</b><br />
            <small>오늘 근무 매장 자동 적용</small>
          </div>
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
            <small>요청자</small><br />
            <b>{currentUser}</b><br />
            <small>로그인 직원 자동 적용</small>
          </div>
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
          <b>자주 주문하는 소모품</b>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.4fr", gap: 8, marginTop: 12 }}>
            <select
              value={staffSupplyItemCategoryFilter}
              onChange={(e) => {
                setStaffSupplyItemCategoryFilter(e.target.value);
                setStaffSupplyItemPage(1);
              }}
              style={{ ...input, width: "100%", background: "#fff" }}
            >
              <option>전체</option>
              {activeSupplyItemCategoryOptions.map((category) => <option key={category}>{category}</option>)}
            </select>
            <input
              value={staffSupplyItemSearch}
              onChange={(e) => {
                setStaffSupplyItemSearch(e.target.value);
                setStaffSupplyItemPage(1);
              }}
              placeholder="소모품명 검색"
              style={{ ...input, width: "100%", background: "#fff" }}
            />
          </div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>
            조회 {filteredActiveSupplyItems.length}개 / 전체 활성 {activeSupplyItems.length}개
          </div>
          {filteredActiveSupplyItems.length === 0 ? (
            <div style={{ background: "#fff", border: "1px dashed #d1d5db", borderRadius: 14, padding: 14, color: "#6b7280", fontSize: 13, marginTop: 10 }}>
              조회된 소모품이 없습니다.
            </div>
          ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginTop: 10 }}>
            {pagedActiveSupplyItems.map((item) => {
              const qty = getSupplyCartQty(item.name);
              return (
                <div key={item.name} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, minWidth: 0, boxSizing: "border-box" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                    <span style={{ minWidth: 0, wordBreak: "break-word", overflowWrap: "anywhere" }}><b>{item.name}</b><br /><small>{item.unit}</small></span>
                    <div style={{ display: "grid", gridTemplateColumns: "40px 32px 40px", gap: 6, alignItems: "center" }}>
                      <button style={{ ...darkButton, padding: "10px 0" }} onClick={() => handleSupplyQtyChange(item.name, -1)}>-</button>
                      <b style={{ textAlign: "center" }}>{qty}</b>
                      <button style={{ ...darkButton, padding: "10px 0" }} onClick={() => handleSupplyQtyChange(item.name, 1)}>+</button>
                    </div>
                  </div>
                  <button style={{ ...redButton, width: "100%", marginTop: 10 }} onClick={() => handleSupplyQtyChange(item.name, 1)}>
                    {qty > 0 ? "수량 추가" : "추가"}
                  </button>
                </div>
              );
            })}
          </div>
          )}
          {filteredActiveSupplyItems.length > staffSupplyItemPageSize && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", marginTop: 12 }}>
              <button style={safeStaffSupplyItemPage === 1 ? grayButton : darkButton} disabled={safeStaffSupplyItemPage === 1} onClick={() => setStaffSupplyItemPage((prev) => Math.max(1, prev - 1))}>이전</button>
              {staffSupplyItemPageNumbers.map((page) => (
                <button key={page} style={page === safeStaffSupplyItemPage ? redButton : grayButton} onClick={() => setStaffSupplyItemPage(page)}>
                  {page}
                </button>
              ))}
              <button style={safeStaffSupplyItemPage === staffSupplyItemTotalPages ? grayButton : darkButton} disabled={safeStaffSupplyItemPage === staffSupplyItemTotalPages} onClick={() => setStaffSupplyItemPage((prev) => Math.min(staffSupplyItemTotalPages, prev + 1))}>다음</button>
            </div>
          )}
        </div>

        <div style={{ marginTop: 14, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <b>기타 소모품 추가</b>
            <button style={darkButton} onClick={handleAddOtherSupplyRow}>+ 기타 품목 추가</button>
          </div>

          {otherSupplyRows.length === 0 ? (
            <div style={{ background: "#fff", border: "1px dashed #d1d5db", borderRadius: 14, padding: 14, color: "#6b7280", fontSize: 13 }}>
              목록에 없는 소모품은 기타 품목 추가 버튼을 눌러 직접 입력하세요.
            </div>
          ) : otherSupplyRows.map((row) => (
            <div key={row.id} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 0.5fr 1.4fr auto", gap: 8, marginBottom: 8 }}>
              <input value={row.name} onChange={(e) => handleChangeOtherSupplyRow(row.id, "name", e.target.value)} placeholder="기타 품목명" style={{ ...input, width: "100%" }} />
              <input value={row.qty} onChange={(e) => handleChangeOtherSupplyRow(row.id, "qty", e.target.value)} placeholder="수량" inputMode="numeric" style={{ ...input, width: "100%" }} />
              <input value={row.link} onChange={(e) => handleChangeOtherSupplyRow(row.id, "link", e.target.value)} placeholder="참고 링크 또는 메모" style={{ ...input, width: "100%" }} />
              <button style={grayButton} onClick={() => handleRemoveOtherSupplyRow(row.id)}>삭제</button>
            </div>
          ))}
        </div>

        {(supplyCart.length > 0 || otherSupplyRows.some((row) => row.name.trim())) && (
          <div style={{ marginTop: 14, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 18, padding: 16 }}>
            <b>주문 예정 품목</b>
            <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7, minWidth: 0 }}>
              {supplyCart.map((item) => <div key={item.name} style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>- {item.name} / {item.qty}{item.unit ? ` (${item.unit})` : ""}</div>)}
              {otherSupplyRows.filter((row) => row.name.trim()).map((row) => (
                <div key={row.id} style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                  - {row.name} / {row.qty || "1"}개 {row.link ? `/ ${row.link}` : ""}
                </div>
              ))}
            </div>
          </div>
        )}

        <textarea
          value={supplyOrderReason}
          onChange={(e) => setSupplyOrderReason(e.target.value)}
          placeholder="기타 소모품이 필요한 사유 또는 주문 참고사항을 작성해주세요."
          style={{ ...input, width: "100%", minHeight: 86, marginTop: 12 }}
        />
        <button
          style={{ ...(isSubmittingSupplyOrder ? grayButton : redButton), width: "100%", padding: 14, marginTop: 12 }}
          onClick={handleSubmitSupplyOrder}
          disabled={isSubmittingSupplyOrder}
        >
          {isSubmittingSupplyOrder ? "등록중입니다..." : "소모품 주문 요청"}
        </button>

        <div style={{ marginTop: 28 }}>
          <SectionTitle title="소모품 주문 내역" desc="접수된 소모품 주문의 진행 상태를 확인합니다." />

          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 14, marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr", gap: 8 }}>
              <div style={{ ...input, width: "100%", background: "#fff", color: "#374151" }}>
                {currentWorkStore} 주문내역
              </div>
              <select value={supplyOrderFilter.status} onChange={(e) => setSupplyOrderFilter((prev) => ({ ...prev, status: e.target.value }))} style={{ ...input, width: "100%" }}>
                <option>전체</option>
                <option>미처리</option>
                <option>접수</option>
                <option>본사 확인</option>
                <option>처리완료</option>
              </select>
              <input value={supplyOrderFilter.start} onChange={(e) => setSupplyOrderFilter((prev) => ({ ...prev, start: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
              <input value={supplyOrderFilter.end} onChange={(e) => setSupplyOrderFilter((prev) => ({ ...prev, end: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
            </div>
            <button style={{ ...grayButton, width: "100%", marginTop: 8 }} onClick={() => setSupplyOrderFilter({ store: currentWorkStore, status: "전체", start: "", end: "" })}>검색조건 초기화</button>
          </div>

          {visibleSupplyOrders.length === 0 ? (
            <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 소모품 주문 내역이 없습니다.</div>
          ) : visibleSupplyOrders.map((order) => (
            <div key={order.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <b>{order.store} · {order.items.length}개 품목</b>
                <StatusBadge tone={order.status === "처리완료" ? "blue" : order.status === "본사 확인" ? "yellow" : "red"}>{order.status}</StatusBadge>
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{order.date} · 요청자 {order.writer}</div>
              <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.7, minWidth: 0 }}>
                {order.items.map((item, idx) => (
                  <div key={`${item.name}-${idx}`} style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>- {item.name} / {item.qty}{item.unit && item.unit !== "기타" ? ` (${item.unit})` : ""}{item.link ? ` / ${item.link}` : ""}</div>
                ))}
              </div>
              {order.reason && order.reason !== "-" && <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>요청 사유: {order.reason}</div>}
              {order.adminMemo && <div style={{ marginTop: 10, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 10, fontSize: 13 }}>본사 처리 메모: {order.adminMemo}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ExpiryPage = () => {
    const formatDashDate = (dateText) => dateText?.replace(/-/g, ".") || "-";
    const staffExpiryProductList = expiryProductList.filter((item) => String(item.expiry || "").trim());
    const isWithinStaffExpiryRange = (expiryDate, months) => {
      const daysLeft = getExpiryDaysLeft(expiryDate);
      return daysLeft !== null && daysLeft <= months * 31;
    };
    const getStaffExpiryRangeCount = (months) => staffExpiryProductList.filter((item) => isWithinStaffExpiryRange(item.expiry, months)).length;
    const expiryCategories = ["전체", ...Array.from(new Set(staffExpiryProductList.map((item) => item.category || "기타")))];

    const filteredExpiryProducts = staffExpiryProductList
      .filter((item) => {
        const keyword = expirySearch.trim().toLowerCase();
        const categoryOk = expiryCategory === "전체" || (item.category || "기타") === expiryCategory;
        const rangeOk = expiryRangeFilter === "전체" || isWithinStaffExpiryRange(item.expiry, Number(expiryRangeFilter));
        const keywordOk = !keyword || (
          String(item.name || "").toLowerCase().includes(keyword) ||
          String(item.code || "").toLowerCase().includes(keyword) ||
          String(item.lot || "").toLowerCase().includes(keyword) ||
          String(item.category || "기타").toLowerCase().includes(keyword)
        );
        return categoryOk && rangeOk && keywordOk;
      })
      .sort((a, b) => {
        const aValue = a[expirySort.key] || "";
        const bValue = b[expirySort.key] || "";
        if (aValue === bValue) return 0;
        const result = aValue > bValue ? 1 : -1;
        return expirySort.order === "asc" ? result : -result;
      });

    const expiryCount6 = getStaffExpiryRangeCount(6);
    const expiryCount3 = getStaffExpiryRangeCount(3);
    const expiryCount1 = getStaffExpiryRangeCount(1);
    const urgentCount = expiryRangeFilter === "전체" ? expiryCount6 : filteredExpiryProducts.length;
    const expiryTotalPages = Math.max(1, Math.ceil(filteredExpiryProducts.length / expiryPageSize));
    const safeExpiryPage = Math.min(expiryPage, expiryTotalPages);
    const pagedExpiryProducts = filteredExpiryProducts.slice((safeExpiryPage - 1) * expiryPageSize, safeExpiryPage * expiryPageSize);
    const expiryDebugInfo = {
      totalProducts: productList.length,
      activeProducts: activeProductList.length,
      productsWithExpiry: staffExpiryProductList.length,
      withinSixMonths: expiryCount6,
      currentUser,
      selectedStore,
      activeStore,
      storeFilter: "none",
      filters: {
        category: expiryCategory,
        range: expiryRangeFilter,
        search: expirySearch,
        sort: expirySort,
      },
    };
    if (typeof console !== "undefined") console.log("[redstaff-expiry-debug]", expiryDebugInfo);

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="유통기한 관리" desc="유통기한 관리가 필요한 상품만 확인합니다." />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
          {[
            { label: "6개월 이내", value: "6", count: expiryCount6, tone: "yellow" },
            { label: "3개월 이내", value: "3", count: expiryCount3, tone: "orange" },
            { label: "1개월 이내", value: "1", count: expiryCount1, tone: "red" },
          ].map((item) => (
            <button key={item.value} type="button" onClick={() => { setExpiryRangeFilter(expiryRangeFilter === item.value ? "전체" : item.value); setExpirySearch(""); setExpiryCategory("전체"); setExpiryPage(1); }} onMouseEnter={handleClickableCardMouseEnter} onMouseLeave={handleClickableCardMouseLeave} style={{ ...clickableCardStyle, background: expiryRangeFilter === item.value ? "#fff5f5" : "#fff", border: expiryRangeFilter === item.value ? "2px solid #dc2626" : "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
              <b>{item.label} 확인 필요</b><br />
              <span style={{ fontSize: 28, fontWeight: "bold", color: item.value === "1" ? "#b91c1c" : item.value === "3" ? "#c2410c" : "#92400e" }}>{item.count}건</span>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>클릭하면 해당 상품만 표시됩니다.</div>
            </button>
          ))}
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1fr 1fr 1fr", gap: 8 }}>
            <input
              value={expirySearch}
              onChange={(e) => { setExpirySearch(e.target.value); setExpiryPage(1); }}
              placeholder="상품명, 상품코드, 생산차수 검색"
              style={{ ...input, width: "100%" }}
            />
            <select
              value={expiryCategory}
              onChange={(e) => { setExpiryCategory(e.target.value); setExpiryPage(1); }}
              style={{ ...input, width: "100%" }}
            >
              {expiryCategories.map((category) => <option key={category}>{category}</option>)}
            </select>
            <select
              value={expirySort.key}
              onChange={(e) => setExpirySort((prev) => ({ ...prev, key: e.target.value }))}
              style={{ ...input, width: "100%" }}
            >
              <option value="expiry">유통기한순</option>
              <option value="productionDate">생산일자순</option>
              <option value="name">상품명순</option>
            </select>
            <select
              value={expirySort.order}
              onChange={(e) => setExpirySort((prev) => ({ ...prev, order: e.target.value }))}
              style={{ ...input, width: "100%" }}
            >
              <option value="asc">오름차순</option>
              <option value="desc">내림차순</option>
            </select>
          </div>
          {expiryRangeFilter !== "전체" && (
            <button style={{ ...grayButton, width: "100%", marginTop: 10 }} onClick={() => { setExpiryRangeFilter("전체"); setExpiryPage(1); }}>임박 필터 해제</button>
          )}
        </div>

        {filteredExpiryProducts.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 유통기한 관리 상품이 없습니다.</div>
        ) : (
          <>
            {pagedExpiryProducts.map((item) => {
              const expiryBadge = getExpiryBadgeInfo(item.expiry);
              const visibleExpiryBadge = expiryBadge.label === "기간 종료" ? { ...expiryBadge, label: "유통기한 지남" } : expiryBadge;
              const urgent = visibleExpiryBadge.urgent;
              return (
                <div
                  key={item.id}
                  style={{
                    background: urgent ? "#fff5f5" : "#f9fafb",
                    border: urgent ? "1px solid #fecaca" : "1px solid #e5e7eb",
                    borderLeft: urgent ? "5px solid #dc2626" : "5px solid #111827",
                    padding: 16,
                    borderRadius: 18,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "72px 1fr" : "88px 1fr auto", gap: 12, alignItems: "start" }}>
                    <div style={{ width: isMobile ? 72 : 88, height: isMobile ? 72 : 88, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <span style={{ fontSize: 11, color: "#9ca3af", textAlign: "center" }}>이미지 없음</span>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <b style={{ fontSize: 16 }}>{item.name}</b>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{item.category || "기타"} · {item.code} · {item.lot}</div>
                    </div>
                    <div style={{ gridColumn: isMobile ? "1 / span 2" : "auto", justifySelf: isMobile ? "start" : "end" }}>
                      <StatusBadge tone={visibleExpiryBadge.tone}>{visibleExpiryBadge.label}</StatusBadge>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, 1fr)", gap: 8, marginTop: 12, fontSize: 13 }}>
                    <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>생산일자</small><br /><b>{formatDashDate(item.productionDate)}</b></div>
                    <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>유통기한</small><br /><b style={{ color: urgent ? "#b91c1c" : "#111827" }}>{formatDashDate(item.expiry)}</b></div>
                    <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>판매가</small><br /><b>{formatProductPrice(item.salePrice, true)}</b></div>
                    <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>CT입수량</small><br /><b>{item.ctQuantity || "-"}</b></div>
                    <div style={{ background: "#fff", borderRadius: 12, padding: 10, gridColumn: isMobile ? "1 / span 2" : "auto" }}><small>메모</small><br /><b>{item.note}</b></div>
                  </div>
                </div>
              );
            })}
            {expiryTotalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                <button style={safeExpiryPage === 1 ? grayButton : darkButton} disabled={safeExpiryPage === 1} onClick={() => setExpiryPage((prev) => Math.max(1, prev - 1))}>이전</button>
                <span style={{ padding: "10px 12px", fontSize: 13, color: "#374151" }}>{safeExpiryPage} / {expiryTotalPages}</span>
                <button style={safeExpiryPage === expiryTotalPages ? grayButton : darkButton} disabled={safeExpiryPage === expiryTotalPages} onClick={() => setExpiryPage((prev) => Math.min(expiryTotalPages, prev + 1))}>다음</button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const AttendancePage = () => {
    const myAttendance = getFilteredAttendance(
      attendance.filter((a) => a.name === currentUser),
      staffAttendanceFilter
    );

    const getAttendanceDateLabel = (row) => {
      if (!row.endDate || row.endDate === "-" || row.endDate === row.date) return row.date;
      return `${row.date} 출근 / ${row.endDate} 퇴근`;
    };

    // 모바일 카드형 UI
    if (isMobile) {
      return (
        <div style={{ ...card, padding: 16 }}>
          <SectionTitle title="내 근무 기록" desc="출근일 기준으로 내 근무 기록을 조회합니다." />

          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 14, marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
              <select
                value={staffAttendanceFilter.store}
                onChange={(e) => setStaffAttendanceFilter((prev) => ({ ...prev, store: e.target.value }))}
                style={{ ...input, width: "100%" }}
              >
                <option>전체 매장</option>
                {getAllowedStores().map((s) => <option key={s}>{s}</option>)}
              </select>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <input
                  value={staffAttendanceFilter.start}
                  onChange={(e) => setStaffAttendanceFilter((prev) => ({ ...prev, start: e.target.value }))}
                  type="date"
                  style={{ ...input, width: "100%" }}
                />
                <input
                  value={staffAttendanceFilter.end}
                  onChange={(e) => setStaffAttendanceFilter((prev) => ({ ...prev, end: e.target.value }))}
                  type="date"
                  style={{ ...input, width: "100%" }}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <button style={darkButton} onClick={handleSearchStaffAttendance}>검색</button>
                <button style={grayButton} onClick={handleResetStaffAttendance}>초기화</button>
              </div>
            </div>
          </div>

          {myAttendance.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280", background: "#f9fafb", borderRadius: 18 }}>
              조회된 근무 기록이 없습니다.
            </div>
          ) : myAttendance.map((row) => (
            <div
              key={row.id}
              style={{
                background: "#f9fafb",
                borderRadius: 18,
                padding: 16,
                marginBottom: 12,
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <b>{row.store}</b>
                <StatusBadge tone="blue">{row.status}</StatusBadge>
              </div>

              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
                기준일: {getAttendanceDateLabel(row)}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 14 }}>
                <div>출근: {row.start}</div>
                <div>퇴근: {row.end}</div>
                <div>휴게: {row.breakStart} ~ {row.breakEnd}</div>
                <div>휴게시간: {formatDuration(getBreakMinutes(row))}</div>
              </div>

              <div style={{ marginTop: 10 }}>
                <span style={getWorkTimeStyle(row)}>
                  총 근무시간 {formatDuration(getWorkMinutes(row))}
                </span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // PC 기존 테이블 유지
    return (
      <div style={{ ...card, padding: 24 }}>
        <SectionTitle title="내 근무 기록" desc="내 출근/퇴근 기록을 날짜와 매장 기준으로 확인합니다." />

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <select
              value={staffAttendanceFilter.store}
              onChange={(e) => setStaffAttendanceFilter((prev) => ({ ...prev, store: e.target.value }))}
              style={{ ...input, width: "100%" }}
            >
              <option>전체 매장</option>
              {getAllowedStores().map((s) => <option key={s}>{s}</option>)}
            </select>
            <input
              value={staffAttendanceFilter.start}
              onChange={(e) => setStaffAttendanceFilter((prev) => ({ ...prev, start: e.target.value }))}
              type="date"
              style={{ ...input, width: "100%" }}
            />
            <input
              value={staffAttendanceFilter.end}
              onChange={(e) => setStaffAttendanceFilter((prev) => ({ ...prev, end: e.target.value }))}
              type="date"
              style={{ ...input, width: "100%" }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
            <button style={darkButton} onClick={handleSearchStaffAttendance}>검색</button>
            <button style={grayButton} onClick={handleResetStaffAttendance}>초기화</button>
          </div>
        </div>
        <div style={{ ...card, padding: 24 }}>
          <SectionTitle title="내 근무 기록" desc="내 출근/퇴근 기록을 날짜와 매장 기준으로 확인합니다." />

          <div style={{ ...tableScroll }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th>출근일</th>
                  <th>퇴근일</th>
                  <th>근무 매장</th>
                  <th>출근</th>
                  <th>휴게시작</th>
                  <th>휴게종료</th>
                  <th>휴게시간</th>
                  <th>퇴근</th>
                  <th>총 근무시간</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {myAttendance.length === 0 ? (
                  <tr><td colSpan="10" style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>조회된 근무 기록이 없습니다.</td></tr>
                ) : myAttendance.map((row) => (
                  <tr key={row.id} style={{ textAlign: "center", borderTop: "1px solid #e5e7eb" }}>
                    <td>{row.date}</td>
                    <td>{row.endDate || "-"}</td>
                    <td>{row.store}</td>
                    <td>{row.start}</td>
                    <td>{row.breakStart}</td>
                    <td>{row.breakEnd}</td>
                    <td>{formatDuration(getBreakMinutes(row))}</td>
                    <td>{row.end}</td>
                    <td><span style={getWorkTimeStyle(row)}>{formatDuration(getWorkMinutes(row))}</span></td>
                    <td><StatusBadge tone="blue">{row.status}</StatusBadge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const StaffDashboard = () => {
    const pendingNoticeCount = getUncheckedVisibleStaffNoticeCount({
      categories: ["이벤트", "전달사항"],
      storeName: activeStore || selectedStore,
      forceWorking: isWorking,
      debugContext: "대시보드 공지 카드",
    });
    const pendingNoticeTaskCount = getIncompleteTaskCount();
    const pendingHandoverCount = getPendingHandovers().length;
    const startPromptBaseCounts = getUncheckedStaffStartItems(activeStore || selectedStore, { noticeCount: pendingNoticeCount });
    const startPromptCounts = {
      ...startPromptBaseCounts,
      noticeCount: pendingNoticeCount,
      total: pendingNoticeCount + startPromptBaseCounts.handoverCount,
    };
    const pendingWorkRequestCount = getVisibleWorkRequestsForStaff().filter((request) => request.status !== "처리완료").length;
    const pendingSupplyOrderCount = getFilteredSupplyOrders().filter((order) => order.status !== "처리완료").length;

    const today = new Date();
    const sixMonthsLater = new Date(today);
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
    const urgentExpiryCount = expiryProductList.filter((item) => new Date(item.expiry) <= sixMonthsLater).length;

    const dashboardCards = [
      { title: "공지사항", desc: "미확인 이벤트/전달사항", value: `${pendingNoticeCount}건`, menu: "공지사항", tone: "red", noticeFilter: "전체" },
      { title: "공지 업무 요청", desc: "공지 말머리 업무 요청", value: `${pendingNoticeTaskCount}건`, menu: "공지사항", tone: "red", noticeFilter: "업무 요청" },
      { title: "인수인계", desc: "확인 필요한 인수인계", value: `${pendingHandoverCount}건`, menu: "인수인계", tone: "yellow", handoverStatus: "확인 필요" },
      { title: "업무 요청", desc: "처리완료 전 요청", value: `${pendingWorkRequestCount}건`, menu: "업무 요청", tone: "red", workRequestStatus: "미처리" },
      { title: "소모품 주문", desc: "처리완료 전 주문", value: `${pendingSupplyOrderCount}건`, menu: "소모품 주문", tone: "blue", supplyOrderStatus: "미처리" },
      { title: "유통기한 관리", desc: "6개월 이내 확인 필요", value: `${urgentExpiryCount}건`, menu: "유통기한 관리", tone: "red", expiryRange: "6" },
    ];
    const handleStaffDashboardCardClick = (item) => {
      changeStaffMenu(item.menu, {
        noticeFilter: item.noticeFilter,
        handoverStatus: item.handoverStatus,
        workRequestStatus: item.workRequestStatus,
        supplyOrderStatus: item.supplyOrderStatus,
        expiryRange: item.expiryRange,
      });
    };

    const quickActions = [
      { label: "업무 요청 등록", menu: "업무 요청" },
      { label: "소모품 주문", menu: "소모품 주문" },
      { label: "인수인계 작성", menu: "인수인계" },
    ];

    const currentRecord = getOpenAttendanceRecordForUser(attendance, currentUser);
    const lastRecord = currentRecord || attendance.filter((row) => isAttendanceForUser(row, currentUser)).slice(-1)[0];

    return (
      <div>
        <section style={{ ...card, display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 16, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 6, fontWeight: "bold", lineHeight: 1.5 }}>
              사용 중 문의사항이나 수정 요청이 필요한 경우 본사 노효자 매니저에게 연락해주세요. (내선 204번 / 카카오톡 ID: redmanager)
            </div>
            <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 22 }}>오늘 근무 매장 선택</h2>
            <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 0 }}>로그인 후 오늘 실제 근무하는 매장을 선택합니다.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "150px repeat(4, auto)", gap: 10 }}>
            <select
              value={selectedStore}
              onChange={(e) => handleStoreSelect(e.target.value)}
              style={{ ...input, fontWeight: "bold", gridColumn: isMobile ? "1 / span 2" : "auto" }}
            >
              {getAllowedStores().map((s) => <option key={s}>{s}</option>)}
            </select>
            <button style={redButton} onClick={handleWorkStart}>근무 시작</button>
            <button style={orangeButton} onClick={handleBreakStart}>휴게 시작</button>
            <button style={blueButton} onClick={handleBreakEnd}>휴게 종료</button>
            <button style={darkButton} onClick={handleWorkEnd}>근무 종료</button>
          </div>
        </section>

        {startPromptCounts.total > 0 && (
          <section style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 18, padding: isMobile ? 14 : 16, marginBottom: 18, display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ color: "#991b1b", fontWeight: "bold", lineHeight: 1.6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#dc2626", display: "inline-block", flex: "0 0 auto" }} />
              <span>미확인 공지사항 {startPromptCounts.noticeCount}건 · 업무인수인계 {startPromptCounts.handoverCount}건이 있습니다. 업무 시작 전 확인해 주세요.</span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={{ ...redButton, padding: "9px 12px" }} onClick={() => changeStaffMenu("공지사항", { noticeFilter: "전체" })}>공지 확인</button>
              <button style={{ ...orangeButton, padding: "9px 12px" }} onClick={() => changeStaffMenu("인수인계", { handoverStatus: "확인 필요" })}>인수인계 확인</button>
            </div>
          </section>
        )}

        <section style={{ background: "#111827", color: "#fff", borderRadius: 24, padding: isMobile ? 18 : 24, marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: isMobile ? 24 : 30, fontWeight: "bold", color: isWorking ? "#fecaca" : workEndTime ? "#bfdbfe" : "#fff" }}>
              {isWorking ? `오늘 ${activeStore} 근무중입니다.` : workEndTime ? `오늘 ${activeStore} 근무가 종료되었습니다.` : "현재 근무 시작 전입니다."}
            </h2>
            <span style={{ background: isWorking ? "#dc2626" : workEndTime ? "#2563eb" : "#6b7280", color: "#fff", padding: "8px 14px", borderRadius: 999, fontSize: 13, fontWeight: "bold" }}>
              {currentRecord?.status || (workEndTime ? "퇴근완료" : "근무 시작 전")}
            </span>
          </div>
          <div style={{ background: "#fff", color: "#111827", borderRadius: 18, padding: 18, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4,1fr)", gap: 14 }}>
            <div><small>선택 매장</small><br /><b>{activeStore}</b></div>
            <div><small>근무 시작 / 종료</small><br /><b>{workStartTime || "-"} / {workEndTime || "-"}</b></div>
            <div><small>휴게 시작 / 종료</small><br /><b>{lastRecord?.breakStart || "-"} / {lastRecord?.breakEnd || "-"}</b></div>
            <div><small>오늘 확인할 항목</small><br /><b>공지 {pendingNoticeCount}건 · 공지업무 {pendingNoticeTaskCount}건 · 인수인계 {pendingHandoverCount}건 · 업무 {pendingWorkRequestCount}건</b></div>
          </div>
        </section>

        <section style={{ ...card, padding: isMobile ? 18 : 24, marginBottom: 18 }}>
          <h2 style={{ margin: "0 0 6px", fontSize: isMobile ? 24 : 28 }}>오늘 확인해야 할 것</h2>
          <p style={{ margin: "0 0 16px", color: "#6b7280", fontSize: 14 }}>카드를 누르면 해당 메뉴로 이동합니다.</p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
            {dashboardCards.map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => handleStaffDashboardCardClick(item)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 10px 22px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                style={{
                  textAlign: "left",
                  background: item.tone === "red" ? "#fff5f5" : item.tone === "yellow" ? "#fffbeb" : "#eff6ff",
                  border: item.tone === "red" ? "1px solid #fecaca" : item.tone === "yellow" ? "1px solid #fde68a" : "1px solid #bfdbfe",
                  borderRadius: 18,
                  padding: 18,
                  cursor: "pointer",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
              >
                <div style={{ color: "#6b7280", fontSize: 13 }}>{item.title}</div>
                <div style={{ fontSize: 30, fontWeight: "bold", marginTop: 8 }}>{item.value}</div>
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>{item.desc}</div>
              </button>
            ))}
          </div>
        </section>

        <section style={{ ...card, padding: isMobile ? 18 : 24 }}>
          <h2 style={{ margin: "0 0 14px", fontSize: isMobile ? 24 : 28 }}>빠른 작업</h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10 }}>
            {quickActions.map((action) => (
              <button key={action.label} style={{ ...darkButton, padding: 14 }} onClick={() => changeStaffMenu(action.menu)}>{action.label}</button>
            ))}
          </div>
        </section>
      </div>
    );
  };


  const StoreInfoPage = () => {
    const [staffStoreKeyword, setStaffStoreKeyword] = useState("");
    const [openStaffStoreIds, setOpenStaffStoreIds] = useState({});
    const toggleStaffStoreOpen = (storeId) => {
      setOpenStaffStoreIds((prev) => ({ ...prev, [storeId]: !prev[storeId] }));
    };
    const visibleStores = storeRecords
      .filter((store) => store.status === "활성화" && !isSystemStoreName(store.name))
      .filter((store) => {
        const keyword = staffStoreKeyword.trim();
        if (!keyword) return true;
        return String(store.name || "").includes(keyword);
      });

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="매장정보" desc="근무 매장의 연락처와 기본 정보를 확인합니다. 온라인몰, 본사 등 시스템 항목은 제외됩니다." />
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 14, marginBottom: 14 }}>
          <input
            value={staffStoreKeyword}
            onChange={(e) => setStaffStoreKeyword(e.target.value)}
            placeholder="매장명으로 검색"
            style={{ ...input, width: "100%" }}
          />
        </div>
        {visibleStores.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 매장정보가 없습니다.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            {visibleStores.map((store) => {
              const opened = Boolean(openStaffStoreIds[store.id]);
              return (
                <div key={store.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                    <div>
                      <h3 style={{ margin: 0 }}>{store.name}</h3>
                      <div style={{ fontSize: 14, lineHeight: 1.7, marginTop: 12 }}>
                        <div><b>연락처</b><br />{store.contact || "-"}</div>
                        <div style={{ marginTop: 8 }}><b>주소</b><br />{store.address || "-"}</div>
                      </div>
                    </div>
                    <button style={{ ...darkButton, padding: "8px 10px", borderRadius: 10, whiteSpace: "nowrap" }} onClick={() => toggleStaffStoreOpen(store.id)}>
                      {opened ? "접기" : "펼쳐보기"}
                    </button>
                  </div>
                  {opened && (
                    <div style={{ display: "grid", gap: 8, fontSize: 14, lineHeight: 1.6, marginTop: 14, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12 }}>
                      <div><b>내선번호</b><br />{store.extension || "-"}</div>
                      <div><b>대표자</b><br />{store.owner || "-"}</div>
                      <div><b>사업자등록번호</b><br />{store.businessNumber || "-"}</div>
                      <div><b>은행/계좌번호/예금주</b><br />{store.bank1 || "-"}{store.bank2 ? <><br />{store.bank2}</> : null}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const MyPage = () => (
    <div style={card}>
      <SectionTitle title="마이페이지" desc="내 연락처, 주소, 비밀번호를 관리합니다." />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
          <small>이름</small><br /><b>{currentEmployee?.name || currentUser}</b>
        </div>
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
          <small>권한</small><br /><b>{currentEmployee?.role || "-"}</b>
        </div>
        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 14, padding: 14 }}>
          <small>입사일</small><br /><b>{currentEmployee?.hireDate || "-"}</b>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
        <input ref={myContactRef} defaultValue={currentEmployee?.contact || ""} placeholder="연락처" style={input} />
        <input ref={myAddressRef} defaultValue={currentEmployee?.address || ""} placeholder="주소" style={input} />
      </div>
      <button
        style={{ ...(isSavingMyPage ? grayButton : redButton), marginTop: 12 }}
        onClick={handleSaveMyInfo}
        disabled={isSavingMyPage}
      >
        {isSavingMyPage ? "저장중입니다..." : "정보 저장"}
      </button>
      <hr style={{ margin: "24px 0", border: 0, borderTop: "1px solid #e5e7eb" }} />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
        <input ref={currentPwRef} type="password" placeholder="현재 비밀번호" style={input} />
        <input ref={newPwRef} type="password" placeholder="새 비밀번호" style={input} />
        <input ref={newPwConfirmRef} type="password" placeholder="새 비밀번호 확인" style={input} />
      </div>
      <button
        style={{ ...(isSavingMyPage ? grayButton : darkButton), marginTop: 12 }}
        onClick={handleChangePassword}
        disabled={isSavingMyPage}
      >
        {isSavingMyPage ? "저장중입니다..." : "비밀번호 변경"}
      </button>
    </div>
  );

  const AdminAttendancePage = () => {
    const filteredRows = getFilteredAttendance(attendance, adminAttendanceFilter).filter((row) => !showUnclosedOnly || isOpenAttendanceRecord(row));
    const adminAttendanceTotalPages = Math.max(1, Math.ceil(filteredRows.length / adminAttendancePageSize));
    const safeAdminAttendancePage = Math.min(adminAttendancePage, adminAttendanceTotalPages);
    const pagedRows = filteredRows.slice((safeAdminAttendancePage - 1) * adminAttendancePageSize, safeAdminAttendancePage * adminAttendancePageSize);
    const adminAttendancePageNumbers = getManagementPageNumbers(adminAttendanceTotalPages, safeAdminAttendancePage);
    const workingRows = attendance.filter((row) => isOpenAttendanceRecord(row));
    const doneRows = filteredRows.filter((row) => row.status === "퇴근완료");
    const totalMinutes = doneRows.reduce((sum, row) => sum + getWorkMinutes(row), 0);
    const editPreviewRecord = getAttendanceEditPreviewRecord();

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="직원 근무일지" desc="전체 직원의 출퇴근 기록을 조회하고, 관리자가 누락 기록을 추가하거나 잘못 입력된 시간을 수정할 수 있습니다." />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 16, padding: 14 }}><small>조회 결과</small><br /><b style={{ fontSize: 26 }}>{filteredRows.length}건</b></div>
          <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 16, padding: 14 }}><small>현재 근무중</small><br /><b style={{ fontSize: 26 }}>{workingRows.length}건</b></div>
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 16, padding: 14 }}><small>퇴근완료</small><br /><b style={{ fontSize: 26 }}>{doneRows.length}건</b></div>
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 16, padding: 14 }}><small>총 근무시간</small><br /><b style={{ fontSize: 26 }}>{formatDuration(totalMinutes)}</b></div>
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr 1fr", gap: 8 }}>
            <select ref={adminAttendanceStoreRef} defaultValue={adminAttendanceFilter.store} style={{ ...input, width: "100%" }}>
              <option>전체 매장</option>
              {stores.map((store) => <option key={store}>{store}</option>)}
            </select>
            <input ref={adminAttendanceNameRef} defaultValue={adminAttendanceFilter.name} placeholder="직원명 검색" style={{ ...input, width: "100%" }} />
            <select ref={adminAttendanceStatusRef} defaultValue={adminAttendanceFilter.status || "전체"} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              <option>근무중</option>
              <option>휴게중</option>
              <option>퇴근완료</option>
            </select>
            <input ref={adminAttendanceStartRef} defaultValue={adminAttendanceFilter.start} type="date" style={{ ...input, width: "100%" }} />
            <input ref={adminAttendanceEndRef} defaultValue={adminAttendanceFilter.end} type="date" style={{ ...input, width: "100%" }} />
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 13 }}>
            <input type="checkbox" checked={showUnclosedOnly} onChange={(e) => { setShowUnclosedOnly(e.target.checked); setAdminAttendancePage(1); }} /> 퇴근 미처리만 보기
          </label>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 8, marginTop: 10 }}>
            <button style={darkButton} onClick={handleSearchAdminAttendance}>검색</button>
            <button style={grayButton} onClick={handleResetAdminAttendance}>초기화</button>
            <button style={blueButton} onClick={downloadAdminAttendanceList}>근무일지 CSV 다운로드</button>
          </div>
        </div>

        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>근무 기록 추가</h3>
          <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginBottom: 12 }}>직원이 출근/퇴근을 깜빡했을 때 관리자만 수동으로 추가합니다.</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr", gap: 8 }}>
            <select ref={newAttendanceNameRef} style={{ ...input, width: "100%" }}>
              <option value="">직원 선택</option>
              {employees.map((employee) => <option key={employee.id} value={employee.name}>{employee.name}</option>)}
            </select>
            <select ref={newAttendanceStoreRef} style={{ ...input, width: "100%" }}>
              <option value="">매장 선택</option>
              {stores.map((store) => <option key={store}>{store}</option>)}
            </select>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#374151" }}>출근일<input ref={newAttendanceDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#374151" }}>퇴근일<input ref={newAttendanceEndDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#374151" }}>출근<input ref={newAttendanceStartRef} type="time" style={{ ...input, width: "100%" }} /></label>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#374151" }}>휴게시작<input ref={newAttendanceBreakStartRef} type="time" style={{ ...input, width: "100%" }} /></label>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#374151" }}>휴게종료<input ref={newAttendanceBreakEndRef} type="time" style={{ ...input, width: "100%" }} /></label>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#374151" }}>퇴근<input ref={newAttendanceEndRef} type="time" style={{ ...input, width: "100%" }} /></label>
            <select ref={newAttendanceStatusRef} defaultValue="퇴근완료" style={{ ...input, width: "100%" }}>
              <option>퇴근완료</option>
              <option>근무중</option>
              <option>휴게중</option>
            </select>
            <button style={{ ...redButton, gridColumn: isMobile ? "auto" : "span 3" }} onClick={handleAddAttendanceRecord}>근무 기록 추가</button>
          </div>
        </div>

        {filteredRows.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 근무 기록이 없습니다.</div>
        ) : isMobile ? (
          <div style={{ display: "grid", gap: 12 }}>
            {pagedRows.map((row) => (
              <div key={row.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                  <b>{row.name} · {row.store}</b>
                  <StatusBadge tone={row.status === "퇴근완료" ? "blue" : "red"}>{row.status}</StatusBadge>
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>{row.date} 출근 / {row.endDate || "-"} 퇴근</div>
                <div style={{ marginTop: 8, lineHeight: 1.7, fontSize: 13 }}>
                  출근 {row.start} · 휴게 {row.breakStart || "-"} ~ {row.breakEnd || "-"} · 퇴근 {row.end}<br />
                  휴게시간 {formatDuration(getBreakMinutes(row))} · 총근무 {formatDuration(getWorkMinutes(row))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
                  <button style={{ ...darkButton, width: "100%" }} onClick={() => handleStartEditAttendance(row)}>수정</button>
                  <button style={{ ...redButton, width: "100%" }} onClick={() => handleDeleteAttendanceRecord(row.id)}>삭제</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={tableScroll}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
              <thead>
                <tr style={{ background: "#111827", color: "#fff" }}>
                  <th style={{ padding: 10 }}>출근일</th>
                  <th style={{ padding: 10 }}>퇴근일</th>
                  <th style={{ padding: 10 }}>직원명</th>
                  <th style={{ padding: 10 }}>매장</th>
                  <th style={{ padding: 10 }}>출근</th>
                  <th style={{ padding: 10 }}>휴게시작</th>
                  <th style={{ padding: 10 }}>휴게종료</th>
                  <th style={{ padding: 10 }}>휴게시간</th>
                  <th style={{ padding: 10 }}>퇴근</th>
                  <th style={{ padding: 10 }}>총근무시간</th>
                  <th style={{ padding: 10 }}>상태</th>
                  <th style={{ padding: 10 }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((row) => (
                  <tr key={row.id} style={{ textAlign: "center", borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{row.date}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{row.endDate || "-"}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{row.name}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{row.store}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{row.start}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{row.breakStart}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{row.breakEnd}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{formatDuration(getBreakMinutes(row))}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{row.end}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}><span style={getWorkTimeStyle(row)}>{formatDuration(getWorkMinutes(row))}</span></td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}><StatusBadge tone={row.status === "퇴근완료" ? "blue" : "red"}>{row.status}</StatusBadge></td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>
                      <button style={{ ...darkButton, padding: "8px 10px", marginRight: 4 }} onClick={() => handleStartEditAttendance(row)}>수정</button>
                      <button style={{ ...redButton, padding: "8px 10px" }} onClick={() => handleDeleteAttendanceRecord(row.id)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filteredRows.length > 0 && adminAttendanceTotalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
            <button style={safeAdminAttendancePage === 1 ? grayButton : darkButton} disabled={safeAdminAttendancePage === 1} onClick={() => setAdminAttendancePage((prev) => Math.max(1, prev - 1))}>이전</button>
            {adminAttendancePageNumbers.map((page) => <button key={page} style={page === safeAdminAttendancePage ? redButton : grayButton} onClick={() => setAdminAttendancePage(page)}>{page}</button>)}
            <button style={safeAdminAttendancePage === adminAttendanceTotalPages ? grayButton : darkButton} disabled={safeAdminAttendancePage === adminAttendanceTotalPages} onClick={() => setAdminAttendancePage((prev) => Math.min(adminAttendanceTotalPages, prev + 1))}>다음</button>
          </div>
        )}
        {attendanceEditDraft && editPreviewRecord && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div style={{ background: "#fff", borderRadius: 20, padding: isMobile ? 16 : 22, width: "min(760px, 100%)", maxHeight: "90vh", overflow: "auto", boxShadow: "0 24px 60px rgba(0,0,0,0.22)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ margin: 0 }}>근무일지 수정</h3>
                <button style={grayButton} onClick={handleCancelAttendanceEdit}>닫기</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 10 }}>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>출근일<input type="date" value={attendanceEditDraft.date} onChange={(e) => handleAttendanceEditDraftChange("date", e.target.value)} style={{ ...input, width: "100%" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>퇴근일<input type="date" value={attendanceEditDraft.endDate} onChange={(e) => handleAttendanceEditDraftChange("endDate", e.target.value)} disabled={attendanceEditDraft.status !== "퇴근완료"} style={{ ...input, width: "100%", background: attendanceEditDraft.status !== "퇴근완료" ? "#f3f4f6" : "#fff" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>직원<select value={attendanceEditDraft.name} onChange={(e) => handleAttendanceEditDraftChange("name", e.target.value)} style={{ ...input, width: "100%" }}>{employees.map((employee) => <option key={employee.id} value={employee.name}>{employee.name}</option>)}</select></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>매장<select value={attendanceEditDraft.store} onChange={(e) => handleAttendanceEditDraftChange("store", e.target.value)} style={{ ...input, width: "100%" }}>{stores.map((store) => <option key={store}>{store}</option>)}</select></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>출근시간<input type="time" value={attendanceEditDraft.start} onChange={(e) => handleAttendanceEditDraftChange("start", e.target.value)} style={{ ...input, width: "100%" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>퇴근시간<input type="time" value={attendanceEditDraft.end} onChange={(e) => handleAttendanceEditDraftChange("end", e.target.value)} disabled={attendanceEditDraft.status !== "퇴근완료"} style={{ ...input, width: "100%", background: attendanceEditDraft.status !== "퇴근완료" ? "#f3f4f6" : "#fff" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>휴게시작<input type="time" value={attendanceEditDraft.breakStart} onChange={(e) => handleAttendanceEditDraftChange("breakStart", e.target.value)} style={{ ...input, width: "100%" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>휴게종료<input type="time" value={attendanceEditDraft.breakEnd} onChange={(e) => handleAttendanceEditDraftChange("breakEnd", e.target.value)} style={{ ...input, width: "100%" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>상태<select value={attendanceEditDraft.status} onChange={(e) => handleAttendanceEditDraftChange("status", e.target.value)} style={{ ...input, width: "100%" }}><option>근무중</option><option>휴게중</option><option>퇴근완료</option></select></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold", gridColumn: isMobile ? "auto" : "1 / span 2" }}>메모<textarea value={attendanceEditDraft.memo} onChange={(e) => handleAttendanceEditDraftChange("memo", e.target.value)} style={{ ...input, width: "100%", minHeight: 72 }} /></label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, marginTop: 14 }}>
                <div><small>휴게시간</small><br /><b>{formatDuration(getBreakMinutes(editPreviewRecord))}</b></div>
                <div><small>총근무시간</small><br /><b>{formatDuration(getWorkMinutes(editPreviewRecord))}</b></div>
                <div><small>저장될 퇴근일</small><br /><b>{editPreviewRecord.endDate || "-"}</b></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8, marginTop: 14 }}>
                <button style={grayButton} onClick={handleCancelAttendanceEdit}>취소</button>
                <button style={redButton} onClick={handleSaveAttendanceEdit}>저장</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const AdminDashboard = () => {
    const today = formatDateOnly();
    const workingNow = attendance.filter((a) => isOpenAttendanceRecord(a));
    const todayAttendance = attendance.filter((a) => a.date === today || a.endDate === today);
    const openWorkRequests = getAdminIncompleteWorkRequests().length;
    const openSupplyOrders = supplyOrders.filter((order) => order.status !== "처리완료").length;
    const pendingHandovers = handovers.filter((item) => item.status === "확인 필요").length;
    const visibleNotices = getAdminVisibleNotices().length;
    const activeStores = storeRecords.filter((store) => store.status === "활성화").length;
    const activeEmployees = employees.filter((employee) => employee.status === "활성화").length;
    const todaySalesRows = salesRecords.filter((record) => record.date === new Date().toISOString().slice(0, 10));
    const recentSalesRows = todaySalesRows.length > 0 ? todaySalesRows : salesRecords.slice(0, 20);
    const dashboardSalesTotal = recentSalesRows.reduce((sum, record) => sum + toNumber(record.netSales || record.grossSales), 0);
    const salesIssueCount = salesRecords.filter((record) => record.status === "누락" || record.status === "수집실패").length;
    const urgentExpiryCount = getExpiryRangeCount(productList, 6);

    const dashboardItems = [
      { title: "금일/최근 매출", value: formatSalesWon(dashboardSalesTotal), desc: salesIssueCount > 0 ? `수집 확인 필요 ${salesIssueCount}건` : "매출조회 화면으로 이동", menu: "매출조회", tone: salesIssueCount > 0 ? "red" : "blue" },
      { title: "임대료 납부 임박", value: `${rentDueSoonStores.length}건`, desc: "5일 이내 납부 예정 매장", menu: "매장 관리", tone: "red" },
      { title: "근무중", value: `${workingNow.length}건`, desc: "현재 퇴근 전 근무 기록", menu: "직원 근무일지", tone: "blue" },
      { title: "미처리 업무 요청", value: `${openWorkRequests}건`, desc: "접수/본사 확인 상태", menu: "업무 요청 관리", tone: "red" },
      { title: "소모품 주문", value: `${openSupplyOrders}건`, desc: "처리완료 전 주문", menu: "소모품 주문 관리", tone: "yellow" },
      { title: "인수인계", value: `${pendingHandovers}건`, desc: "확인 필요 상태", menu: "인수인계 관리", tone: "red" },
      { title: "노출 공지", value: `${visibleNotices}건`, desc: "현재 직원 노출 대상", menu: "공지 작성 / 관리", tone: "blue" },
      { title: "유통기한 임박", value: `${urgentExpiryCount}건`, desc: "6개월 이내 확인 필요", menu: "상품 DB 관리", tone: "red" },
      { title: "활성 직원", value: `${activeEmployees}명`, desc: "로그인 가능 직원", menu: "직원 계정 관리", tone: "blue" },
      { title: "활성 매장", value: `${activeStores}개`, desc: "직원 화면 노출 매장", menu: "매장 관리", tone: "blue" },
    ];

    const recentWorkRequests = [...workRequests].slice(0, 3);
    const recentSupplyOrders = [...supplyOrders].slice(0, 3);
    const recentHandovers = [...handovers].slice(0, 3);
    const handleAdminDashboardCardClick = (item) => {
      if (item.menu === "직원 근무일지") {
        setAdminAttendanceFilter({ store: "전체 매장", start: "", end: "", name: "", status: "전체" });
        setAdminAttendancePage(1);
        setShowUnclosedOnly(item.title === "근무중");
      }
      if (item.menu === "업무 요청 관리") {
        setAdminWorkRequestFilter({ type: "전체", status: "미처리", store: "전체 매장", start: "", end: "", keyword: "" });
        setOpenAdminWorkRequestId(null);
      }
      if (item.menu === "소모품 주문 관리") {
        setAdminSupplyOrderFilter({ store: "전체 매장", status: "미처리", start: "", end: "", keyword: "" });
        setOpenAdminSupplyOrderId(null);
      }
      if (item.menu === "인수인계 관리") {
        setAdminHandoverFilter({ store: "전체 매장", status: "확인 필요", start: "", end: "", keyword: "", importantOnly: false });
        setOpenAdminHandoverId(null);
      }
      if (item.menu === "공지 작성 / 관리") {
        setAdminNoticeFilter({ category: "전체", target: "전체", keyword: "", visibleOnly: item.title === "노출 공지" });
        setOpenAdminNoticeId(null);
      }
      if (item.menu === "상품 DB 관리") {
        if (productDbSearchRef.current) productDbSearchRef.current.value = "";
        setProductDbSearch("");
        setProductDbCategoryFilter("전체");
        setProductDbExpiryFilter("6");
        setProductDbPage(1);
      }
      changeAdminMenu(item.menu);
    };

    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
          <SectionTitle title="관리자 대시보드" desc="직원 운영 현황, 미처리 항목, 주요 데이터를 한눈에 확인합니다." />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12 }}>
            {dashboardItems.map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => handleAdminDashboardCardClick(item)}
                onMouseEnter={handleClickableCardMouseEnter}
                onMouseLeave={handleClickableCardMouseLeave}
                style={{
                  ...clickableCardStyle,
                  background: item.tone === "red" ? "#fff5f5" : item.tone === "yellow" ? "#fffbeb" : "#eff6ff",
                  border: item.tone === "red" ? "1px solid #fecaca" : item.tone === "yellow" ? "1px solid #fde68a" : "1px solid #bfdbfe",
                  borderRadius: 18,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 13, color: "#6b7280" }}>{item.title}</div>
                <div style={{ fontSize: isMobile ? 24 : 30, fontWeight: "bold", marginTop: 6 }}>{item.value}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>{item.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ ...card, padding: 18 }}>
          <h3 style={{ marginTop: 0 }}>임대료 납부 5일 이내 매장</h3>
          {rentDueSoonStores.length === 0 ? (
            <div style={{ color: "#6b7280" }}>5일 이내 임대료 납부 예정 매장이 없습니다.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10 }}>
              {rentDueSoonStores.map((store) => (
                <button key={store.id} onClick={() => changeAdminMenu("매장 관리")} style={{ textAlign: "left", background: "#fff5f5", border: "1px solid #fecaca", borderLeft: "5px solid #dc2626", borderRadius: 14, padding: 12, cursor: "pointer" }}>
                  <b>{store.name}</b><br />
                  <small style={{ color: "#b91c1c", fontWeight: "bold" }}>납부일 {store.rentPayDay}일 · {store.rentDaysLeft === 0 ? "오늘 납부" : `${store.rentDaysLeft}일 남음`}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14 }}>
          <div style={{ ...card, padding: 18 }}>
            <h3 style={{ marginTop: 0 }}>최근 업무 요청</h3>
            {recentWorkRequests.map((item) => (
              <div key={item.id} style={{ borderTop: "1px solid #e5e7eb", padding: "10px 0", fontSize: 13 }}>
                <b>{item.type} · {item.receiveStore}</b><br />
                <span style={{ color: "#6b7280" }}>{item.date} · {item.status}</span>
              </div>
            ))}
            <button style={{ ...darkButton, width: "100%", marginTop: 8 }} onClick={() => changeAdminMenu("업무 요청 관리")}>업무 요청 관리로 이동</button>
          </div>

          <div style={{ ...card, padding: 18 }}>
            <h3 style={{ marginTop: 0 }}>최근 소모품 주문</h3>
            {recentSupplyOrders.map((item) => (
              <div key={item.id} style={{ borderTop: "1px solid #e5e7eb", padding: "10px 0", fontSize: 13 }}>
                <b>{item.store} · {item.items.length}개 품목</b><br />
                <span style={{ color: "#6b7280" }}>{item.date} · {item.status}</span>
              </div>
            ))}
            <button style={{ ...darkButton, width: "100%", marginTop: 8 }} onClick={() => changeAdminMenu("소모품 주문 관리")}>소모품 주문 관리로 이동</button>
          </div>

          <div style={{ ...card, padding: 18 }}>
            <h3 style={{ marginTop: 0 }}>최근 인수인계</h3>
            {recentHandovers.map((item) => (
              <div key={item.id} style={{ borderTop: "1px solid #e5e7eb", padding: "10px 0", fontSize: 13 }}>
                <b>{item.store} · {item.title}</b><br />
                <span style={{ color: "#6b7280" }}>{item.date} · {item.status}</span>
              </div>
            ))}
            <button style={{ ...darkButton, width: "100%", marginTop: 8 }} onClick={() => changeAdminMenu("인수인계 관리")}>인수인계 관리로 이동</button>
          </div>
        </div>

        <div style={{ ...card, padding: 18 }}>
          <h3 style={{ marginTop: 0 }}>현재 근무중 직원</h3>
          {workingNow.length === 0 ? (
            <div style={{ color: "#6b7280" }}>현재 근무중인 직원이 없습니다.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10 }}>
              {workingNow.map((row) => (
                <div key={row.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12 }}>
                  <b>{row.name}</b> · {row.store}<br />
                  <small>출근 {row.date} {row.start} · 상태 {row.status}</small>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 13, color: "#6b7280" }}>오늘 출퇴근 기록: {todayAttendance.length}건</div>
        </div>
      </div>
    );
  };


  const AdminSalesPage = () => {
    const summaryAvgTicket = salesSummary.totalCount ? Math.round(salesSummary.totalSales / salesSummary.totalCount) : 0;
    const externalCards = [

      {

        ...externalSalesSummary.cafe24,

        sales: externalSalesSummary.cafe24.totalSales,

        count: externalSalesSummary.cafe24.totalCount,

      },

      {

        ...externalSalesSummary.ksnet,

        sales: externalSalesSummary.ksnet.totalSales,

        count: externalSalesSummary.ksnet.totalCount,

      },

    ];

    const MobileSalesStoreCard = ({ row, index }) => (
      <div style={{ background: row.issueCount > 0 ? "#fff5f5" : "#fff", border: row.issueCount > 0 ? "1px solid #fecaca" : "1px solid #e5e7eb", borderRadius: 18, padding: 14, display: "grid", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: "bold" }}>#{index + 1}</div>
            <b style={{ fontSize: 18, wordBreak: "keep-all" }}>{row.store}</b>
            {row.issueCount > 0 ? <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4, fontWeight: "bold" }}>확인필요 {row.issueCount}건</div> : null}
          </div>
          <b style={{ fontSize: 18, color: "#111827", whiteSpace: "nowrap" }}>{formatSalesWon(row.totalSales)}</b>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div style={{ background: "#f9fafb", borderRadius: 14, padding: 10 }}><small>POS</small><br /><b>{formatSalesWon(row.posSales)}</b><br /><span style={{ fontSize: 12, color: "#6b7280" }}>{formatNumber(row.posCount)}건</span></div>
          <div style={{ background: "#f9fafb", borderRadius: 14, padding: 10 }}><small>KIOSK</small><br /><b>{formatSalesWon(row.kioskSales)}</b><br /><span style={{ fontSize: 12, color: "#6b7280" }}>{formatNumber(row.kioskCount)}건</span></div>
        </div>
        <div style={{ background: "#111827", color: "#fff", borderRadius: 14, padding: 10, display: "flex", justifyContent: "space-between" }}>
          <span>합계 건수</span><b>{formatNumber(row.totalCount)}건</b>
        </div>
      </div>
    );

    const MobileSalesRecordCard = ({ record }) => {
      const editing = editingSalesId === record.id;
      const issue = record.status === "누락" || record.status === "수집실패" || record.status === "확인필요";
      if (editing) {
        return (
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 18, padding: 14, display: "grid", gap: 8 }}>
            <input ref={editSalesDateRef} type="date" defaultValue={record.date} style={{ ...input, width: "100%" }} />
            <select ref={editSalesStoreRef} defaultValue={record.store} style={{ ...input, width: "100%" }}>{salesInputStoreOptions.map((store) => <option key={store}>{store}</option>)}</select>
            <select ref={editSalesChannelRef} defaultValue={record.channel} style={{ ...input, width: "100%" }}>{salesChannels.filter((item) => item !== "전체").map((channel) => <option key={channel}>{channel}</option>)}</select>
            <input ref={editSalesNetRef} defaultValue={record.netSales} placeholder="실매출" style={{ ...input, width: "100%" }} />
            <input ref={editSalesOrderCountRef} defaultValue={record.orderCount} placeholder="건수" style={{ ...input, width: "100%" }} />
            <input ref={editSalesGrossRef} defaultValue={record.grossSales} placeholder="총매출" style={{ ...input, width: "100%" }} />
            <select ref={editSalesStatusRef} defaultValue={record.status || "정상"} style={{ ...input, width: "100%" }}>{salesStatusOptions.filter((item) => item !== "전체").map((status) => <option key={status}>{status}</option>)}</select>
            <input ref={editSalesMemoRef} defaultValue={record.memo || ""} placeholder="메모" style={{ ...input, width: "100%" }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <button style={redButton} onClick={() => handleSaveEditSales(record.id)}>저장</button>
              <button style={grayButton} onClick={() => setEditingSalesId(null)}>취소</button>
            </div>
          </div>
        );
      }
      return (
        <div style={{ background: issue ? "#fff5f5" : "#fff", border: issue ? "1px solid #fecaca" : "1px solid #e5e7eb", borderRadius: 18, padding: 14, display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <b style={{ fontSize: 16 }}>{record.store}</b>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{record.date} · {record.channel}</div>
            </div>
            <StatusBadge tone={issue ? "red" : record.status === "수정됨" ? "yellow" : "blue"}>{record.status || "정상"}</StatusBadge>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div style={{ background: "#f9fafb", borderRadius: 14, padding: 10 }}><small>실매출</small><br /><b>{record.netSales || record.grossSales ? formatSalesWon(record.netSales || record.grossSales) : "-"}</b></div>
            <div style={{ background: "#f9fafb", borderRadius: 14, padding: 10 }}><small>건수 / 객단가</small><br /><b>{record.orderCount || "-"}건</b><br /><span style={{ fontSize: 12, color: "#6b7280" }}>{getSalesAvgTicket(record) ? formatSalesWon(getSalesAvgTicket(record)) : "-"}</span></div>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>출처: {record.source || "-"}<br />수집/수정일: {record.collectedAt || record.updatedAt || "-"}<br />메모: {record.memo || "-"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <button style={darkButton} onClick={() => handleStartEditSales(record)}>수정</button>
            <button style={grayButton} onClick={() => handleDeleteSalesRecord(record.id)}>삭제</button>
          </div>
        </div>
      );
    };

    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
          <SectionTitle title="매출조회" desc="일자별 매장 매출을 POS(OKPOS)와 KIOSK(OrderQueen) 기준으로 통합 조회합니다. 누락/수집실패 데이터는 재수집하거나 아래 목록에서 수정하세요." />

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 18, padding: 16 }}><small>검색 매출 합계</small><br /><b style={{ fontSize: 26 }}>{formatSalesWon(salesSummary.totalSales)}</b></div>
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}><small>결제 건수</small><br /><b style={{ fontSize: 26 }}>{formatNumber(salesSummary.totalCount)}건</b></div>
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}><small>객단가</small><br /><b style={{ fontSize: 26 }}>{formatSalesWon(summaryAvgTicket)}</b></div>
            <div style={{ background: salesSummary.issueCount > 0 ? "#fff5f5" : "#f0fdf4", border: salesSummary.issueCount > 0 ? "1px solid #fecaca" : "1px solid #bbf7d0", borderRadius: 18, padding: 16 }}><small>확인 필요</small><br /><b style={{ fontSize: 26 }}>{salesSummary.issueCount}건</b><div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>수정됨 {salesSummary.modifiedCount}건</div></div>
          </div>

          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 18, padding: 16, marginBottom: 16, lineHeight: 1.7, fontSize: 13, color: "#9a3412" }}>
            <b>최종 연계 구조</b><br />자동화 서버가 매일 OKPOS, OrderQueen, Cafe24, KSNET 데이터를 수집해 저장하면 이 화면은 서버 데이터를 조회합니다. 값이 없거나 실패한 행은 재수집 버튼으로 다시 호출하고, 그래도 값이 없으면 아래 매출 데이터 목록의 수정 버튼으로 보정합니다.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr 1fr 1.5fr", gap: 8, marginBottom: 10 }}>
            <input ref={salesStartRef} defaultValue={salesFilter.start} type="date" style={{ ...input, width: "100%" }} />
            <input ref={salesEndRef} defaultValue={salesFilter.end} type="date" style={{ ...input, width: "100%" }} />
            <select ref={salesStoreRef} defaultValue={salesFilter.store} style={{ ...input, width: "100%" }}>{salesStoreOptions.map((store) => <option key={store}>{store}</option>)}</select>
            <select ref={salesChannelRef} defaultValue={salesFilter.channel} style={{ ...input, width: "100%" }}>{salesChannels.map((channel) => <option key={channel}>{channel}</option>)}</select>
            <select ref={salesStatusRef} defaultValue={salesFilter.status} style={{ ...input, width: "100%" }}>{salesStatusOptions.map((status) => <option key={status}>{status}</option>)}</select>
            <input ref={salesKeywordRef} defaultValue={salesFilter.keyword} placeholder="메모/출처/상태 검색" style={{ ...input, width: "100%" }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 8 }}>
            <button style={darkButton} onClick={handleSearchSales}>검색</button>
            <button style={grayButton} onClick={handleResetSalesFilter}>초기화</button>
            <button style={blueButton} onClick={handleFetchSalesFromServer}>서버 데이터 가져오기</button>
            <button style={isRecollectingSales ? grayButton : orangeButton} onClick={handleRecollectSales} disabled={isRecollectingSales || isFullRecheckingSales}>{isRecollectingSales ? "재수집 요청중..." : "누락/실패 재수집"}</button>
            <button style={isFullRecheckingSales ? grayButton : redButton} onClick={handleRecheckYesterdaySales} disabled={isFullRecheckingSales || isRecollectingSales}>{isFullRecheckingSales ? "어제 전체 재검수중..." : "어제 전체 재검수"}</button>
            <button style={darkButton} onClick={downloadSalesTemplate}>CSV 양식</button>
            <button style={redButton} onClick={downloadFilteredSalesList}>검색결과 CSV</button>
          </div>

          {salesRecheckSummary && (
            <div style={{ marginTop: 12, background: "#f0fdf4", border: "1px solid #bbf7d0", padding: 14, color: "#166534", lineHeight: 1.7 }}>
              <b>최근 전체 재검수 결과</b><br />
              대상 {salesRecheckSummary.targetStoreCount || 0}개 매장 ·
              OKPOS 성공 {salesRecheckSummary.okposSuccess || 0} / 실패 {salesRecheckSummary.okposFailed || 0} ·
              OrderQueen 성공 {salesRecheckSummary.orderqueenSuccess || 0} / 실패 {salesRecheckSummary.orderqueenFailed || 0}<br />
              신규 {salesRecheckSummary.created || 0} · 갱신 {salesRecheckSummary.updated || 0} ·
              유지 {salesRecheckSummary.maintained || 0} · 수동 보호 {salesRecheckSummary.manualProtected || 0} ·
              확인 필요 {salesRecheckSummary.needsCheck || 0}
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <input ref={salesImportFileRef} type="file" accept=".csv" onChange={handleImportSalesCsv} style={{ ...input, width: "100%", background: "#fff" }} />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>CSV 업로드는 같은 날짜/매장/채널 데이터가 있으면 새 값으로 교체됩니다.</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 360px", gap: 14 }}>
          <div style={{ ...card, padding: isMobile ? 16 : 18 }}>
            <h3 style={{ marginTop: 0 }}>일자별 매장 매출 통합표</h3>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>OKPOS는 POS, OrderQueen은 KIOSK로 합산 표시합니다.</div>
            {offlineSalesTableRows.length === 0 ? (
              <div style={{ color: "#6b7280", background: "#f9fafb", borderRadius: 14, padding: 16 }}>조회된 매장 매출이 없습니다.</div>
            ) : isMobile ? (
              <div style={{ display: "grid", gap: 10 }}>
                {offlineSalesTableRows.map((row, index) => <MobileSalesStoreCard key={row.store} row={row} index={index} />)}
              </div>
            ) : (
              <div style={tableScroll}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 840 }}>
                  <thead>
                    <tr style={{ background: "#111827", color: "#fff" }}>
                      {["순위", "매장", "POS 매출", "POS 건수", "KIOSK 매출", "KIOSK 건수", "합계금액", "합계건수"].map((head) => (
                        <th key={head} style={{ textAlign: head === "매장" ? "left" : "right", padding: 11, borderBottom: "1px solid #374151", fontSize: 13 }}>{head}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {offlineSalesTableRows.map((row, index) => (
                      <tr key={row.store} style={{ background: row.issueCount > 0 ? "#fff5f5" : "#fff" }}>
                        <td style={{ textAlign: "right", padding: 10, borderBottom: "1px solid #e5e7eb" }}>{index + 1}</td>
                        <td style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb", fontWeight: "bold" }}>
                          {row.store}{row.issueCount > 0 ? <small style={{ color: "#dc2626" }}> · 확인필요 {row.issueCount}</small> : null}
                        </td>
                        <td style={{ textAlign: "right", padding: 10, borderBottom: "1px solid #e5e7eb" }}>{formatNumber(row.posSales)}</td>
                        <td style={{ textAlign: "right", padding: 10, borderBottom: "1px solid #e5e7eb" }}>{formatNumber(row.posCount)}</td>
                        <td style={{ textAlign: "right", padding: 10, borderBottom: "1px solid #e5e7eb" }}>{formatNumber(row.kioskSales)}</td>
                        <td style={{ textAlign: "right", padding: 10, borderBottom: "1px solid #e5e7eb" }}>{formatNumber(row.kioskCount)}</td>
                        <td style={{ textAlign: "right", padding: 10, borderBottom: "1px solid #e5e7eb", fontWeight: "bold" }}>{formatNumber(row.totalSales)}</td>
                        <td style={{ textAlign: "right", padding: 10, borderBottom: "1px solid #e5e7eb", fontWeight: "bold" }}>{formatNumber(row.totalCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ ...card, padding: isMobile ? 16 : 18 }}>
            <h3 style={{ marginTop: 0 }}>기타 매출</h3>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>당일 또는 검색기간의 온라인몰/인형뽑기 매출입니다.</div>
            {externalCards.map((row) => (
              <div key={row.label} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 16, padding: 14, marginBottom: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center" }}>
                  <div>
                    <b>{row.label}</b>
                    {row.issueCount > 0 ? <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>확인필요 {row.issueCount}건</div> : null}
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>{formatNumber(row.totalCount)}건</div>
                  </div>
                  <b style={{ fontSize: 20, whiteSpace: "nowrap", textAlign: "right" }}>{formatSalesWon(row.totalSales)}</b>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <SectionTitle title="매출 데이터 목록" desc="원천 데이터 단위 목록입니다. 잘못된 값은 수정 버튼으로 보정하고, 누락/실패 데이터는 재수집하세요." />
            <button
              style={isManualSalesFormOpen ? grayButton : redButton}
              onClick={() => {
                if (isManualSalesFormOpen) {
                  setIsManualSalesFormOpen(false);
                  resetManualSalesForm();
                } else {
                  setIsManualSalesFormOpen(true);
                  setManualSalesDuplicate(null);
                }
              }}
            >
              {isManualSalesFormOpen ? "수동 입력 닫기" : "수동 매출 추가"}
            </button>
          </div>

          {isManualSalesFormOpen && (
            <div style={{ background: "#f9fafb", borderTop: "3px solid #dc2626", padding: 16, marginBottom: 18 }}>
              <h3 style={{ marginTop: 0 }}>수동 매출 신규 입력</h3>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 8 }}>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>날짜<input ref={salesDateRef} type="date" defaultValue={getYesterdayInputDate()} style={{ ...input, width: "100%" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>매장<select ref={salesStoreAddRef} defaultValue={salesInputStoreOptions[0] || ""} style={{ ...input, width: "100%" }}>{salesInputStoreOptions.map((store) => <option key={store}>{store}</option>)}</select></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>채널<select ref={salesChannelAddRef} defaultValue="OKPOS" style={{ ...input, width: "100%" }}>{["OKPOS", "OrderQueen", "Cafe24", "KSNET"].map((channel) => <option key={channel}>{channel}</option>)}</select></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>상태<select ref={salesStatusAddRef} defaultValue="정상" style={{ ...input, width: "100%" }}>{["정상", "확인필요"].map((status) => <option key={status}>{status}</option>)}</select></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>실매출<input ref={salesNetRef} inputMode="numeric" placeholder="실매출" onInput={updateManualSalesPreview} style={{ ...input, width: "100%" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold" }}>건수<input ref={salesOrderCountRef} inputMode="numeric" placeholder="건수" onInput={updateManualSalesPreview} style={{ ...input, width: "100%" }} /></label>
                <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: "bold", gridColumn: isMobile ? "auto" : "span 2" }}>메모<input ref={salesMemoRef} placeholder="선택 입력" style={{ ...input, width: "100%" }} /></label>
              </div>
              <div style={{ marginTop: 10, fontSize: 13, color: "#4b5563" }}>
                객단가: {manualSalesPreview.count > 0 ? formatSalesWon(Math.round(manualSalesPreview.sales / manualSalesPreview.count)) : "-"}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                <button style={isSavingManualSales ? grayButton : redButton} disabled={isSavingManualSales} onClick={() => handleAddSalesRecord()}>{isSavingManualSales ? "저장중입니다..." : "수동 매출 저장"}</button>
                {manualSalesDuplicate && (
                  <>
                    <button style={darkButton} disabled={isSavingManualSales} onClick={handleEditExistingManualSales}>기존 행 수정</button>
                    <button style={orangeButton} disabled={isSavingManualSales} onClick={() => handleAddSalesRecord({ overwrite: true })}>수동 값으로 덮어쓰기</button>
                  </>
                )}
              </div>
            </div>
          )}

          {filteredSalesRecords.length === 0 ? (
            <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 매출 데이터가 없습니다.</div>
          ) : isMobile ? (
            <div style={{ display: "grid", gap: 10 }}>
              {filteredSalesRecords.map((record) => <MobileSalesRecordCard key={record.id} record={record} />)}
            </div>
          ) : (
            <div style={tableScroll}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
                <thead><tr style={{ background: "#f9fafb" }}>{["날짜", "매장", "채널", "실매출", "건수", "객단가", "상태", "출처", "수집/수정일", "메모", "관리"].map((head) => <th key={head} style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb", fontSize: 13 }}>{head}</th>)}</tr></thead>
                <tbody>
                  {filteredSalesRecords.map((record) => {
                    const editing = editingSalesId === record.id;
                    const issue = record.status === "누락" || record.status === "수집실패" || record.status === "확인필요";
                    return (
                      <tr key={record.id} style={{ background: issue ? "#fff5f5" : "#fff" }}>
                        {editing ? (
                          <>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}><input ref={editSalesDateRef} type="date" defaultValue={record.date} style={{ ...input, width: 140 }} /></td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}><select ref={editSalesStoreRef} defaultValue={record.store} style={{ ...input, width: 140 }}>{salesInputStoreOptions.map((store) => <option key={store}>{store}</option>)}</select></td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}><select ref={editSalesChannelRef} defaultValue={record.channel} style={{ ...input, width: 120 }}>{salesChannels.filter((item) => item !== "전체").map((channel) => <option key={channel}>{channel}</option>)}</select></td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}><input ref={editSalesNetRef} defaultValue={record.netSales} style={{ ...input, width: 120 }} /></td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}><input ref={editSalesOrderCountRef} defaultValue={record.orderCount} style={{ ...input, width: 80 }} /></td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}><input ref={editSalesGrossRef} defaultValue={record.grossSales} style={{ ...input, width: 120 }} /></td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}><select ref={editSalesStatusRef} defaultValue={record.status || "정상"} style={{ ...input, width: 120 }}>{salesStatusOptions.filter((item) => item !== "전체").map((status) => <option key={status}>{status}</option>)}</select></td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>{record.source || "-"}</td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>{record.collectedAt || record.updatedAt || "-"}</td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}><input ref={editSalesMemoRef} defaultValue={record.memo || ""} style={{ ...input, width: 180 }} /></td>
                            <td style={{ padding: 8, borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}><button style={{ ...redButton, padding: "8px 10px", marginRight: 4 }} onClick={() => handleSaveEditSales(record.id)}>저장</button><button style={{ ...grayButton, padding: "8px 10px" }} onClick={() => setEditingSalesId(null)}>취소</button></td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{record.date}</td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}><b>{record.store}</b></td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{record.channel}</td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb", fontWeight: "bold" }}>{record.netSales || record.grossSales ? formatSalesWon(record.netSales || record.grossSales) : "-"}</td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{record.orderCount || "-"}</td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{getSalesAvgTicket(record) ? formatSalesWon(getSalesAvgTicket(record)) : "-"}</td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}><StatusBadge tone={issue ? "red" : record.status === "수정됨" ? "yellow" : "blue"}>{record.status || "정상"}</StatusBadge></td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{record.source || "-"}</td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb", fontSize: 12 }}>{record.collectedAt || record.updatedAt || "-"}</td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb", maxWidth: 220, wordBreak: "break-all" }}>{record.memo || "-"}</td>
                            <td style={{ padding: 10, borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}><button style={{ ...darkButton, padding: "8px 10px", marginRight: 4 }} onClick={() => handleStartEditSales(record)}>수정</button><button style={{ ...grayButton, padding: "8px 10px" }} onClick={() => handleDeleteSalesRecord(record.id)}>삭제</button></td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const AdminEmployeePage = () => {
    const selectedAllStores = selectedEmployeeStores.length === stores.length;

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="직원 계정 관리" desc="직원 계정을 만들고, 근무 가능 매장과 계정 상태를 관리합니다." />

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>직원 계정 등록</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
            <input ref={employeeNameRef} placeholder="직원명" style={{ ...input, width: "100%" }} />
            <input ref={employeeIdRef} placeholder="로그인 ID" autoCapitalize="none" style={{ ...input, width: "100%" }} />
            <input ref={employeeContactRef} placeholder="연락처" style={{ ...input, width: "100%" }} />
            <input ref={employeeAddressRef} placeholder="주소" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 2" }} />
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>입사일<input ref={employeeHireDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <select ref={employeeRoleRef} defaultValue="직원" style={{ ...input, width: "100%" }}>
              <option>직원</option>
              <option>아르바이트</option>
              <option>관리자</option>
            </select>
          </div>

          <div style={{ marginTop: 14, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
              <b>근무 가능 매장</b>
              <label style={{ fontSize: 13 }}>
                <input type="checkbox" checked={selectedAllStores} onChange={(e) => toggleAllEmployeeStores(e.target.checked)} /> 전체 선택
              </label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 8 }}>
              {stores.map((store) => (
                <label key={store} style={{ background: selectedEmployeeStores.includes(store) ? "#fff5f5" : "#f9fafb", border: selectedEmployeeStores.includes(store) ? "1px solid #fecaca" : "1px solid #e5e7eb", borderRadius: 12, padding: 10, fontSize: 13 }}>
                  <input type="checkbox" checked={selectedEmployeeStores.includes(store)} onChange={() => toggleEmployeeStore(store)} /> {store}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto auto", gap: 10, alignItems: "center", marginTop: 14 }}>
            <div style={{ background: "#111827", color: "#fff", borderRadius: 14, padding: 14 }}>
              <small>초기 비밀번호</small><br />
              <b style={{ fontSize: 22 }}>{tempPassword}</b>
            </div>
            <button style={grayButton} onClick={regenerateTempPassword}>비밀번호 재생성</button>
            <button style={redButton} onClick={handleAddEmployee}>직원 등록</button>
          </div>
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>직원 일괄 등록</h3>
          <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginBottom: 12 }}>
            CSV 양식을 다운로드해 Excel에서 작성한 뒤 CSV UTF-8 형식으로 저장해서 업로드하세요. 업로드가 끝나면 임시 비밀번호가 포함된 결과 CSV가 자동으로 다운로드됩니다.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "auto 1fr auto", gap: 8, alignItems: "center" }}>
            <button style={blueButton} onClick={downloadEmployeeTemplate}>일괄등록 양식 다운로드</button>
            <input ref={employeeImportFileRef} type="file" accept=".csv" onChange={handleImportEmployeesCsv} style={{ ...input, width: "100%", background: "#fff" }} />
            <button style={grayButton} onClick={() => { if (employeeImportFileRef.current) employeeImportFileRef.current.value = ""; }}>선택 초기화</button>
          </div>
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "0.8fr 1.4fr auto auto", gap: 8 }}>
            <select
              ref={employeeRoleFilterRef}
              defaultValue={employeeRoleFilter}
              style={{ ...input, width: "100%" }}
            >
              <option>전체</option>
              <option>직원</option>
              <option>아르바이트</option>
              <option>관리자</option>
            </select>
            <input
              ref={employeeSearchRef}
              placeholder="직원명 또는 ID 검색"
              defaultValue={employeeSearch}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setEmployeeRoleFilter(employeeRoleFilterRef.current?.value || "전체");
                  setEmployeeSearch(employeeSearchRef.current?.value || "");
                }
              }}
              style={{ ...input, width: "100%" }}
            />
            <button style={darkButton} onClick={() => {
              setEmployeeRoleFilter(employeeRoleFilterRef.current?.value || "전체");
              setEmployeeSearch(employeeSearchRef.current?.value || "");
            }}>검색</button>
            <button style={grayButton} onClick={() => {
              if (employeeSearchRef.current) employeeSearchRef.current.value = "";
              if (employeeRoleFilterRef.current) employeeRoleFilterRef.current.value = "전체";
              setEmployeeSearch("");
              setEmployeeRoleFilter("전체");
            }}>초기화</button>
          </div>
          <div style={{ textAlign: "right", marginTop: 10 }}>
            <button style={blueButton} onClick={downloadEmployeeList}>직원 목록 CSV 다운로드</button>
          </div>
        </div>

        {filteredEmployees.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 직원이 없습니다.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {filteredEmployees.map((employee) => {
              const isEditing = editingEmployeeId === employee.id;
              return (
                <div key={employee.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
                  {!isEditing ? (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                          <b style={{ fontSize: 18 }}>{employee.name}</b>
                          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>ID: {employee.id} · 권한: {employee.role} · 근무: {getEmployeeStoreSummary(employee)}</div>
                        </div>
                        <StatusBadge tone={employee.status === "활성화" ? "blue" : "red"}>{employee.status}</StatusBadge>
                      </div>
                      {expandedEmployeeIds[employee.id] && (
                        <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.7, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12 }}>
                          연락처: {employee.contact}<br />
                          주소: {employee.address}<br />
                          입사일: {employee.hireDate || "-"}<br />
                          현재 비밀번호: {employee.tempPassword}<br />
                          근무 가능 매장: {employee.stores}
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(6, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                        <button style={grayButton} onClick={() => toggleEmployeeExpanded(employee.id)}>{expandedEmployeeIds[employee.id] ? "접기" : "펼쳐보기"}</button>
                        <button style={orangeButton} onClick={() => handleResetEmployeePassword(employee.id)}>비밀번호 초기화</button>
                        <button style={blueButton} onClick={() => handleSetEmployeePassword(employee.id)}>비밀번호 직접변경</button>
                        <button style={grayButton} onClick={() => handleToggleEmployeeStatus(employee.id)}>{employee.status === "활성화" ? "비활성화" : "활성화"}</button>
                        <button style={{ ...grayButton, background: "#991b1b" }} onClick={() => handleDeleteEmployee(employee.id)}>삭제</button>
                        <button style={darkButton} onClick={() => handleStartEditEmployee(employee)}>수정</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
                        <input ref={editEmployeeNameRef} placeholder="직원명" style={{ ...input, width: "100%" }} />
                        <input value={employee.id} readOnly style={{ ...input, width: "100%", background: "#e5e7eb" }} />
                        <input ref={editEmployeeContactRef} placeholder="연락처" style={{ ...input, width: "100%" }} />
                        <input ref={editEmployeeAddressRef} placeholder="주소" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 2" }} />
                        <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>입사일<input ref={editEmployeeHireDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
                        <select ref={editEmployeeRoleRef} defaultValue={employee.role || "직원"} style={{ ...input, width: "100%" }}>
                          <option>직원</option>
                          <option>아르바이트</option>
                          <option>관리자</option>
                        </select>
                      </div>
                      {employee.role !== "관리자" && (
                        <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
                          <b>근무 가능 매장 수정</b>
                          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 8, marginTop: 10 }}>
                            {stores.map((store) => (
                              <label key={store} style={{ background: editEmployeeStores.includes(store) ? "#fff5f5" : "#f9fafb", border: editEmployeeStores.includes(store) ? "1px solid #fecaca" : "1px solid #e5e7eb", borderRadius: 12, padding: 10, fontSize: 13 }}>
                                <input
                                  type="checkbox"
                                  checked={editEmployeeStores.includes(store)}
                                  onChange={() => setEditEmployeeStores((prev) => prev.includes(store) ? prev.filter((item) => item !== store) : [...prev, store])}
                                /> {store}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                        <button style={grayButton} onClick={() => setEditingEmployeeId(null)}>취소</button>
                        <button style={redButton} onClick={() => handleSaveEditEmployee(employee.id)}>저장</button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const AdminStorePage = () => (
    <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
      <SectionTitle title="매장 관리" desc="매장 정보를 등록하고 활성화/비활성화 상태를 관리합니다. 직원 화면에는 활성화된 매장명만 표시됩니다." />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", background: storeError ? "#fff5f5" : "#eff6ff", border: storeError ? "1px solid #fecaca" : "1px solid #bfdbfe", borderRadius: 16, padding: 14, marginBottom: 18 }}>
        <div style={{ fontSize: 13, color: storeError ? "#b91c1c" : "#1d4ed8", lineHeight: 1.6 }}>
          <b>{storeLoading ? "stores API 불러오는 중..." : storeError ? "stores API 연결 실패" : "stores API 연결됨"}</b><br />
          {storeError || "매장 목록은 서버 DB 기준으로 표시됩니다."}
        </div>
        <button style={blueButton} onClick={loadStoresFromApi}>서버 매장 새로고침</button>
      </div>

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>매장 등록</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10 }}>
          <input ref={storeNameRef} placeholder="매장명" style={{ ...input, width: "100%" }} />
          <input ref={storeContactRef} placeholder="연락처" style={{ ...input, width: "100%" }} />
          <input ref={storeExtensionRef} placeholder="내선번호" style={{ ...input, width: "100%" }} />
          <input ref={storeAddressRef} placeholder="주소" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 3" }} />
          <input ref={storeOwnerRef} placeholder="대표자" style={{ ...input, width: "100%" }} />
          <input ref={storeBusinessNumberRef} placeholder="사업자등록번호" style={{ ...input, width: "100%" }} />
          <select ref={storeRentPayDayRef} defaultValue="" style={{ ...input, width: "100%" }}>
            <option value="">임대료 납부일 선택</option>
            {rentPayDayOptions.map((day) => <option key={day} value={day}>{day}일</option>)}
          </select>
          <input ref={storeBank1Ref} placeholder="은행 / 계좌번호 / 예금주 (1)" style={{ ...input, width: "100%" }} />
          <input ref={storeBank2Ref} placeholder="은행 / 계좌번호 / 예금주 (2)" style={{ ...input, width: "100%" }} />
          <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>임대계약일<input ref={storeLeaseStartRef} type="date" style={{ ...input, width: "100%" }} /></label>
          <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>임대종료일<input ref={storeLeaseEndRef} type="date" style={{ ...input, width: "100%" }} /></label>
          <textarea ref={storeMemoRef} placeholder="메모" style={{ ...input, width: "100%", minHeight: 74, gridColumn: isMobile ? "auto" : "1 / span 3" }} />
        </div>
        <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
          <b>매장 첨부파일</b>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>사업자등록증, 임대차계약서 등 매장 관련 파일을 여러 개 첨부할 수 있습니다.</div>
          <input ref={storeFileRef} type="file" multiple onChange={(e) => handleStoreFileChange(e, "new")} style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }} />
          {storeAttachments.length > 0 && (
            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {storeAttachments.map((file) => (
                <div key={file.id} style={{ display: "grid", gridTemplateColumns: isImageFile(file) ? "72px 1fr auto" : "1fr auto", gap: 10, alignItems: "center", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                  {isImageFile(file) && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10 }} />}
                  <a href={file.previewUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#2563eb", fontWeight: "bold", wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} ({formatFileSize(file.size)})</a>
                  <button style={grayButton} onClick={() => handleRemoveStoreAttachment(file.id)}>삭제</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button style={{ ...redButton, width: "100%", marginTop: 12, padding: 14 }} onClick={handleAddStore}>매장 등록</button>
      </div>

      <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0 }}>임대료 납부 5일 이내 매장</h3>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>오늘 기준 납부 예정일이 5일 이하로 남은 활성 매장입니다.</div>
          </div>
          <b style={{ color: "#b91c1c", fontSize: 24 }}>{rentDueSoonStores.length}건</b>
        </div>
        {rentDueSoonStores.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 12, padding: 12, marginTop: 12, color: "#6b7280", fontSize: 13 }}>5일 이내 임대료 납부 예정 매장이 없습니다.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10, marginTop: 12 }}>
            {rentDueSoonStores.map((store) => (
              <button
                key={store.id}
                type="button"
                onClick={() => {
                  if (storeSearchRef.current) storeSearchRef.current.value = store.name;
                  setStoreSearch(store.name);
                  setEditingStoreId(null);
                  setExpandedStoreIds((prev) => prev.includes(store.id) ? prev : [...prev, store.id]);
                }}
                onMouseEnter={handleClickableCardMouseEnter}
                onMouseLeave={handleClickableCardMouseLeave}
                style={{ ...clickableCardStyle, background: "#fff", border: "1px solid #fecaca", borderRadius: 14, padding: 12 }}
              >
                <b>{store.name}</b>
                <div style={{ color: "#b91c1c", fontWeight: "bold", marginTop: 6 }}>납부일 {store.rentPayDay}일 · {store.rentDaysLeft === 0 ? "오늘 납부" : `${store.rentDaysLeft}일 남음`}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{store.contact || "연락처 -"} · {store.address || "주소 -"}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto auto", gap: 8 }}>
          <input ref={storeSearchRef} defaultValue={storeSearch} placeholder="매장명으로 검색" onKeyDown={(e) => { if (e.key === "Enter") setStoreSearch(storeSearchRef.current?.value || ""); }} style={{ ...input, width: "100%" }} />
          <button style={darkButton} onClick={() => setStoreSearch(storeSearchRef.current?.value || "")}>검색</button>
          <button style={grayButton} onClick={() => { if (storeSearchRef.current) storeSearchRef.current.value = ""; setStoreSearch(""); }}>초기화</button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
          <div style={{ fontSize: 13, color: "#6b7280" }}>전체 {storeRecords.length}개 · 활성화 {storeRecords.filter((s) => s.status === "활성화").length}개</div>
          <button style={blueButton} onClick={downloadStoreList}>매장 목록 CSV 다운로드</button>
        </div>
      </div>

      {filteredStoreRecords.length === 0 ? (
        <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 매장이 없습니다.</div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {filteredStoreRecords.map((store) => {
            const isEditing = editingStoreId === store.id;
            const isExpanded = expandedStoreIds.includes(store.id);
            const rentDaysLeft = getDaysUntilRentPay(store.rentPayDay);
            const isRentDueSoon = rentDaysLeft !== null && rentDaysLeft >= 0 && rentDaysLeft <= 5;
            return (
              <div key={store.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
                {!isEditing ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div><b style={{ fontSize: 18 }}>{store.name}</b><div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>연락처: {store.contact} · 내선: {store.extension}</div></div>
                      <StatusBadge tone={store.status === "활성화" ? "blue" : "red"}>{store.status}</StatusBadge>
                    </div>
                    {isExpanded && (
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10, marginTop: 12, fontSize: 13, lineHeight: 1.7 }}>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>연락처</small><br /><b>{store.contact || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>내선번호</small><br /><b>{store.extension || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>상태</small><br /><b>{store.status || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10, gridColumn: isMobile ? "auto" : "1 / span 3" }}><small>주소</small><br /><b>{store.address || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>대표자</small><br /><b>{store.owner || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>사업자등록번호</small><br /><b>{store.businessNumber || "-"}</b></div>
                        <div style={{ background: isRentDueSoon ? "#fff5f5" : "#fff", border: isRentDueSoon ? "1px solid #fecaca" : "none", borderRadius: 12, padding: 10 }}><small>임대료 납부일</small><br /><b style={{ color: isRentDueSoon ? "#b91c1c" : "#111827" }}>{store.rentPayDay && store.rentPayDay !== "-" ? `${store.rentPayDay}일` : "-"}</b>{isRentDueSoon && <div style={{ fontSize: 12, color: "#b91c1c", fontWeight: "bold", marginTop: 4 }}>{rentDaysLeft === 0 ? "오늘 납부" : `${rentDaysLeft}일 남음`}</div>}</div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>임대계약일</small><br /><b>{store.leaseStart || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>임대종료일</small><br /><b>{store.leaseEnd || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>계좌정보 1</small><br /><b>{store.bank1 && store.bank1 !== "-" ? store.bank1 : "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>계좌정보 2</small><br /><b>{store.bank2 && store.bank2 !== "-" ? store.bank2 : "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>등록일시</small><br /><b>{store.createdAt || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>수정일시</small><br /><b>{store.updatedAt || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10, gridColumn: isMobile ? "auto" : "1 / span 3" }}><small>메모</small><br /><b>{store.memo || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10, gridColumn: isMobile ? "auto" : "1 / span 3" }}>
                          <small>첨부파일</small><br />
                          {(store.attachments || []).length === 0 ? <b>-</b> : (
                            <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                              {(store.attachments || []).map((file) => (
                                <div key={file.id || file.name} style={{ display: "grid", gridTemplateColumns: isImageFile(file) && file.previewUrl ? "72px 1fr" : "1fr", gap: 10, alignItems: "center", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                                  {isImageFile(file) && file.previewUrl && <a href={file.previewUrl} target="_blank" rel="noreferrer"><img src={file.previewUrl} alt={file.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10 }} /></a>}
                                  {file.previewUrl ? <a href={file.previewUrl} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: "bold", wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} <span style={{ color: "#6b7280" }}>({formatFileSize(file.size)})</span></a> : <b style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} <span style={{ color: "#6b7280" }}>({formatFileSize(file.size)})</span></b>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                      <button style={blueButton} onClick={() => toggleStoreExpanded(store.id)}>{isExpanded ? "접기" : "펼쳐보기"}</button>
                      <button style={grayButton} onClick={() => handleToggleStoreStatus(store.id)}>{store.status === "활성화" ? "비활성화" : "활성화"}</button>
                      <button style={darkButton} onClick={() => handleStartEditStore(store)}>수정</button>
                      <button style={redButton} onClick={() => handleDeleteStore(store.id)}>삭제</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10 }}>
                      <input ref={editStoreNameRef} placeholder="매장명" style={{ ...input, width: "100%" }} />
                      <input ref={editStoreContactRef} placeholder="연락처" style={{ ...input, width: "100%" }} />
                      <input ref={editStoreExtensionRef} placeholder="내선번호" style={{ ...input, width: "100%" }} />
                      <input ref={editStoreAddressRef} placeholder="주소" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 3" }} />
                      <input ref={editStoreOwnerRef} placeholder="대표자" style={{ ...input, width: "100%" }} />
                      <input ref={editStoreBusinessNumberRef} placeholder="사업자등록번호" style={{ ...input, width: "100%" }} />
                      <select ref={editStoreRentPayDayRef} defaultValue={store.rentPayDay === "-" ? "" : String(store.rentPayDay || "").replace(/[^0-9]/g, "")} style={{ ...input, width: "100%" }}>
                        <option value="">임대료 납부일 선택</option>
                        {rentPayDayOptions.map((day) => <option key={day} value={day}>{day}일</option>)}
                      </select>
                      <input ref={editStoreBank1Ref} placeholder="은행 / 계좌번호 / 예금주 (1)" style={{ ...input, width: "100%" }} />
                      <input ref={editStoreBank2Ref} placeholder="은행 / 계좌번호 / 예금주 (2)" style={{ ...input, width: "100%" }} />
                      <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>임대계약일<input ref={editStoreLeaseStartRef} type="date" style={{ ...input, width: "100%" }} /></label>
                      <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>임대종료일<input ref={editStoreLeaseEndRef} type="date" style={{ ...input, width: "100%" }} /></label>
                      <textarea ref={editStoreMemoRef} placeholder="메모" style={{ ...input, width: "100%", minHeight: 74, gridColumn: isMobile ? "auto" : "1 / span 3" }} />
                    </div>
                    <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
                      <b>첨부파일 수정</b>
                      <input ref={editStoreFileRef} type="file" multiple onChange={(e) => handleStoreFileChange(e, "edit")} style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }} />
                      {store.attachments?.length > 0 && (
                        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                          {store.attachments.map((file) => (
                            <div key={file.id || file.name} style={{ display: "grid", gridTemplateColumns: isImageFile(file) ? "72px 1fr auto" : "1fr auto", gap: 10, alignItems: "center", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                              {isImageFile(file) && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10 }} />}
                              <a href={file.previewUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#2563eb", fontWeight: "bold", wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} ({formatFileSize(file.size)})</a>
                              <button style={grayButton} onClick={() => handleRemoveStoreAttachment(file.id, "edit", store.id)}>삭제</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                      <button style={grayButton} onClick={() => setEditingStoreId(null)}>취소</button>
                      <button style={redButton} onClick={() => handleSaveEditStore(store.id)}>저장</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );


  const AdminStoreAliasPage = () => {
    const activeStoresForMapping = storeRecords.filter((store) => store.status === "활성화" && !isSystemStoreName(store.name));
    const defaultStoreId = activeStoresForMapping[0]?.id || "";

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="매장 매핑 관리" desc="매출 수집 DB의 원본 매장명을 매장관리의 표준 매장명에 연결합니다. 연결 후 매출조회 집계는 표준 매장명 기준으로 합산됩니다." />

        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <b style={{ color: "#1d4ed8" }}>매핑 구조</b>
          <div style={{ fontSize: 13, color: "#374151", marginTop: 6, lineHeight: 1.6 }}>
            예: <b>레컨-강남점</b> 또는 <b>홍대어울마당점</b> 같은 원본 매장명을 <b>강남24시점</b>, <b>홍대어울마당24시점</b> 같은 표준 매장에 연결합니다.
          </div>
        </div>

        {storeAliasError && <div style={{ background: "#fff5f5", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 14, padding: 12, marginBottom: 14 }}>{storeAliasError}</div>}

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>수동 매핑 등록</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr 0.8fr auto", gap: 10, alignItems: "center" }}>
            <input ref={aliasNameRef} placeholder="원본 매장명 예: 레컨-강남점" style={{ ...input, width: "100%" }} />
            <select ref={aliasStoreRef} defaultValue={defaultStoreId} style={{ ...input, width: "100%" }}>
              <option value="">표준 매장 선택</option>
              {activeStoresForMapping.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
            </select>
            <input ref={aliasSourceRef} defaultValue="manual" placeholder="출처 예: OKPOS" style={{ ...input, width: "100%" }} />
            <button style={redButton} onClick={handleAddStoreAlias}>매핑 등록</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 18 }}>
          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 18, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>미매핑 매장명</h3>
              <button style={{ ...blueButton, padding: "8px 12px" }} onClick={loadUnmappedStores}>새로고침</button>
            </div>
            <div style={{ color: "#92400e", fontSize: 13, marginBottom: 10 }}>매출 DB에는 있으나 표준 매장 또는 매핑 테이블에 없는 원본 매장명입니다.</div>
            {unmappedStores.length === 0 ? (
              <div style={{ background: "#fff", borderRadius: 14, padding: 14, color: "#6b7280" }}>미매핑 매장명이 없습니다.</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {unmappedStores.filter((name) => !isSystemStoreName(name)).map((name) => (
                  <div key={name} style={{ background: "#fff", border: "1px solid #fed7aa", borderRadius: 14, padding: 12 }}>
                    <div style={{ fontWeight: "bold", marginBottom: 8, wordBreak: "break-all" }}>{name}</div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 8 }}>
                      <select value={unmappedDrafts[name] || ""} onChange={(e) => setUnmappedDrafts((prev) => ({ ...prev, [name]: e.target.value }))} style={{ ...input, width: "100%" }}>
                        <option value="">표준 매장 선택</option>
                        {activeStoresForMapping.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                      </select>
                      <button style={redButton} onClick={() => handleMapUnmappedStore(name)}>매핑</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>등록된 매장 매핑</h3>
              <button style={{ ...blueButton, padding: "8px 12px" }} onClick={loadStoreAliases}>새로고침</button>
            </div>
            {storeAliasLoading ? (
              <div style={{ background: "#fff", borderRadius: 14, padding: 14 }}>불러오는 중...</div>
            ) : storeAliases.length === 0 ? (
              <div style={{ background: "#fff", borderRadius: 14, padding: 14, color: "#6b7280" }}>등록된 매장 매핑이 없습니다.</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {storeAliases.filter((alias) => !isSystemStoreName(alias.aliasName) && !isSystemStoreName(alias.storeName)).map((alias) => (
                  <div key={alias.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>원본 매장명</div>
                        <b style={{ wordBreak: "break-all" }}>{alias.aliasName}</b>
                        <div style={{ fontSize: 13, color: "#374151", marginTop: 6 }}>→ 표준 매장: <b>{alias.storeName || "-"}</b></div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>출처: {alias.source || "-"}</div>
                      </div>
                      <button style={grayButton} onClick={() => handleDeleteStoreAlias(alias.id)}>삭제</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const AdminNoticePage = () => {
    const filteredNotices = getFilteredAdminNotices();
    const adminNoticeSummaryCards = [
      { key: "전체", label: "전체", count: companyNotices.length, background: "#f9fafb", border: "#e5e7eb", color: "#111827" },
      { key: "이벤트", label: "이벤트", count: companyNotices.filter((notice) => notice.category === "이벤트").length, background: "#fff7ed", border: "#fed7aa", color: "#c2410c" },
      { key: "전달사항", label: "전달사항", count: companyNotices.filter((notice) => notice.category === "전달사항").length, background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
      { key: "업무 요청", label: "업무 요청", count: companyNotices.filter((notice) => isNoticeCategory(notice, "업무 요청")).length, background: "#fff5f5", border: "#fecaca", color: "#b91c1c" },
    ];
    const getAdminNoticeSummaryStyle = (item) => ({
      ...clickableCardStyle,
      background: item.background,
      border: adminNoticeFilter.category === item.key ? "2px solid #dc2626" : `1px solid ${item.border}`,
      borderRadius: 16,
      padding: 14,
    });

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="공지 작성 / 관리" desc="직원 공지사항에 노출될 이벤트, 전달사항, 업무 요청을 등록하고 관리합니다." />

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>{editingNoticeId ? "공지 수정" : "공지 등록"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
            <select ref={adminNoticeCategoryRef} defaultValue="이벤트" style={{ ...input, width: "100%" }}>
              <option>이벤트</option>
              <option>전달사항</option>
              <option>업무 요청</option>
            </select>
            <select ref={adminNoticeTargetRef} defaultValue="전체 매장" style={{ ...input, width: "100%" }}>
              <option>전체 매장</option>
              {stores.map((store) => <option key={store}>{store}</option>)}
            </select>
            <input ref={adminNoticeTitleRef} placeholder="제목" style={{ ...input, width: "100%" }} />
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>시작일<input ref={adminNoticeStartRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>종료일<input ref={adminNoticeEndRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <input ref={adminNoticeFileRef} type="file" multiple onChange={handleAdminNoticeFileChange} style={{ ...input, width: "100%", background: "#fff" }} />
          </div>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, marginTop: 10 }}>
            <b>관련 상품 선택</b>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 8, marginTop: 10 }}>
              <input ref={adminNoticeProductKeywordRef} onKeyDown={handleAdminNoticeProductSearchKeyDown} placeholder="상품명 또는 상품코드 검색" style={{ ...input, width: "100%" }} />
              <button style={darkButton} onClick={handleAdminNoticeProductSearch}>상품 검색</button>
            </div>
            {adminNoticeProductResults.length > 0 && (
              <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                {adminNoticeProductResults.slice(0, adminNoticeProductVisibleCount).map((product) => (
                  <div key={product.code} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                    <div><b>{product.name}</b><br /><small>{product.code}</small></div>
                    <button style={darkButton} onClick={() => handleAddAdminNoticeProduct(product)}>추가</button>
                  </div>
                ))}
                {adminNoticeProductResults.length > adminNoticeProductVisibleCount && (
                  <button style={grayButton} onClick={() => setAdminNoticeProductVisibleCount((prev) => prev + 5)}>더보기</button>
                )}
              </div>
            )}
            {adminNoticeSelectedProducts.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <b>선택된 상품</b>
                {adminNoticeSelectedProducts.map((product) => (
                  <div key={product.code} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: 10, marginTop: 8 }}>
                    <div><b>{product.name}</b><br /><small>{product.code}</small></div>
                    <button style={grayButton} onClick={() => handleRemoveAdminNoticeProduct(product.code)}>삭제</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <textarea ref={adminNoticeContentRef} placeholder="공지 내용을 입력하세요." style={{ ...input, width: "100%", minHeight: 110, marginTop: 10 }} />
          {adminNoticeAttachments.length > 0 && (
            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
              {adminNoticeAttachments.map((file) => (
                <div key={file.id} style={{ display: "grid", gridTemplateColumns: isImageFile(file) ? "64px 1fr auto" : "1fr auto", gap: 10, alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                  {isImageFile(file) && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 10 }} />}
                  <span style={{ fontSize: 13, wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} ({formatFileSize(file.size)})</span>
                  <button style={grayButton} onClick={() => handleRemoveAdminNoticeFile(file.id)}>삭제</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 8, marginTop: 12 }}>
            <button
              style={{ ...(adminNoticeSaving ? grayButton : redButton), padding: 14 }}
              onClick={handleSubmitAdminNotice}
              disabled={adminNoticeSaving}
            >
              {adminNoticeSaving ? (editingNoticeId ? "저장중입니다..." : "등록중입니다...") : editingNoticeId ? "공지 수정 저장" : "공지 등록"}
            </button>
            {editingNoticeId && <button style={grayButton} onClick={resetAdminNoticeForm} disabled={adminNoticeSaving}>수정 취소</button>}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          {adminNoticeSummaryCards.map((item) => (
            <button key={item.key} type="button" style={getAdminNoticeSummaryStyle(item)} onClick={() => { setAdminNoticeFilter({ category: item.key, target: "전체", keyword: "", visibleOnly: false }); setOpenAdminNoticeId(null); }} onMouseEnter={handleClickableCardMouseEnter} onMouseLeave={handleClickableCardMouseLeave}>
              <small>{item.label}</small><br /><b style={{ fontSize: 26, color: item.color }}>{item.count}건</b>
            </button>
          ))}
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1.5fr", gap: 8 }}>
            <select value={adminNoticeFilter.category} onChange={(e) => setAdminNoticeFilter((prev) => ({ ...prev, category: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              <option>이벤트</option>
              <option>전달사항</option>
              <option>업무 요청</option>
            </select>
            <select value={adminNoticeFilter.target} onChange={(e) => setAdminNoticeFilter((prev) => ({ ...prev, target: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              <option>전체 매장</option>
              {stores.map((store) => <option key={store}>{store}</option>)}
            </select>
            <input value={adminNoticeFilter.keyword} onChange={(e) => setAdminNoticeFilter((prev) => ({ ...prev, keyword: e.target.value }))} placeholder="제목/내용 검색" style={{ ...input, width: "100%" }} />
          </div>
        </div>

        {filteredNotices.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>등록된 공지가 없습니다.</div>
        ) : filteredNotices.map((notice) => {
          const confirmationSummary = getAdminNoticeConfirmationSummary(notice);
          const checkedCount = confirmationSummary.checkedCount;
          const uncheckedCount = confirmationSummary.uncheckedEmployees.length;
          const uncheckedNames = confirmationSummary.uncheckedEmployees
            .map((employee) => employee.name || employee.id || employee.userName || "")
            .filter(Boolean);
          return (
            <div key={notice.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div><b style={{ fontSize: 18 }}>[{notice.category}] {notice.title}</b><div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{notice.target} · {noticeDateLabel(notice)}</div></div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <StatusBadge tone={notice.hidden ? "red" : "blue"}>{notice.hidden ? "비노출" : "노출중"}</StatusBadge>
                  {isNoticeCategory(notice, "업무 요청") && (
                    <StatusBadge tone={notice.taskStatus !== "처리완료" ? "red" : "blue"}>{notice.taskStatus || "미확인"}</StatusBadge>
                  )}
                  <StatusBadge tone="blue">{`확인 ${checkedCount}명`}</StatusBadge>
                  {confirmationSummary.isAllStore && (
                    <StatusBadge tone={uncheckedCount > 0 ? "red" : "blue"}>{uncheckedCount > 0 ? `미확인 ${uncheckedCount}명` : "전체 확인 완료"}</StatusBadge>
                  )}
                </div>
              </div>
              {openAdminNoticeId === notice.id && (
                <>
                  <div style={{ marginTop: 10, lineHeight: 1.7 }}>{notice.content}</div>
                  <AttachmentList files={notice.attachments || []} title="첨부파일" />
                  {notice.products?.length > 0 && <div style={{ marginTop: 10, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: 10, fontSize: 13 }}><b>관련 상품</b>{notice.products.map((product) => <div key={product.code} style={{ wordBreak: "break-all", overflowWrap: "anywhere", marginTop: 4 }}>- {product.name} ({product.code})</div>)}</div>}
                  <div style={{ marginTop: 10, fontSize: 13, color: "#374151" }}>확인자: {confirmationSummary.checkedNames.length ? confirmationSummary.checkedNames.join(", ") : "-"}</div>
                  {confirmationSummary.isAllStore && (
                    <div style={{ marginTop: 6, fontSize: 13, color: uncheckedCount > 0 ? "#b91c1c" : "#2563eb" }}>미확인자: {uncheckedNames.length ? uncheckedNames.join(", ") : "없음"}</div>
                  )}
                  {isNoticeCategory(notice, "업무 요청") && <div style={{ marginTop: 6, fontSize: 13, color: "#374151" }}>확인자: {notice.taskCheckedBy || "-"} / 처리자: {notice.taskCompletedBy || "-"}</div>}
                </>
              )}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                <button style={darkButton} onClick={() => openNoticeDetail(notice.id, "admin")}>{openAdminNoticeId === notice.id ? "접기" : "내용보기"}</button>
                <button style={adminNoticeSaving ? grayButton : notice.hidden ? blueButton : orangeButton} onClick={() => handleToggleAdminNoticeHidden(notice.id)} disabled={adminNoticeSaving}>{adminNoticeSaving ? "처리중입니다..." : notice.hidden ? "재노출" : "비노출"}</button>
                <button style={adminNoticeSaving ? grayButton : darkButton} onClick={() => handleEditAdminNotice(notice)} disabled={adminNoticeSaving}>수정</button>
                <button style={grayButton} onClick={() => handleDeleteAdminNotice(notice.id)} disabled={adminNoticeSaving}>{adminNoticeSaving ? "처리중입니다..." : "삭제"}</button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const AdminWorkRequestPage = () => {
    const filteredAdminRequests = getFilteredAdminWorkRequests();
    const summary = {
      total: workRequests.length,
      received: workRequests.filter((request) => request.status === "접수").length,
      checking: workRequests.filter((request) => request.status === "본사 확인").length,
      done: workRequests.filter((request) => request.status === "처리완료").length,
    };
    const adminWorkRequestSummaryCards = [
      { status: "전체", label: "전체", count: summary.total, background: "#f9fafb", border: "#e5e7eb", color: "#111827" },
      { status: "접수", label: "접수", count: summary.received, background: "#fff5f5", border: "#fecaca", color: "#b91c1c" },
      { status: "본사 확인", label: "본사 확인", count: summary.checking, background: "#fffbeb", border: "#fde68a", color: "#92400e" },
      { status: "처리완료", label: "처리완료", count: summary.done, background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
    ];
    const getAdminWorkRequestSummaryStyle = (item) => ({
      ...clickableCardStyle,
      background: item.background,
      border: adminWorkRequestFilter.status === item.status ? "2px solid #dc2626" : `1px solid ${item.border}`,
      borderRadius: 16,
      padding: 14,
    });

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="업무 요청 관리" desc="직원이 등록한 이슈보고, A/S, 재고이동, 반품, 배송 요청을 확인하고 처리합니다." />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          {adminWorkRequestSummaryCards.map((item) => (
            <button key={item.status} type="button" style={getAdminWorkRequestSummaryStyle(item)} onClick={() => { setAdminWorkRequestFilter({ type: "전체", status: item.status, store: "전체 매장", start: "", end: "", keyword: "" }); setOpenAdminWorkRequestId(null); }} onMouseEnter={handleClickableCardMouseEnter} onMouseLeave={handleClickableCardMouseLeave}>
              <small>{item.label}</small><br /><b style={{ fontSize: 26, color: item.color }}>{item.count}건</b>
            </button>
          ))}
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr 1fr 1.4fr", gap: 8 }}>
            <select value={adminWorkRequestFilter.type} onChange={(e) => setAdminWorkRequestFilter((prev) => ({ ...prev, type: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              {requestTypes.map((type) => <option key={type}>{type}</option>)}
            </select>
            <select value={adminWorkRequestFilter.status} onChange={(e) => setAdminWorkRequestFilter((prev) => ({ ...prev, status: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              <option>미처리</option>
              <option>접수</option>
              <option>본사 확인</option>
              <option>처리완료</option>
            </select>
            <select value={adminWorkRequestFilter.store} onChange={(e) => setAdminWorkRequestFilter((prev) => ({ ...prev, store: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체 매장</option>
              {stores.map((store) => <option key={store}>{store}</option>)}
              <option>본사</option>
            </select>
            <input value={adminWorkRequestFilter.start} onChange={(e) => setAdminWorkRequestFilter((prev) => ({ ...prev, start: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
            <input value={adminWorkRequestFilter.end} onChange={(e) => setAdminWorkRequestFilter((prev) => ({ ...prev, end: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
            <input value={adminWorkRequestFilter.keyword} onChange={(e) => setAdminWorkRequestFilter((prev) => ({ ...prev, keyword: e.target.value }))} placeholder="요청내용/요청자/상품 검색" style={{ ...input, width: "100%" }} />
          </div>
          <button style={{ ...grayButton, width: "100%", marginTop: 8 }} onClick={handleResetAdminWorkRequestFilter}>검색조건 초기화</button>
        </div>

        {filteredAdminRequests.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 업무 요청이 없습니다.</div>
        ) : filteredAdminRequests.map((request) => {
          const open = openAdminWorkRequestId === request.id;
          return (
            <div key={request.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderLeft: request.status === "처리완료" ? "5px solid #2563eb" : request.status === "본사 확인" ? "5px solid #f59e0b" : "5px solid #dc2626", borderRadius: 18, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <b style={{ fontSize: 17 }}>{request.type} · {request.receiveStore} → {request.targetStore}</b>
                  <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{request.date} · 요청자 {request.writer}</div>
                </div>
                <StatusBadge tone={request.status === "처리완료" ? "blue" : request.status === "본사 확인" ? "yellow" : "red"}>{request.status}</StatusBadge>
              </div>

              <div style={{ marginTop: 10, lineHeight: 1.6, fontSize: 14, color: "#374151" }}>
                {open ? request.content : `${request.content.slice(0, 80)}${request.content.length > 80 ? "..." : ""}`}
              </div>

              {open && (
                <>
                  {request.products?.length > 0 && (
                    <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>
                      <b>요청 상품</b>
                      {request.products.map((product, idx) => (
                        <div key={idx} style={{ marginTop: 5, wordBreak: "break-all", overflowWrap: "anywhere" }}>- {product.name} ({product.code}) / 수량 {product.qty || "1"}{product.option ? ` / ${product.option}` : ""}</div>
                      ))}
                    </div>
                  )}

                  <AttachmentList files={getWorkRequestDisplayAttachments(request)} title="첨부파일" />

                  {(request.customerName || request.customerContact) && (
                    <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>고객정보: {request.customerName || "-"} / {request.customerContact || "-"}</div>
                  )}

                  {request.asInfo && (
                    <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13, lineHeight: 1.7 }}>
                      <b>A/S 접수 정보</b><br />
                      고객 주소: {request.asInfo.customerAddress || "-"}<br />
                      구매 지점: {request.asInfo.purchaseStore || "-"} / 구매일: {request.asInfo.purchaseDate || "-"}<br />
                      증상: {request.asInfo.symptom || "-"} / 수령방법: {request.asInfo.receiveMethod || "-"}<br />
                      접수 담당자: {request.asInfo.receptionManager || "-"} / 접수 지점: {request.asInfo.receptionStore || "-"}
                    </div>
                  )}

                  {request.deliveryInfo && <div style={{ marginTop: 10, fontSize: 13, overflowWrap: "anywhere" }}>배송 주소: {requestAddressText(request.deliveryInfo) || "-"}</div>}

                  <textarea
                    value={adminWorkRequestMemos[request.id] ?? request.adminMemo ?? ""}
                    onChange={(e) => setAdminWorkRequestMemos((prev) => ({ ...prev, [request.id]: e.target.value }))}
                    placeholder="본사 처리 메모 입력"
                    style={{ ...input, width: "100%", minHeight: 78, marginTop: 12 }}
                  />
                  {request.adminMemo && <div style={{ marginTop: 8, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 10, fontSize: 13 }}>기존 처리 메모: {request.adminMemo}</div>}
                </>
              )}

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                <button style={darkButton} onClick={() => openAdminWorkRequestDetail(request.id)}>{open ? "접기" : "내용보기"}</button>
                <button style={orangeButton} onClick={() => handleUpdateWorkRequestStatus(request.id, "본사 확인")}>본사 확인</button>
                <button style={blueButton} onClick={() => handleUpdateWorkRequestStatus(request.id, "처리완료")}>처리완료</button>
                <button style={grayButton} onClick={() => handleDeleteWorkRequest(request.id)}>삭제</button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const AdminSupplyOrderPage = () => {
    const filteredOrders = getFilteredAdminSupplyOrders();
    const summary = {
      total: supplyOrders.length,
      received: supplyOrders.filter((order) => order.status === "접수").length,
      checking: supplyOrders.filter((order) => order.status === "본사 확인").length,
      done: supplyOrders.filter((order) => order.status === "처리완료").length,
    };
    const adminSupplyOrderSummaryCards = [
      { status: "전체", label: "전체", count: summary.total, background: "#f9fafb", border: "#e5e7eb", color: "#111827" },
      { status: "접수", label: "접수", count: summary.received, background: "#fff5f5", border: "#fecaca", color: "#b91c1c" },
      { status: "본사 확인", label: "본사 확인", count: summary.checking, background: "#fffbeb", border: "#fde68a", color: "#92400e" },
      { status: "처리완료", label: "처리완료", count: summary.done, background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
    ];
    const getAdminSupplyOrderSummaryStyle = (item) => ({
      ...clickableCardStyle,
      background: item.background,
      border: adminSupplyOrderFilter.status === item.status ? "2px solid #dc2626" : `1px solid ${item.border}`,
      borderRadius: 16,
      padding: 14,
    });

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="소모품 주문 관리" desc="매장에서 요청한 소모품 주문을 확인하고 처리합니다." />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          {adminSupplyOrderSummaryCards.map((item) => (
            <button key={item.status} type="button" style={getAdminSupplyOrderSummaryStyle(item)} onClick={() => { setAdminSupplyOrderFilter({ store: "전체 매장", status: item.status, start: "", end: "", keyword: "" }); setOpenAdminSupplyOrderId(null); }} onMouseEnter={handleClickableCardMouseEnter} onMouseLeave={handleClickableCardMouseLeave}>
              <small>{item.label}</small><br /><b style={{ fontSize: 26, color: item.color }}>{item.count}건</b>
            </button>
          ))}
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr 1.5fr", gap: 8 }}>
            <select value={adminSupplyOrderFilter.store} onChange={(e) => setAdminSupplyOrderFilter((prev) => ({ ...prev, store: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체 매장</option>
              {stores.map((store) => <option key={store}>{store}</option>)}
            </select>
            <select value={adminSupplyOrderFilter.status} onChange={(e) => setAdminSupplyOrderFilter((prev) => ({ ...prev, status: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              <option>미처리</option>
              <option>접수</option>
              <option>본사 확인</option>
              <option>처리완료</option>
            </select>
            <input value={adminSupplyOrderFilter.start} onChange={(e) => setAdminSupplyOrderFilter((prev) => ({ ...prev, start: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
            <input value={adminSupplyOrderFilter.end} onChange={(e) => setAdminSupplyOrderFilter((prev) => ({ ...prev, end: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
            <input value={adminSupplyOrderFilter.keyword} onChange={(e) => setAdminSupplyOrderFilter((prev) => ({ ...prev, keyword: e.target.value }))} placeholder="요청자/품목/사유 검색" style={{ ...input, width: "100%" }} />
          </div>
          <button style={{ ...grayButton, width: "100%", marginTop: 8 }} onClick={handleResetAdminSupplyOrderFilter}>검색조건 초기화</button>
        </div>

        {filteredOrders.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 소모품 주문이 없습니다.</div>
        ) : filteredOrders.map((order) => {
          const open = openAdminSupplyOrderId === order.id;
          return (
            <div key={order.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderLeft: order.status === "처리완료" ? "5px solid #2563eb" : order.status === "본사 확인" ? "5px solid #f59e0b" : "5px solid #dc2626", borderRadius: 18, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <b style={{ fontSize: 17 }}>{order.store} · {order.items.length}개 품목</b>
                  <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{order.date} · 요청자 {order.writer}</div>
                </div>
                <StatusBadge tone={order.status === "처리완료" ? "blue" : order.status === "본사 확인" ? "yellow" : "red"}>{order.status}</StatusBadge>
              </div>

              {open && (
                <>
                  <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13, lineHeight: 1.7 }}>
                    <b>주문 품목</b>
                    {order.items.map((item, idx) => (
                      <div key={`${item.name}-${idx}`} style={{ marginTop: 5, wordBreak: "break-all", overflowWrap: "anywhere" }}>
                        - {item.name} / {item.qty}{item.unit && item.unit !== "기타" ? ` (${item.unit})` : ""}{item.link ? ` / ${item.link}` : ""}
                      </div>
                    ))}
                  </div>
                  {order.reason && order.reason !== "-" && <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>요청 사유: {order.reason}</div>}
                  <textarea
                    value={adminSupplyOrderMemos[order.id] ?? order.adminMemo ?? ""}
                    onChange={(e) => setAdminSupplyOrderMemos((prev) => ({ ...prev, [order.id]: e.target.value }))}
                    placeholder="본사 처리 메모 입력"
                    style={{ ...input, width: "100%", minHeight: 78, marginTop: 12 }}
                  />
                  {order.adminMemo && <div style={{ marginTop: 8, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 10, fontSize: 13 }}>기존 처리 메모: {order.adminMemo}</div>}
                </>
              )}

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                <button style={darkButton} onClick={() => setOpenAdminSupplyOrderId(open ? null : order.id)}>{open ? "접기" : "내용보기"}</button>
                <button style={orangeButton} onClick={() => handleUpdateSupplyOrderStatus(order.id, "본사 확인")}>본사 확인</button>
                <button style={blueButton} onClick={() => handleUpdateSupplyOrderStatus(order.id, "처리완료")}>처리완료</button>
                <button style={grayButton} onClick={() => handleDeleteSupplyOrder(order.id)}>삭제</button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const AdminSupplyItemPage = () => (
    <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
      <SectionTitle title="소모품 DB 관리" desc="직원 소모품 주문 페이지에 표시될 자주 주문하는 소모품 목록을 관리합니다." />

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>소모품 등록</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr 1fr", gap: 10 }}>
          <input ref={supplyItemNameRef} placeholder="소모품명 예: 쇼핑백" style={{ ...input, width: "100%" }} />
          <input ref={supplyItemUnitRef} placeholder="기본 단위 예: 100장" style={{ ...input, width: "100%" }} />
          <input ref={supplyItemCategoryRef} placeholder="카테고리 예: 포장" style={{ ...input, width: "100%" }} />
          <input ref={supplyItemMemoRef} placeholder="관리 메모 예: 코스모스몰 구매 / 쿠팡 링크 / 본사 창고 재고 확인" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 2" }} />
          <button style={redButton} onClick={handleAddSupplyItem}>등록</button>
        </div>
      </div>

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "0.8fr 1fr auto auto", gap: 8 }}>
          <select
            value={supplyItemCategoryFilter}
            onChange={(e) => {
              setSupplyItemCategoryFilter(e.target.value);
              setSupplyItemPage(1);
            }}
            style={{ ...input, width: "100%", background: "#fff" }}
          >
            <option>전체</option>
            {supplyItemCategoryOptions.map((category) => <option key={category}>{category}</option>)}
          </select>
          <input
            ref={supplyItemSearchRef}
            defaultValue={supplyItemSearch}
            placeholder="소모품명, 단위, 카테고리, 메모 검색"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSupplyItemSearch(supplyItemSearchRef.current?.value || "");
                setSupplyItemPage(1);
              }
            }}
            style={{ ...input, width: "100%" }}
          />
          <button style={darkButton} onClick={() => { setSupplyItemSearch(supplyItemSearchRef.current?.value || ""); setSupplyItemPage(1); }}>검색</button>
          <button style={grayButton} onClick={handleResetSupplyItemSearch}>초기화</button>
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 10 }}>
          전체 {supplyItems.length}개 · 활성화 {activeSupplyItems.length}개 · 조회 {filteredSupplyItems.length}개
        </div>
      </div>

      {filteredSupplyItems.length === 0 ? (
        <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 소모품이 없습니다.</div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {pagedSupplyItems.map((item) => {
            const isEditing = editingSupplyItemId === item.id;
            return (
              <div key={item.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, minWidth: 0, boxSizing: "border-box", maxWidth: "100%" }}>
                {!isEditing ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ minWidth: 0, maxWidth: "100%" }}>
                        <b style={{ fontSize: 18, wordBreak: "break-word", overflowWrap: "anywhere" }}>{item.name}</b>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4, wordBreak: "break-word", overflowWrap: "anywhere" }}>단위: {item.unit} · 카테고리: {item.category}</div>
                        {item.memo && <div style={{ fontSize: 13, color: "#374151", marginTop: 8, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10, wordBreak: "break-word", overflowWrap: "anywhere", maxWidth: "100%", boxSizing: "border-box" }}>메모: {item.memo}</div>}
                      </div>
                      <StatusBadge tone={item.status === "활성화" ? "blue" : "red"}>{item.status}</StatusBadge>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                      <button style={grayButton} onClick={() => handleToggleSupplyItemStatus(item.id)}>{item.status === "활성화" ? "비활성화" : "활성화"}</button>
                      <button style={darkButton} onClick={() => handleStartEditSupplyItem(item)}>수정</button>
                      <button style={redButton} onClick={() => handleDeleteSupplyItem(item.id)}>삭제</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr 1fr", gap: 10 }}>
                      <input ref={editSupplyItemNameRef} placeholder="소모품명" style={{ ...input, width: "100%" }} />
                      <input ref={editSupplyItemUnitRef} placeholder="기본 단위" style={{ ...input, width: "100%" }} />
                      <input ref={editSupplyItemCategoryRef} placeholder="카테고리" style={{ ...input, width: "100%" }} />
                      <input ref={editSupplyItemMemoRef} placeholder="관리 메모" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 3" }} />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                      <button style={grayButton} onClick={() => setEditingSupplyItemId(null)}>취소</button>
                      <button style={redButton} onClick={() => handleSaveEditSupplyItem(item.id)}>저장</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      {filteredSupplyItems.length > supplyItemPageSize && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", marginTop: 14 }}>
          <button style={safeSupplyItemPage === 1 ? grayButton : darkButton} disabled={safeSupplyItemPage === 1} onClick={() => setSupplyItemPage((prev) => Math.max(1, prev - 1))}>이전</button>
          {supplyItemPageNumbers.map((page) => (
            <button key={page} style={page === safeSupplyItemPage ? redButton : grayButton} onClick={() => setSupplyItemPage(page)}>
              {page}
            </button>
          ))}
          <button style={safeSupplyItemPage === supplyItemTotalPages ? grayButton : darkButton} disabled={safeSupplyItemPage === supplyItemTotalPages} onClick={() => setSupplyItemPage((prev) => Math.min(supplyItemTotalPages, prev + 1))}>다음</button>
        </div>
      )}
    </div>
  );

  const AdminHandoverPage = () => {
    const filteredAdminHandovers = getFilteredAdminHandovers();
    const summary = {
      total: handovers.length,
      pending: handovers.filter((item) => item.status === "확인 필요").length,
      done: handovers.filter((item) => item.status === "처리완료").length,
      important: handovers.filter((item) => item.important).length,
    };
    const adminHandoverSummaryCards = [
      { status: "전체", importantOnly: false, label: "전체", count: summary.total, background: "#f9fafb", border: "#e5e7eb", color: "#111827" },
      { status: "확인 필요", importantOnly: false, label: "확인 필요", count: summary.pending, background: "#fff5f5", border: "#fecaca", color: "#b91c1c" },
      { status: "처리완료", importantOnly: false, label: "처리완료", count: summary.done, background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
      { status: "전체", importantOnly: true, label: "중요", count: summary.important, background: "#fffbeb", border: "#fde68a", color: "#92400e" },
    ];
    const getAdminHandoverSummaryStyle = (item) => ({
      ...clickableCardStyle,
      background: item.background,
      border: adminHandoverFilter.status === item.status && Boolean(adminHandoverFilter.importantOnly) === item.importantOnly ? "2px solid #dc2626" : `1px solid ${item.border}`,
      borderRadius: 16,
      padding: 14,
    });

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="인수인계 관리" desc="매장별 인수인계 현황과 처리 상태를 확인합니다." />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          {adminHandoverSummaryCards.map((item) => (
            <button key={`${item.label}-${item.importantOnly}`} type="button" style={getAdminHandoverSummaryStyle(item)} onClick={() => { setAdminHandoverFilter({ store: "전체 매장", status: item.status, start: "", end: "", keyword: "", importantOnly: item.importantOnly }); setOpenAdminHandoverId(null); }} onMouseEnter={handleClickableCardMouseEnter} onMouseLeave={handleClickableCardMouseLeave}>
              <small>{item.label}</small><br /><b style={{ fontSize: 26, color: item.color }}>{item.count}건</b>
            </button>
          ))}
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr 1.4fr", gap: 8 }}>
            <select value={adminHandoverFilter.store} onChange={(e) => setAdminHandoverFilter((prev) => ({ ...prev, store: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체 매장</option>
              {stores.map((store) => <option key={store}>{store}</option>)}
            </select>
            <select value={adminHandoverFilter.status} onChange={(e) => setAdminHandoverFilter((prev) => ({ ...prev, status: e.target.value, importantOnly: false }))} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              <option>확인 필요</option>
              <option>처리완료</option>
            </select>
            <input value={adminHandoverFilter.start} onChange={(e) => setAdminHandoverFilter((prev) => ({ ...prev, start: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
            <input value={adminHandoverFilter.end} onChange={(e) => setAdminHandoverFilter((prev) => ({ ...prev, end: e.target.value }))} type="date" style={{ ...input, width: "100%" }} />
            <input value={adminHandoverFilter.keyword} onChange={(e) => setAdminHandoverFilter((prev) => ({ ...prev, keyword: e.target.value }))} placeholder="제목/내용/작성자/처리자 검색" style={{ ...input, width: "100%" }} />
          </div>
          <button style={{ ...grayButton, width: "100%", marginTop: 8 }} onClick={handleResetAdminHandoverFilter}>검색조건 초기화</button>
        </div>

        {filteredAdminHandovers.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 인수인계가 없습니다.</div>
        ) : filteredAdminHandovers.map((item) => {
          const open = openAdminHandoverId === item.id;
          return (
            <div key={item.id} style={{ background: item.important ? "#fff5f5" : "#f9fafb", border: item.important ? "1px solid #fecaca" : "1px solid #e5e7eb", borderLeft: item.status === "처리완료" ? "5px solid #2563eb" : "5px solid #dc2626", borderRadius: 18, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <b style={{ fontSize: 17 }}>{item.store} · {item.title}</b>
                  <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{item.date} · 작성자 {item.writer}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {item.important && <StatusBadge tone="yellow">중요</StatusBadge>}
                  <StatusBadge tone={item.status === "처리완료" ? "blue" : "red"}>{item.status}</StatusBadge>
                </div>
              </div>

              {open && (
                <>
                  <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 12, lineHeight: 1.7 }}>{item.content}</div>
                  <AttachmentList files={item.attachments || []} title="첨부파일" />
                  <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13, lineHeight: 1.7 }}>
                    확인 필수: {item.needCheck ? "예" : "아니오"}<br />
                    처리자: {item.checkedBy || "-"}{item.checkedAt ? ` · 처리일시 ${item.checkedAt}` : ""}
                  </div>
                </>
              )}

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : item.status === "처리완료" ? "repeat(2, auto)" : "repeat(3, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                <button style={darkButton} onClick={() => openAdminHandoverDetail(item.id)}>{open ? "접기" : "내용보기"}</button>
                {item.status !== "처리완료" && <button style={blueButton} onClick={() => handleAdminCompleteHandover(item.id)}>관리자 처리완료</button>}
                <button style={grayButton} onClick={() => handleDeleteHandover(item.id)}>삭제</button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const AdminProductDbPage = () => (
    <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
      <datalist id="product-category-options">
        {productCategories.map((category) => <option key={category} value={category} />)}
      </datalist>
      <SectionTitle title="상품 DB / 유통기한 관리" desc="상품 DB를 엑셀 양식(CSV)으로 일괄 등록하고, 유통기한 관리 대상 상품도 함께 관리합니다." />

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10, marginBottom: 18 }}>
        {[
          { label: "6개월 이내", value: "6", count: getExpiryRangeCount(expiryProductList, 6), color: "#92400e" },
          { label: "3개월 이내", value: "3", count: getExpiryRangeCount(expiryProductList, 3), color: "#c2410c" },
          { label: "1개월 이내", value: "1", count: getExpiryRangeCount(expiryProductList, 1), color: "#b91c1c" },
        ].map((item) => (
          <button key={item.value} type="button" onClick={() => { setProductDbExpiryFilter(productDbExpiryFilter === item.value ? "전체" : item.value); setProductDbSearch(""); setProductDbCategoryFilter("전체"); if (productDbSearchRef.current) productDbSearchRef.current.value = ""; setProductDbPage(1); }} onMouseEnter={handleClickableCardMouseEnter} onMouseLeave={handleClickableCardMouseLeave} style={{ ...clickableCardStyle, background: productDbExpiryFilter === item.value ? "#fff5f5" : "#fff", border: productDbExpiryFilter === item.value ? "2px solid #dc2626" : "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
            <b>유통기한 {item.label}</b><br />
            <span style={{ fontSize: 28, fontWeight: "bold", color: item.color }}>{item.count}건</span>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>클릭하면 해당 상품만 조회됩니다.</div>
          </button>
        ))}
      </div>

      <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>엑셀 양식 업로드</h3>
        <div style={{ fontSize: 13, color: "#9a3412", lineHeight: 1.7, marginBottom: 12 }}>
          양식 파일을 다운로드한 뒤 Excel에서 작성하고 CSV 형식으로 저장해 업로드하세요.<br />
          필수값은 상품명/상품코드이며, 상품코드가 기존 DB와 중복되면 해당 상품은 등록되지 않습니다.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "auto 1fr", gap: 10 }}>
          <button style={darkButton} onClick={downloadProductTemplate}>상품 등록 양식 다운로드</button>
          <input
            ref={productImportFileRef}
            type="file"
            accept=".csv"
            disabled={isImportingProductCsv}
            onClick={handleProductCsvFileDialogOpen}
            onChange={handleImportProductCsv}
            style={{ ...input, width: "100%", background: "#fff", opacity: isImportingProductCsv ? 0.65 : 1 }}
          />
        </div>
        {isImportingProductCsv && <div style={{ fontSize: 13, color: "#9a3412", marginTop: 8 }}>상품 저장중입니다...</div>}
      </div>

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>상품 개별 등록</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.5fr 1fr", gap: 10 }}>
          <input ref={productCodeRef} placeholder="상품코드 예: RC-GEL-015" style={{ ...input, width: "100%" }} />
          <input ref={productNameRef} placeholder="상품명" style={{ ...input, width: "100%" }} />
          <input ref={productCategoryRef} list="product-category-options" placeholder="카테고리 예: 콘돔, 젤, 향수" style={{ ...input, width: "100%" }} />
          <input ref={productBarcodeRef} placeholder="바코드" style={{ ...input, width: "100%" }} />
          <input ref={productSalePriceRef} placeholder="판매가" inputMode="numeric" style={{ ...input, width: "100%" }} />
          <input ref={productWholesalePriceRef} placeholder="도매가" inputMode="numeric" style={{ ...input, width: "100%" }} />
          <input ref={productCtQuantityRef} placeholder="CT입수량" inputMode="numeric" style={{ ...input, width: "100%" }} />
          <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>제조일자<input ref={productProductionDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
          <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>유통기한<input ref={productExpiryRef} type="date" style={{ ...input, width: "100%" }} /></label>
          <input ref={productImageUrlRef} placeholder="이미지경로 URL 예: https://example.com/product.jpg" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 2" }} />
          <input ref={productNoteRef} placeholder="기타메시지 예: 선진열 권장" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 3" }} />
        </div>
        <button style={{ ...redButton, width: "100%", marginTop: 10 }} onClick={handleAddProductDbItem}>등록</button>
        <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>제조일자/유통기한을 입력한 상품은 사용자 유통기한 관리 페이지에 표시됩니다.</div>
      </div>

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "0.8fr 0.8fr 1.4fr auto auto", gap: 8 }}>
          <select value={productDbCategoryFilter} onChange={(e) => { setProductDbCategoryFilter(e.target.value); setProductDbPage(1); }} style={{ ...input, width: "100%" }}>
            <option>전체</option>
            {productCategories.map((category) => <option key={category}>{category}</option>)}
          </select>
          <select value={productDbExpiryFilter} onChange={(e) => { setProductDbExpiryFilter(e.target.value); setProductDbPage(1); }} style={{ ...input, width: "100%" }}>
            <option value="전체">유통기한 전체</option>
            <option value="6">6개월 이내</option>
            <option value="3">3개월 이내</option>
            <option value="1">1개월 이내</option>
          </select>
          <input
            ref={productDbSearchRef}
            defaultValue={productDbSearch}
            placeholder="상품코드, 상품명, 카테고리, 바코드, CT입수량, 이미지경로 검색"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearchProductDb();
            }}
            style={{ ...input, width: "100%" }}
          />
          <button style={darkButton} onClick={handleSearchProductDb}>검색</button>
          <button style={grayButton} onClick={handleResetProductDbSearch}>초기화</button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
          <div style={{ fontSize: 13, color: "#6b7280" }}>전체 {productList.length}개 · 활성화 {activeProductList.length}개 · 조회 {filteredProductDbItems.length}개 · 유통기한 관리 {expiryProductList.length}개</div>
          <button style={blueButton} onClick={downloadFilteredProductDbList}>검색결과 CSV 다운로드</button>
        </div>
      </div>

      {filteredProductDbItems.length === 0 ? (
        <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 상품이 없습니다.</div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {pagedProductDbItems.map((product) => {
            const isEditing = editingProductCode === product.code;
            const expiryBadge = getExpiryBadgeInfo(product.expiry);
            return (
              <div key={product.code} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
                {!isEditing ? (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "96px 1fr auto", gap: 12, alignItems: "center" }}>
                      <div style={{ width: 96, height: 96, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        ) : (
                          <span style={{ fontSize: 12, color: "#9ca3af" }}>이미지 없음</span>
                        )}
                      </div>
                      <div>
                        <b style={{ fontSize: 18 }}>{product.name}</b>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>코드: {product.code} · 카테고리: {product.category || "기타"} · 바코드: {product.barcode || "-"}</div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>판매가: {formatProductPrice(product.salePrice)} · 도매가: {formatProductPrice(product.wholesalePrice)} · CT입수량: {getProductCtQuantity(product) || "-"}</div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>제조일자: {product.productionDate || "-"} · 유통기한: {product.expiry || "-"} · 메시지: {product.note || "-"}</div>
                        {product.imageUrl && <div style={{ fontSize: 12, color: "#2563eb", marginTop: 4, wordBreak: "break-all", overflowWrap: "anywhere" }}>이미지: {product.imageUrl}</div>}
                      </div>
                      <div style={{ display: "flex", gap: 6, justifyContent: isMobile ? "flex-start" : "flex-end", flexWrap: "wrap" }}>
                        <StatusBadge tone={product.status === "비활성화" ? "red" : "blue"}>{product.status || "활성화"}</StatusBadge>
                        {expiryBadge.urgent && <StatusBadge tone={expiryBadge.tone}>{expiryBadge.label}</StatusBadge>}
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                      <button style={grayButton} onClick={() => handleToggleProductDbStatus(product.code)}>{product.status === "비활성화" ? "활성화" : "비활성화"}</button>
                      <button style={darkButton} onClick={() => handleStartEditProductDb(product)}>수정</button>
                      <button style={redButton} onClick={() => handleDeleteProductDbItem(product.code)}>삭제</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.5fr 1fr", gap: 10 }}>
                      <input ref={editProductCodeRef} placeholder="상품코드" style={{ ...input, width: "100%" }} />
                      <input ref={editProductNameRef} placeholder="상품명" style={{ ...input, width: "100%" }} />
                      <input ref={editProductCategoryRef} list="product-category-options" placeholder="카테고리" style={{ ...input, width: "100%" }} />
                      <input ref={editProductBarcodeRef} placeholder="바코드" style={{ ...input, width: "100%" }} />
                      <input ref={editProductSalePriceRef} placeholder="판매가" inputMode="numeric" style={{ ...input, width: "100%" }} />
                      <input ref={editProductWholesalePriceRef} placeholder="도매가" inputMode="numeric" style={{ ...input, width: "100%" }} />
                      <input ref={editProductCtQuantityRef} placeholder="CT입수량" inputMode="numeric" style={{ ...input, width: "100%" }} />
                      <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>제조일자<input ref={editProductProductionDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
                      <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>유통기한<input ref={editProductExpiryRef} type="date" style={{ ...input, width: "100%" }} /></label>
                      <input ref={editProductImageUrlRef} placeholder="이미지경로 URL" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 2" }} />
                      <input ref={editProductNoteRef} placeholder="기타메시지" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 3" }} />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                      <button style={grayButton} onClick={() => setEditingProductCode(null)}>취소</button>
                      <button style={redButton} onClick={() => handleSaveEditProductDb(product.code)}>저장</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          {productDbTotalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
              <button style={safeProductDbPage === 1 ? grayButton : darkButton} disabled={safeProductDbPage === 1} onClick={() => setProductDbPage((prev) => Math.max(1, prev - 1))}>이전</button>
              {productDbPageNumbers.map((page, index) => (
                <button
                  key={`${page}-${index}`}
                  style={page === safeProductDbPage ? redButton : grayButton}
                  onClick={() => setProductDbPage(page)}
                >
                  {page}
                </button>
              ))}
              <button style={safeProductDbPage === productDbTotalPages ? grayButton : darkButton} disabled={safeProductDbPage === productDbTotalPages} onClick={() => setProductDbPage((prev) => Math.min(productDbTotalPages, prev + 1))}>다음</button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const AdminProductionPage = () => {
    const liveMaterials = getProductionMaterialRowsForSave();
    const liveCosts = getProductionCostSummary(productionQtyRef.current?.value || "", liveMaterials, productionOtherCostRef.current?.value || "");
    const renderProductCard = (product) => {
      const imageUrl = getProductImageUrl(product);
      return (
        <button key={product.code || product.name} type="button" onClick={() => handleSelectProductionProduct(product)} style={{ textAlign: "left", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 10, cursor: "pointer" }}>
          <div style={{ display: "grid", gridTemplateColumns: "58px 1fr", gap: 10, alignItems: "center" }}>
            <div style={{ width: 58, height: 58, borderRadius: 12, background: "#f3f4f6", border: "1px solid #e5e7eb", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {imageUrl ? <img src={imageUrl} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span style={{ fontSize: 11, color: "#9ca3af" }}>이미지 없음</span>}
            </div>
            <div>
              <b>{product.name}</b>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>코드 {product.code || "-"} · 바코드 {product.barcode || "-"}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>판매가 {formatProductPrice(product.salePrice, true)} · CT입수량 {getProductCtQuantity(product) || "-"}</div>
            </div>
          </div>
        </button>
      );
    };

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="생산관리" desc="자체 생산 또는 제조 상품의 생산 이력과 원가를 누적 관리합니다." />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          {[
            { label: "총 기록 수", value: `${productionSummary.count}건` },
            { label: "이번 달 기록", value: `${productionSummary.monthCount}건` },
            { label: "총 생산수량", value: `${formatNumber(productionSummary.quantity)}개` },
            { label: "총 생산원가", value: formatCostWon(productionSummary.totalCost) },
          ].map((item) => (
            <div key={item.label} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 16, padding: 14 }}>
              <small style={{ color: "#6b7280", fontWeight: "bold" }}>{item.label}</small><br />
              <b style={{ fontSize: 24 }}>{item.value}</b>
            </div>
          ))}
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>{editingProductionId ? "생산기록 수정" : "생산기록 등록"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 8 }}>
            <input ref={productionProductKeywordRef} placeholder="상품명, 상품코드, 바코드 검색" onKeyDown={(e) => { if (e.key === "Enter") handleProductionProductSearch(); }} style={{ ...input, width: "100%" }} />
            <button style={darkButton} onClick={handleProductionProductSearch}>상품 검색</button>
          </div>
          {productionProductResults.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 8, marginTop: 10 }}>
              {productionProductResults.map(renderProductCard)}
            </div>
          )}
          {selectedProductionProduct && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "72px 1fr", gap: 12, alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, marginTop: 12 }}>
              <div style={{ width: 72, height: 72, borderRadius: 12, background: "#f3f4f6", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {selectedProductionProduct.imageUrl ? <img src={selectedProductionProduct.imageUrl} alt={selectedProductionProduct.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 11, color: "#9ca3af" }}>이미지 없음</span>}
              </div>
              <div>
                <b>{selectedProductionProduct.name}</b>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>코드 {selectedProductionProduct.code || "-"} · 바코드 {selectedProductionProduct.barcode || "-"} · 판매가 {formatProductPrice(selectedProductionProduct.salePrice, true)} · CT입수량 {getProductCtQuantity(selectedProductionProduct) || "-"}</div>
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, marginTop: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>생산일<input ref={productionDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>유통기한<input ref={productionExpiryRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <input ref={productionQtyRef} placeholder="생산수량" inputMode="numeric" onChange={() => setProductionCalcTick((prev) => prev + 1)} style={{ ...input, width: "100%" }} />
            <input ref={productionOtherCostRef} placeholder="기타비용" inputMode="numeric" onChange={() => setProductionCalcTick((prev) => prev + 1)} style={{ ...input, width: "100%" }} />
          </div>

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <b>부자재/원재료 항목</b>
              <button style={grayButton} onClick={handleAddProductionMaterialRow}>항목 추가</button>
            </div>
            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
              {productionMaterialRows.map((row) => {
                const amount = toNumber(row.qty) * toNumber(row.unitPrice);
                return (
                  <div key={row.id} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.3fr 1.2fr .7fr .8fr .8fr auto", gap: 8, alignItems: "start" }}>
                    <input value={row.name} onChange={(e) => handleProductionMaterialChange(row.id, "name", e.target.value)} placeholder="항목명" style={{ ...input, width: "100%" }} />
                    <div style={{ position: "relative" }}>
                      <input value={getProductionMaterialSupplierName(row)} onChange={(e) => handleProductionMaterialSupplierChange(row.id, e.target.value)} placeholder="생산처/구입처" style={{ ...input, width: "100%" }} />
                      {row.supplierResults?.length > 0 && (
                        <div style={{ position: isMobile ? "static" : "absolute", zIndex: 20, top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, boxShadow: "0 10px 20px rgba(15,23,42,0.12)", marginTop: 4, overflow: "hidden" }}>
                          {row.supplierResults.map((partner) => (
                            <button key={partner.id || partner.name} type="button" onClick={() => handleSelectProductionMaterialSupplier(row.id, partner)} style={{ display: "block", width: "100%", textAlign: "left", background: "#fff", border: 0, borderBottom: "1px solid #f3f4f6", padding: "9px 10px", cursor: "pointer", fontSize: 13 }}>
                              <b>{partner.name}</b>{partner.type ? <span style={{ color: "#6b7280" }}> · {partner.type}</span> : null}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input value={row.qty} onChange={(e) => handleProductionMaterialChange(row.id, "qty", e.target.value)} placeholder="수량" inputMode="numeric" style={{ ...input, width: "100%" }} />
                    <input value={row.unitPrice} onChange={(e) => handleProductionMaterialChange(row.id, "unitPrice", e.target.value)} placeholder="단가" inputMode="numeric" style={{ ...input, width: "100%" }} />
                    <div style={{ background: "#f9fafb", borderRadius: 12, padding: 12, fontSize: 13, fontWeight: "bold" }}>{formatCostWon(amount)}</div>
                    <button style={grayButton} onClick={() => handleRemoveProductionMaterialRow(row.id)}>삭제</button>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10, marginTop: 12 }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 12 }}><small>총 부자재비</small><br /><b>{formatCostWon(liveCosts.totalMaterialCost)}</b></div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 12 }}><small>총 생산원가</small><br /><b>{formatCostWon(liveCosts.totalCost)}</b></div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 12 }}><small>개당 생산원가</small><br /><b>{formatCostWon(liveCosts.unitCost)}</b></div>
          </div>

          <textarea ref={productionMemoRef} placeholder="메모" style={{ ...input, width: "100%", minHeight: 80, marginTop: 12 }} />
          <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
            <b>첨부파일</b>
            <input ref={productionFileRef} type="file" multiple onChange={handleProductionFileChange} style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }} />
            {renderManagementAttachmentDrafts(productionAttachments, handleRemoveProductionAttachment)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 8, marginTop: 12 }}>
            <button style={{ ...redButton, padding: 14 }} onClick={handleSaveProductionRecord}>{editingProductionId ? "생산기록 수정 저장" : "생산기록 등록"}</button>
            {editingProductionId && <button style={grayButton} onClick={resetProductionForm}>수정 취소</button>}
          </div>
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : ".9fr .9fr 1.6fr auto auto auto auto", gap: 8 }}>
            <input type="date" value={productionFilter.start} onChange={(e) => { setProductionFilter((prev) => ({ ...prev, start: e.target.value })); setProductionPage(1); }} style={{ ...input, width: "100%" }} />
            <input type="date" value={productionFilter.end} onChange={(e) => { setProductionFilter((prev) => ({ ...prev, end: e.target.value })); setProductionPage(1); }} style={{ ...input, width: "100%" }} />
            <input ref={productionFilterKeywordRef} value={productionFilter.keyword} onChange={(e) => { setProductionFilter((prev) => ({ ...prev, keyword: e.target.value })); setProductionPage(1); }} placeholder="상품명, 상품코드, 바코드, 메모 검색" style={{ ...input, width: "100%" }} />
            <button style={grayButton} onClick={() => { setProductionFilter({ start: "", end: "", keyword: "" }); setProductionPage(1); }}>초기화</button>
            <button style={darkButton} onClick={downloadProductionUploadTemplate}>양식 다운로드</button>
            <button style={orangeButton} onClick={() => productionImportFileRef.current?.click()}>CSV 업로드</button>
            <button style={blueButton} onClick={downloadProductionRecordsCsv}>CSV 다운로드</button>
            <input ref={productionImportFileRef} type="file" accept=".csv,text/csv" onChange={handleImportProductionCsv} style={{ display: "none" }} />
          </div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 10 }}>전체 {productionRecords.length}건 · 조회 {filteredProductionRecords.length}건</div>
        </div>

        {filteredProductionRecords.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 생산기록이 없습니다.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {pagedProductionRecords.map((record) => {
              const opened = Boolean(openProductionRecordIds[record.id]);
              const thumbnailUrl = getManagementRecordImageUrl(record);
              const costSummary = getProductionRecordCostSummary(record);
              return (
                <div key={record.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "72px 1fr auto", gap: 12, alignItems: "center" }}>
                    <div style={{ width: 72, height: 72, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {thumbnailUrl ? <img src={thumbnailUrl} alt={record.product?.name || "상품 이미지"} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 11, color: "#9ca3af" }}>이미지 없음</span>}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <b style={{ fontSize: 18 }}>{record.product?.name || "-"}</b>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>생산일자 {record.date || "-"} · 유통기한 {record.productionExpiryDate || record.expiryDate || "-"} · 생산수량 {formatNumber(toNumber(record.quantity))}</div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>총원가 {formatCostWon(costSummary.totalCost)} · 개당 {formatCostWon(costSummary.unitCost)} · {String(record.memo || "").slice(0, 40) || "메모 -"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button style={darkButton} onClick={() => setOpenProductionRecordIds((prev) => ({ ...prev, [record.id]: !prev[record.id] }))}>{opened ? "접기" : "펼쳐보기"}</button>
                      <button style={darkButton} onClick={() => handleStartEditProductionRecord(record)}>수정</button>
                      <button style={grayButton} onClick={() => handleDeleteProductionRecord(record.id)}>삭제</button>
                    </div>
                  </div>
                  {opened && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, fontSize: 13 }}>
                        {(record.productionExpiryDate || record.expiryDate) && <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>유통기한</small><br /><b>{record.productionExpiryDate || record.expiryDate}</b></div>}
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>총 부자재비</small><br /><b>{formatCostWon(costSummary.totalMaterialCost)}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>기타비용</small><br /><b>{formatCostWon(toNumber(record.otherCost))}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>총 생산원가</small><br /><b>{formatCostWon(costSummary.totalCost)}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>개당 생산원가</small><br /><b>{formatCostWon(costSummary.unitCost)}</b></div>
                      </div>
                      {(record.materials || []).length > 0 && (
                        <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>
                          <b>부자재/원재료</b>
                          {(record.materials || []).map((item) => (
                            <div key={item.id || item.name} style={{ marginTop: 6 }}>
                              {item.name || "-"} · 생산처: {getProductionMaterialSupplierName(item) || "-"} · 수량 {item.qty || "-"} · 단가 {formatCostWon(toNumber(item.unitPrice))} · 금액 {formatCostWon(item.amount)}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}><b>메모</b><br />{record.memo || "-"}</div>
                      <AttachmentList files={record.attachments || []} title="첨부파일" />
                    </div>
                  )}
                </div>
              );
            })}
            {productionTotalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
                <button style={safeProductionPage === 1 ? grayButton : darkButton} disabled={safeProductionPage === 1} onClick={() => setProductionPage((prev) => Math.max(1, prev - 1))}>이전</button>
                {productionPageNumbers.map((page) => <button key={page} style={page === safeProductionPage ? redButton : grayButton} onClick={() => setProductionPage(page)}>{page}</button>)}
                <button style={safeProductionPage === productionTotalPages ? grayButton : darkButton} disabled={safeProductionPage === productionTotalPages} onClick={() => setProductionPage((prev) => Math.min(productionTotalPages, prev + 1))}>다음</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const AdminPurchasePage = () => {
    const currency = purchaseCurrencyRef.current?.value || "KRW";
    const liveCosts = getPurchaseCostSummary(
      purchaseQtyRef.current?.value || "",
      currency,
      purchaseForeignUnitPriceRef.current?.value || "",
      purchaseExchangeRateRef.current?.value || ""
    );
    const registeredPartnerIds = new Set(partners.map((partner) => String(partner.id)));
    const manualPartnerOptions = Array.from(new Set(
      purchaseRecords
        .filter((record) => record.partnerName && (!record.partnerId || !registeredPartnerIds.has(String(record.partnerId))))
        .map((record) => record.partnerName)
    ));
    const partnerFilterOptions = [
      ...partners.map((partner) => ({ value: String(partner.id), label: partner.name })),
      ...manualPartnerOptions.map((name) => ({ value: name, label: name })),
    ];
    const renderProductCard = (product) => {
      const imageUrl = getProductImageUrl(product);
      return (
        <button key={product.code || product.name} type="button" onClick={() => handleSelectPurchaseProduct(product)} style={{ textAlign: "left", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 10, cursor: "pointer" }}>
          <div style={{ display: "grid", gridTemplateColumns: "58px 1fr", gap: 10, alignItems: "center" }}>
            <div style={{ width: 58, height: 58, borderRadius: 12, background: "#f3f4f6", border: "1px solid #e5e7eb", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {imageUrl ? <img src={imageUrl} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span style={{ fontSize: 11, color: "#9ca3af" }}>이미지 없음</span>}
            </div>
            <div>
              <b>{product.name}</b>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>코드 {product.code || "-"} · 바코드 {product.barcode || "-"}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>판매가 {formatProductPrice(product.salePrice, true)} · CT입수량 {getProductCtQuantity(product) || "-"}</div>
            </div>
          </div>
        </button>
      );
    };

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="수입/구입 관리" desc="상품 수입 또는 국내구입 이력과 당시 환율 기준 개당 구입원가를 누적 관리합니다." />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          {[
            { label: "총 기록 수", value: `${purchaseSummary.count}건` },
            { label: "이번 달 기록", value: `${purchaseSummary.monthCount}건` },
            { label: "총 구입수량", value: `${formatNumber(purchaseSummary.quantity)}개` },
            { label: "총 구입원가", value: formatCostWon(purchaseSummary.totalCost) },
          ].map((item) => (
            <div key={item.label} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 16, padding: 14 }}>
              <small style={{ color: "#6b7280", fontWeight: "bold" }}>{item.label}</small><br />
              <b style={{ fontSize: 24 }}>{item.value}</b>
            </div>
          ))}
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>{editingPurchaseId ? "수입/구입 기록 수정" : "수입/구입 기록 등록"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 8 }}>
            <input ref={purchaseProductKeywordRef} placeholder="상품명, 상품코드, 바코드 검색" onKeyDown={(e) => { if (e.key === "Enter") handlePurchaseProductSearch(); }} style={{ ...input, width: "100%" }} />
            <button style={darkButton} onClick={handlePurchaseProductSearch}>상품 검색</button>
          </div>
          {purchaseProductResults.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 8, marginTop: 10 }}>
              {purchaseProductResults.map(renderProductCard)}
            </div>
          )}
          {selectedPurchaseProduct && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "72px 1fr", gap: 12, alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, marginTop: 12 }}>
              <div style={{ width: 72, height: 72, borderRadius: 12, background: "#f3f4f6", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {selectedPurchaseProduct.imageUrl ? <img src={selectedPurchaseProduct.imageUrl} alt={selectedPurchaseProduct.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 11, color: "#9ca3af" }}>이미지 없음</span>}
              </div>
              <div>
                <b>{selectedPurchaseProduct.name}</b>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>코드 {selectedPurchaseProduct.code || "-"} · 바코드 {selectedPurchaseProduct.barcode || "-"} · 판매가 {formatProductPrice(selectedPurchaseProduct.salePrice, true)} · CT입수량 {getProductCtQuantity(selectedPurchaseProduct) || "-"}</div>
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, marginTop: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>구입일/수입일<input ref={purchaseDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <select ref={purchaseTypeRef} defaultValue="수입" style={{ ...input, width: "100%" }}><option>수입</option><option>국내구입</option></select>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, gridColumn: isMobile ? "auto" : "span 2" }}>
              <input ref={purchasePartnerKeywordRef} placeholder="거래처명 검색" onKeyDown={(e) => { if (e.key === "Enter") handlePurchasePartnerSearch(); }} style={{ ...input, width: "100%" }} />
              <button style={darkButton} onClick={handlePurchasePartnerSearch}>거래처 검색</button>
            </div>
            {purchasePartnerResults.length > 0 && (
              <div style={{ display: "grid", gap: 8, gridColumn: isMobile ? "auto" : "1 / span 4" }}>
                {purchasePartnerResults.map((partner) => (
                  <button key={partner.id} type="button" onClick={() => handleSelectPurchasePartner(partner)} style={{ textAlign: "left", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10, cursor: "pointer" }}>
                    <b>{partner.name}</b><span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280" }}>{partner.type || "거래처"} · {partner.manager || partner.contact || ""}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedPurchasePartner && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "#fff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 10, gridColumn: isMobile ? "auto" : "1 / span 4" }}>
                <div><b>{selectedPurchasePartner.name}</b><span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280" }}>{selectedPurchasePartner.type || "선택 거래처"}</span></div>
                <button style={grayButton} onClick={handleClearPurchasePartner}>선택 해제</button>
              </div>
            )}
            <input ref={purchasePartnerNameRef} placeholder={selectedPurchasePartner ? "직접 입력은 선택 거래처 해제 후 사용" : "직접 입력 거래처명"} disabled={Boolean(selectedPurchasePartner)} style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 4", background: selectedPurchasePartner ? "#f3f4f6" : "#fff" }} />
            <input ref={purchaseQtyRef} placeholder="구입수량" inputMode="numeric" onChange={() => setPurchaseCalcTick((prev) => prev + 1)} style={{ ...input, width: "100%" }} />
            <select ref={purchaseCurrencyRef} defaultValue="KRW" onChange={(e) => { if (e.target.value === "KRW" && purchaseExchangeRateRef.current) purchaseExchangeRateRef.current.value = ""; setPurchaseCalcTick((prev) => prev + 1); }} style={{ ...input, width: "100%" }}>
              {purchaseCurrencyOptions.map((item) => <option key={item}>{item}</option>)}
            </select>
            <input ref={purchaseForeignUnitPriceRef} placeholder="외화 단가" inputMode="decimal" onChange={() => setPurchaseCalcTick((prev) => prev + 1)} style={{ ...input, width: "100%" }} />
            <input ref={purchaseExchangeRateRef} placeholder="환율" inputMode="decimal" onChange={() => setPurchaseCalcTick((prev) => prev + 1)} style={{ ...input, width: "100%" }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10, marginTop: 12 }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 12 }}><small>외화 총액</small><br /><b>{formatNumber(liveCosts.foreignTotal)} {currency}</b></div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 12 }}><small>당시 환율 기준 총 원화금액</small><br /><b>{formatCostWon(liveCosts.krwAmount)}</b></div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 12 }}><small>개당 구입원가</small><br /><b>{formatCostWon(liveCosts.unitCost)}</b></div>
          </div>

          <textarea ref={purchaseMemoRef} placeholder="메모" style={{ ...input, width: "100%", minHeight: 80, marginTop: 12 }} />
          <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
            <b>첨부파일</b>
            <input ref={purchaseFileRef} type="file" multiple onChange={handlePurchaseFileChange} style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }} />
            {renderManagementAttachmentDrafts(purchaseAttachments, handleRemovePurchaseAttachment)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 8, marginTop: 12 }}>
            <button style={{ ...redButton, padding: 14 }} onClick={handleSavePurchaseRecord}>{editingPurchaseId ? "수입/구입 기록 수정 저장" : "수입/구입 기록 등록"}</button>
            {editingPurchaseId && <button style={grayButton} onClick={resetPurchaseForm}>수정 취소</button>}
          </div>
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : ".8fr .8fr .8fr .8fr 1fr 1.4fr auto auto auto auto", gap: 8 }}>
            <input type="date" value={purchaseFilter.start} onChange={(e) => { setPurchaseFilter((prev) => ({ ...prev, start: e.target.value })); setPurchasePage(1); }} style={{ ...input, width: "100%" }} />
            <input type="date" value={purchaseFilter.end} onChange={(e) => { setPurchaseFilter((prev) => ({ ...prev, end: e.target.value })); setPurchasePage(1); }} style={{ ...input, width: "100%" }} />
            <select value={purchaseFilter.type} onChange={(e) => { setPurchaseFilter((prev) => ({ ...prev, type: e.target.value })); setPurchasePage(1); }} style={{ ...input, width: "100%" }}><option>전체</option><option>수입</option><option>국내구입</option></select>
            <select value={purchaseFilter.currency} onChange={(e) => { setPurchaseFilter((prev) => ({ ...prev, currency: e.target.value })); setPurchasePage(1); }} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              {purchaseCurrencyOptions.map((item) => <option key={item}>{item}</option>)}
            </select>
            <select value={purchaseFilter.partner} onChange={(e) => { setPurchaseFilter((prev) => ({ ...prev, partner: e.target.value })); setPurchasePage(1); }} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              {partnerFilterOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <input ref={purchaseFilterKeywordRef} value={purchaseFilter.keyword} onChange={(e) => { setPurchaseFilter((prev) => ({ ...prev, keyword: e.target.value })); setPurchasePage(1); }} placeholder="상품명, 상품코드, 바코드, 거래처, 메모 검색" style={{ ...input, width: "100%" }} />
            <button style={grayButton} onClick={() => { setPurchaseFilter({ start: "", end: "", type: "전체", currency: "전체", partner: "전체", keyword: "" }); setPurchasePage(1); }}>초기화</button>
            <button style={darkButton} onClick={downloadPurchaseUploadTemplate}>양식 다운로드</button>
            <button style={orangeButton} onClick={() => purchaseImportFileRef.current?.click()}>CSV 업로드</button>
            <button style={blueButton} onClick={downloadPurchaseRecordsCsv}>CSV 다운로드</button>
            <input ref={purchaseImportFileRef} type="file" accept=".csv,text/csv" onChange={handleImportPurchaseCsv} style={{ display: "none" }} />
          </div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 10 }}>전체 {purchaseRecords.length}건 · 조회 {filteredPurchaseRecords.length}건</div>
        </div>

        {filteredPurchaseRecords.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 수입/구입 기록이 없습니다.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {pagedPurchaseRecords.map((record) => {
              const opened = Boolean(openPurchaseRecordIds[record.id]);
              const summary = getPurchaseRecordCostSummary(record);
              const thumbnailUrl = getManagementRecordImageUrl(record);
              const purchaseDateLabel = record.type === "수입" ? "수입일" : "구입일";
              return (
                <div key={record.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "72px 1fr auto", gap: 12, alignItems: "center" }}>
                    <div style={{ width: 72, height: 72, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {thumbnailUrl ? <img src={thumbnailUrl} alt={record.product?.name || "상품 이미지"} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 11, color: "#9ca3af" }}>이미지 없음</span>}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}><b style={{ fontSize: 18 }}>{record.product?.name || "-"}</b><StatusBadge tone={record.type === "수입" ? "orange" : "blue"}>{record.type || "-"}</StatusBadge></div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{purchaseDateLabel} {record.date || "-"} · 거래처 {record.partnerName || "-"} · 수량 {formatNumber(toNumber(record.quantity))}</div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{record.currency || "KRW"} 단가 {formatNumber(toNumber(record.foreignUnitPrice))} · 환율 {formatNumber(summary.exchangeRate)} · 원화금액 {formatCostWon(summary.krwAmount)} · 개당 {formatCostWon(summary.unitCost)}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button style={darkButton} onClick={() => setOpenPurchaseRecordIds((prev) => ({ ...prev, [record.id]: !prev[record.id] }))}>{opened ? "접기" : "펼쳐보기"}</button>
                      <button style={darkButton} onClick={() => handleStartEditPurchaseRecord(record)}>수정</button>
                      <button style={grayButton} onClick={() => handleDeletePurchaseRecord(record.id)}>삭제</button>
                    </div>
                  </div>
                  {opened && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, fontSize: 13 }}>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>외화 총액</small><br /><b>{formatNumber(summary.foreignTotal)} {record.currency}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>환율</small><br /><b>{formatNumber(summary.exchangeRate)}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>당시 환율 기준 총 원화금액</small><br /><b>{formatCostWon(summary.krwAmount)}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>개당 구입원가</small><br /><b>{formatCostWon(summary.unitCost)}</b></div>
                      </div>
                      <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13, lineHeight: 1.7 }}>
                        메모: {record.memo || "-"}
                      </div>
                      <AttachmentList files={record.attachments || []} title="첨부파일" />
                    </div>
                  )}
                </div>
              );
            })}
            {purchaseTotalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
                <button style={safePurchasePage === 1 ? grayButton : darkButton} disabled={safePurchasePage === 1} onClick={() => setPurchasePage((prev) => Math.max(1, prev - 1))}>이전</button>
                {purchasePageNumbers.map((page) => <button key={page} style={page === safePurchasePage ? redButton : grayButton} onClick={() => setPurchasePage(page)}>{page}</button>)}
                <button style={safePurchasePage === purchaseTotalPages ? grayButton : darkButton} disabled={safePurchasePage === purchaseTotalPages} onClick={() => setPurchasePage((prev) => Math.min(purchaseTotalPages, prev + 1))}>다음</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const AdminPartnerPage = () => (
    <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
      <SectionTitle title="거래처관리" desc="입점사, 광고사, 미팅업체 등 거래처 정보를 등록하고 첨부파일을 관리합니다." />

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>거래처 등록</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.4fr 1fr", gap: 10 }}>
          <select ref={partnerTypeRef} defaultValue="입점사" style={{ ...input, width: "100%" }}>
            {partnerTypes.filter((type) => type !== "전체").map((type) => <option key={type}>{type}</option>)}
          </select>
          <input ref={partnerNameRef} placeholder="거래처명" style={{ ...input, width: "100%" }} />
          <input ref={partnerBusinessNumberRef} placeholder="사업자등록번호" style={{ ...input, width: "100%" }} />
          <input ref={partnerCeoRef} placeholder="대표자" style={{ ...input, width: "100%" }} />
          <input ref={partnerContactRef} placeholder="연락처" style={{ ...input, width: "100%" }} />
          <input ref={partnerEmailRef} placeholder="이메일" style={{ ...input, width: "100%" }} />
          <input ref={partnerAddressRef} placeholder="주소" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 3" }} />
          <input ref={partnerManagerRef} placeholder="담당자" style={{ ...input, width: "100%" }} />
          <input ref={partnerManagerContactRef} placeholder="담당자 연락처" style={{ ...input, width: "100%" }} />
          <input ref={partnerManagerEmailRef} placeholder="담당자 이메일" style={{ ...input, width: "100%" }} />
          <textarea ref={partnerNoteRef} placeholder="기타 정보" style={{ ...input, width: "100%", minHeight: 80, gridColumn: isMobile ? "auto" : "1 / span 3" }} />
        </div>

        <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
          <b>첨부파일</b>
          <input ref={partnerFileRef} type="file" multiple onChange={(e) => handlePartnerFileChange(e, "new")} style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }} />
          {partnerAttachments.length > 0 && (
            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {partnerAttachments.map((file) => (
                <div key={file.id} style={{ display: "grid", gridTemplateColumns: isImageFile(file) ? "72px 1fr auto" : "1fr auto", gap: 10, alignItems: "center", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                  {isImageFile(file) && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10 }} />}
                  <span style={{ fontSize: 13, wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} ({formatFileSize(file.size)})</span>
                  <button style={grayButton} onClick={() => handleRemovePartnerAttachment(file.id)}>삭제</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button style={{ ...redButton, width: "100%", padding: 14, marginTop: 12 }} onClick={handleAddPartner}>거래처 등록</button>
      </div>

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.5fr auto auto", gap: 8 }}>
          <select value={partnerFilter.type} onChange={(e) => setPartnerFilter((prev) => ({ ...prev, type: e.target.value }))} style={{ ...input, width: "100%" }}>
            {partnerTypes.map((type) => <option key={type}>{type}</option>)}
          </select>
          <input ref={partnerKeywordRef} defaultValue={partnerFilter.keyword} placeholder="거래처명, 사업자번호, 담당자, 연락처, 이메일 검색" onKeyDown={(e) => { if (e.key === "Enter") setPartnerFilter((prev) => ({ ...prev, keyword: partnerKeywordRef.current?.value || "" })); }} style={{ ...input, width: "100%" }} />
          <button style={darkButton} onClick={() => setPartnerFilter((prev) => ({ ...prev, keyword: partnerKeywordRef.current?.value || "" }))}>검색</button>
          <button style={grayButton} onClick={() => { if (partnerKeywordRef.current) partnerKeywordRef.current.value = ""; setPartnerFilter({ type: "전체", keyword: "" }); }}>초기화</button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
          <div style={{ fontSize: 13, color: "#6b7280" }}>전체 {partners.length}개 · 조회 {filteredPartners.length}개{partnerUploadSummary ? ` · 최근 업로드: ${partnerUploadSummary}` : ""}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={grayButton} onClick={downloadPartnerTemplate}>업로드 양식 다운로드</button>
            <button style={orangeButton} onClick={() => partnerImportFileRef.current?.click()}>CSV 업로드</button>
            <button style={blueButton} onClick={downloadPartnerList}>거래처 CSV 다운로드</button>
            <input ref={partnerImportFileRef} type="file" accept=".csv,text/csv" onChange={handleImportPartnerCsv} style={{ display: "none" }} />
          </div>
        </div>
      </div>

      {filteredPartners.length === 0 ? (
        <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 거래처가 없습니다.</div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {filteredPartners.map((partner) => {
            const isEditing = editingPartnerId === partner.id;
            return (
              <div key={partner.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16 }}>
                {!isEditing ? (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 10, alignItems: "start" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <b style={{ fontSize: 18 }}>{partner.name}</b>
                          <StatusBadge tone="blue">{partner.type || "기타"}</StatusBadge>
                        </div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4, lineHeight: 1.6 }}>
                          {partner.businessNumber || "사업자번호 -"} · 대표자 {partner.ceo || "-"}
                          <br />담당자 {partner.manager || "-"} · {partner.managerContact || partner.contact || "연락처 -"}
                          {(partner.attachments || []).length > 0 && <><br />첨부파일 {(partner.attachments || []).length}개</>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: isMobile ? "stretch" : "flex-end", flexWrap: "wrap" }}>
                        <button style={darkButton} onClick={() => togglePartnerExpanded(partner.id)}>{expandedPartners[partner.id] ? "접기" : "펼쳐보기"}</button>
                        <button style={darkButton} onClick={() => handleStartEditPartner(partner)}>수정</button>
                        <button style={grayButton} onClick={() => handleDeletePartner(partner.id)}>삭제</button>
                      </div>
                    </div>

                    {expandedPartners[partner.id] && (
                      <>
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10, marginTop: 12, fontSize: 13, lineHeight: 1.7 }}>
                          <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>주소</small><br /><b>{partner.address || "-"}</b></div>
                          <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>연락처</small><br /><b>{partner.contact || "-"}</b></div>
                          <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>이메일</small><br /><b>{partner.email || "-"}</b></div>
                          <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>담당자</small><br /><b>{partner.manager || "-"}</b></div>
                          <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>담당자 연락처</small><br /><b>{partner.managerContact || "-"}</b></div>
                          <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>담당자 이메일</small><br /><b>{partner.managerEmail || "-"}</b></div>
                          <div style={{ background: "#fff", borderRadius: 12, padding: 10, gridColumn: isMobile ? "auto" : "1 / span 3" }}><small>기타</small><br /><b>{partner.note || "-"}</b></div>
                        </div>
                        {partner.attachments?.length > 0 && (
                          <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, padding: 10, fontSize: 13 }}>
                            <b>첨부파일</b>
                            {partner.attachments.map((file) => (
                              <div key={file.id || file.name} style={{ marginTop: 8, wordBreak: "break-all", overflowWrap: "anywhere" }}>
                                {isImageFile(file) && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 10, display: "block", marginBottom: 6 }} />}
                                {getFileOpenUrl(file) ? (
                                  <button type="button" onClick={() => openAttachmentFile(file)} style={{ color: "#2563eb", fontWeight: "bold", wordBreak: "break-all", overflowWrap: "anywhere", textDecoration: "underline", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }}>
                                    - {file.name} ({formatFileSize(file.size)})
                                  </button>
                                ) : (
                                  <>- {file.name} ({formatFileSize(file.size)})</>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.4fr 1fr", gap: 10 }}>
                      <select ref={editPartnerTypeRef} defaultValue={partner.type || "기타"} style={{ ...input, width: "100%" }}>
                        {partnerTypes.filter((type) => type !== "전체").map((type) => <option key={type}>{type}</option>)}
                      </select>
                      <input ref={editPartnerNameRef} placeholder="거래처명" style={{ ...input, width: "100%" }} />
                      <input ref={editPartnerBusinessNumberRef} placeholder="사업자등록번호" style={{ ...input, width: "100%" }} />
                      <input ref={editPartnerCeoRef} placeholder="대표자" style={{ ...input, width: "100%" }} />
                      <input ref={editPartnerContactRef} placeholder="연락처" style={{ ...input, width: "100%" }} />
                      <input ref={editPartnerEmailRef} placeholder="이메일" style={{ ...input, width: "100%" }} />
                      <input ref={editPartnerAddressRef} placeholder="주소" style={{ ...input, width: "100%", gridColumn: isMobile ? "auto" : "1 / span 3" }} />
                      <input ref={editPartnerManagerRef} placeholder="담당자" style={{ ...input, width: "100%" }} />
                      <input ref={editPartnerManagerContactRef} placeholder="담당자 연락처" style={{ ...input, width: "100%" }} />
                      <input ref={editPartnerManagerEmailRef} placeholder="담당자 이메일" style={{ ...input, width: "100%" }} />
                      <textarea ref={editPartnerNoteRef} placeholder="기타 정보" style={{ ...input, width: "100%", minHeight: 80, gridColumn: isMobile ? "auto" : "1 / span 3" }} />
                    </div>
                    <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
                      <b>첨부파일 수정</b>
                      <input ref={editPartnerFileRef} type="file" multiple onChange={(e) => handlePartnerFileChange(e, "edit")} style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }} />
                      {partner.attachments?.length > 0 && (
                        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                          {partner.attachments.map((file) => (
                            <div key={file.id || file.name} style={{ display: "grid", gridTemplateColumns: isImageFile(file) ? "72px 1fr auto" : "1fr auto", gap: 10, alignItems: "center", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
                              {file.isImage && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10 }} />}
                              <span style={{ fontSize: 13, wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} ({formatFileSize(file.size)})</span>
                              <button style={grayButton} onClick={() => handleRemovePartnerAttachment(file.id, "edit", partner.id)}>삭제</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                      <button style={grayButton} onClick={() => setEditingPartnerId(null)}>취소</button>
                      <button style={redButton} onClick={() => handleSaveEditPartner(partner.id)}>저장</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const AdminAdPage = () => {
    const endingSoonCount = ads.filter(isAdEndingSoon).length;
    const ongoingCount = ads.filter((ad) => getAdStatus(ad) === "진행중").length;
    const endedCount = ads.filter((ad) => getAdStatus(ad) === "종료").length;
    const toggleAdOpen = (adId) => setOpenAdIds((prev) => ({ ...prev, [adId]: !prev[adId] }));
    const adSummaryCards = [
      { status: "45일 이내 종료", label: "45일 이내 종료 예정", count: endingSoonCount, background: "#fff5f5", border: "#fecaca", color: "#b91c1c" },
      { status: "진행중", label: "진행중 광고", count: ongoingCount, background: "#eff6ff", border: "#bfdbfe", color: "#111827" },
      { status: "종료", label: "종료 광고", count: endedCount, background: "#f9fafb", border: "#e5e7eb", color: "#111827" },
      { status: "전체", label: "전체 광고", count: ads.length, background: "#fffbeb", border: "#fde68a", color: "#111827" },
    ];
    const getAdSummaryStyle = (item) => ({
      ...clickableCardStyle,
      background: item.background,
      border: adFilter.status === item.status ? "2px solid #dc2626" : `1px solid ${item.border}`,
      borderRadius: 16,
      padding: 14,
    });
    const handleAdSummaryCardClick = (status) => {
      if (adKeywordRef.current) adKeywordRef.current.value = "";
      setAdFilter({ partnerId: "전체 업체", status, store: "전체 매장", media: "전체", keyword: "" });
      setOpenAdIds({});
    };

    const FilePreviewList = ({ files, mode = "view", adId = null }) => (
      files?.length > 0 ? (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          {files.map((file) => (
            <div key={file.id || file.name} style={{ display: "grid", gridTemplateColumns: file.isImage && file.previewUrl ? "96px 1fr auto" : mode === "view" ? "1fr" : "1fr auto", gap: 10, alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 10 }}>
              {file.isImage && file.previewUrl && <img src={file.previewUrl} alt={file.name} style={{ width: 96, height: 64, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb" }} />}
              <span style={{ fontSize: 13, wordBreak: "break-all", overflowWrap: "anywhere" }}>{file.name} ({formatFileSize(file.size)})</span>
              {mode !== "view" && <button style={grayButton} onClick={() => handleRemoveAdFile(file.id, mode, adId)}>삭제</button>}
            </div>
          ))}
        </div>
      ) : null
    );

    const partnerOptions = partners.map((partner) => ({ id: partner.id, name: partner.name, type: partner.type }));

    return (
      <div style={{ ...card, padding: isMobile ? 16 : 24 }}>
        <SectionTitle title="광고관리" desc="거래처 DB와 매장 DB를 연결해 광고 계약, 진행매체, 종료 예정일을 관리합니다." />

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          {adSummaryCards.map((item) => (
            <button key={item.status} type="button" style={getAdSummaryStyle(item)} onClick={() => handleAdSummaryCardClick(item.status)} onMouseEnter={handleClickableCardMouseEnter} onMouseLeave={handleClickableCardMouseLeave}>
              <small>{item.label}</small><br /><b style={{ fontSize: 28, color: item.color }}>{item.count}건</b>
            </button>
          ))}
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>광고 등록</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1.4fr 1fr", gap: 10 }}>
            <select ref={adPartnerRef} defaultValue="" style={{ ...input, width: "100%" }}>
              <option value="">업체 선택</option>
              {partnerOptions.map((partner) => <option key={partner.id} value={partner.id}>{partner.name} ({partner.type})</option>)}
            </select>
            <input ref={adNameRef} placeholder="광고명" style={{ ...input, width: "100%" }} />
            <input ref={adEntryStatusRef} placeholder="입점 여부 예: 위탁입점 40%" style={{ ...input, width: "100%" }} />
            <select ref={adStoreRef} defaultValue={stores[0] || ""} style={{ ...input, width: "100%" }}>
              {stores.map((store) => <option key={store}>{store}</option>)}
            </select>
            <div style={{ background: "#fff", border: "1px solid #d1d5db", borderRadius: 12, padding: 10 }}>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: "bold", marginBottom: 8 }}>진행매체</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                {adMediaOptions.map((media) => (
                  <button
                    key={media}
                    type="button"
                    onClick={() => toggleAdMedia(media, "new")}
                    style={{
                      border: selectedAdMedia.includes(media) ? "1px solid #dc2626" : "1px solid #e5e7eb",
                      background: selectedAdMedia.includes(media) ? "#dc2626" : "#f9fafb",
                      color: selectedAdMedia.includes(media) ? "#fff" : "#111827",
                      borderRadius: 10,
                      padding: "10px 6px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: 13,
                    }}
                  >
                    {media}
                  </button>
                ))}
              </div>
            </div>
            <input ref={adSpecRef} placeholder="규격 예: 1978*278mm" style={{ ...input, width: "100%" }} />
            <select ref={adPeriodRef} defaultValue="1개월" style={{ ...input, width: "100%" }}>
              {adPeriodOptions.map((period) => <option key={period}>{period}</option>)}
            </select>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>광고 시작일<input ref={adStartDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>광고 종료일<input ref={adEndDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
            <input ref={adTotalAdCostRef} placeholder="총 광고비" inputMode="numeric" style={{ ...input, width: "100%" }} />
            <input ref={adTotalPaymentRef} placeholder="총 결제 금액" inputMode="numeric" style={{ ...input, width: "100%" }} />
            <textarea ref={adNoteRef} placeholder="비고" style={{ ...input, width: "100%", minHeight: 78 }} />
          </div>

          <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
            <b>광고 타입/위치 파일 첨부</b>
            <input ref={adFileRef} type="file" multiple onChange={(e) => handleAdFileChange(e, "new")} style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }} />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>라이트패널 이미지, POP 시안, 위치 사진 등을 첨부할 수 있습니다.</div>
            <FilePreviewList files={adFiles} mode="new" />
          </div>
          <button style={{ ...redButton, width: "100%", padding: 14, marginTop: 12 }} onClick={handleAddAd}>광고 등록</button>
        </div>

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr 1fr 1fr 1.5fr auto auto", gap: 8 }}>
            <select value={adFilter.partnerId} onChange={(e) => setAdFilter((prev) => ({ ...prev, partnerId: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체 업체</option>
              {partnerOptions.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}
            </select>
            <select value={adFilter.status} onChange={(e) => setAdFilter((prev) => ({ ...prev, status: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              <option>진행중</option>
              <option>종료</option>
              <option>45일 이내 종료</option>
            </select>
            <select value={adFilter.store} onChange={(e) => setAdFilter((prev) => ({ ...prev, store: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체 매장</option>
              {stores.map((store) => <option key={store}>{store}</option>)}
            </select>
            <select value={adFilter.media} onChange={(e) => setAdFilter((prev) => ({ ...prev, media: e.target.value }))} style={{ ...input, width: "100%" }}>
              <option>전체</option>
              <option>외부</option>
              <option>매장</option>
              <option>POP</option>
              <option>온라인</option>
            </select>
            <input ref={adKeywordRef} defaultValue={adFilter.keyword} placeholder="업체, 광고명, 입점 여부, 규격, 비고 검색" onKeyDown={(e) => { if (e.key === "Enter") setAdFilter((prev) => ({ ...prev, keyword: adKeywordRef.current?.value || "" })); }} style={{ ...input, width: "100%" }} />
            <button style={darkButton} onClick={() => setAdFilter((prev) => ({ ...prev, keyword: adKeywordRef.current?.value || "" }))}>검색</button>
            <button style={grayButton} onClick={() => { if (adKeywordRef.current) adKeywordRef.current.value = ""; setAdFilter({ partnerId: "전체 업체", status: "진행중", store: "전체 매장", media: "전체", keyword: "" }); }}>초기화</button>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <div style={{ fontSize: 13, color: "#6b7280" }}>전체 {ads.length}건 · 조회 {filteredAds.length}건</div>
            <button style={blueButton} onClick={downloadAdList}>광고 목록 CSV 다운로드</button>
          </div>
        </div>

        {filteredAds.length === 0 ? (
          <div style={{ background: "#f9fafb", borderRadius: 18, padding: 20, color: "#6b7280" }}>조회된 광고가 없습니다.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {filteredAds.map((ad) => {
              const isEditing = editingAdId === ad.id;
              const daysLeft = getAdDaysLeft(ad);
              const status = getAdStatus(ad);
              const soon = isAdEndingSoon(ad);
              const partnerName = getAdPartnerName(ad.partnerId, ad.partnerName);
              const opened = Boolean(openAdIds[ad.id]);
              return (
                <div key={ad.id} style={{ background: soon ? "#fff5f5" : "#f9fafb", border: soon ? "1px solid #fecaca" : "1px solid #e5e7eb", borderLeft: status === "종료" ? "5px solid #6b7280" : soon ? "5px solid #dc2626" : "5px solid #2563eb", borderRadius: 18, padding: 16 }}>
                  {!isEditing ? (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                          <b style={{ fontSize: 18 }}>{partnerName} · {ad.adName}</b>
                          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{ad.entryStatus || "입점 여부 -"} · {ad.store} · {(ad.media || []).join(" / ")}</div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <StatusBadge tone={status === "종료" ? "blue" : soon ? "red" : "yellow"}>{status}</StatusBadge>
                          <StatusBadge tone={soon ? "red" : "blue"}>{daysLeft === null ? "잔여일 -" : daysLeft < 0 ? `종료 ${Math.abs(daysLeft)}일 경과` : `잔여 ${daysLeft}일`}</StatusBadge>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, marginTop: 12, fontSize: 13, lineHeight: 1.7 }}>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>광고 매장</small><br /><b>{ad.store || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>진행매체</small><br /><b>{(ad.media || []).join(" / ") || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>광고 시작일</small><br /><b>{ad.startDate || "-"}</b></div>
                        <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>광고 종료일</small><br /><b style={{ color: soon ? "#b91c1c" : "#111827" }}>{ad.endDate || "-"}</b></div>
                      </div>
                      {opened && (
                        <>
                          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, marginTop: 12, fontSize: 13, lineHeight: 1.7 }}>
                            <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>규격</small><br /><b>{ad.spec || "-"}</b></div>
                            <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>광고 기간</small><br /><b>{ad.period || "-"}</b></div>
                            <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>총 광고비</small><br /><b>{formatWon(ad.totalAdCost)}</b></div>
                            <div style={{ background: "#fff", borderRadius: 12, padding: 10 }}><small>총 결제 금액</small><br /><b>{formatWon(ad.totalPayment)}</b></div>
                            <div style={{ background: "#fff", borderRadius: 12, padding: 10, gridColumn: isMobile ? "auto" : "1 / span 4" }}><small>비고</small><br /><b>{ad.note || "-"}</b></div>
                          </div>
                          <FilePreviewList files={ad.files || []} mode="view" />
                        </>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                        <button style={darkButton} onClick={() => toggleAdOpen(ad.id)}>{opened ? "접기" : "펼쳐보기"}</button>
                        <button style={darkButton} onClick={() => handleStartEditAd(ad)}>수정</button>
                        <button style={grayButton} onClick={() => handleDeleteAd(ad.id)}>삭제</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1.4fr 1fr", gap: 10 }}>
                        <select ref={editAdPartnerRef} defaultValue={ad.partnerId || ""} style={{ ...input, width: "100%" }}>
                          <option value="">업체 선택</option>
                          {partnerOptions.map((partner) => <option key={partner.id} value={partner.id}>{partner.name} ({partner.type})</option>)}
                        </select>
                        <input ref={editAdNameRef} placeholder="광고명" style={{ ...input, width: "100%" }} />
                        <input ref={editAdEntryStatusRef} placeholder="입점 여부" style={{ ...input, width: "100%" }} />
                        <select ref={editAdStoreRef} defaultValue={ad.store || stores[0] || ""} style={{ ...input, width: "100%" }}>
                          {stores.map((store) => <option key={store}>{store}</option>)}
                        </select>
                        <div style={{ background: "#fff", border: "1px solid #d1d5db", borderRadius: 12, padding: 10 }}>
                          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: "bold", marginBottom: 8 }}>진행매체</div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                            {adMediaOptions.map((media) => (
                              <button
                                key={media}
                                type="button"
                                onClick={() => toggleAdMedia(media, "edit")}
                                style={{
                                  border: editAdMedia.includes(media) ? "1px solid #dc2626" : "1px solid #e5e7eb",
                                  background: editAdMedia.includes(media) ? "#dc2626" : "#f9fafb",
                                  color: editAdMedia.includes(media) ? "#fff" : "#111827",
                                  borderRadius: 10,
                                  padding: "10px 6px",
                                  cursor: "pointer",
                                  fontWeight: "bold",
                                  fontSize: 13,
                                }}
                              >
                                {media}
                              </button>
                            ))}
                          </div>
                        </div>
                        <input ref={editAdSpecRef} placeholder="규격" style={{ ...input, width: "100%" }} />
                        <select ref={editAdPeriodRef} defaultValue={ad.period || "1개월"} style={{ ...input, width: "100%" }}>
                          {adPeriodOptions.map((period) => <option key={period}>{period}</option>)}
                        </select>
                        <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>광고 시작일<input ref={editAdStartDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
                        <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151", fontWeight: "bold" }}>광고 종료일<input ref={editAdEndDateRef} type="date" style={{ ...input, width: "100%" }} /></label>
                        <input ref={editAdTotalAdCostRef} placeholder="총 광고비" inputMode="numeric" style={{ ...input, width: "100%" }} />
                        <input ref={editAdTotalPaymentRef} placeholder="총 결제 금액" inputMode="numeric" style={{ ...input, width: "100%" }} />
                        <textarea ref={editAdNoteRef} placeholder="비고" style={{ ...input, width: "100%", minHeight: 78 }} />
                      </div>
                      <div style={{ marginTop: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
                        <b>광고 파일 수정</b>
                        <input ref={editAdFileRef} type="file" multiple onChange={(e) => handleAdFileChange(e, "edit")} style={{ ...input, width: "100%", background: "#fff", marginTop: 8 }} />
                        <FilePreviewList files={ad.files || []} mode="edit" adId={ad.id} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, auto)", gap: 8, justifyContent: isMobile ? "stretch" : "end", marginTop: 12 }}>
                        <button style={grayButton} onClick={() => setEditingAdId(null)}>취소</button>
                        <button style={redButton} onClick={() => handleSaveEditAd(ad.id)}>저장</button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const AdminPlaceholder = ({ title }) => (
    <div style={card}>
      <SectionTitle title={title} desc="이 메뉴는 다음 단계에서 상세 기능을 연결합니다." />
      <p style={{ color: "#6b7280" }}>현재는 화면 복구용 기본 페이지입니다.</p>
    </div>
  );

  const renderStaffContent = () => {
    if (staffMenu === "대시보드") return <StaffDashboard />;
    if (staffMenu === "공지사항") return NoticePage();
    if (staffMenu === "인수인계") return HandoverPage();
    if (staffMenu === "업무 요청") return WorkRequestPage();
    if (staffMenu === "소모품 주문") return SupplyOrderPage();
    if (staffMenu === "유통기한 관리") return ExpiryPage();
    if (staffMenu === "매장정보") return <StoreInfoPage />;
    if (staffMenu === "내 근무 기록") return <AttendancePage />;
    if (staffMenu === "마이페이지") return <MyPage />;
    return <StaffDashboard />;
  };

  const renderAdminContent = () => {
    if (adminMenu === "대시보드") return AdminDashboard();
    if (adminMenu === "매출조회") return AdminSalesPage();
    if (adminMenu === "직원 계정 관리") return AdminEmployeePage();
    if (adminMenu === "매장 관리") return AdminStorePage();
    if (adminMenu === "매장 매핑 관리") return AdminStoreAliasPage();
    if (adminMenu === "공지 작성 / 관리") return AdminNoticePage();
    if (adminMenu === "직원 근무일지") return AdminAttendancePage();
    if (adminMenu === "업무 요청 관리") return AdminWorkRequestPage();
    if (adminMenu === "소모품 주문 관리") return AdminSupplyOrderPage();
    if (adminMenu === "소모품 DB 관리") return AdminSupplyItemPage();
    if (adminMenu === "인수인계 관리") return AdminHandoverPage();
    if (adminMenu === "상품 DB 관리") return AdminProductDbPage();
    if (adminMenu === "생산관리") return AdminProductionPage();
    if (adminMenu === "수입/구입 관리") return AdminPurchasePage();
    if (adminMenu === "광고관리") return AdminAdPage();
    if (adminMenu === "거래처관리") return AdminPartnerPage();
    return <AdminPlaceholder title={adminMenu} />;
  };

  const activeMenuKey = isLoggedIn ? (userRole === "admin" ? `admin-${adminMenu}` : `staff-${staffMenu}`) : "login";
  const activeMenuContent = isLoggedIn ? (userRole === "admin" ? renderAdminContent() : renderStaffContent()) : null;

  return isLoggedIn ? (
    <div style={{ fontFamily: "Arial, sans-serif", background: "#f4f4f5", minHeight: "100vh", color: "#111827" }}>
      <header style={{ background: "#000", color: "#fff", padding: isMobile ? "16px" : "20px 32px", borderBottom: "4px solid #dc2626", display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 14 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 22 : 26, margin: 0 }}>레드컨테이너 직원 운영 시스템</h1>
          <div style={{ fontSize: 13, color: "#d4d4d8", marginTop: 4 }}>{currentUser} · 권한: {userRole === "admin" ? "관리자" : "직원"}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, auto)", gap: 10 }}>
          <a href="https://cosmostoy.co.kr" target="_blank" rel="noreferrer" style={{ ...darkButton, textDecoration: "none", textAlign: "center" }}>코스모스 쇼핑몰</a>
          <a href="https://redctn.co.kr" target="_blank" rel="noreferrer" style={{ ...darkButton, textDecoration: "none", textAlign: "center" }}>레드컨테이너 쇼핑몰</a>
          <button style={redButton} onClick={handleLogout}>로그아웃</button>
        </div>
      </header>

      <ConfirmModal />

      <main style={{ maxWidth: 1500, margin: "0 auto", padding: isMobile ? 14 : 30 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : userRole === "admin" ? "280px 1fr" : "260px 1fr", gap: 24, alignItems: "start" }}>
          {isMobile && (
            <div style={{ background: "#111827", color: "#fff", padding: 14, borderRadius: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <b>{userRole === "admin" ? "ADMIN MENU" : "STAFF MENU"}</b>
              <button onClick={toggleMenu} style={{ background: "transparent", color: "#fff", fontSize: 24, border: "none", cursor: "pointer" }}>☰</button>
            </div>
          )}

          {(!isMobile || menuOpen) && (
            <aside style={{ background: "#111827", color: "#fff", borderRadius: 24, padding: 18, position: isMobile ? "relative" : "sticky", top: 20 }}>
              <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 14 }}>{userRole === "admin" ? "ADMIN MENU" : "STAFF MENU"}</div>
              {(userRole === "admin" ? adminMenus : staffMenus).map((menu) => (
                <div
                  key={menu}
                  onClick={() => {
                    const isIOSDevice = isIOSLikeDevice();

                    if (!isIOSDevice) {
                      try {
                        const url = window.location.href;

                        window.history.pushState(
                          { rcIntranetMenuGuard: true, menu, step: 1, time: Date.now() },
                          "",
                          url
                        );

                        window.history.pushState(
                          { rcIntranetMenuGuard: true, menu, step: 2, time: Date.now() },
                          "",
                          url
                        );
                      } catch (error) {
                        console.warn("menu history guard 설정 실패:", error);
                      }
                    }

                    if (userRole === "admin") {
                      changeAdminMenu(menu);
                    } else {
                      changeStaffMenu(menu);
                    }

                    setMenuOpen(false);


                  }}
                  style={{ background: (userRole === "admin" ? adminMenu : staffMenu) === menu ? "#dc2626" : "transparent", padding: "14px 16px", borderRadius: 14, marginBottom: 8, cursor: "pointer", border: (userRole === "admin" ? adminMenu : staffMenu) === menu ? "none" : "1px solid rgba(255,255,255,0.08)", fontWeight: (userRole === "admin" ? adminMenu : staffMenu) === menu ? "bold" : "normal" }}
                >
                  {menu}
                </div>
              ))}
            </aside>
          )}

          <div key={activeMenuKey} style={{ minWidth: 0 }}>{activeMenuContent}</div>
        </div>
      </main>
    </div>
  ) : (
    <LoginPage />
  );
}

export default function RedContainerStaffSiteDraft() {
  return (
    <AppErrorBoundary>
      <RedContainerStaffSiteDraftContent />
    </AppErrorBoundary>
  );
}
